import { prepareTableTemplate } from '../utils/tableTemplateCompatibility';
import type { FieldAliasMap } from '../utils/fieldAliases';
import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, memo, useCallback, useContext } from 'react';
import { useVisibilityAwareInterval } from '../hooks/useVisibilityAwareInterval';
import { createPortal } from 'react-dom';
import userSettingsService from '../services/userSettings.service';
import { ToastContext } from '../contexts/ToastContext';
import { activityTracker } from '../services/activity.service';
import { useDisplayPreferences } from '../contexts/DisplayPreferencesContext';
import ExcelJS from 'exceljs';
import { parseCurrencyText } from '../utils/money';
import { parsePercentText, percentNumFmt } from '../utils/percent';
import { exportsAsNumber, numberNumFmt, parseNumberText } from '../utils/numberText';
import { getApiFieldName, getDbFieldName } from '../utils/dynamicColumns';
import { saveBlob } from '../utils/saveFile';
import { hasNoClockTime, toLocalDateKey } from '../utils/dateUtils';
import {
  computeAggregation,
  formatAggValue,
  calcsForType,
  normalizeAggDefault,
  resolveAggColumnType,
  resolveEffectiveCalc,
  resolveShowAggFooter,
  renderedTextLooksLikeMoney,
  type AggCalcSetting,
  type AggColumnType,
  type AggId,
} from '../utils/tableAggregations';
import { AggCalcPicker, AggFooterCell, SigmaIcon } from './TanTableAggFooter';
import { config } from '../config/config';
import { useVirtualizer } from '@tanstack/react-virtual';
import { TanTableFacetList } from './TanTableFacetList';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getGroupedRowModel,
  getExpandedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  flexRender,
  ColumnDef,
  SortingState,
  ColumnFiltersState,
  VisibilityState,
  PaginationState,
  GroupingState,
  ExpandedState,
  ColumnOrderState,
  ColumnSizingState,
  RowSelectionState,
  Header,
} from '@tanstack/react-table';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  Squares2X2Icon,
  FunnelIcon,
  LockClosedIcon,
  LockOpenIcon,
  PencilIcon,
  PlusIcon,
  Cog6ToothIcon,
  CalendarIcon,
  TrashIcon,
  ArchiveBoxIcon,
  Bars3BottomLeftIcon,
  CalculatorIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentListIcon,
  PencilSquareIcon,
  EyeSlashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  NoSymbolIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { FunnelIcon as FunnelIconSolid } from '@heroicons/react/24/solid';
import {
  resolveDateColumns,
  toDateFacetValue,
  normalizeDateSelection,
  buildDateTree,
  filterTreeBySearch,
  leafKeys,
  nodeCheckState,
  buildColumnValueReaders,
  type DateTreeNode,
  type NodeCheckState,
} from './tanTableDateFacets';

const TextFilterIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M4.5 2H2v2.5" />
    <path d="M19.5 2H22v2.5" />
    <path d="M4.5 22H2v-2.5" />
    <path d="M19.5 22H22v-2.5" />
    <path d="M7 8.5h10M12 8.5v7" strokeWidth="2" />
  </svg>
);
import TableIcon from './icons/TableIcon';
import TabGroupIcon from './icons/TabGroupIcon';
import { Tooltip } from './ui/Tooltip';
import { WarningConfirmModal } from './ui/WarningConfirmModal';
import { FieldNameBadge } from './FieldNameBadge';
import { ProTableCell } from './ProTableCell';
import { VIEWED_BLUE_GROUP } from '../contexts/ViewedProsContext';
import DateTimePicker from './DateTimePicker';
// A selectable field for the column insert picker.
interface AddableField { id: string; label: string; description?: string; expands?: boolean }
import PermissionsContainer from './PermissionsContainer';
import TableActionBar, { type BulkAction, ActionBarToggleIcon } from './TableActionBar';
import ModalHeader from './ui/ModalHeader';
import { useModalDrag } from '../hooks/useModalDrag';
import { SaveTemplateModal, LoadTemplateModal, SaveViewModal, ManageViewsModal } from './TableTemplateModals';
import { splitPastedListValues } from '../utils/customOrderSearch';

/** Column id of the opt-in row-selection checkbox column (`enableRowSelection`).
 *  Exported so a host can place it via `lockedColumns` — e.g.
 *  `{ id: SELECT_COLUMN_ID, side: 'right' }` pins the checkboxes to the right edge
 *  instead of their default leading position. */
export const SELECT_COLUMN_ID = '__select__';

// Global flag to track column drag state (to prevent parent drag handlers from interfering)
let isColumnDragging = false;

// Export a function to check if column is being dragged
export const getIsColumnDragging = () => isColumnDragging;

// Stable empty array to prevent unnecessary re-renders when tabs aren't used
const EMPTY_TAB_ARRAY: string[] = [];

// Stable empty array default for optional string[] props whose identity feeds a memo
// (an inline `= []` default would re-derive the memo on every render).
const EMPTY_STRING_ARRAY: string[] = [];

// Excel Export Icon Component
/* ---------------------------------------------------------------------------
   ICON SIZING

   Every toolbar button renders its glyph at `h-4 w-4` — a 16px box. That sizes the
   BOX, not the drawing inside it, and the two are not the same thing: heroicons are
   drawn with padding built into their 24-unit grid, so their ink fills roughly 63-75%
   of the box (10-12px). Art pulled from icon sites is usually drawn edge-to-edge, so
   at the same `h-4 w-4` it renders the full 16px and towers over its neighbours. The
   toolbar had icons ranging from 4px to 16px of ink in identical 16px boxes.

   Fix per icon type:
     * fill icons  — pad the viewBox so the ink lands at ~72%. The path is untouched,
       so the artwork cannot distort; only the framing changes.
     * stroke icons — drawn here by hand, so the coordinates themselves are set to span
       the same band, at strokeWidth 1.5 to match the heroicons' weight.

   The HIGHLIGHT behind an icon is a separate guarantee and does not depend on the art at
   all: every toolbar icon button is `inline-flex items-center justify-center p-1 rounded`
   around a 16px icon, so the active background is 24x24 everywhere. The flex centring is
   what makes that exact — a bare inline <svg> is baseline-aligned, so without it the
   button's height came from the line box (line-height plus the descender gap under the
   baseline) rather than from the icon, and two buttons with identical padding and
   identical 16px icons could still come out different heights.

   Adding an icon: use that button class verbatim, and check what fraction of the new
   icon's viewBox the art actually occupies before trusting `h-4 w-4` to make it match.

   Where to look: the toolbar's icons come from three places, and the first sweep of this
   missed two of them — heroicons (already in band), the components defined in this file
   (ExcelIcon) and in TanTableAggFooter / TableActionBar, and `./icons/*` (TableIcon,
   TabGroupIcon).

   HOW to audit, learned the hard way over three bad passes: parse the whole <button>
   element. Scanning N lines down from `<button` silently skips any button whose onClick
   is a multi-line handler — className lands past the window, so the button reads as
   "not an icon button" and is missed by the fix AND by the check meant to catch the fix
   missing it. That is what left the column-filter and density buttons unsquared through
   two rounds of "verified clean". Match on what the element RENDERS, never on distance
   from a line.
   --------------------------------------------------------------------------- */

/* Padded viewBox — see ICON SIZING above. The source art draws edge-to-edge (ink spans
   the full 0..24), so at h-4 w-4 it rendered 16px tall against ~10-12px for the
   heroicons beside it in the toolbar. The padding is on the viewBox, not the path, so
   the artwork is untouched. */
const ExcelIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    id="Layer_1"
    data-name="Layer 1"
    viewBox="-4.67 -4.67 33.33 33.33"
    width="512"
    height="512"
    className={className}
  >
    <path
      fill="currentColor"
      d="M14.41,0H5c-1.65,0-3,1.35-3,3V24H22V7.59L14.41,0Zm.59,3.41l3.59,3.59h-3.59V3.41Zm5,18.59H4V3c0-.55,.45-1,1-1H13v7h7v13ZM12,13.59l1.6-2.59h2.35l-2.77,4.5,2.77,4.5h-2.35l-1.6-2.59-1.6,2.59h-2.35l2.77-4.5-2.77-4.5h2.35l1.6,2.59Z"
    />
  </svg>
);

// Global ("tan table" search box) filter: case-insensitive substring match on a
// column's value — matching the previous 'auto' behavior — PLUS a hidden
// "{columnId}_full" companion on the row when one exists. That lets a column
// shown as a short alias (e.g. a location's cyalias) still be found by its full
// name, which rides along in "{field}_full". Columns without a companion behave
// exactly as before. Module-scope so its identity stays stable across renders
// (a new identity each render would re-run filtering on every render).
const globalSearchFilterFn = (row: any, columnId: string, filterValue: any): boolean => {
  const fv = String(filterValue ?? '').toLowerCase().trim();
  if (!fv) return true;
  const val = row.getValue(columnId);
  if (val != null && String(val).toLowerCase().includes(fv)) return true;
  const full = row.original?.[`${columnId}_full`];
  return full != null && String(full).toLowerCase().includes(fv);
};

// Collapse faceted-filter display values case-insensitively so values that differ
// only by letter case (e.g. "PARIS" / "paris") become ONE checkbox instead of two.
// Each bucket is labeled with its most-frequent original casing (first-seen wins
// ties, since Map preserves insertion order and we only replace on a strictly
// greater count) and its count is the sum across casings. The result is sorted by
// label with `numeric: true`, so numeric-looking strings sort by magnitude ("2"
// before "10") — fixing filter-dropdown ordering on Miles / count-style columns
// without disturbing alphabetic sorts. Sentinels like "(Blank)"/"(None)" have a
// single fixed casing, so they pass through unchanged. Module-scope + exported so
// the merge logic stays unit-testable in isolation.
// Reuse the numeric collator; localeCompare with options rebuilds it on every
// comparison, which dominates opening high-cardinality BOL/Record filters.
const facetCollator = new Intl.Collator(undefined, { numeric: true });

export const mergeFacetValuesCaseInsensitive = (
  displayValues: string[],
): { value: string; count: number }[] => {
  const buckets = new Map<string, { total: number; casings: Map<string, number> }>();
  for (const valueStr of displayValues) {
    const key = valueStr.toLowerCase();
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = { total: 0, casings: new Map() };
      buckets.set(key, bucket);
    }
    bucket.total += 1;
    bucket.casings.set(valueStr, (bucket.casings.get(valueStr) || 0) + 1);
  }
  return Array.from(buckets.values())
    .map(({ total, casings }) => {
      let label = '';
      let best = -1;
      casings.forEach((count, casing) => {
        if (count > best) {
          best = count;
          label = casing;
        }
      });
      return { value: label, count: total };
    })
    .sort((a, b) => facetCollator.compare(a.value, b.value));
};

// ── "Match List" column filter ──────────────────────────────────────────────────
//
// A pasted list of values (pros, invoice numbers) narrows a column instead of the
// user ticking facet checkboxes one at a time. It reuses the dropdown filter's
// stored shape — a string[] handed to column.setFilterValue — and its filter fns,
// so the matching engine is unchanged; what is new is how the values are collected
// and that the MISSES are reported back.
//
// It is a distinct filter TYPE rather than values injected into the dropdown's
// array because a pasted list contains values absent from the facet set, and the
// check-all / uncheck-all helpers deliberately assume selection ⊆ facets (see
// fetchDistinctValues). Sharing the type would let one "Check All" click silently
// rewrite the user's list.

/** How a column's filter collects its values. `list`/`listExclude` are the pasted-list
 *  modes; `listExclude` and `exclusion` both NEGATE their selection. */
export type ColumnFilterType = 'dropdown' | 'text' | 'exclusion' | 'list' | 'listExclude' | 'number';

// ── "Number" column filter ──────────────────────────────────────────────────────
//
// Narrows a column by magnitude — "< 54000", "between 54000 and 61000" — instead of
// by membership. It is its own filter TYPE with its own stored shape (an operator and
// up to two bounds) because none of the membership modes can express a range, and
// parsing operators out of the text box would make "-" mean three different things.
// Values are compared after coercion (see toFilterNumber), so a column that renders
// "54,000 lbs" filters the same way as one holding the bare number.

export type NumberFilterOp = 'lt' | 'lte' | 'gt' | 'gte' | 'eq' | 'neq' | 'between' | 'empty' | 'notEmpty';

export interface NumberFilterValue {
  op: NumberFilterOp;
  a?: number;
  b?: number;
}

/** Operators in the order the popover lists them, with their labels. */
export const NUMBER_FILTER_OPS: ReadonlyArray<{ op: NumberFilterOp; label: string }> = [
  { op: 'lt', label: 'Less than' },
  { op: 'lte', label: 'Less than or equal to' },
  { op: 'gt', label: 'Greater than' },
  { op: 'gte', label: 'Greater than or equal to' },
  { op: 'eq', label: 'Equal to' },
  { op: 'neq', label: 'Not equal to' },
  { op: 'between', label: 'Between' },
  { op: 'empty', label: 'Is blank' },
  { op: 'notEmpty', label: 'Is not blank' },
];

const NUMBER_FILTER_OP_SET = new Set<string>(NUMBER_FILTER_OPS.map(o => o.op));

/** Operators that need a bound (`a`), and the one that needs two. */
export const numberFilterNeedsBound = (op: NumberFilterOp): boolean => op !== 'empty' && op !== 'notEmpty';
export const numberFilterNeedsSecondBound = (op: NumberFilterOp): boolean => op === 'between';

/** Coerce a cell to a number for comparison. Reads the first numeric token out of a
 *  formatted string ("54,000 lbs" → 54000, "$1,234.50" → 1234.5). Blank, null and
 *  non-numeric cells are NULL, never zero: an order with no weight on file must not
 *  land under "less than 54,000". */
export const toFilterNumber = (cell: unknown): number | null => {
  if (typeof cell === 'number') return Number.isFinite(cell) ? cell : null;
  if (typeof cell !== 'string') return null;
  const match = cell.match(/-?\d[\d,]*(?:\.\d+)?|-?\.\d+/);
  if (!match) return null;
  const n = Number(match[0].replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
};

/** The stored shape of a number-mode filter, as opposed to the string[] of the
 *  membership modes or the string of the text mode. */
export const isNumberFilterValue = (value: unknown): value is NumberFilterValue =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
  && NUMBER_FILTER_OP_SET.has((value as { op?: unknown }).op as string);

/** Does this cell satisfy the filter? A filter missing a bound it needs matches
 *  everything — a stale saved value must not blank the grid. */
export const numberFilterMatches = (cell: unknown, filter: NumberFilterValue): boolean => {
  const n = toFilterNumber(cell);
  switch (filter.op) {
    case 'empty': return n === null;
    case 'notEmpty': return n !== null;
    default: break;
  }
  const a = filter.a;
  if (typeof a !== 'number') return true;
  if (filter.op === 'between') {
    const b = filter.b;
    if (typeof b !== 'number') return true;
    if (n === null) return false;
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    return n >= lo && n <= hi;
  }
  if (n === null) return false;
  switch (filter.op) {
    case 'lt': return n < a;
    case 'lte': return n <= a;
    case 'gt': return n > a;
    case 'gte': return n >= a;
    case 'eq': return n === a;
    case 'neq': return n !== a;
    default: return true;
  }
};

const numberFilterFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 4 });

/** The short form shown in the header filter cell: "< 54,000", "54,000 – 61,000". */
export const formatNumberFilterSummary = (filter: NumberFilterValue): string => {
  const fmt = (n: number | undefined) => (typeof n === 'number' ? numberFilterFormatter.format(n) : '?');
  switch (filter.op) {
    case 'lt': return `< ${fmt(filter.a)}`;
    case 'lte': return `≤ ${fmt(filter.a)}`;
    case 'gt': return `> ${fmt(filter.a)}`;
    case 'gte': return `≥ ${fmt(filter.a)}`;
    case 'eq': return `= ${fmt(filter.a)}`;
    case 'neq': return `≠ ${fmt(filter.a)}`;
    case 'between': {
      const a = filter.a; const b = filter.b;
      if (typeof a === 'number' && typeof b === 'number') return `${fmt(Math.min(a, b))} – ${fmt(Math.max(a, b))}`;
      return `${fmt(a)} – ${fmt(b)}`;
    }
    case 'empty': return '(blank)';
    case 'notEmpty': return '(not blank)';
    default: return '';
  }
};

/** The popover's draft: the operator plus the raw input strings, kept per column so
 *  reopening the popover shows what was typed. */
interface NumberFilterDraft { op: NumberFilterOp; a: string; b: string }

const DEFAULT_NUMBER_DRAFT: NumberFilterDraft = { op: 'lt', a: '', b: '' };

/** Rebuild a draft from a stored value (e.g. a filter restored from saved settings). */
const numberDraftFromValue = (value: unknown): NumberFilterDraft | undefined => {
  if (!isNumberFilterValue(value)) return undefined;
  return {
    op: value.op,
    a: typeof value.a === 'number' ? String(value.a) : '',
    b: typeof value.b === 'number' ? String(value.b) : '',
  };
};

/** Turn a draft into the stored value, or undefined while it is incomplete so no
 *  half-typed filter is ever applied. */
const numberValueFromDraft = (draft: NumberFilterDraft): NumberFilterValue | undefined => {
  if (!numberFilterNeedsBound(draft.op)) return { op: draft.op };
  const a = draft.a.trim() === '' ? null : toFilterNumber(draft.a);
  if (a === null) return undefined;
  if (!numberFilterNeedsSecondBound(draft.op)) return { op: draft.op, a };
  const b = draft.b.trim() === '' ? null : toFilterNumber(draft.b);
  if (b === null) return undefined;
  return { op: draft.op, a, b };
};

/** The pasted-list modes — the ones whose popover is a textarea, not a facet list. */
export const isListFilterType = (type: ColumnFilterType): boolean =>
  type === 'list' || type === 'listExclude';

/** The modes whose stored selection means "hide these" rather than "show these".
 *  Every site that special-cases `exclusion` must use this, or a list-exclude filter
 *  silently behaves as an include — the tab-count matcher, which re-implements row
 *  matching independently of the filter fns, is the easy one to miss. */
export const isExcludeFilterType = (type: ColumnFilterType): boolean =>
  type === 'exclusion' || type === 'listExclude';

/** Parse a pasted blob into the list to match on: split + cleaned by the shared V1
 *  rules, then de-duplicated case-insensitively (first casing wins).
 *
 *  Dedupe is case-INSENSITIVE because matching is: keeping "ABC" and "abc" as two
 *  entries would inflate the denominator of "31 of 50 matched" with a value that was
 *  never a separate query. */
export const parseListFilterValues = (raw: string): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of splitPastedListValues(raw)) {
    const key = value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }
  return out;
};

/** Partition a pasted list against the values actually present in this column's
 *  current view, so the user can be told what did NOT land.
 *
 *  This is the whole point of the mode: a filter on its own answers "here are 31
 *  rows" and says nothing about the other 19, while the question being asked is "are
 *  all 50 of mine here?". `presentValues` are the column's FACET values, which
 *  TanStack computes with every other filter applied but not this column's own — so
 *  the answer is scoped to the view the user is looking at and is stable whether the
 *  mode is include or exclude.
 *
 *  Unmatched values keep the user's own casing and order: the list is meant to be
 *  copied straight back out into another view. */
export const reconcileListValues = (
  values: string[],
  presentValues: string[],
): { matched: string[]; unmatched: string[] } => {
  const present = new Set(presentValues.map(v => v.toLowerCase()));
  const matched: string[] = [];
  const unmatched: string[] = [];
  for (const value of values) {
    (present.has(value.toLowerCase()) ? matched : unmatched).push(value);
  }
  return { matched, unmatched };
};

// Case-insensitive membership test for faceted-filter selections. Facet values are
// bucketed and displayed by their most-frequent casing (see above), so a selected
// label like "PARIS" must still match rows whose cell reads "paris". Sentinels are
// compared the same way — harmless since they have a single fixed casing. Exported
// for unit tests.
// Filter state arrays are immutable. Cache membership by identity so unchecking
// one value in a large facet doesn't scan the remaining selection for every row.
const facetSelectionSets = new WeakMap<string[], Set<string>>();
export const facetSelectionIncludes = (selected: string[], displayValue: string): boolean => {
  let values = facetSelectionSets.get(selected);
  if (!values) {
    values = new Set(selected.map(value => value.toLowerCase()));
    facetSelectionSets.set(selected, values);
  }
  return values.has(displayValue.toLowerCase());
};

// Truncated Cell Component - shows tooltip when content is truncated
const TruncatedCell = ({ children, cellValue, disableTooltip }: { children: React.ReactNode; cellValue: any; disableTooltip?: boolean }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useEffect(() => {
    const checkTruncation = () => {
      if (ref.current) {
        setIsTruncated(ref.current.scrollWidth > ref.current.clientWidth);
      }
    };
    // Check after a brief delay to ensure layout is complete
    const timeoutId = setTimeout(checkTruncation, 50);
    // Re-check on resize
    window.addEventListener('resize', checkTruncation);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkTruncation);
    };
  }, [children]);

  const tooltipContent = cellValue != null ? String(cellValue) : '';

  if (isTruncated && tooltipContent && !disableTooltip) {
    return (
      <Tooltip content={tooltipContent} placement="top">
        <div ref={ref} className="truncate min-w-0 flex-1">
          {children}
        </div>
      </Tooltip>
    );
  }

  return (
    <div ref={ref} className="truncate min-w-0 flex-1">
      {children}
    </div>
  );
};

// Column Context Menu Component
const ColumnContextMenu = ({
  x,
  y,
  column,
  isPinnedLeft,
  isPinnedRight,
  isLocked,
  onCopyColumn,
  onCopyAllRows,
  onCopyColumnCommaDelimited,
  onCopyAllRowsCommaDelimited,
  onSortAsc,
  onSortDesc,
  onTogglePinLeft,
  onTogglePinRight,
  isBold,
  onToggleBold,
  onHide,
  onFilterByList,
  canEdit,
  isEditing,
  onToggleEdit,
  portalContainer,
  allowPin = true,
}: {
  x: number;
  y: number;
  column: any;
  isPinnedLeft: boolean;
  isPinnedRight: boolean;
  isLocked: boolean;
  onCopyColumn: () => void;
  onCopyAllRows: () => void;
  onCopyColumnCommaDelimited: () => void;
  onCopyAllRowsCommaDelimited: () => void;
  onSortAsc: () => void;
  onSortDesc: () => void;
  onTogglePinLeft: () => void;
  onTogglePinRight: () => void;
  /** This column's data cells currently render bold. */
  isBold: boolean;
  onToggleBold: () => void;
  onHide: () => void;
  /** Switch this column to the pasted-list filter and open its paste box. Absent when
   *  the column cannot be filtered at all. */
  onFilterByList?: () => void;
  /** The host offers inline editing on this column (see canEditColumn). */
  canEdit?: boolean;
  isEditing?: boolean;
  onToggleEdit?: () => void;
  portalContainer?: HTMLElement | null;
  allowPin?: boolean;
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // Adjust position to keep menu within viewport
  const [adjustedPos, setAdjustedPos] = useState({ x, y });
  useEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const newX = x + rect.width > window.innerWidth ? window.innerWidth - rect.width - 8 : x;
      const newY = y + rect.height > window.innerHeight ? window.innerHeight - rect.height - 8 : y;
      setAdjustedPos({ x: newX, y: newY });
    }
  }, [x, y]);

  const menuItemClass = 'flex items-center gap-2 w-full px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer whitespace-nowrap';
  const disabledClass = 'flex items-center gap-2 w-full px-3 py-1.5 text-sm text-gray-400 dark:text-gray-500 cursor-not-allowed whitespace-nowrap';
  const separatorClass = 'border-t border-rule my-1';

  const canSort = column.getCanSort();

  return createPortal(
    <div
      ref={menuRef}
      className="fixed bg-panel border border-rule rounded-lg shadow-lg z-[9999] py-1 min-w-[180px]"
      style={{ left: adjustedPos.x, top: adjustedPos.y }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Inline editing — first, because it is the reason a user opens this menu on an
          editable column, and it is the only entry here that CHANGES data. Offered
          only where the host says the column supports it. */}
      {canEdit && (
        <>
          <button className={menuItemClass} onClick={onToggleEdit}>
            <PencilSquareIcon className="h-4 w-4" />
            {isEditing ? 'Stop Editing' : 'Edit Mode'}
          </button>
          <div className={separatorClass} />
        </>
      )}

      {/* Copy Actions */}
      <button className={menuItemClass} onClick={onCopyColumn}>
        <ClipboardDocumentIcon className="h-4 w-4" />
        Copy Column
      </button>
      <button className={menuItemClass} onClick={onCopyAllRows}>
        <ClipboardDocumentIcon className="h-4 w-4" />
        Copy Column (All Rows)
      </button>
      <button className={menuItemClass} onClick={onCopyColumnCommaDelimited}>
        <ClipboardDocumentIcon className="h-4 w-4" />
        Copy Column (Comma Delimited)
      </button>
      <button className={menuItemClass} onClick={onCopyAllRowsCommaDelimited}>
        <ClipboardDocumentIcon className="h-4 w-4" />
        Copy Column (All Rows, Comma Delimited)
      </button>

      {/* Sits with the copy actions on purpose: "copy this column out" and "paste a
          list back in" are the two halves of the same errand, and this menu is where a
          user already comes looking for the first one. */}
      {onFilterByList && (
        <button className={menuItemClass} onClick={onFilterByList}>
          <ClipboardDocumentListIcon className="h-4 w-4" />
          Filter by Pasted List
        </button>
      )}

      <div className={separatorClass} />

      {/* Sort Actions */}
      <button className={canSort ? menuItemClass : disabledClass} onClick={canSort ? onSortAsc : undefined} disabled={!canSort}>
        <ArrowUpIcon className="h-4 w-4" />
        Sort Ascending
      </button>
      <button className={canSort ? menuItemClass : disabledClass} onClick={canSort ? onSortDesc : undefined} disabled={!canSort}>
        <ArrowDownIcon className="h-4 w-4" />
        Sort Descending
      </button>

      <div className={separatorClass} />

      {/* Pin Actions */}
      {!isLocked && allowPin && (
        <>
          <button className={menuItemClass} onClick={onTogglePinLeft}>
            {isPinnedLeft ? <LockOpenIcon className="h-4 w-4" /> : <LockClosedIcon className="h-4 w-4" />}
            {isPinnedLeft ? 'Unpin Left' : 'Pin Left'}
          </button>
          <button className={menuItemClass} onClick={onTogglePinRight}>
            {isPinnedRight ? <LockOpenIcon className="h-4 w-4" /> : <LockClosedIcon className="h-4 w-4" />}
            {isPinnedRight ? 'Unpin Right' : 'Pin Right'}
          </button>
        </>
      )}

      {/* Bold the column's values. Display-only, so unlike Pin/Hide it is offered on
          locked columns too. */}
      <button className={menuItemClass} onClick={onToggleBold}>
        <Bars3BottomLeftIcon className="h-4 w-4" />
        {isBold ? 'Normal Values' : 'Bold Values'}
      </button>

      {/* Hide Column */}
      {!isLocked && (
        <button className={menuItemClass} onClick={onHide}>
          <EyeSlashIcon className="h-4 w-4" />
          Hide Column
        </button>
      )}
    </div>,
    portalContainer || document.body
  );
};

// Column id (== accessorKey) used for Pro # columns across the app. Ctrl/Cmd+clicking
// a cell in this column collects the value into the ProCollectorPopover below.
const PRO_COLUMN_ID = 'pronumber';

// Injected/structural column ids (selection checkbox, expander, actions) — these
// never map to a card field, so card mode's visibleColumnIds excludes them.
const SPECIAL_COLUMN_IDS = new Set(['__select__', 'select', 'expander', 'actions']);

// Floating, draggable panel listing the Pro #s collected via Ctrl/Cmd+click.
// Opens centered; drag the header to move it. No backdrop, so the user can keep
// Ctrl/Cmd+clicking pro cells while it's open. Dismissed by Escape or a plain
// (non-modifier) click outside; modifier-clicks are ignored so collecting more
// pros keeps it open.
const ProCollectorPopover = ({
  pros,
  onClear,
  onCopy,
  onOpenV1,
  onClose,
  portalContainer,
}: {
  pros: string[];
  onClear: () => void;
  onCopy: () => void;
  onOpenV1: () => void;
  onClose: () => void;
  portalContainer?: HTMLElement | null;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  // null position => centered via CSS; once dragged, an absolute {x, y}.
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const dragRef = useRef<{ dx: number; dy: number } | null>(null);

  // Dismiss on Escape or a plain click outside; a modifier-click is a "collect"
  // gesture and must keep the panel open.
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (e.ctrlKey || e.metaKey) return;
      if (ref.current && e.target instanceof Node && ref.current.contains(e.target)) return;
      onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  // Drag handling — the header is the grab handle.
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      setPos({ x: e.clientX - dragRef.current.dx, y: e.clientY - dragRef.current.dy });
    };
    const onUp = () => { dragRef.current = null; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []);

  const startDrag = (e: React.MouseEvent) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    dragRef.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    setPos({ x: rect.left, y: rect.top }); // switch from centered to absolute at the current spot
    e.preventDefault();
  };

  const btnClass = 'flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors';
  const positionStyle: React.CSSProperties = pos
    ? { left: pos.x, top: pos.y }
    : { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-label="Selected pros"
      className="fixed bg-panel border border-rule rounded-lg shadow-2xl z-[9999] w-[300px]"
      style={positionStyle}
    >
      <div
        onMouseDown={startDrag}
        className="flex items-center justify-between px-3 py-2 border-b border-rule cursor-move select-none rounded-t-lg bg-gray-50 dark:bg-gray-700/50"
      >
        <span className="text-xs font-semibold text-muted">
          {pros.length} {pros.length === 1 ? 'pro' : 'pros'} selected
        </span>
        <button
          onMouseDown={(e) => e.stopPropagation()}
          onClick={onClose}
          className="p-0.5 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
          aria-label="Close"
          title="Close"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
      <div className="p-3">
        <div className="max-h-40 overflow-y-auto text-sm text-body break-words mb-3">
          {pros.join(', ')}
        </div>
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onClear}
            className={`${btnClass} text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700`}
          >
            <TrashIcon className="h-4 w-4" />
            Clear
          </button>
          <button
            onClick={onCopy}
            className={`${btnClass} text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700`}
          >
            <ClipboardDocumentIcon className="h-4 w-4" />
            Copy
          </button>
          <button
            onClick={onOpenV1}
            className={`${btnClass} bg-blue-600 hover:bg-blue-700 text-white`}
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            Open in V1
          </button>
        </div>
      </div>
    </div>,
    portalContainer || document.body
  );
};

interface FilterCondition {
  id: number;
  field: string;
  compare: string;
  values: string;
}

export interface TabData<T = any> {
  id: string;
  label: string;
  filterFn?: (row: T) => boolean;
}

export interface LockedColumn {
  id: string;
  side?: 'left' | 'right';
}

export interface BulkEditMeta {
  type: 'text' | 'number' | 'dropdown' | 'checkbox' | 'date';
  options?: { value: string; label: string }[];
}

interface TanTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  loading?: boolean;
  // When set (and not loading), the table shows a "couldn't load" state instead
  // of the generic empty message — so a failed fetch is distinguishable from a
  // genuinely empty result. onRetry, if provided, renders a Retry button.
  errorMessage?: string | null;
  onRetry?: () => void;
  settingsKey?: string;
  searchPlaceholder?: string;
  className?: string;
  /** Fired for a click anywhere on a data row (the event is passed so a
   *  consumer can ignore clicks that landed on the row's own controls). */
  onRowClick?: (row: T, event?: React.MouseEvent<HTMLElement>) => void;
  getRowClassName?: (row: T, meta: { zebra: boolean }) => string;
  /** Native hover tooltip for a data row (not applied to group rows). */
  getRowTitle?: (row: T) => string | undefined;
  /** Extra classes for one data cell's <td> (e.g. an inset ring to mark an
   *  unsaved edit). Not applied to group rows. */
  getCellClassName?: (row: T, columnId: string) => string;
  /**
   * A host-supplied bar under the toolbar, collapsed behind its own toggle icon —
   * the same affordance as the action bar, and rendered just above it.
   *
   * For controls that change WHAT THE TABLE FETCHES, which is why they can't live in
   * the action bar (that one is for acting on rows already fetched) and shouldn't be
   * always-visible chrome in a short dashboard tile. The Documents view uses it for
   * the date-range / doc-type / revision request filters.
   */
  subHeaderBar?: {
    /** Toolbar toggle glyph. */
    icon: React.ReactNode;
    /** Tooltip + aria wording, e.g. "Document Filters". */
    label: string;
    content: React.ReactNode;
    /** Open on first render. */
    defaultOpen?: boolean;
    /** Marks the toggle when the bar holds a non-default value, so a filter that is
     *  narrowing the table is visible while the bar is collapsed. */
    active?: boolean;
  };
  /** Opt-in "viewed PRO" tracking for one or more columns. Cells in the named
   *  column(s) get a green pill once clicked, and a green ring on the single
   *  most-recently clicked PRO (the ring moves as the user clicks others). State
   *  is shared page-wide via ViewedProsContext — the page must mount
   *  <ViewedProsProvider>. Pass a column id/accessorKey, or an array of them for
   *  tables with several PRO columns (e.g. domestic/import/return pro). */
  trackViewedColumnId?: string | string[];
  /** Derives the tracked identity from a row when it differs from the tracked
   *  column's displayed value — e.g. a "pro-leg" cell (4990454-1) tracked by the
   *  bare pronumber so all legs of a viewed PRO highlight together. Defaults to
   *  the tracked column's cell value. */
  getViewedKey?: (row: T) => string | number | null | undefined;
  /** Same click-to-highlight tracking as `trackViewedColumnId`, but rendered in
   *  light blue and tracked in its OWN namespace — so a table can track two
   *  different things at once (e.g. PRO in green + Ticket # in blue) without the
   *  two colliding or stealing each other's last-clicked ring. */
  trackViewedBlueColumnId?: string | string[];
  /** `getViewedKey` for the blue-tracked column(s). */
  getViewedBlueKey?: (row: T) => string | number | null | undefined;
  /** Notified with the current global search term whenever it changes (empty
   *  string when cleared). Lets a parent reveal rows it had filtered out that
   *  match the active search. */
  onSearchChange?: (term: string) => void;
  getGroupedRowClassName?: (groupValue: any, columnId: string, subRows: T[]) => string;
  enableGrouping?: boolean;
  groupableColumns?: string[];
  /** Optional summary after each group. Receives filtered members; included in Excel export.
   * Footer rows belong to the view, never the source data or TanStack sorting/filtering. */
  groupFooter?: (groupValue: unknown, columnId: string, members: T[]) => string | null;
  defaultGrouping?: string[];  // Default columns to group by on initial load
  defaultSorting?: SortingState;  // Default sorting on initial load
  /** Notified after each user sort change with the resolved SortingState. Pair
   *  with `manualSorting` to drive a server-side ORDER BY from the parent. */
  onSortingChange?: (sorting: SortingState) => void;
  /** When true, the table does NOT re-sort rows client-side — it trusts the
   *  incoming row order (already sorted by the server). The header sort
   *  indicators + `onSortingChange` still work. Defaults to false (client sort).*/
  manualSorting?: boolean;
  /** Opt-in row selection (default off): prepends a checkbox column and reports
   *  the selected row objects via `onSelectionChange`. Pairs with `getRowId` so
   *  selection survives re-fetches. */
  enableRowSelection?: boolean;
  /** Optional eligibility rule, applied to individual and select-all checkboxes. */
  canSelectRow?: (row: T) => boolean;
  onSelectionChange?: (rows: T[]) => void;
  /** Change this to clear the row selection from the host.
   *
   *  The table owns `rowSelection`, so a host that tracks the selection itself has no
   *  way to untick the boxes — a "Clear" button that only resets the host's copy leaves
   *  the checkboxes ticked and the two out of step. Bump this (a counter, a timestamp,
   *  any changing value) and the table clears, the same way it already clears when
   *  `data` changes identity. */
  selectionResetKey?: string | number;
  enableColumnFilters?: boolean;
  columnFilterTypes?: Record<string, ColumnFilterType>;  // Per-column default filter type ('dropdown' when unset)
  /** Columns whose pasted list must be resolved by the SERVER because this grid holds a
   *  capped page rather than the whole table. Requires onListFilterRequest. Columns left
   *  off — and every column when this is unset — match against the loaded rows, which is
   *  correct wherever the grid already holds everything. */
  listFilterServerColumns?: string[];
  /** Refetch this grid's rows scoped to a pasted list. Must resolve once the new rows
   *  have landed: until then the reconciliation would be answering against the page the
   *  list was pasted over, which is exactly the wrong answer this path exists to fix.
   *  An empty `values` means the filter was cleared — reload normally. */
  onListFilterRequest?: (columnId: string, values: string[]) => Promise<void>;
  /** One sentence naming what THIS view leaves out, shown in the list-filter popover for
   *  any column matched client-side.
   *
   *  A pasted list can only ever be reconciled against the rows the grid holds, and a
   *  grid is almost never the whole table — an LC queue holds active tickets for the
   *  selected LCs, a capped grid holds its most recent page. Without this the user reads
   *  "12 not in this view" and cannot tell a real miss from a row the view was never
   *  going to contain. Server-resolved columns suppress it and say so instead, because
   *  there the whole table really was searched. */
  listFilterScopeNote?: string;
  /** Element that all popover/menu portals should mount into. Defaults to
   *  document.body. Set this when the table lives inside a native <dialog>
   *  opened with showModal() — portals to document.body land in the normal
   *  layer and get visually hidden under the dialog's top-layer rendering.
   *  Pass a ref to a div inside the dialog so popovers share the top layer. */
  portalContainer?: HTMLElement | null;
  cardFilterConditions?: FilterCondition[];
  // Card header props
  cardTitle?: string;
  /** When true, suppress the row/page `(count)` that the header normally
   *  appends after the title. Opt-in (default false → existing behavior
   *  unchanged for all other consumers). Use this when `cardTitle` is itself a
   *  fully-formed label that already carries its own count (e.g. the quotes
   *  grid's "Accounts — Results (1–25 of 605 accounts)"). */
  hideTitleCount?: boolean;
  /** Name for the Excel export when `cardTitle` is display-oriented or too
   *  long: the file is named from it in full and only the sheet is cut to
   *  Excel's 31 characters. Defaults to `cardTitle` (file and sheet both from
   *  the 31-char cut, unchanged for existing consumers). */
  exportName?: string;
  onEditFilters?: () => void;
  showEditFiltersButton?: boolean;
  editFiltersButtonIcon?: 'pencil' | 'plus' | 'cog';
  /** Highlight the edit-filters button because a custom filter is currently applied.
   *  Uses the SAME blue as the column-filter and density toggles rather than a colour of
   *  its own: "this control is on" should look identical across the toolbar, whichever
   *  control it is. Without it a custom filter is invisible until you read the title bar. */
  editFiltersActive?: boolean;
  /** How many custom-filter conditions are applied. Drives the "Clear Custom Filters"
   *  button, the sibling of the column-filter clear beside it. 0 = no button. */
  customFilterCount?: number;
  /** Clear every custom-filter condition. Required for the button to render. */
  onClearCustomFilters?: () => void;
  editFiltersButtonTooltip?: string;
  onDuplicate?: () => void;
  duplicateTooltip?: string;
  // Card lock (prevents GridStack drag/resize)
  cardLocked?: boolean;
  onToggleCardLock?: (locked: boolean) => void;
  // Card height settings
  cardHeight?: 'sm' | 'md' | 'lg';
  onCardHeightChange?: (height: 'sm' | 'md' | 'lg') => void;
  fillContainer?: boolean; // When true, use h-full instead of fixed heights (for GridStack)
  // Footer/Pagination settings
  enablePagination?: boolean; // When false (default), shows all rows with virtualization. When true, uses pagination.
  disableVirtualization?: boolean; // When true, renders all rows without virtualization (better for expandable content)
  paginationStyle?: 'fullWidth' | 'compactWithButtons';
  /** Custom footer actions. Also renders without pagination, at all viewport widths. */
  footerLeftContent?: React.ReactNode;
  // When true, renders a sticky <tfoot> row that calls columnDef.footer for each visible leaf column.
  // Footer functions receive { table, column } and typically sum table.getFilteredRowModel().rows.
  showColumnFooter?: boolean;
  // Collapse settings
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  // Vertical tabs settings
  showTabs?: boolean;
  tabData?: TabData<T>[];
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  // Multi-tab selection (opt-in)
  multiTabSelect?: boolean;
  activeTabs?: string[];
  onActiveTabsChange?: (tabIds: string[]) => void;
  allowTabFieldSelection?: boolean; // If true, shows the tab group icon to let users select which field to group tabs by
  tabGroupingField?: string; // The field to use for tab grouping
  // External tab counts from backend (optimization: counts without full data)
  externalTabCounts?: { tab_value: string; count: number }[];
  // Flag indicating data is already filtered by tab on backend (skip client-side tab filtering)
  dataIsTabFiltered?: boolean;
  dateFormat?: 'full' | 'short'; // 'full' = YYYY-MM-DD, 'short' = MM/DD
  // Column IDs to treat as dates in the filter popover (Year > Month > Day tree).
  // Columns whose values all parse as dates are detected automatically; set
  // `meta.dateFilter: true | false` on a column def to force one on or off.
  dateColumns?: string[];
  // Grouping and column visibility callbacks
  onGroupingChange?: (grouping: string[]) => void;
  onTabGroupingChange?: (tabGrouping: string) => void;
  onVisibleColumnsChange?: (visibleColumns: string[]) => void;
  // Dynamic columns settings
  // `expands` marks a field whose join is one-to-many with the row (one order ->
  // many child rows, e.g. trouble tickets). Adding it multiplies the result set;
  // the column picker flags it with "(pros may dupe)" so users opt in knowingly.
  availableFields?: { fieldname: string; displayname: string; abbreviation?: string | null; type: string; expands?: boolean }[];
  onAdditionalFieldsChange?: (additionalFields: string[]) => void;
  additionalFields?: string[]; // Currently selected additional fields
  /** What this grid's column ids mean in registry terms — see FieldAliasMap.
   *
   *  Load View offers a layout saved on ANY grid (tableTemplatesService.list's
   *  all-keys listing, plus every System View), and three layers of this app id the
   *  same field differently: `billtoname` on the LC drill grids, `billto_name` on
   *  the registry-driven bucket cards, `billToName` on the static PRO set. Without
   *  this a View crossing between them is refused as "columns unavailable" even
   *  though every column is right there under another name.
   *
   *  Only needed where a column id is NOT derivable from the fieldname: the
   *  registry-driven grids id their columns by the fieldname (or its API remap)
   *  already, and prepareTableTemplate reduces those to a match on its own.
   *  `resolveFieldAliases` (proCardColumns) builds it from the same rules that give
   *  a column its registry title, and `useAdditionalFields` returns one ready-made.
   *
   *  Also stamped into every layout saved from here (buildTemplateSnapshot), so a
   *  View carries what its ids meant rather than leaving the next grid to infer it. */
  fieldAliases?: FieldAliasMap;
  // Delete toggle settings
  showDeletedToggle?: boolean;
  showDeleted?: boolean;
  onShowDeletedChange?: (showDeleted: boolean) => void;
  // Archived toggle settings (a second "reveal more rows" toggle, e.g. EDI Events
  // folding in an archive table). Works like the delete toggle.
  showArchivedToggle?: boolean;
  showArchived?: boolean;
  onShowArchivedChange?: (showArchived: boolean) => void;
  /** Optional third toolbar toggle: a page-owned row filter. The NARROWING twin of
   *  the deleted/archived pair — those fold more rows in, this one holds rows back,
   *  so a grid can open on the subset it exists for (Melrose Orders → only imports
   *  that failed). The page owns the filtering and the state; this renders the
   *  control only. `active` = filter applied (fewer rows). */
  rowFilterToggle?: {
    active: boolean;
    onChange: (active: boolean) => void;
    /** Tooltip while the filter is ON — say what clicking does, e.g. 'Show all rows'. */
    onTooltip: string;
    /** Tooltip while it is OFF, e.g. 'Show only rows with errors'. */
    offTooltip: string;
    /** Icon naming the rows this filter is about, the way the trash names deleted
     *  rows and the archive box names archived ones. Supplied by the caller rather
     *  than chosen here: this control is generic, and only the page knows what its
     *  filtered-in rows have in common. Must NOT be a funnel — that is already the
     *  column-filter icon in this same toolbar. */
    Icon: React.ComponentType<{ className?: string }>;
  };
  // Column locking settings
  lockedColumns?: (string | LockedColumn)[];
  // Excel export settings
  enableExcelExport?: boolean;
  /** Use the report's workbook export for this toolbar action (e.g. all status
   * tabs), instead of generating a worksheet from only the visible table. */
  onExcelExport?: () => void | Promise<void>;
  excelExportLoading?: boolean;
  /** Optional final report row keyed by column ID, computed from filtered source rows.
   * It never enters the sortable/filterable data model. */
  exportFooter?: (rows: T[]) => Record<string, string | number>;
  /** Extra columns included in Excel export only (not displayed in the table).
   *  Values are pulled from row.original[key]. */
  extraExportColumns?: { key: string; header: string }[];
  /** Fixed Excel-export column order. When provided, the export emits exactly
   *  these columns in this order — independent of on-screen column order,
   *  visibility, and extraExportColumns. Each value is pulled via the matching
   *  table column's formatted value (so cell formatting / meta.exportValue
   *  apply), falling back to raw row.original[key] for keys with no column.
   *  The on-screen table is unaffected; most tables should omit this and let
   *  the export follow the user's column order. */
  exportColumnOrder?: { key: string; header: string }[];
  // Table controls settings
  enableSearch?: boolean;
  enableReset?: boolean;
  enableShowHideCols?: boolean;
  defaultColumnVisibility?: VisibilityState;  // Columns hidden by default (used when no saved settings, and as fallback for new columns in saved settings)
  /** Admin-managed default layout for this table (visibility/order/sizing/additionalFields/sorting/grouping/tabGrouping).
   *  Seeds the table when the user has no saved settings of their own, and is the target "Reset all settings" restores to. */
  defaultSettings?: any;
  /** Show the admin "Edit Default Settings" control. Parent gates this on a shared/locked bucket AND the
   *  'edit-default-table-settings' component permission. */
  canEditDefaults?: boolean;
  /** Persist a new default layout for this table. Parent owns the confirmation dialog + PUT + local cache update. */
  onSaveDefaults?: (settingsKey: string, settings: any) => Promise<void> | void;
  /** Grouping field(s) the bucket requires — shown in the editor but cannot be removed (admins may layer more on top). */
  mandatoryGroupingFields?: string[];
  /** Gate column reorder/resize behind an explicit edit mode (pencil toggle), with
   *  buffered Save/Cancel. Defaults true. Set false for tables that should never
   *  expose column editing. */
  enableColumnEditing?: boolean;
  enableTotalsBar?: boolean;
  /** Allow the user to pin/unpin columns via the column context menu + header
   *  pin button. Defaults true. Columns passed in `lockedColumns` stay pinned
   *  regardless — this only removes the user-facing option to add more. */
  enableColumnPinning?: boolean;
  /** When true, the table never loads or saves per-user settings — column order,
   *  sort, visibility, filters and sizing always come from the props/defaults.
   *  Use for tables whose column set is dynamic (e.g. date matrices) where a
   *  persisted layout would go stale and fight the defaults. Defaults false. */
  disableSettingsPersistence?: boolean;
  /** Seed the layout from `initialSettings` and then NEVER write it back — no
   *  user_settings save, no localStorage cache read or write.
   *
   *  Distinct from `disableSettingsPersistence`, which bails out BEFORE
   *  `initialSettings` is consumed and so yields the hard prop defaults. This is
   *  for showing someone else's layout on loan: the search-results grid rendering
   *  a manager-published View, where the user's own saved layout for that grid
   *  must survive untouched and come back the moment the View is dropped.
   *  Defaults false. */
  settingsReadOnly?: boolean;
  /** Fired when the user hits edit-mode Save while `settingsReadOnly` is on. The
   *  layout has just been written to their OWN settings row, so the host should
   *  stop lending it one (drop the View) and let normal persistence resume. */
  onReadOnlyLayoutSaved?: () => void;
  /** Offer "Save As System View" in the edit-mode toolbar — publishing this layout
   *  org-wide for the Custom Search / Custom Filter View dropdown. Only the
   *  search-results grid sets this, because that is the only grid that PUBLISHES
   *  one; the button is additionally gated on the save-search-view component
   *  permission, so non-managers never see it. Other grids can still CONSUME
   *  System Views from Load View, which lists them app-wide. Defaults false. */
  enableViews?: boolean;
  /** Pool the named layouts ("Views") under this key instead of `settingsKey`.
   *
   *  ONLY the layout-template calls (list / save) use it — `settingsKey` keeps
   *  owning this grid's own persisted layout, its localStorage cache and its admin
   *  defaults. So a grid still remembers its own arrangement while sharing a pool
   *  of named layouts with its siblings.
   *
   *  Exists for the LC/Dispatch dashboard CUSTOM cards: each is keyed
   *  `card-<timestamp>` at creation time, so without this a View saved on one card
   *  is invisible on every other one, even though they are the same component over
   *  the same field registry. Built-in cards deliberately do NOT pass it — their
   *  curated column sets should not be interchangeable with a general-purpose card
   *  layout. Defaults to `settingsKey`. */
  templateScopeKey?: string;
  enableRowsPerPage?: boolean;
  hideAllRowsOption?: boolean;
  rowsPerPageOptions?: number[];
  defaultPageSize?: number;
  enableZebraStriping?: boolean;
  showGridLines?: boolean;
  // Table header styling
  tableHeaderBgColor?: string;
  // Global control overrides (from parent dashboard)
  globalResetTrigger?: number; // Increment to trigger reset
  // Field name for LLM help integration
  fieldName?: string; // kebab-case identifier for this table (e.g., 'per-diem-table')
  globalPageSize?: number; // Override page size
  globalShowFilters?: boolean; // Override show column filters
  globalSuperCompact?: boolean; // Override super compact mode
  globalCardHeight?: 'sm' | 'md' | 'lg'; // Override card height
  // Pre-loaded settings to avoid individual API call (for batch loading optimization)
  initialSettings?: any; // If provided, skip the API call and use this directly
  // Batch settings loading state:
  // - undefined (default): Legacy mode - fetch settings individually if no initialSettings
  // - false: Batch mode - waiting for parent to complete batch loading, don't fetch yet
  // - true: Batch mode complete - use initialSettings or defaults, no individual fetch
  settingsReady?: boolean;
  // Batch mode only: set true by the parent when its batch settings fetch FAILED
  // (DB/network error, not "no settings"). Tells TanTable to enter protected mode:
  // restore from cache, block saving, and wait for the parent to recover.
  settingsLoadFailed?: boolean;
  // Callback when settings are saved - allows parent to keep its cache in sync
  onSettingsSaved?: (settingsKey: string, settings: any) => void;
  // Legacy mode only, opt-in: when true, synchronously apply the user's cached layout
  // (column visibility/order/widths/sorting/pinned) from localStorage BEFORE first paint,
  // eliminating the default→saved "flash" while the individual settings fetch is in flight.
  // The async fetch still runs and reconciles. No effect on a cold cache (new device).
  seedLayoutFromCache?: boolean;
  // Super compact mode - removes all padding/margins for maximum density
  superCompact?: boolean;
  onSuperCompactChange?: (superCompact: boolean) => void;
  // Sub-row support
  getSubRows?: (row: T) => T[] | undefined;  // Function to get child rows
  subRowColumns?: string[];                   // Column IDs that show data in sub-rows (others show empty)
  defaultExpanded?: boolean;                  // Start with sub-rows expanded (default: false)
  alwaysExpanded?: boolean;                   // Force all rows expanded and hide expand/collapse toggle
  subRowsGroupLabel?: string;                 // Label for the sub-rows level in Expand/Collapse Groups panel (e.g. "Grouped Legs")
  // Custom expanded content - renders a custom div below the row instead of sub-rows
  renderExpandedContent?: (row: T) => React.ReactNode;
  /** Card mode: when set, the column header is not rendered and each data row
   *  renders as ONE full-width cell containing this card. Filtering, search,
   *  sorting, export, selection and the toolbar all keep working — they operate
   *  on the row model, not the markup. `ctx.visibleColumnIds` is the current
   *  visible leaf-column id set (drives which fields the card shows).
   *  `ctx.visibleColumns` is the same set in COLUMN ORDER with display labels,
   *  minus `cardHeaderColumnIds` — the card renders its fields in this order, so a
   *  column drag repositions the field on every card. `ctx.renderCell(id)` renders
   *  one column of THIS row through the column's own `cell` renderer (undefined
   *  for an unknown id) — how a card shows a column it has no hand-written field
   *  for (a user-added dynamic column, the SF/TT badges) exactly as the table would. */
  renderCardRow?: (
    row: T,
    ctx: {
      visibleColumnIds: ReadonlySet<string>;
      visibleColumns: { id: string; label: string }[];
      renderCell: (columnId: string) => React.ReactNode;
    },
  ) => React.ReactNode;
  /** Columns the card renders inside its own header strip (e.g. the PRO number and
   *  its badges). Excluded from `ctx.visibleColumns` AND (later) the card-mode chip
   *  grid, so chip column N lines up with card field column N. Pass a stable
   *  reference (module constant or memo) — an inline array literal re-derives every render. */
  cardHeaderColumnIds?: string[];
  // Default value for the global search filter (e.g. populated from a URL query param)
  defaultGlobalFilter?: string;
  // Custom row ID function for stable row identification (important for expansion state preservation)
  getRowId?: (row: T) => string;
  // Filtered rows callback - reports filtered rows after all filters (tabs, global, column) are applied
  onFilteredRowsChange?: (filteredRows: T[]) => void;
  // Fires when the user changes the search input or column filters (after initial mount).
  // Does not fire for tab changes, sorting, or data refreshes.
  onUserFilterChange?: () => void;
  // Optional content rendered in the card header next to the search input
  // (also appears in the narrow-mode secondary toolbar). Use for per-card view controls.
  headerExtra?: React.ReactNode;
  // Optional control rendered in the toolbar icon cluster, just before the
  // Export icon (wide + narrow toolbars). A render-prop given a getter for the
  // CURRENTLY-FILTERED row originals, so an action like Trace All operates on
  // what's visible (column filters + search), not the full dataset.
  traceAllControl?: (getFilteredRows: () => any[]) => React.ReactNode;
  // Expandable "action bar" of mass actions (Change All, Change Template for
  // All, Trace All, bucket-specific sends, …), toggled open by the action-bar
  // icon in the toolbar. Each action runs against this table's CURRENTLY-
  // FILTERED rows, so on a workspace of several tables a mass action only
  // touches the tile it was launched from. When empty/undefined the toggle is
  // hidden. See TableActionBar for the descriptor shape.
  bulkActions?: BulkAction[];
  // Optional content rendered immediately BEFORE the search input (wide + narrow
  // toolbars). Use for per-card controls that belong next to search (e.g. filters).
  searchPrefix?: React.ReactNode;
  // Related rows function - when filtering, if any related row matches, include all related rows
  // This is useful for grouped/linked rows that should always appear together
  getRelatedRows?: (row: T) => T[];
  // Row indicator settings - shows a colored indicator cell on the left side of each row
  showIndicator?: boolean;
  getIndicatorInfo?: (row: T) => { color: string; label: string | null } | null;
  debugCellWidth?: boolean;
  onBulkEdit?: (columnId: string, rows: T[], value: unknown) => void;
  /**
   * Per-column inline editing, opted into from the column menu rather than always on.
   *
   * The host owns all three: which columns CAN be edited (`canEditColumn`), which are
   * currently in edit mode (`isColumnEditing`), and the toggle. The table only renders
   * the menu entry — it has no opinion about what editing means, because that differs
   * per surface (a tablemaint row writes one table, a search row can write an order, a
   * leg or an invoice).
   */
  /**
   * Size the card to the space actually left below it instead of the fixed
   * `calc(100vh - 280px)`, which is a guess at how much page chrome sits above.
   *
   * A host that renders banners above the grid invalidates that guess by however tall
   * they are, and the card keeps claiming the same height — so its bottom edge, and
   * with it the horizontal scrollbar, slides under the footer. Measured from the
   * card's own top offset, re-measured whenever anything above it changes size.
   */
  autoHeight?: boolean;
  /** Space to leave below the card when autoHeight is on (default 24px). */
  autoHeightGap?: number;
  canEditColumn?: (columnId: string) => boolean;
  isColumnEditing?: (columnId: string) => boolean;
  onToggleColumnEditing?: (columnId: string) => void;
}

interface BulkEditModalProps<T> {
  columnId: string;
  bulkEditMeta: BulkEditMeta;
  selectedRows: T[];
  onApply: (value: unknown) => void;
  onCancel: () => void;
}

function BulkEditModal<T>({ bulkEditMeta, selectedRows, onApply, onCancel }: BulkEditModalProps<T>) {
  const [value, setValue] = useState<unknown>(bulkEditMeta.type === 'checkbox' ? false : '');
  // Mounted only while open by the parent, so unmount resets the drag offset.
  const bulkEditDrag = useModalDrag(true);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-scrim/30">
      <div
        ref={bulkEditDrag.panelRef}
        style={bulkEditDrag.panelStyle}
        className="bg-panel rounded-lg shadow-xl w-72 overflow-hidden"
      >
        <ModalHeader
          title={`Edit ${selectedRows.length} cell${selectedRows.length !== 1 ? 's' : ''}`}
          onClose={onCancel}
          dragHandleProps={bulkEditDrag.dragHandleProps}
        />
        <div className="p-4 flex flex-col gap-3">

        {(bulkEditMeta.type === 'text' || bulkEditMeta.type === 'number') && (
          <input
            autoFocus
            type={bulkEditMeta.type}
            value={value as string}
            onChange={e => setValue(bulkEditMeta.type === 'number' && e.target.value !== '' ? Number(e.target.value) : e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-700 text-body focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        )}

        {bulkEditMeta.type === 'date' && (
          // DateTimePicker's typed-input parser handles day-offset shortcuts
          // (0 = today, 1 = tomorrow, -1 = yesterday, `n` = now), so users
          // can bulk-set dates without clicking through the calendar. We
          // emit 'YYYY-MM-DD' so existing onBulkEdit handlers (which treat
          // value as a string) work unchanged.
          <DateTimePicker
            mode="date"
            selectionType="single"
            inputVariant="modal"
            labelType="none"
            autoApply
            openDirection="down"
            autoFlip
            usePortal
            value={
              typeof value === 'string' && value
                ? (() => {
                    const [y, m, d] = value.split('-').map(Number);
                    return Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d)
                      ? new Date(y, m - 1, d)
                      : null;
                  })()
                : null
            }
            onChange={(val) => {
              const d = val instanceof Date ? val : null;
              if (!d) {
                setValue('');
                return;
              }
              setValue(
                `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
              );
            }}
          />
        )}

        {bulkEditMeta.type === 'dropdown' && (
          <select
            autoFocus
            value={value as string}
            onChange={e => setValue(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-700 text-body focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">— select —</option>
            {bulkEditMeta.options?.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        )}

        {bulkEditMeta.type === 'checkbox' && (
          <label className="flex items-center gap-2 text-sm text-body cursor-pointer">
            <input
              type="checkbox"
              checked={value as boolean}
              onChange={e => setValue(e.target.checked)}
              className="rounded"
            />
            Check all selected
          </label>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 text-label hover:bg-highlight"
          >
            Cancel
          </button>
          <button
            onClick={() => onApply(value)}
            className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
          >
            Apply
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Leaf column defs of a possibly-grouped column tree.
 *
 * A grouped ("band") header def carries `columns: [...]` and no accessor of its
 * own, so its id names a header cell, not a column of values. Everything that
 * reasons about real columns — column order, the settings restore's
 * "does this column still exist?" checks, staged field sets — must look at the
 * LEAVES. Flat column arrays are returned unchanged, so this is a no-op for the
 * tables that don't use groups.
 */
const leafColumnDefs = (cols: any[]): any[] =>
  (cols || []).flatMap((c: any) => (Array.isArray(c?.columns) ? leafColumnDefs(c.columns) : [c]));

/** Ids of every leaf column, in visual order. */
const leafColumnIds = (cols: any[]): string[] =>
  leafColumnDefs(cols)
    .map((c: any) => c.id || c.accessorKey)
    .filter(Boolean);

export const TanTable = memo(function TanTable<T>({
  data,
  columns,
  loading = false,
  errorMessage = null,
  onRetry,
  settingsKey = 'tan-table',
  searchPlaceholder = 'Filter Results...',
  className = '',
  onRowClick,
  getRowClassName,
  getRowTitle,
  getCellClassName,
  subHeaderBar,
  trackViewedColumnId,
  getViewedKey,
  trackViewedBlueColumnId,
  getViewedBlueKey,
  onSearchChange,
  getGroupedRowClassName,
  enableGrouping = false,
  groupableColumns = [],
  defaultGrouping = [],
  groupFooter,
  defaultSorting = [],
  onSortingChange: onSortingChangeProp,
  manualSorting = false,
  enableRowSelection = false,
  canSelectRow,
  onSelectionChange,
  selectionResetKey,
  enableColumnFilters = false,
  columnFilterTypes = {},
  listFilterServerColumns,
  onListFilterRequest,
  listFilterScopeNote,
  portalContainer,
  cardFilterConditions = [],
  cardTitle,
  hideTitleCount = false,
  exportName,
  onEditFilters,
  showEditFiltersButton = false,
  editFiltersButtonIcon = 'pencil',
  editFiltersActive = false,
  customFilterCount = 0,
  onClearCustomFilters,
  editFiltersButtonTooltip = 'Edit Filters',
  onDuplicate,
  duplicateTooltip = 'Duplicate',
  cardLocked,
  onToggleCardLock,
  cardHeight = 'lg',
  onCardHeightChange,
  fillContainer = false,
  enablePagination = false,
  disableVirtualization = false,
  paginationStyle = 'fullWidth',
  footerLeftContent,
  showColumnFooter = false,
  collapsed = false,
  onToggleCollapse,
  showTabs = false,
  tabData = [],
  activeTab,
  onTabChange,
  multiTabSelect = false,
  activeTabs,
  onActiveTabsChange,
  allowTabFieldSelection = true,
  tabGroupingField,
  externalTabCounts,
  dataIsTabFiltered = false,
  dateFormat = 'full',
  dateColumns = [],
  showDeletedToggle = false,
  showDeleted = false,
  onShowDeletedChange,
  showArchivedToggle = false,
  rowFilterToggle,
  showArchived = false,
  onShowArchivedChange,
  lockedColumns = [],
  enableExcelExport = true,
  onExcelExport,
  excelExportLoading = false,
  extraExportColumns = [],
  exportColumnOrder,
  exportFooter,
  enableSearch = true,
  enableReset = true,
  enableShowHideCols = true,
  defaultColumnVisibility = {},
  defaultSettings,
  canEditDefaults = false,
  onSaveDefaults,
  mandatoryGroupingFields = [],
  enableColumnEditing = true,
  enableTotalsBar = true,
  enableColumnPinning = true,
  disableSettingsPersistence = false,
  settingsReadOnly = false,
  onReadOnlyLayoutSaved,
  enableViews = false,
  templateScopeKey,
  enableRowsPerPage = true,
  hideAllRowsOption = false,
  rowsPerPageOptions = [10, 25, 50, 100],
  defaultPageSize,
  enableZebraStriping,
  showGridLines = false,
  tableHeaderBgColor,
  onGroupingChange,
  onTabGroupingChange,
  onVisibleColumnsChange,
  availableFields,
  onAdditionalFieldsChange,
  additionalFields = [],
  fieldAliases,
  globalResetTrigger,
  globalPageSize,
  globalShowFilters,
  globalSuperCompact,
  globalCardHeight,
  fieldName,
  initialSettings,
  settingsReady, // undefined = legacy mode, false = waiting for batch, true = batch complete
  settingsLoadFailed = false,
  onSettingsSaved,
  seedLayoutFromCache = false,
  superCompact = false,
  onSuperCompactChange,
  getSubRows,
  subRowColumns,
  subRowsGroupLabel,
  defaultExpanded = false,
  alwaysExpanded = false,
  renderExpandedContent,
  renderCardRow,
  cardHeaderColumnIds = EMPTY_STRING_ARRAY,
  defaultGlobalFilter = '',
  getRowId: getRowIdProp,
  onFilteredRowsChange,
  onUserFilterChange,
  headerExtra,
  traceAllControl,
  bulkActions,
  searchPrefix,
  getRelatedRows,
  showIndicator = false,
  getIndicatorInfo,
  debugCellWidth = false,
  onBulkEdit,
  autoHeight = false,
  autoHeightGap = 24,
  canEditColumn,
  isColumnEditing,
  onToggleColumnEditing,
}: TanTableProps<T>) {
  // Get display preferences from context
  const displayPreferences = useDisplayPreferences();

  // Use props as overrides, otherwise fall back to context values
  // When the prop is explicitly passed (true or false) it wins; otherwise fall
  // back to the user-level display preference. This lets a page force zebra off
  // (e.g. when row-level highlights would compete with the stripes).
  const effectiveZebraStriping = enableZebraStriping !== undefined
    ? enableZebraStriping
    : displayPreferences.showTableZebraStriping;
  const effectiveGridLines = showGridLines || displayPreferences.showTableGridlines;
  const gridlineV = effectiveGridLines && displayPreferences.showVerticalGridlines;
  const gridlineH = effectiveGridLines && displayPreferences.showHorizontalGridlines;
  const anyGridline = gridlineV || gridlineH;
  const effectiveDebugCellWidth = debugCellWidth || displayPreferences.showColumnWidths;

  // Get zebra stripe class from context (uses CSS class with dark mode support)
  const zebraClass = displayPreferences.getZebraClass();

  // Field name visibility for LLM help integration
  const { showFieldNames, highlightedFieldId } = displayPreferences;

  // Global table display modes from context
  const { globalSuperCompact: contextSuperCompact, setGlobalSuperCompact, globalFitToWidth: contextFitToWidth, setGlobalFitToWidth, globalShowColumnFilters: contextShowColumnFilters, setGlobalShowColumnFilters } = displayPreferences;

  // Helper function to format dates
  // Supports meta.isDate to identify date columns
  // Supports meta.showTime (default true) - if true shows MM/DD HH:MM, if false shows MM/DD
  const formatDate = (dateStr: string | null | undefined, showTime: boolean = true): string => {
    if (!dateStr || dateStr === '0000-00-00' || dateStr === '0000-00-00 00:00:00') return '';

    // Parse datetime (handle both ISO and space-separated formats)
    let dateObj: Date;
    if (dateStr.includes('T')) {
      dateObj = new Date(dateStr);
    } else if (dateStr.includes(' ')) {
      const [datePart, timePart] = dateStr.split(' ');
      dateObj = new Date(`${datePart}T${timePart}`);
    } else {
      // Date only
      dateObj = new Date(dateStr + 'T00:00:00');
    }

    // Check for invalid date
    if (isNaN(dateObj.getTime())) return dateStr;

    // Format as MM/DD (or MM/DD/YY when not current year)
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const isCurrentYear = dateObj.getFullYear() === new Date().getFullYear();
    const yearSuffix = isCurrentYear ? '' : `/${String(dateObj.getFullYear()).slice(-2)}`;

    if (showTime && (dateStr.includes('T') || dateStr.includes(' '))) {
      // Format with time: MM/DD[/YY] HH:MM
      const hours = String(dateObj.getHours()).padStart(2, '0');
      const minutes = String(dateObj.getMinutes()).padStart(2, '0');
      return `${month}/${day}${yearSuffix} ${hours}:${minutes}`;
    }

    // Format without time: MM/DD[/YY]
    return `${month}/${day}${yearSuffix}`;
  };
  // Validate and cap rowsPerPageOptions at 500
  const validatedRowsPerPageOptions = rowsPerPageOptions.map(val => Math.min(val, 500));

  // State
  const [sorting, setSorting] = useState<SortingState>(defaultSorting || []);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState(defaultGlobalFilter);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(defaultColumnVisibility);

  // Activity tracking: searches/filters/sorts are debounced so typing emits
  // one event instead of one per keystroke. target identifies the table.
  // activityUserActedRef gates out saved-settings rehydration: it's only set
  // by the table's interaction callbacks (and the search inputs), which
  // rehydration bypasses by calling the state setters directly.
  const activityTableId = settingsKey || cardTitle || 'table';
  const activityUserActedRef = useRef(false);

  useEffect(() => {
    onSearchChange?.(globalFilter ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilter]);

  useEffect(() => {
    if (!activityUserActedRef.current || !globalFilter) return;
    const t = setTimeout(() => {
      activityTracker.track({
        event_type: 'search',
        target: activityTableId,
        detail: `"${globalFilter}"`,
      });
    }, 1500);
    return () => clearTimeout(t);
  }, [globalFilter, activityTableId]);

  useEffect(() => {
    if (!activityUserActedRef.current || columnFilters.length === 0) return;
    const t = setTimeout(() => {
      const summary = columnFilters
        .map((f) => `${f.id}=${Array.isArray(f.value) ? (f.value as unknown[]).join('|') : String(f.value)}`)
        .join(', ');
      activityTracker.track({
        event_type: 'table_action',
        target: activityTableId,
        detail: `filter: ${summary}`,
      });
    }, 1500);
    return () => clearTimeout(t);
  }, [columnFilters, activityTableId]);

  useEffect(() => {
    if (!activityUserActedRef.current || sorting.length === 0) return;
    const t = setTimeout(() => {
      const summary = sorting.map((s) => `${s.id} ${s.desc ? 'desc' : 'asc'}`).join(', ');
      activityTracker.track({
        event_type: 'table_action',
        target: activityTableId,
        detail: `sort: ${summary}`,
      });
    }, 500);
    return () => clearTimeout(t);
  }, [sorting, activityTableId]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: enablePagination
      ? (defaultPageSize && validatedRowsPerPageOptions.includes(defaultPageSize)
          ? defaultPageSize
          : validatedRowsPerPageOptions[0] || 25)
      : Number.MAX_SAFE_INTEGER, // Show all rows when pagination disabled
  });
  // Note: useEffect for enablePagination removed - initial state handles this correctly.
  // If enablePagination changes after mount, parent should use key prop to reset component.

  // Reset to page 1 when data changes (e.g., from filtering)
  const prevDataLengthRef = useRef(data.length);
  useEffect(() => {
    if (data.length !== prevDataLengthRef.current) {
      prevDataLengthRef.current = data.length;
      setPagination(prev => ({ ...prev, pageIndex: 0 }));
    }
  }, [data.length]);

  const [grouping, setGrouping] = useState<GroupingState>(defaultGrouping || []);
  // When defaultExpanded or alwaysExpanded is true and getSubRows is provided, start with all rows expanded
  const [expanded, setExpanded] = useState<ExpandedState>((defaultExpanded || alwaysExpanded) && getSubRows ? true : {});
  const effectiveSubRowsGroupLabel = subRowsGroupLabel !== undefined ? subRowsGroupLabel : getSubRows ? 'Grouped Legs' : undefined;
  // Initialize column order with locked columns in correct positions
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(() => {
    const currentColumnIds = leafColumnIds(columns as any[]);
    // Compute locked columns from props
    const lockedLeft = lockedColumns
      .map(col => typeof col === 'string' ? { id: col, side: 'left' as const } : col)
      .filter(col => (col.side || 'left') === 'left')
      .map(col => col.id);
    const lockedRight = lockedColumns
      .map(col => typeof col === 'string' ? { id: col, side: 'left' as const } : col)
      .filter(col => col.side === 'right')
      .map(col => col.id);
    const allLocked = [...lockedLeft, ...lockedRight];

    if (allLocked.length > 0) {
      const lockedSet = new Set(allLocked);
      const nonLocked = currentColumnIds.filter(id => !lockedSet.has(id));
      return [...lockedLeft, ...nonLocked, ...lockedRight];
    }
    return currentColumnIds;
  });

  // Note: useEffect for defaultExpanded removed - initial state handles this correctly.
  // If defaultExpanded changes after mount, parent should use key prop to reset component.
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  // "Fit All in View" sizing is a front-end-only overlay applied on top of the
  // user's saved widths — it is never persisted and never overwrites
  // columnSizing, so toggling fit on/off is lossless.
  const [fitOverlaySizing, setFitOverlaySizing] = useState<ColumnSizingState>({});
  // Latest saved widths, read by applyFitToView without making it depend on
  // columnSizing (which would re-subscribe the resize observer and cause the
  // fit calc to fight live column drags).
  const columnSizingRef = useRef(columnSizing);
  columnSizingRef.current = columnSizing;
  const [debugWidthColumn, setDebugWidthColumn] = useState<{ id: string; width: number } | null>(null);

  // Compute effective sorting that includes grouping columns at the start
  // This is derived from sorting + grouping, computed during render instead of useEffect
  const effectiveSorting = useMemo(() => {
    if (grouping.length === 0) return sorting;
    // Create sorting entries for each grouped column
    const groupingSorts: SortingState = grouping.map(columnId => ({
      id: columnId,
      desc: false // false = ascending (oldest to newest for dates, A-Z for strings)
    }));
    // Merge with existing non-grouping sorts
    const nonGroupingSorts = sorting.filter(sort => !grouping.includes(sort.id));
    return [...groupingSorts, ...nonGroupingSorts];
  }, [grouping, sorting]);
  const [showColumnToggle, setShowColumnToggle] = useState(false);
  // Card mode only: the "Edit Fields" section (Columns picker + the draggable
  // filter-chip grid). With ~45 columns that grid is several rows tall, so it is
  // collapsed by default and revealed by the toolbar's TableIcon button — the
  // same slot that hosts Column Edit Mode in table mode. Persisted per user
  // alongside the rest of the table settings (see buildSettingsData).
  const [showCardFields, setShowCardFields] = useState(false);
  // Totals bar (aggregation footer): the Σ toolbar button shows a row of
  // per-column calculation slots at the bottom of the table. Both the toggle and
  // the per-column choices are persisted per user (see buildSettingsData).
  //
  // Tri-state, like superCompactOverride / localFilterOverride: null means this
  // user has never touched the toggle here, so a published admin default still
  // reaches them. Once they toggle it, their choice sticks.
  const [showAggFooterOverride, setShowAggFooterOverride] = useState<boolean | null>(null);
  // This user's per-column choices. An unset column is NOT the same as a cleared
  // one — a clear is stored as 'none' so a default can't creep back over it.
  const [aggCalcs, setAggCalcs] = useState<Record<string, AggCalcSetting>>({});
  // The published default for THIS grid ("Save as Defaults"), applied as a
  // per-column fallback layer. Kept separate from the all-or-nothing
  // defaultSettings seed so it keeps reaching users who already have their own
  // settings row — which is everyone who has ever sorted or filtered.
  const [aggAdminCalcs, setAggAdminCalcs] = useState<Record<string, AggCalcSetting>>({});
  const [aggAdminShowFooter, setAggAdminShowFooter] = useState<boolean | null>(null);
  const [aggPicker, setAggPicker] = useState<{ columnId: string; x: number; y: number } | null>(null);
  // Column edit mode: reorder/resize are only available while editing. Edits are
  // buffered against a baseline snapshot taken on entry, so the user can Save
  // (commit) or Cancel (discard). Outside edit mode columns are read-only.
  const [editMode, setEditMode] = useState(false);
  const [editDirty, setEditDirty] = useState(false);
  // Fields added (by api id) during edit mode but not yet committed — rendered as
  // blank placeholder columns (no data fetch, no settings write) until Save.
  const [pendingAddedFields, setPendingAddedFields] = useState<string[]>([]);
  // Prompt shown when leaving edit mode with unsaved changes.
  const [showEditExitPrompt, setShowEditExitPrompt] = useState(false);
  // Drag-to-move for this component's own inline modals. One hook per modal,
  // keyed to that modal's open state so each reopens centered.
  const debugWidthDrag = useModalDrag(!!debugWidthColumn);
  const editExitDrag = useModalDrag(showEditExitPrompt);
  // Confirm dialog for "Clear All Filters & Settings" — the reset is easy to
  // click by accident and isn't recoverable, so guard it behind a confirmation.
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  // Named layout templates (save the current column layout / apply a saved or
  // shared one) — reachable from the column edit-mode toolbar.
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [showLoadTemplate, setShowLoadTemplate] = useState(false);
  const [showSaveView, setShowSaveView] = useState(false);
  const [showManageViews, setShowManageViews] = useState(false);
  const [columnSearchFilter, setColumnSearchFilter] = useState('');
  const columnListRef = useRef<HTMLDivElement>(null);
  const [showGroupingMenu, setShowGroupingMenu] = useState(false);
  const [showExpandMenu, setShowExpandMenu] = useState(false);
  const [showColumnFilters, setShowColumnFilters] = useState(contextShowColumnFilters);
  const [localFilterOverride, setLocalFilterOverride] = useState<boolean | null>(null);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [isSuperCompact, setIsSuperCompact] = useState(superCompact);
  const [localSuperCompactOverride, setLocalSuperCompactOverride] = useState<boolean | null>(null);
  const [columnFitMode, setColumnFitMode] = useState<'normal' | 'fitToView' | 'fitToContent'>('normal');
  const [localFitToWidthOverride, setLocalFitToWidthOverride] = useState<boolean | null>(null);
  const [showHeightMenu, setShowHeightMenu] = useState(false);
  const [showRowsMenu, setShowRowsMenu] = useState(false);
  const [showTabGroupMenu, setShowTabGroupMenu] = useState(false);
  const [selectedTabGroupField, setSelectedTabGroupField] = useState<string | null>(null);
  const [activeFilterColumn, setActiveFilterColumn] = useState<string | null>(null);
  const [filterSearchText, setFilterSearchText] = useState<{ [key: string]: string }>({});
  // Text filter input values (for 'text' type filters with debounce)
  const [textFilterInputs, setTextFilterInputs] = useState<Record<string, string>>({});
  // Raw pasted text for the list modes, per column, so reopening the popover shows
  // what was pasted. Session-only and deliberately unpersisted — see buildSettingsData.
  const [listFilterInputs, setListFilterInputs] = useState<Record<string, string>>({});
  // Number-mode drafts (operator + raw bound strings), per column. Session-only; the
  // applied value lives in columnFilters and is rebuilt into a draft on reopen.
  const [numberFilterInputs, setNumberFilterInputs] = useState<Record<string, NumberFilterDraft>>({});
  // Column whose list is mid-refetch on the server, so the popover can say so rather
  // than showing a reconciliation computed from rows that are about to be replaced.
  const [listFilterPending, setListFilterPending] = useState<string | null>(null);
  const listFilterServerColumnsKey = (listFilterServerColumns ?? []).join('\u0000');
  const textFilterDebounceRef = useRef<Record<string, NodeJS.Timeout>>({});
  // User-selected filter types (persisted to settings, overrides columnFilterTypes prop)
  const [userFilterTypes, setUserFilterTypes] = useState<Record<string, ColumnFilterType>>({});
  // Filter dropdown size (persisted, shared across all columns)
  const [filterDropdownSize, setFilterDropdownSize] = useState<{ width: number; height: number }>({ width: 256, height: 384 });
  const isResizingFilterRef = useRef(false);
  const [distinctValues, setDistinctValues] = useState<{ [key: string]: { value: string; count: number }[] }>({});
  // Year > Month > Day tree expansion for the open date-column popover. Keyed by
  // node key; unset = default (years open, months closed). Reset per popover open.
  const [dateTreeToggles, setDateTreeToggles] = useState<Record<string, boolean>>({});
  const [loadingDistinctValues, setLoadingDistinctValues] = useState<{ [key: string]: boolean }>({});
  const [filterInputPosition, setFilterInputPosition] = useState<{ top: number; left: number } | null>(null);
  const filterMenuRef = useRef<HTMLDivElement>(null);
  // Anchor element of the open filter dropdown — a header filter input (table
  // mode) or a card-bar filter chip button (card mode).
  const filterAnchorRef = useRef<HTMLElement | null>(null);
  const groupingMenuRef = useRef<HTMLDivElement>(null);
  const expandMenuRef = useRef<HTMLDivElement>(null);
  const columnToggleRef = useRef<HTMLDivElement>(null);
  const heightMenuRef = useRef<HTMLDivElement>(null);
  const rowsMenuRef = useRef<HTMLDivElement>(null);
  const tabGroupMenuRef = useRef<HTMLDivElement>(null);
  const tabDropdownRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  // Portal dropdown refs (for click outside detection)
  const columnTogglePortalRef = useRef<HTMLDivElement>(null);
  const groupingPortalRef = useRef<HTMLDivElement>(null);
  const rowsPortalRef = useRef<HTMLDivElement>(null);
  const tabGroupPortalRef = useRef<HTMLDivElement>(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const [iconsExpanded, setIconsExpanded] = useState(true);
  // Whether the inline bulk-action bar is expanded. Toggled by the action-bar
  // icon; only meaningful when `bulkActions` is non-empty. Default closed in
  // table mode, but OPEN in card mode: before card rendering moved into this
  // table, the cards page showed Change Template for All / Change All / Trace
  // All as always-visible header buttons, and open-by-default keeps those mass
  // actions discoverable instead of hiding them behind an icon on every mount.
  const [actionBarOpen, setActionBarOpen] = useState(
    () => !!renderCardRow && (bulkActions?.length ?? 0) > 0,
  );
  const hasBulkActions = !!bulkActions && bulkActions.some((a) => !a.hidden);
  const [subBarOpen, setSubBarOpen] = useState(() => !!subHeaderBar?.defaultOpen);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  // Column whose resize edge is hovered — drives the full-height resize line.
  const [resizeHoverColId, setResizeHoverColId] = useState<string | null>(null);
  // Column whose insert "+" is hovered (edit mode) — the host the new column
  // lands after. Drives a full-column highlight so you can see where it inserts.
  const [insertHoverColId, setInsertHoverColId] = useState<string | null>(null);

  // Loading timer state
  const loadStartTimeRef = useRef<number | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [loadedInSeconds, setLoadedInSeconds] = useState<number | null>(null);
  const loadCompletedRef = useRef(false);

  // Track loading state changes for timer
  useEffect(() => {
    if (loading) {
      // Start timing
      loadStartTimeRef.current = Date.now();
      loadCompletedRef.current = false;
      setElapsedSeconds(0);
      setLoadedInSeconds(null);
    } else if (loadStartTimeRef.current && !loadCompletedRef.current) {
      // Loading just finished - calculate final time (only once per load cycle)
      const finalTime = Math.floor((Date.now() - loadStartTimeRef.current) / 1000);
      setLoadedInSeconds(finalTime);
      loadStartTimeRef.current = null;
      loadCompletedRef.current = true;

      // Clear "Loaded in" message after 5 seconds
      setTimeout(() => {
        setLoadedInSeconds(null);
      }, 5000);
    }
  }, [loading]);

  // Update elapsed time every second while loading (pauses when tab is hidden)
  useVisibilityAwareInterval(
    () => {
      if (loadStartTimeRef.current) {
        const elapsed = Math.floor((Date.now() - loadStartTimeRef.current) / 1000);
        setElapsedSeconds(elapsed);
      }
    },
    loading ? 1000 : null,
    false
  );

  // Format seconds as M:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Tabs state
  const [tabsCollapsed, setTabsCollapsed] = useState(false);
  const [internalActiveTabs, setInternalActiveTabs] = useState<string[]>(tabData[0]?.id ? [tabData[0].id] : []);
  const [showTabDropdown, setShowTabDropdown] = useState(false);

  // Initialize selectedTabGroupField from prop
  useEffect(() => {
    if (tabGroupingField) {
      setSelectedTabGroupField(tabGroupingField);
    }
  }, [tabGroupingField]);

  // Global reset trigger effect
  useEffect(() => {
    if (globalResetTrigger && globalResetTrigger > 0) {
      // Reset all table settings
      setGlobalFilter('');
      setColumnFilters([]);
      setTextFilterInputs({});
      setListFilterInputs({});
      setNumberFilterInputs({});
      setUserFilterTypes({});
      // Clear user-added (row) grouping, but restore the locked/default tab
      // grouping so a card with a locked grouping (e.g. Per Diem) keeps it.
      setGrouping([]);
      setSorting(defaultSorting || []);
      setPinnedColumns([]);
      setShowColumnFilters(false);
      setSelectedTabGroupField(tabGroupingField ?? null);
      localStorage.removeItem(userSettingsService.getTableCacheKey(settingsKey));
      // Notify parent of reset: clear row grouping, keep the locked/default tab grouping
      onGroupingChange?.([]);
      onTabGroupingChange?.(tabGroupingField || '');
    }
  }, [globalResetTrigger]);

  // Global page size effect
  useEffect(() => {
    if (globalPageSize !== undefined && globalPageSize > 0) {
      setPagination(prev => ({ ...prev, pageSize: globalPageSize, pageIndex: 0 }));
    }
  }, [globalPageSize]);

  // Global show filters effect (prop-level override takes precedence over context)
  useEffect(() => {
    if (globalShowFilters !== undefined) {
      setShowColumnFilters(globalShowFilters);
    }
  }, [globalShowFilters]);

  // Sync local state with context global settings (for controlled component behavior)
  useEffect(() => {
    setIsSuperCompact(contextSuperCompact);
  }, [contextSuperCompact]);

  useEffect(() => {
    if (globalShowFilters === undefined && localFilterOverride === null) {
      setShowColumnFilters(contextShowColumnFilters);
    }
  }, [contextShowColumnFilters, localFilterOverride]);

  // When a local filter override is explicitly set, apply it immediately
  useEffect(() => {
    if (localFilterOverride !== null) {
      setShowColumnFilters(localFilterOverride);
    }
  }, [localFilterOverride]);

  useEffect(() => {
    // Fit mode is derived, not persisted per-table: a local (this-table)
    // override wins, otherwise follow the global "Fit All in View" preference.
    // Keeping it derived means toggling the global no longer writes a setting
    // to every table.
    setColumnFitMode((localFitToWidthOverride ?? contextFitToWidth) ? 'fitToView' : 'normal');
  }, [contextFitToWidth, localFitToWidthOverride]);

  // Drop the fit overlay whenever we leave fit mode, so re-entering recomputes
  // it fresh from the user's current saved widths.
  useEffect(() => {
    if (columnFitMode !== 'fitToView') setFitOverlaySizing({});
  }, [columnFitMode]);

  // Use context global settings as the effective values (props can still override if needed)
  const effectiveSuperCompact = globalSuperCompact !== undefined
    ? globalSuperCompact
    : localSuperCompactOverride !== null
      ? localSuperCompactOverride
      : contextSuperCompact;

  // Global card height effect
  useEffect(() => {
    if (globalCardHeight && onCardHeightChange) {
      onCardHeightChange(globalCardHeight);
    }
  }, [globalCardHeight]);

  // Helper to extract date string (YYYY-MM-DD) from various date formats
  const extractDateString = (value: any): string | null => {
    if (!value || value === '0000-00-00' || value === '0000-00-00 00:00:00') return null;
    const valueStr = String(value).trim();

    // Handle YYYY-MM-DD or YYYY-MM-DD HH:MM:SS formats
    const datePart = valueStr.split(' ')[0].split('T')[0]; // Also handle ISO format with T
    if (datePart.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return datePart;
    }

    // Handle MM/DD/YYYY format
    const mmddyyyyMatch = valueStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (mmddyyyyMatch) {
      const [, month, day, year] = mmddyyyyMatch;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    return null;
  };

  // Helper to format date as MM/DD
  const formatDateShort = (dateStr: string): string => {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const [, month, day] = parts;
      return `${parseInt(month)}/${parseInt(day)}`;
    }
    return dateStr;
  };

  // Generate dynamic tabs based on selected field
  const dynamicTabData = useMemo((): TabData<T>[] => {
    if (!selectedTabGroupField) {
      return [];
    }

    // Check if this is a date-related field
    const fieldLower = selectedTabGroupField.toLowerCase();
    const isDateField = fieldLower.includes('date') || fieldLower.includes('datetime');

    if (isDateField && data.length > 0) {
      // Date-based tab grouping with Past Due, Today, and future dates
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

      // Collect all unique dates and check for past due
      const futureDatesSet = new Set<string>();
      let hasPastDue = false;
      let hasToday = false;
      let parsedAnyDate = false;

      data.forEach((row: any) => {
        const value = row[selectedTabGroupField];
        const dateStr = extractDateString(value);
        if (!dateStr) return;

        parsedAnyDate = true;
        if (dateStr < todayStr) {
          hasPastDue = true;
        } else if (dateStr === todayStr) {
          hasToday = true;
        } else {
          futureDatesSet.add(dateStr);
        }
      });

      // If we couldn't parse any dates, fall through to original behavior
      if (parsedAnyDate) {
        const tabs: TabData<T>[] = [];

        // Add Past Due tab if there are past due items
        if (hasPastDue) {
          tabs.push({
            id: 'past-due',
            label: 'Past Due',
            filterFn: (row: any) => {
              const value = (row as any)[selectedTabGroupField];
              const dateStr = extractDateString(value);
              if (!dateStr) return false;
              return dateStr < todayStr;
            }
          });
        }

        // Add Today tab if there are items for today
        if (hasToday) {
          tabs.push({
            id: 'today',
            label: 'Today',
            filterFn: (row: any) => {
              const value = (row as any)[selectedTabGroupField];
              const dateStr = extractDateString(value);
              return dateStr === todayStr;
            }
          });
        }

        // Add future date tabs sorted chronologically
        const sortedFutureDates = Array.from(futureDatesSet).sort();
        sortedFutureDates.forEach((dateStr) => {
          tabs.push({
            id: `date-${dateStr}`,
            label: formatDateShort(dateStr),
            filterFn: (row: any) => {
              const value = (row as any)[selectedTabGroupField];
              const rowDateStr = extractDateString(value);
              return rowDateStr === dateStr;
            }
          });
        });

        return tabs;
      }
      // If no dates were parsed, fall through to original non-date behavior below
    }

    // Non-date field: original behavior - get distinct values
    const distinctValuesMap = new Map<string, number>();
    data.forEach((row: any) => {
      const value = row[selectedTabGroupField];
      const valueStr = value != null ? String(value) : 'No Value';
      distinctValuesMap.set(valueStr, (distinctValuesMap.get(valueStr) || 0) + 1);
    });

    // Create tabs for each distinct value
    const tabs: TabData<T>[] = Array.from(distinctValuesMap.entries())
      .sort((a, b) => b[1] - a[1]) // Sort by count descending
      .map(([value]) => ({
        id: `tab-${value}`,
        label: value,
        filterFn: (row: any) => {
          const rowValue = row[selectedTabGroupField];
          const rowValueStr = rowValue != null ? String(rowValue) : 'No Value';
          return rowValueStr === value;
        }
      }));

    return tabs;
  }, [selectedTabGroupField, data]);

  // Generate tabs from external tab counts (backend optimization)
  const externalTabData = useMemo((): TabData<T>[] => {
    if (!externalTabCounts || externalTabCounts.length === 0) {
      return [];
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    return externalTabCounts.map(({ tab_value, count }) => {
      // Determine tab ID and label
      let id: string;
      let label: string;

      if (tab_value === 'past_due') {
        id = 'past-due';
        label = 'Past Due';
      } else if (tab_value === todayStr) {
        id = 'today';
        label = 'Today';
      } else {
        id = `date-${tab_value}`;
        label = formatDateShort(tab_value);
      }

      return {
        id,
        label,
        // No filterFn needed - backend handles filtering when dataIsTabFiltered=true
        // But we still provide count for display
        filterFn: undefined as any,
        _externalCount: count, // Store count for use in tabCounts
      };
    });
  }, [externalTabCounts]);

  // Determine which tab data to use (dynamic or provided)
  // Priority: 1) external tab counts, 2) provided tabData, 3) dynamic from data
  const hasExternalTabs = externalTabCounts && externalTabCounts.length > 0;
  const effectiveShowTabs = hasExternalTabs || (showTabs && tabData.length > 0) || selectedTabGroupField !== null;

  // Skip all tab logic when tabs aren't being used - return stable empty arrays
  const effectiveTabData = useMemo(() => {
    if (!effectiveShowTabs) return EMPTY_TAB_ARRAY as any[];
    if (hasExternalTabs) return externalTabData;
    if (showTabs && tabData.length > 0) return tabData;
    if (selectedTabGroupField) return dynamicTabData;
    return EMPTY_TAB_ARRAY as any[];
  }, [effectiveShowTabs, hasExternalTabs, externalTabData, showTabs, tabData, selectedTabGroupField, dynamicTabData]);

  // Use controlled or uncontrolled tab state - skip when no tabs
  const currentActiveTabs: string[] = useMemo(() => {
    if (!effectiveShowTabs) return EMPTY_TAB_ARRAY;
    if (multiTabSelect) {
      if (activeTabs !== undefined && activeTabs.length > 0) return activeTabs;
      return internalActiveTabs.length > 0
        ? internalActiveTabs
        : (effectiveTabData[0]?.id ? [effectiveTabData[0].id] : []);
    }
    // Single-tab mode: wrap in array for unified internal handling
    const singleTab = activeTab !== undefined ? activeTab : internalActiveTabs[0] || '';
    return singleTab ? [singleTab] : [];
  }, [effectiveShowTabs, multiTabSelect, activeTabs, activeTab, internalActiveTabs, effectiveTabData]);

  // Backwards-compatible single value for legacy code paths
  const currentActiveTab = currentActiveTabs[0] || '';

  const handleTabChange = (tabId: string) => {
    if (multiTabSelect) {
      const isSelected = currentActiveTabs.includes(tabId);
      // Prevent deselecting the last tab
      if (isSelected && currentActiveTabs.length <= 1) return;

      const newTabs = isSelected
        ? currentActiveTabs.filter(id => id !== tabId)
        : [...currentActiveTabs, tabId];

      if (onActiveTabsChange) onActiveTabsChange(newTabs);
      else setInternalActiveTabs(newTabs);
    } else {
      if (onTabChange) onTabChange(tabId);
      else setInternalActiveTabs([tabId]);
    }
  };

  // Update internal active tabs when effective tab data changes
  useEffect(() => {
    if (effectiveTabData.length > 0) {
      const validTabs = currentActiveTabs.filter(
        tabId => effectiveTabData.some(tab => tab.id === tabId)
      );
      if (validTabs.length === 0) {
        const fallback = [effectiveTabData[0].id];
        setInternalActiveTabs(fallback);
        if (multiTabSelect && onActiveTabsChange) onActiveTabsChange(fallback);
        if (onTabChange) onTabChange(fallback[0]);
      } else if (validTabs.length !== currentActiveTabs.length) {
        setInternalActiveTabs(validTabs);
        if (multiTabSelect && onActiveTabsChange) onActiveTabsChange(validTabs);
      }
    }
  }, [effectiveTabData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Notify parent of current active tab on mount when tabs exist
  useEffect(() => {
    if (effectiveTabData.length > 0) {
      const activeId = currentActiveTab || effectiveTabData[0]?.id;
      if (activeId) {
        if (onTabChange) onTabChange(activeId);
        if (multiTabSelect && onActiveTabsChange && currentActiveTabs.length > 0) {
          onActiveTabsChange(currentActiveTabs);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveTabData.length > 0]);

  // Filter data based on active tab
  // If dataIsTabFiltered=true, data is already filtered by backend, skip client-side filtering
  const filteredDataByTab = useMemo(() => {
    if (!effectiveShowTabs || effectiveTabData.length === 0) {
      return data;
    }

    // If data is already filtered by tab on backend, return as-is
    if (dataIsTabFiltered) {
      return data;
    }

    const activeEntries = effectiveTabData.filter(tab => currentActiveTabs.includes(tab.id));
    const filterFns = activeEntries
      .map(tab => tab.filterFn)
      .filter((fn): fn is (row: T) => boolean => fn !== undefined);

    if (filterFns.length === 0) return data;

    // Union: row passes if it matches ANY selected tab's filter
    return data.filter(row => filterFns.some(fn => fn(row)));
  }, [data, effectiveShowTabs, effectiveTabData, currentActiveTabs, dataIsTabFiltered]);

  // Date columns for the filter popover: explicit prop ∪ auto-detected (every
  // non-blank value parses as a date) ∪ `meta.dateFilter` override. Keyed on a
  // joined string because the prop default `[]` is a fresh array every render.
  const dateColumnsKey = dateColumns.join(' ');
  const effectiveDateColumns = useMemo(
    () => resolveDateColumns(data as unknown[], columns as any[], dateColumns),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, columns, dateColumnsKey],
  );

  // Shared helper: format a raw value to its facet value. Date columns reduce to
  // a canonical "YYYY-MM-DD" day key (or "(Blank)"), which the popover renders as
  // a Year > Month > Day tree. Everything else is the raw string.
  const toFilterDisplayValue = useCallback((value: unknown, columnId: string): string => {
    if (effectiveDateColumns.has(columnId)) return toDateFacetValue(value);
    if (value == null || String(value) === '') return '(Blank)';
    return String(value);
  }, [effectiveDateColumns]);

  // Read a stored dropdown/exclusion selection. Date columns normalize legacy
  // entries ("05/18", "05/18/2025", ISO datetimes) to day keys — cached by array
  // identity, so this is free on the hot filter path.
  const readSelection = useCallback((columnId: string, raw: unknown): string[] | undefined => {
    if (!Array.isArray(raw)) return undefined;
    return effectiveDateColumns.has(columnId) ? normalizeDateSelection(raw as string[]) : (raw as string[]);
  }, [effectiveDateColumns]);

  // Cell readers for the tab-count matcher below: a column id is often not a row
  // key (Dispatch Planner's `live` → isLiveLoad, `rs` → rescheduleCount, accessorFn
  // columns), so reading `row[filter.id]` matched nothing and zeroed every tab.
  const columnValueReaders = useMemo(() => buildColumnValueReaders(columns as any[]), [columns]);

  // Compute tab counts that respect global filter and column filters
  // This applies the same filtering logic as the table to the base data, then counts per tab
  // If external tab counts are provided, use those instead of computing from data
  const tabCounts = useMemo(() => {
    if (!effectiveShowTabs || effectiveTabData.length === 0) {
      return {};
    }

    // If using external tab counts, use the _externalCount property from externalTabData
    if (hasExternalTabs) {
      const counts: Record<string, number> = {};
      effectiveTabData.forEach((tab: any) => {
        counts[tab.id] = tab._externalCount ?? 0;
      });
      return counts;
    }

    // Apply global filter and column filters to base data
    let filteredData = data;

    // Apply global filter if set
    if (globalFilter && globalFilter.trim() !== '') {
      const searchLower = globalFilter.toLowerCase();
      filteredData = filteredData.filter((row: any) => {
        // Search across all string values in the row
        return Object.values(row).some(value =>
          value != null && String(value).toLowerCase().includes(searchLower)
        );
      });
    }

    // Apply column filters if set
    if (columnFilters.length > 0) {
      filteredData = filteredData.filter((row: any) => {
        return columnFilters.every(filter => {
          const filterValue = readSelection(filter.id, filter.value) ?? filter.value;
          const filterType = userFilterTypes[filter.id] ?? columnFilterTypes[filter.id] ?? 'dropdown';
          const isExclusion = isExcludeFilterType(filterType);

          // Helper to check if a single row matches this filter
          const read = columnValueReaders.get(filter.id);
          const rowMatchesFilter = (r: any) => {
            const v = read ? read(r, 0) : r[filter.id];
            if (isNumberFilterValue(filterValue)) {
              return numberFilterMatches(v, filterValue);
            }
            if (Array.isArray(filterValue)) {
              if (filterValue.length === 0) return true;
              // Handle array cell values (multi-value columns)
              let included: boolean;
              if (Array.isArray(v)) {
                included = v.length === 0 ? filterValue.includes('(None)') : v.some((item: any) => facetSelectionIncludes(filterValue, toFilterDisplayValue(item, filter.id)));
              } else {
                included = facetSelectionIncludes(filterValue, toFilterDisplayValue(v, filter.id));
              }
              return isExclusion ? !included : included;
            }
            if (typeof filterValue === 'string') {
              return String(v ?? '').toLowerCase().includes(filterValue.toLowerCase());
            }
            return true;
          };

          // Check if this row matches
          if (rowMatchesFilter(row)) return true;

          // If getRelatedRows is provided, check if any related row matches
          if (getRelatedRows) {
            const relatedRows = getRelatedRows(row);
            for (const relatedRow of relatedRows) {
              if (rowMatchesFilter(relatedRow)) {
                return true;
              }
            }
          }

          return false;
        });
      });
    }

    // Count per tab
    const counts: Record<string, number> = {};
    effectiveTabData.forEach(tab => {
      if (tab.filterFn) {
        counts[tab.id] = filteredData.filter(tab.filterFn).length;
      } else {
        counts[tab.id] = filteredData.length;
      }
    });

    return counts;
  }, [data, effectiveShowTabs, effectiveTabData, globalFilter, columnFilters, hasExternalTabs, getRelatedRows, columnValueReaders]);

  // Parse locked columns into left and right arrays
  const lockedColumnsLeft = useMemo(() => {
    return lockedColumns
      .map(col => typeof col === 'string' ? { id: col, side: 'left' as const } : col)
      .filter(col => (col.side || 'left') === 'left')
      .map(col => col.id);
  }, [lockedColumns]);

  const lockedColumnsRight = useMemo(() => {
    return lockedColumns
      .map(col => typeof col === 'string' ? { id: col, side: 'left' as const } : col)
      .filter(col => col.side === 'right')
      .map(col => col.id);
  }, [lockedColumns]);

  const allLockedColumnIds = useMemo(() => {
    return [...lockedColumnsLeft, ...lockedColumnsRight];
  }, [lockedColumnsLeft, lockedColumnsRight]);

  // Compute default column order with locked columns in correct positions
  const defaultColumnOrder = useMemo(() => {
    const currentColumnIds = columns.map(col => (col as any).id || (col as any).accessorKey);
    if (allLockedColumnIds.length > 0) {
      const lockedSet = new Set(allLockedColumnIds);
      const nonLockedIds = currentColumnIds.filter(colId => !lockedSet.has(colId));
      return [
        ...lockedColumnsLeft,
        ...nonLockedIds,
        ...lockedColumnsRight,
      ];
    }
    return currentColumnIds;
  }, [columns, allLockedColumnIds, lockedColumnsLeft, lockedColumnsRight]);

  // Custom column pinning state - initialize with locked columns
  const [pinnedColumns, setPinnedColumns] = useState<string[]>(() => {
    return lockedColumns
      .map(col => typeof col === 'string' ? { id: col, side: 'left' as const } : col)
      .filter(col => (col.side || 'left') === 'left')
      .map(col => col.id);
  });
  const [pinnedColumnsRight, setPinnedColumnsRight] = useState<string[]>(() => {
    return lockedColumns
      .map(col => typeof col === 'string' ? { id: col, side: 'left' as const } : col)
      .filter(col => col.side === 'right')
      .map(col => col.id);
  });
  // Columns whose DATA CELLS render bold ("Bold Values" in the header context menu).
  // A display-only marking: it never touches headers, sorting or the data itself.
  const [boldColumns, setBoldColumns] = useState<string[]>([]);
  const toggleBoldColumn = useCallback((columnId: string) => {
    setBoldColumns(prev => prev.includes(columnId) ? prev.filter(id => id !== columnId) : [...prev, columnId]);
  }, []);
  // Column context menu state
  const [contextMenu, setContextMenu] = useState<{ columnId: string; x: number; y: number; anchor: HTMLElement } | null>(null);
  // Pro #s collected via Ctrl/Cmd+click on a `pronumber` cell, and whether the panel is open.
  const [collectedPros, setCollectedPros] = useState<string[]>([]);
  const [proCollectorOpen, setProCollectorOpen] = useState(false);
  // Bulk drag-edit selection state
  const [dragSelect, setDragSelect] = useState<{
    columnId: string;
    bulkEditMeta: BulkEditMeta;
    startRowIdx: number;
    endRowIdx: number;
  } | null>(null);
  const dragSelectRef = useRef<{
    columnId: string;
    bulkEditMeta: BulkEditMeta;
    startRowIdx: number;
    endRowIdx: number;
  } | null>(null);
  dragSelectRef.current = dragSelect;
  const [bulkEditModal, setBulkEditModal] = useState<{
    columnId: string;
    bulkEditMeta: BulkEditMeta;
    selectedRows: T[];
  } | null>(null);
  const [flashColumnId, setFlashColumnId] = useState<string | null>(null);
  const headerRefs = useRef<{ [key: string]: HTMLTableCellElement | null }>({});
  const [pinnedPositions, setPinnedPositions] = useState<{ [key: string]: number }>({});
  const [pinnedPositionsRight, setPinnedPositionsRight] = useState<{ [key: string]: number }>({});
  const originalPositions = useRef<{ [key: string]: number }>({});
  // Ref to access current pinnedColumns inside setColumnOrder functional updaters without stale closure
  const pinnedColumnsRef = useRef<string[]>(pinnedColumns);
  pinnedColumnsRef.current = pinnedColumns;

  // Track auto-size state: 'header' means last auto-size was to header, 'content' means to content
  const autoSizeState = useRef<{ [key: string]: 'header' | 'content' }>({});

  // Measure text width using a canvas (more performant than DOM manipulation)
  const measureTextWidth = useCallback((text: string, font: string = '14px sans-serif'): number => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return 100;
    context.font = font;
    return Math.ceil(context.measureText(text).width);
  }, []);

  // Calculate left position for a pinned column based on actual rendered widths
  const getCustomPinnedLeft = (columnId: string, allHeaders: Header<any, unknown>[]): number => {
    let left = 0;
    const visibleHeaders = allHeaders.filter(h => h.column.getIsVisible());

    // Find all columns that should be pinned before this one
    const pinnedBeforeThis: string[] = [];
    for (const header of visibleHeaders) {
      if (header.column.id === columnId) break;
      if (pinnedColumns.includes(header.column.id)) {
        pinnedBeforeThis.push(header.column.id);
      }
    }

    // Sum up their actual widths
    for (const id of pinnedBeforeThis) {
      const element = headerRefs.current[id];
      if (element) {
        left += element.offsetWidth;
      }
    }

    return left;
  };

  // Calculate right position for a right-pinned column based on actual rendered widths
  // The rightmost pinned column should have right: 0, second-from-right should have right: [width of rightmost], etc.
  const getCustomPinnedRight = (columnId: string, allHeaders: Header<any, unknown>[]): number => {
    let right = 0;
    const visibleHeaders = allHeaders.filter(h => h.column.getIsVisible());

    // Find all pinned-right columns that come AFTER this one in the DOM order (to its right)
    // These are the columns we need to make room for
    const pinnedAfterThis: string[] = [];
    let foundColumn = false;
    for (let i = 0; i < visibleHeaders.length; i++) {
      const header = visibleHeaders[i];
      if (header.column.id === columnId) {
        foundColumn = true;
        continue;
      }
      // Collect pinned-right columns that come after the target
      if (foundColumn && pinnedColumnsRight.includes(header.column.id)) {
        pinnedAfterThis.push(header.column.id);
      }
    }

    // Sum up their actual widths
    for (const id of pinnedAfterThis) {
      const element = headerRefs.current[id];
      if (element) {
        right += element.offsetWidth;
      }
    }

    return right;
  };

  // Toggle custom pinning
  const toggleCustomPin = (columnId: string) => {
    // Don't allow toggling locked columns
    if (allLockedColumnIds.includes(columnId)) {
      return;
    }

    const isPinned = pinnedColumns.includes(columnId);

    if (isPinned) {
      // Unpinning - restore original position
      setPinnedColumns(prev => prev.filter(id => id !== columnId));

      const currentOrder = table.getState().columnOrder;
      const allColumns = table.getAllColumns().map(c => c.id);
      const orderToUse = currentOrder.length > 0 ? currentOrder : allColumns;

      // Get the saved original position
      const originalIndex = originalPositions.current[columnId];

      if (originalIndex !== undefined) {
        // Remove from current position
        const newOrder = orderToUse.filter(id => id !== columnId);

        // Insert back at original position (or as close as possible)
        const insertIndex = Math.min(originalIndex, newOrder.length);
        newOrder.splice(insertIndex, 0, columnId);

        setColumnOrder(newOrder);

        // Clear the saved position
        delete originalPositions.current[columnId];
      }
    } else {
      // Pinning - save original position and move to pinned group
      const currentOrder = table.getState().columnOrder;
      const allColumns = table.getAllColumns().map(c => c.id);
      const orderToUse = currentOrder.length > 0 ? currentOrder : allColumns;

      // Save original position before moving
      const currentIndex = orderToUse.indexOf(columnId);
      if (currentIndex >= 0) {
        originalPositions.current[columnId] = currentIndex;
      }

      setPinnedColumns(prev => [...prev, columnId]);

      // Find the index of the last currently pinned column
      let lastPinnedIndex = -1;
      for (let i = 0; i < orderToUse.length; i++) {
        if (pinnedColumns.includes(orderToUse[i])) {
          lastPinnedIndex = i;
        }
      }

      // Remove the column from its current position
      const newOrder = orderToUse.filter(id => id !== columnId);

      // Insert it after the last pinned column
      const insertIndex = lastPinnedIndex >= 0 ? lastPinnedIndex + 1 : 0;
      newOrder.splice(insertIndex, 0, columnId);

      setColumnOrder(newOrder);
    }
  };

  // Toggle right pin for a column
  const toggleCustomPinRight = (columnId: string) => {
    if (allLockedColumnIds.includes(columnId)) return;

    const isPinned = pinnedColumnsRight.includes(columnId);
    if (isPinned) {
      setPinnedColumnsRight(prev => prev.filter(id => id !== columnId));
    } else {
      // If pinned left, remove from left first
      setPinnedColumns(prev => prev.filter(id => id !== columnId));
      setPinnedColumnsRight(prev => [...prev, columnId]);
    }
  };

  // Recalculate pinned positions whenever pinnedColumns changes or after render
  useEffect(() => {
    const calculatePositions = () => {
      const newPositions: { [key: string]: number } = {};
      const newPositionsRight: { [key: string]: number } = {};
      // Group headers and their leaves occupy different rows. Calculate against
      // the target's row so adjacent pinned leaves do not all get offset zero.
      const headersFor = (columnId: string) =>
        table.getHeaderGroups().find(group => group.headers.some(header => header.column.id === columnId))?.headers || [];

      // Calculate left positions
      for (const columnId of pinnedColumns) {
        newPositions[columnId] = getCustomPinnedLeft(columnId, headersFor(columnId));
      }

      // Calculate right positions
      for (const columnId of pinnedColumnsRight) {
        newPositionsRight[columnId] = getCustomPinnedRight(columnId, headersFor(columnId));
      }

      setPinnedPositions(newPositions);
      setPinnedPositionsRight(newPositionsRight);
    };

    // Calculate immediately
    calculatePositions();

    // Also recalculate on a slight delay to ensure DOM is fully rendered
    const timer = setTimeout(calculatePositions, 100);
    return () => clearTimeout(timer);
  }, [pinnedColumns, pinnedColumnsRight, data]);

  // Close context menu on click outside or Escape
  useEffect(() => {
    if (!contextMenu) return;
    const handleClick = () => setContextMenu(null);
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setContextMenu(null);
    };
    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [contextMenu]);

  // Handler for column visibility changes - ensures locked columns are always visible
  const handleColumnVisibilityChange = useCallback((updater: VisibilityState | ((old: VisibilityState) => VisibilityState)) => {
    setColumnVisibility(prev => {
      const newVisibility = typeof updater === 'function' ? updater(prev) : updater;
      // Force locked columns to always be visible
      if (allLockedColumnIds.length > 0) {
        const protectedVisibility = { ...newVisibility };
        for (const lockedId of allLockedColumnIds) {
          protectedVisibility[lockedId] = true;
        }
        return protectedVisibility;
      }
      return newVisibility;
    });
  }, [allLockedColumnIds]);

  // Handler for column order changes - only used for tables WITHOUT locked columns
  // Tables WITH locked columns manage their own order via drag handlers and settings loading
  const handleColumnOrderChange = useCallback((updater: ColumnOrderState | ((old: ColumnOrderState) => ColumnOrderState)) => {
    // If there are locked columns, ignore any external column order changes from TanStack Table
    // to prevent the table from reordering columns unexpectedly.
    // Column reordering for locked tables is handled directly via onDrop and settings loading.
    if (allLockedColumnIds.length > 0) {
      return; // Ignore the change
    }

    // No locked columns - allow the change
    setColumnOrder(prev => {
      const newOrder = typeof updater === 'function' ? updater(prev) : updater;
      return newOrder;
    });
  }, [allLockedColumnIds]);

  // Read availableFields via a ref inside columnsWithFilterFn so the memo (and the
  // TanStack column model) doesn't rebuild when callers pass an unstable
  // availableFields reference (only needed for placeholder labels in edit mode).
  const availableFieldsRef = useRef(availableFields);
  availableFieldsRef.current = availableFields;

  // Read getViewedKey via a ref so an unstable inline function from the caller
  // doesn't rebuild the column model (and drop focus) on every render.
  const getViewedKeyRef = useRef(getViewedKey);
  getViewedKeyRef.current = getViewedKey;
  const getViewedBlueKeyRef = useRef(getViewedBlueKey);
  getViewedBlueKeyRef.current = getViewedBlueKey;

  // Normalize trackViewedColumnId (string | string[]) to a stable array keyed by
  // content, so an inline array literal doesn't rebuild the column model each
  // render. Joined on NUL — a separator no column id can contain.
  const trackViewedKey = Array.isArray(trackViewedColumnId)
    ? trackViewedColumnId.join('\u0000')
    : (trackViewedColumnId ?? '');
  const trackViewedIds = useMemo(
    () => (trackViewedKey ? trackViewedKey.split('\u0000') : []),
    [trackViewedKey],
  );
  // Same normalization for the second, independently tracked (light blue) slot.
  const trackViewedBlueKey = Array.isArray(trackViewedBlueColumnId)
    ? trackViewedBlueColumnId.join('\u0000')
    : (trackViewedBlueColumnId ?? '');
  const trackViewedBlueIds = useMemo(
    () => (trackViewedBlueKey ? trackViewedBlueKey.split('\u0000') : []),
    [trackViewedBlueKey],
  );

  // Apply appropriate filter function to columns based on user preferences and columnFilterTypes prop
  // Priority: userFilterTypes > columnFilterTypes > 'dropdown' (default)
  const columnsWithFilterFn = useMemo((): ColumnDef<T>[] => {
    // Grouped (nested) column defs: a band header carries `columns: [...]` and
    // no accessor of its own. The filterFn and viewed-tracking decoration below
    // belong on the LEAVES — a band has no values to filter — so recurse and
    // pass the band through untouched apart from its rebuilt children. Without
    // this a grouped leaf keeps TanStack's `auto` filterFn while the dropdown
    // filter writes an array value, and filtering that column matches nothing.
    const decorate = (col: any): any => {
      if (Array.isArray(col.columns)) {
        return { ...col, columns: col.columns.map(decorate) };
      }
      const colId = (col as any).accessorKey || (col as any).id;
      const effectiveType = userFilterTypes[colId] ?? columnFilterTypes[colId] ?? 'dropdown';
      // The list modes store the same string[] the dropdown does, so they reuse its
      // filter fns outright — a pasted list and a set of ticked facets are the same
      // question. Only the popover that collects them differs.
      const filterFn = effectiveType === 'text'
        ? 'textContains'
        : effectiveType === 'number'
          ? 'numberRange'
          : isExcludeFilterType(effectiveType) ? 'arrExcludes' : 'arrIncludes';
      // Opt-in viewed tracking: wrap the named column's rendered cell in the
      // ProTableCell pill (colored when viewed + ring on the last-clicked value,
      // click-to-mark). Identity comes from getViewedKey(row) when provided,
      // else the cell's own value. Two independent slots: the default green PRO
      // slot and a light blue one (own namespace, own last-clicked ring) so a
      // table can track a second column — e.g. Ticket # alongside PRO.
      const trackedGreen = trackViewedIds.includes(colId);
      const trackedBlue = !trackedGreen && trackViewedBlueIds.includes(colId);
      if (trackedGreen || trackedBlue) {
        const origCell = col.cell;
        const keyRef = trackedBlue ? getViewedBlueKeyRef : getViewedKeyRef;
        return {
          ...col,
          filterFn: filterFn as any,
          cell: ((ctx: any) => {
            const rendered = origCell != null
              ? flexRender(origCell as any, ctx)
              : (ctx.getValue() as React.ReactNode);
            let key: string | number | null | undefined;
            try {
              key = keyRef.current ? keyRef.current(ctx.row?.original) : ctx.getValue();
            } catch {
              key = undefined;
            }
            return trackedBlue
              ? <ProTableCell pro={key} tone="blue" group={VIEWED_BLUE_GROUP}>{rendered}</ProTableCell>
              : <ProTableCell pro={key}>{rendered}</ProTableCell>;
          }) as any,
        };
      }
      return {
        ...col,
        filterFn: filterFn as any,
      };
    };
    const mapped = columns.map(decorate);
    // Staged adds (edit mode): blank placeholder columns until the user saves.
    // No data is fetched and nothing is persisted while they sit here.
    if (editMode && pendingAddedFields.length > 0) {
      const existing = new Set(mapped.map(c => (c as any).accessorKey || (c as any).id));
      for (const apiId of pendingAddedFields) {
        if (existing.has(apiId)) continue;
        const f = availableFieldsRef.current?.find(af => getApiFieldName(af.fieldname) === apiId);
        const label = (f?.abbreviation && f.abbreviation.trim()) || f?.displayname || apiId;
        mapped.push({
          id: apiId,
          accessorKey: apiId,
          header: label,
          enableSorting: false,
          enableColumnFilter: false,
          filterFn: 'arrIncludes',
          cell: () => '',
        } as any);
      }
    }
    if (!enableRowSelection) return mapped;
    // Opt-in leading checkbox column. Display column (no accessor): excluded
    // from sort / filter / hide / export so it never pollutes those features.
    const selectColumn: ColumnDef<T> = {
      id: SELECT_COLUMN_ID,
      size: 36,
      enableSorting: false,
      enableHiding: false,
      enableColumnFilter: false,
      enableResizing: false,
      meta: { excludeFromExport: true } as any,
      header: ({ table }) => (
        <input
          type="checkbox"
          aria-label="Select all rows"
          className="cursor-pointer"
          checked={table.getIsAllRowsSelected()}
          ref={(el) => { if (el) el.indeterminate = !table.getIsAllRowsSelected() && table.getIsSomeRowsSelected(); }}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          aria-label="Select row"
          className="cursor-pointer"
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
    };
    return [selectColumn, ...mapped];
  }, [columns, columnFilterTypes, userFilterTypes, enableRowSelection, editMode, pendingAddedFields, trackViewedIds, trackViewedBlueIds]);

  // Create table instance
  const table = useReactTable<T>({
    data: filteredDataByTab,
    columns: columnsWithFilterFn,
    defaultColumn: {
      minSize: 20,
      maxSize: 1000,
      // Disable automatic aggregation (e.g., sum for numeric fields) when grouping
      // This can be overridden per-column if needed in the future
      aggregationFn: () => null,
      aggregatedCell: () => null,
    },
    state: {
      sorting: effectiveSorting,
      columnFilters,
      globalFilter,
      columnVisibility,
      pagination,
      grouping,
      expanded,
      columnOrder,
      // In fit mode, render with the overlay laid on top of the saved widths
      // (falling back to saved widths until the overlay is computed).
      columnSizing: columnFitMode === 'fitToView' && Object.keys(fitOverlaySizing).length > 0
        ? fitOverlaySizing
        : columnSizing,
      rowSelection,
    },
    enableRowSelection: enableRowSelection && (canSelectRow ? (row) => canSelectRow(row.original) : true),
    onRowSelectionChange: setRowSelection,
    onSortingChange: (updater) => {
      activityUserActedRef.current = true;
      setSorting((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        if (onSortingChangeProp) onSortingChangeProp(next);
        return next;
      });
    },
    onColumnFiltersChange: (updater) => {
      activityUserActedRef.current = true;
      setColumnFilters(updater);
    },
    onGlobalFilterChange: (updater) => {
      activityUserActedRef.current = true;
      setGlobalFilter(updater);
    },
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onPaginationChange: setPagination,
    onGroupingChange: setGrouping,
    onExpandedChange: setExpanded,
    onColumnOrderChange: handleColumnOrderChange,
    onColumnSizingChange: (updater: any) => {
      // In fit mode, resizes adjust the (non-persisted) overlay only — so a user
      // can shrink everything to fit and then drag one column open, without
      // touching their saved widths. Re-fitting (on container resize) still
      // recomputes from the saved widths, so the overlay can't drift.
      // In normal mode, resizes update the saved widths as usual.
      if (columnFitMode === 'fitToView') {
        setFitOverlaySizing(updater);
      } else {
        setColumnSizing(updater);
      }
    },
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    autoResetExpanded: false,
    autoResetPageIndex: false,
    ...(getSubRows && { getSubRows }),
    // Allow rows to expand: either they have sub-rows OR renderExpandedContent is provided for top-level rows
    ...(renderExpandedContent && !getSubRows && {
      getRowCanExpand: (row) => row.depth === 0,
    }),
    // Use custom getRowId if provided, otherwise use 'id' field or fall back to index
    getRowId: (row: T, index: number) => {
      if (getRowIdProp) {
        return getRowIdProp(row);
      }
      const rowWithId = row as T & { id?: string | number | null };
      return rowWithId.id != null ? String(rowWithId.id) : String(index);
    },
    filterFns: {
      arrIncludes: (row, columnId, filterValue) => {
        if (!filterValue || !Array.isArray(filterValue) || filterValue.length === 0) {
          return true;
        }
        // Date columns: legacy saved entries normalized to day keys (cached).
        const selection = readSelection(columnId, filterValue) ?? filterValue;
        const cellValue = row.getValue(columnId);

        // Handle array cell values (multi-value columns like equipment/flags)
        if (Array.isArray(cellValue)) {
          if (cellValue.length === 0) {
            return selection.includes('(None)');
          }
          return cellValue.some(v => facetSelectionIncludes(selection, toFilterDisplayValue(v, columnId)));
        }

        // If this row matches, return true (case-insensitive — facet values are
        // bucketed by their most-frequent casing, so "paris" rows match a "PARIS" pick)
        if (facetSelectionIncludes(selection, toFilterDisplayValue(cellValue, columnId))) {
          return true;
        }
        // If getRelatedRows is provided, check if any related row matches
        if (getRelatedRows) {
          const relatedRows = getRelatedRows(row.original);
          for (const relatedRow of relatedRows) {
            if (facetSelectionIncludes(selection, toFilterDisplayValue((relatedRow as any)[columnId], columnId))) {
              return true;
            }
          }
        }
        return false;
      },
      arrExcludes: (row, columnId, filterValue) => {
        if (!filterValue || !Array.isArray(filterValue) || filterValue.length === 0) {
          return true;
        }
        const selection = readSelection(columnId, filterValue) ?? filterValue;
        const cellValue = row.getValue(columnId);
        if (Array.isArray(cellValue)) {
          if (cellValue.length === 0) return !selection.includes('(None)');
          return !cellValue.some((v: any) => facetSelectionIncludes(selection, toFilterDisplayValue(v, columnId)));
        }
        if (facetSelectionIncludes(selection, toFilterDisplayValue(cellValue, columnId))) {
          return false;
        }
        if (getRelatedRows) {
          const relatedRows = getRelatedRows(row.original);
          for (const relatedRow of relatedRows) {
            if (facetSelectionIncludes(selection, toFilterDisplayValue((relatedRow as any)[columnId], columnId))) {
              return false;
            }
          }
        }
        return true;
      },
      textContains: (row, columnId, filterValue) => {
        if (!filterValue || typeof filterValue !== 'string' || filterValue.trim() === '') {
          return true;
        }
        const cellValue = String(row.getValue(columnId) ?? '').toLowerCase();
        const searchValue = filterValue.toLowerCase().trim();
        return cellValue.includes(searchValue);
      },
      numberRange: (row, columnId, filterValue) => {
        if (!isNumberFilterValue(filterValue)) return true;
        return numberFilterMatches(row.getValue(columnId), filterValue);
      },
    },
    globalFilterFn: globalSearchFilterFn,
    manualSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });

  const totalPages = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex + 1;

  // Report selected row objects to the parent whenever the selection changes
  // (opt-in row selection). Keyed off rowSelection so it fires on toggle/clear.
  useEffect(() => {
    if (!enableRowSelection || !onSelectionChange) return;
    onSelectionChange(table.getSelectedRowModel().rows.map((r) => r.original));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelection, enableRowSelection]);

  // Host-driven clear: its own Clear button, or after it has acted on the selection.
  // Always a full clear — that is what the host asked for.
  useEffect(() => {
    if (enableRowSelection) setRowSelection({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionResetKey, enableRowSelection]);

  // Keep the selection honest when the underlying data changes (a new search, a
  // background refresh, the host filtering `data` upstream): nothing may stay selected
  // that is no longer on screen.
  //
  // WITH `getRowId` that means PRUNE, not clear. The ids are stable identities, so rows
  // that survived the change are still the same rows and un-ticking them is pure loss —
  // it is what made an upstream filter toggle (EDI Pending Invoices' "Errors only") wipe
  // a selection the user had just built, while an in-table column filter left it alone.
  //
  // WITHOUT `getRowId` it still means clear. TanStack falls back to the row INDEX, so a
  // surviving id can point at a completely different row after the data changes; keeping
  // it would silently re-target the selection, which is worse than losing it.
  const getRowIdRef = useRef(getRowIdProp);
  getRowIdRef.current = getRowIdProp;
  useEffect(() => {
    if (!enableRowSelection) return;
    const rowId = getRowIdRef.current;
    if (!rowId) { setRowSelection({}); return; }
    setRowSelection((prev) => {
      const keys = Object.keys(prev);
      if (keys.length === 0) return prev;
      const present = new Set(data.filter((row) => !canSelectRow || canSelectRow(row)).map((row) => rowId(row)));
      const survivors = keys.filter((k) => present.has(k));
      // Same object back when nothing was dropped, so this cannot loop on a re-render
      // (hosts pass `getRowId` inline, so its identity changes constantly).
      if (survivors.length === keys.length) return prev;
      const next: RowSelectionState = {};
      for (const k of survivors) next[k] = prev[k];
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, enableRowSelection, canSelectRow]);

  // Active column filters (entries with a meaningful value) — drives the funnel
  // badge, the hidden-filter warning chip, and the header funnel glyphs
  const activeColumnFilters = useMemo(
    () => columnFilters.filter(f =>
      Array.isArray(f.value) ? f.value.length > 0 : f.value !== '' && f.value != null
    ),
    [columnFilters]
  );
  const activeFilterColumnLabels = activeColumnFilters.map(f => {
    const col = table.getColumn(f.id);
    return typeof col?.columnDef.header === 'string' ? col.columnDef.header : f.id;
  });
  const clearAllColumnFilters = () => {
    setColumnFilters([]);
    setTextFilterInputs({});
    setNumberFilterInputs({});
    setFilterSearchText({});
  };

  // Get the filtered rows (after all filters: tabs, global, column filters)
  const filteredRows = table.getFilteredRowModel().rows;

  // Notify parent of filtered rows changes
  useEffect(() => {
    if (onFilteredRowsChange) {
      const filteredData = filteredRows.map(row => row.original);
      onFilteredRowsChange(filteredData);
    }
  }, [filteredRows, onFilteredRowsChange]);

  // Notify parent when the user changes search input or column filters (skip initial mount)
  const userFilterChangeMounted = useRef(false);
  useEffect(() => {
    if (!userFilterChangeMounted.current) {
      userFilterChangeMounted.current = true;
      return;
    }
    onUserFilterChange?.();
  }, [globalFilter, columnFilters, onUserFilterChange]);

  // Get the rows from the table model for virtualization
  const { rows } = table.getRowModel();
  const displayRows = useMemo(() => {
    type Entry = { row: typeof rows[number]; index: number; footer?: string };
    const result: Entry[] = [];
    const groups: Entry[] = [];
    const closeGroup = () => {
      const entry = groups.pop()!;
      const footer = groupFooter?.(entry.row.groupingValue, entry.row.groupingColumnId!, entry.row.getLeafRows().map(r => r.original));
      if (footer) result.push({ ...entry, footer });
    };
    rows.forEach((row, index) => {
      while (groups.length && groups[groups.length - 1].row.depth >= row.depth) closeGroup();
      const entry = { row, index };
      result.push(entry);
      if (groupFooter && row.getIsGrouped()) groups.push(entry);
    });
    while (groups.length) closeGroup();
    return result;
  }, [rows, groupFooter]);

  // Handle double-click on column resize handle to auto-size
  const handleAutoSizeColumn = useCallback((columnId: string, headerText: string) => {
    const currentState = autoSizeState.current[columnId];
    const padding = 48; // Account for padding, icons, etc.

    if (currentState !== 'header') {
      // First double-click or after content-fit: size to header width
      const headerWidth = measureTextWidth(headerText) + padding;
      setColumnSizing(prev => ({ ...prev, [columnId]: Math.max(headerWidth, 60) }));
      autoSizeState.current[columnId] = 'header';
    } else {
      // Second double-click: size to content width
      const visibleRows = rows.slice(0, 100); // Sample first 100 visible rows
      let maxContentWidth = 0;

      for (const row of visibleRows) {
        const cell = row.getAllCells().find(c => c.column.id === columnId);
        if (cell) {
          const value = cell.getValue();
          const textValue = value != null ? String(value) : '';
          const width = measureTextWidth(textValue);
          maxContentWidth = Math.max(maxContentWidth, width);
        }
      }

      const contentWidth = maxContentWidth + padding;
      setColumnSizing(prev => ({ ...prev, [columnId]: Math.max(contentWidth, 60) }));
      autoSizeState.current[columnId] = 'content';
    }
  }, [measureTextWidth, rows]);

  // Copy column data to clipboard
  const handleCopyColumn = useCallback(async (columnId: string, allRows: boolean, commaDelimited = false) => {
    const rows = allRows ? table.getCoreRowModel().rows : table.getFilteredRowModel().rows;
    const values = rows.map(row => {
      const val = row.getValue(columnId);
      const value = val == null ? '' : String(val);
      return commaDelimited && /[,"\r\n]/.test(value)
        ? `"${value.replace(/"/g, '""')}"`
        : value;
    });
    const text = values.join(commaDelimited ? ',' : '\n');

    try {
      await navigator.clipboard.writeText(text);
      setFlashColumnId(columnId);
      setTimeout(() => setFlashColumnId(null), 500);
    } catch {
      // Fallback for older browsers / permission denied
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        setFlashColumnId(columnId);
        setTimeout(() => setFlashColumnId(null), 500);
      } catch {
        console.error('Failed to copy column data to clipboard');
      }
    }
  }, [table]);

  // Copy arbitrary text to the clipboard (clipboard API with execCommand fallback).
  const copyTextToClipboard = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      } catch {
        console.error('Failed to copy to clipboard');
      }
    }
  }, []);

  // Toggle a Pro # in the collected list and open the (centered) collector panel.
  const handleProCellModifierClick = useCallback((e: React.MouseEvent, value: unknown) => {
    e.preventDefault();
    e.stopPropagation();
    const pro = value == null ? '' : String(value).trim();
    if (!pro) return;
    setCollectedPros(prev => (prev.includes(pro) ? prev.filter(p => p !== pro) : [...prev, pro]));
    setProCollectorOpen(true);
  }, []);

  // Apply fit-to-view sizing (used by toggle and resize observer)
  // Distributes available width proportionally based on each column's defined size,
  // so small columns (checkbox, flags) stay compact and larger columns get more space.
  const applyFitToView = useCallback(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const visibleColumns = table.getAllLeafColumns().filter(col => col.getIsVisible());
    if (visibleColumns.length === 0) return;

    const scrollbarBuffer = 20;
    const containerWidth = container.clientWidth - scrollbarBuffer;
    const minColumnWidth = 15;

    // Weight each column by its *saved* width (falling back to the column's
    // default size) — NOT its current on-screen size. In fit mode the on-screen
    // size is the overlay itself, so using it would scale the overlay against
    // itself and let a manually-set width leak through instead of being fit.
    const baseWidth = (col: typeof visibleColumns[number]) =>
      columnSizingRef.current[col.id] ?? (col.columnDef.size ?? 150);
    const totalDefinedSize = visibleColumns.reduce((sum, col) => sum + baseWidth(col), 0);
    if (totalDefinedSize <= 0) return;
    const scale = containerWidth / totalDefinedSize;

    const newSizing: Record<string, number> = {};
    for (const column of visibleColumns) {
      newSizing[column.id] = Math.max(Math.floor(baseWidth(column) * scale), minColumnWidth);
    }
    // Apply to the overlay only — never to the persisted columnSizing.
    setFitOverlaySizing(newSizing);
  }, [table]);

  // Handle toggling column fit modes: fitToView <-> normal
  const handleToggleColumnFit = useCallback((e?: React.MouseEvent) => {
    const container = tableContainerRef.current;
    if (!container) return;

    const visibleColumns = table.getAllLeafColumns().filter(col => col.getIsVisible());
    if (visibleColumns.length === 0) return;

    if (e?.shiftKey) {
      // Shift+click: local override only, don't affect global
      if (columnFitMode !== 'fitToView') {
        applyFitToView();
        setColumnFitMode('fitToView');
        setLocalFitToWidthOverride(true);
      } else {
        setColumnFitMode('normal');
        setLocalFitToWidthOverride(false);
      }
    } else {
      // Normal click: update global and clear local override
      setLocalFitToWidthOverride(null);
      if (columnFitMode !== 'fitToView') {
        applyFitToView();
        setColumnFitMode('fitToView');
        setGlobalFitToWidth(true);
      } else {
        setColumnFitMode('normal');
        setGlobalFitToWidth(false);
      }
    }
  }, [table, columnFitMode, applyFitToView, setGlobalFitToWidth]);

  // Resize observer to recalculate column widths when in fitToView mode
  useEffect(() => {
    if (columnFitMode !== 'fitToView') return;

    const container = tableContainerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      applyFitToView();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [columnFitMode, applyFitToView]);

  // Card mode: current visible DATA-column id set, passed to renderCardRow so the
  // card knows which fields to show. Keyed on the leaf-column model itself (TanStack
  // memoizes it — the ref changes iff the visible leaf model changes), so this also
  // reacts to columns-prop changes (e.g. additionalFields), not just visibility/order.
  const visibleLeafColumns = table.getVisibleLeafColumns();
  const cardVisibleColumnIds = useMemo(
    () => new Set(
      visibleLeafColumns.map((c) => c.id).filter((id) => !SPECIAL_COLUMN_IDS.has(id)),
    ),
    [visibleLeafColumns],
  );
  // Same columns, in COLUMN ORDER with display labels, minus the ones the card
  // renders in its own header strip. This is the ordered contract the card lays its
  // fields out with (and, later, the card-mode chip grid) — so both stay aligned.
  const cardHeaderIdSet = useMemo(() => new Set(cardHeaderColumnIds), [cardHeaderColumnIds]);
  const cardVisibleColumns = useMemo(
    () => visibleLeafColumns
      .filter((c) => !SPECIAL_COLUMN_IDS.has(c.id) && !cardHeaderIdSet.has(c.id))
      .map((c) => ({
        id: c.id,
        label: typeof c.columnDef.header === 'string' ? c.columnDef.header : c.id,
      })),
    [visibleLeafColumns, cardHeaderIdSet],
  );

  // Row virtualization for performance
  const estimateSize = useCallback((index: number) => {
    if (displayRows[index]?.footer) return 40;
    if (renderCardRow) return 240; // approx card height; measureElement corrects per-row
    if (!renderExpandedContent) return 40;
    const row = displayRows[index]?.row;
    if (!row) return 40;
    // Check if this row is expanded — return larger estimate to reduce layout thrash
    const isExpanded = row.getIsExpanded() && !row.getIsGrouped() && row.depth === 0;
    return isExpanded ? 290 : 40;
  }, [displayRows, renderExpandedContent, renderCardRow, expanded]); // eslint-disable-line react-hooks/exhaustive-deps

  const rowVirtualizer = useVirtualizer({
    count: displayRows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize,
    overscan: 10, // Render extra rows above/below viewport for smooth scrolling
  });

  // Remeasure rows when expansion state changes (for custom expanded content),
  // and when the card/table estimator flips: estimateSize returns 240 in card
  // mode vs 40 otherwise, but react-virtual's measurementsCache is index-keyed
  // and isn't invalidated by a new estimator, so a Single Row -> Multi Row
  // toggle would otherwise keep every cached 40px row until it scrolls into
  // view (visible reflow / scroll jump). `isCardMode` is a boolean, so this
  // only re-runs when the mode actually changes, not on every render.
  //
  // Same for `hasExpandedContent`: key on whether a renderer EXISTS, never on
  // its identity. Parents commonly rebuild that callback on every data update
  // (the Scheduler does, on each date entry and 15s poll); keyed on identity,
  // every update wiped the size cache, rows fell back to the 40px estimate,
  // and the resize corrections that followed shifted the scroll offset — the
  // grid "jumped" away from the row the user was editing.
  const isCardMode = !!renderCardRow;
  const hasExpandedContent = !!renderExpandedContent;
  useEffect(() => {
    if (hasExpandedContent || isCardMode) {
      // Use requestAnimationFrame to ensure DOM has painted before remeasuring
      let rafId: number;
      const raf = (fn: () => void) => { rafId = requestAnimationFrame(fn); };
      // Double RAF to ensure layout is complete
      raf(() => raf(() => {
        rowVirtualizer.measure();
        // measure() only CLEARS the size cache — it does not re-measure the rows
        // that are already on screen, and nothing else will either: React does not
        // re-invoke the (stable) measureElement ref for an unchanged node, and the
        // ResizeObserver stays silent because the DOM did not actually change size.
        // Left alone, every mounted row falls back to estimateSize (240 in card
        // mode) while a real card is taller, so each card gets overlapped by the
        // next one — the reported "cards overlap, last field row cut off".
        //
        // Re-measure the mounted rows one frame LATER, not inline: resizeItem()
        // compares the fresh measurement against measurementsCache, which still
        // holds the pre-measure() sizes until this render commits. Inline, the
        // delta is 0, the write is skipped and the cleared cache stays empty.
        raf(() => {
          rowVirtualizer.elementsCache.forEach((el) => {
            if (el.isConnected) rowVirtualizer.measureElement(el);
          });
        });
      }));
      return () => cancelAnimationFrame(rafId);
    }
  }, [expanded, hasExpandedContent, isCardMode, rowVirtualizer]);

  // ---------------------------------------------------------------------------
  // Totals bar (aggregation footer)
  //
  // Table mode only: card mode has no visible columns to sit under, so the Σ
  // button is hidden there and nothing below runs. The row also steps aside in
  // column edit mode, where the header and body grow "insert column" cells that
  // the totals row does not mirror — it would sit a column out of alignment.
  // The toggle itself stays saved, so the bar returns on leaving edit mode.
  // ---------------------------------------------------------------------------
  const showAggFooter = enableTotalsBar && resolveShowAggFooter(showAggFooterOverride, aggAdminShowFooter);
  const aggFooterActive = showAggFooter && !isCardMode && !editMode;

  /** Columns eligible for a slot, in on-screen order. */
  const aggColumns = useMemo(
    () => (aggFooterActive
      ? table.getVisibleLeafColumns().filter((c) => !SPECIAL_COLUMN_IDS.has(c.id))
      : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [aggFooterActive, table.getVisibleLeafColumns().map((c) => c.id).join('|')],
  );

  /**
   * The rows the calcs run over: every filtered row (all pages), or just the
   * selected ones when there is a selection. A selection that has since been
   * filtered away doesn't count.
   */
  const aggScopeRows = useMemo(() => {
    if (!aggFooterActive) return [];
    const selectedIds = Object.keys(rowSelection).filter((id) => rowSelection[id]);
    if (selectedIds.length > 0) {
      const selectedSet = new Set(selectedIds);
      const selected = filteredRows.filter((r) => selectedSet.has(r.id));
      if (selected.length > 0) return selected;
    }
    return filteredRows;
  }, [aggFooterActive, filteredRows, rowSelection]);

  const aggUsingSelection = useMemo(() => {
    if (!aggFooterActive) return false;
    const selectedCount = Object.keys(rowSelection).filter((id) => rowSelection[id]).length;
    return selectedCount > 0 && aggScopeRows.length !== filteredRows.length;
  }, [aggFooterActive, rowSelection, aggScopeRows.length, filteredRows.length]);

  /** Read a column's raw value off a row, honoring the meta.aggValue escape hatch. */
  const readAggValue = useCallback((column: any, row: any): unknown => {
    const custom = (column.columnDef.meta as any)?.aggValue;
    if (typeof custom === 'function') {
      try {
        return custom(row.original);
      } catch {
        return undefined;
      }
    }
    if (!column.accessorFn) return undefined;
    try {
      return row.getValue(column.id);
    } catch {
      return undefined;
    }
  }, []);

  /**
   * Per-column data type, which decides the calcs the picker offers. Inferred
   * from a sample of the current rows (plus meta.isDate / meta.aggType), and
   * upgraded to money when the column's rendered cell reads as currency.
   */
  const aggColumnTypes = useMemo(() => {
    const types: Record<string, AggColumnType> = {};
    if (!aggFooterActive) return types;
    const sampleRows = aggScopeRows.slice(0, 200);
    for (const column of aggColumns) {
      const meta = column.columnDef.meta as any;
      const values = sampleRows.map((row) => readAggValue(column, row));

      // Money detection: invoke the cell renderer for one row and see whether the
      // text starts with "$". A renderer needing React internals throws — caught,
      // and the column simply reads as a plain number.
      let rendersAsMoney = false;
      const firstRow = sampleRows[0];
      const cellDef = column.columnDef.cell;
      if (firstRow && typeof cellDef === 'function' && !(cellDef as any).prototype?.isReactComponent) {
        try {
          const cell = firstRow.getAllCells().find((c: any) => c.column.id === column.id);
          if (cell) rendersAsMoney = renderedTextLooksLikeMoney((cellDef as any)(cell.getContext()));
        } catch {
          rendersAsMoney = false;
        }
      }

      types[column.id] = resolveAggColumnType({ values, meta, rendersAsMoney });
    }
    return types;
  }, [aggFooterActive, aggColumns, aggScopeRows, readAggValue]);

  /**
   * The admin-configured starting calc per column, from the field definition's
   * `aggregate_default` (Field Manager). Registry-driven columns carry it on
   * `meta.aggDefault` — stamped by generateColumnDef from the definition itself,
   * so there is no column-id-to-field-name guessing; hand-written columns can
   * set the same meta by hand.
   */
  const aggFieldDefaults = useMemo(() => {
    const out: Record<string, AggId> = {};
    if (!aggFooterActive) return out;
    for (const column of aggColumns) {
      const fieldDefault = normalizeAggDefault((column.columnDef.meta as any)?.aggDefault);
      if (fieldDefault) out[column.id] = fieldDefault;
    }
    return out;
  }, [aggFooterActive, aggColumns]);

  /**
   * The calc each column actually shows: the user's own pick, else the admin
   * default. Nothing shows where neither is set.
   */
  const aggEffectiveCalcs = useMemo(() => {
    const out: Record<string, AggId> = {};
    if (!aggFooterActive) return out;
    for (const column of aggColumns) {
      const calc = resolveEffectiveCalc(aggColumnTypes[column.id] ?? 'text', {
        user: aggCalcs[column.id],
        admin: aggAdminCalcs[column.id],
        field: aggFieldDefaults[column.id],
      });
      if (calc) out[column.id] = calc;
    }
    return out;
  }, [aggFooterActive, aggColumns, aggColumnTypes, aggCalcs, aggAdminCalcs, aggFieldDefaults]);

  /**
   * Formatted results, computed only for columns that actually show a calc — an
   * empty column costs nothing. Note the calc runs regardless of the inferred
   * type: a Sum restored onto a column whose feed went all-text shows an em dash
   * rather than a fake 0.
   */
  const aggResults = useMemo(() => {
    const out: Record<string, string> = {};
    if (!aggFooterActive) return out;
    for (const column of aggColumns) {
      const calc = aggEffectiveCalcs[column.id];
      if (!calc) continue;
      const meta = column.columnDef.meta as any;
      const values = aggScopeRows.map((row) => readAggValue(column, row));
      const result = computeAggregation(calc, values);
      out[column.id] = formatAggValue(result, {
        money: aggColumnTypes[column.id] === 'money',
        // Same rule as the cell renderer: a column that hides an exactly-midnight
        // time must hide it in a Min/Max too, or the footer prints the 00:00 the
        // column exists to suppress.
        formatDate: (value) => formatDate(
          value,
          meta?.showTime !== false
            && !(meta?.hideMidnightTime && hasNoClockTime(value as string)),
        ),
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggFooterActive, aggColumns, aggEffectiveCalcs, aggScopeRows, aggColumnTypes, readAggValue]);

  const aggScopeHint = aggUsingSelection
    ? `Σ ${aggScopeRows.length.toLocaleString()} selected`
    : `Σ ${aggScopeRows.length.toLocaleString()} ${aggScopeRows.length === 1 ? 'row' : 'rows'}`;

  /** The leading slot carries the scope line. */
  const aggHintColumnId = aggColumns[0]?.id ?? null;

  const setAggCalcForColumn = (columnId: string, calc: AggId | null) => {
    setAggCalcs((prev) => {
      const next = { ...prev };
      if (calc) {
        next[columnId] = calc;
      } else if (aggAdminCalcs[columnId] || aggFieldDefaults[columnId]) {
        // Clearing a column that has a default underneath has to be recorded, or
        // that default would immediately take its place again.
        next[columnId] = 'none';
      } else {
        delete next[columnId];
      }
      return next;
    });
    setAggPicker(null);
  };

  // Get groupable columns from visible columns (excluding special columns like actions, selection)
  // If groupableColumns prop is provided, use it as a filter; otherwise allow all visible columns
  const validGroupableColumns = useMemo(() => {
    const visibleColumns = table.getAllLeafColumns().filter(col => {
      if (!col.getIsVisible()) return false;

      // Exclude special columns that shouldn't be groupable
      const columnId = col.id;
      if (columnId === 'select' || columnId === 'actions' || columnId === 'expander') return false;

      // Columns with a JSX/function header can opt in via meta.groupLabel
      if ((col.columnDef.meta as any)?.groupLabel) return true;

      // Exclude columns with no meaningful header
      const header = typeof col.columnDef.header === 'string' ? col.columnDef.header : '';
      if (header.toLowerCase() === 'actions' || header === '') return false;

      return true;
    });

    const visibleColumnIds = visibleColumns.map(col => col.id);

    // If groupableColumns prop is provided, filter to only those; otherwise use all visible
    if (groupableColumns.length > 0) {
      return visibleColumnIds.filter(colId => groupableColumns.includes(colId));
    }

    return visibleColumnIds;
  }, [groupableColumns, table]);

  // Export to Excel function
  const exportToExcel = async () => {
    if (loading || excelExportLoading) return;
    if (onExcelExport) {
      await onExcelExport();
      return;
    }
    // Default exports use the current table's filtered rows.
    if (table.getFilteredRowModel().rows.length === 0) {
      return;
    }

    activityTracker.track({
      event_type: 'table_action',
      target: activityTableId,
      detail: `export (${table.getFilteredRowModel().rows.length} rows)`,
    });

    // Get all visible columns, excluding Actions column
    const visibleColumns = table.getAllLeafColumns().filter(col => {
      if (!col.getIsVisible()) return false;

      // Exclude Actions column
      const header = typeof col.columnDef.header === 'string' ? col.columnDef.header : col.id;
      if (header.toLowerCase() === 'actions') return false;

      return true;
    });

    // Get filtered and sorted rows (all rows, not just current page)
    // Note: getSortedRowModel includes filtering since sorting happens after filtering in the pipeline
    const rows = table.getSortedRowModel().rows;

    // Resolve the ordered set of export columns.
    //   • exportColumnOrder (when provided) fully dictates the export — order,
    //     headers, and membership — regardless of on-screen order/visibility.
    //   • Otherwise: visible table columns followed by export-only extras that
    //     aren't already visible (the default behavior).
    // Each field carries its matching leaf column (if any) so values go through
    // the same formatted-value path; keys with no column fall back to raw data.
    const leafColumnsById = new Map(table.getAllLeafColumns().map(c => [c.id, c]));
    const visibleColumnIds = new Set(visibleColumns.map(col => col.id));
    const exportFields: { key: string; header: string; column?: any }[] =
      exportColumnOrder && exportColumnOrder.length > 0
        ? exportColumnOrder.map(({ key, header }) => ({ key, header, column: leafColumnsById.get(key) }))
        : [
            ...visibleColumns
              .filter(col => !(col.columnDef.meta as any)?.excludeFromExport)
              .map(col => ({
                key: col.id,
                header: typeof col.columnDef.header === 'string' ? col.columnDef.header : col.id,
                column: col,
              })),
            ...extraExportColumns
              .filter(ec => !visibleColumnIds.has(ec.key))
              .map(ec => ({ key: ec.key, header: ec.header, column: undefined })),
          ];
    const headers = exportFields.map(f => f.header);

    // Run a column's cell renderer and return whatever it produces (a string, or
    // a React element tree for extractText to walk).
    //
    // NOT flexRender: for a function renderer flexRender returns an UNRENDERED
    // React element (<Comp {...cellContext} />) whose only props are the cell
    // context — no children to walk — so the export silently fell through to the
    // raw row value for every function-rendered column. That is why datetimes
    // exported as "2026-08-19T07:00:00" instead of the on-screen local "08/19
    // 07:00", and booleans as "true" instead of "Y". Invoking the renderer gives
    // the export the value the user actually sees. A renderer that needs React
    // internals (hooks) or is a class component throws here; the caller catches
    // and falls back to the raw value, exactly as before this fix.
    const renderCellForExport = (column: any, cell: any) => {
      const cellDef = column.columnDef.cell;
      if (typeof cellDef === 'function' && !cellDef.prototype?.isReactComponent) {
        return cellDef(cell.getContext());
      }
      return flexRender(cellDef, cell.getContext());
    };

    // Helper function to get formatted value from a cell
    const getFormattedValue = (cell: any, renderedCell: any, row: any, column: any): string => {
      // Check if column has a custom exportValue function in meta
      if (column.columnDef.meta?.exportValue && typeof column.columnDef.meta.exportValue === 'function') {
        try {
          const exportValue = column.columnDef.meta.exportValue(row);
          if (exportValue != null) {
            return String(exportValue);
          }
        } catch (err) {
          // Fall through to default handling
        }
      }

      // Recursive function to extract text from React elements
      const extractText = (element: any, depth: number = 0): string => {
        if (depth > 10) return ''; // Prevent infinite recursion

        if (!element) return '';

        // Handle primitive values
        if (typeof element === 'string' || typeof element === 'number' || typeof element === 'boolean') {
          return String(element);
        }

        // Handle arrays
        if (Array.isArray(element)) {
          return element.map(e => extractText(e, depth + 1)).filter(Boolean).join(', ');
        }

        // Handle React elements
        if (typeof element === 'object' && element !== null) {
          // Check if it's a React element with props
          if ('props' in element) {
            // Anchors are action affordances (e.g. an icon link titled "Open in
            // legacy CMS"); their title/value props are UI text, never cell data.
            // Export only their visible text.
            if (element.type === 'a') {
              return element.props?.children != null
                ? extractText(element.props.children, depth + 1)
                : '';
            }
            // A native <select>'s children are its options, not its selection,
            // so the generic children walk below exports every choice
            // ("—, Y, N") on every row. Match the selected option and export
            // its label — what the cell actually shows — and fall back to the
            // value when no option matches (e.g. a stored-but-retired code).
            if (element.type === 'select') {
              const selectedValue = element.props?.value;
              if (selectedValue === undefined || selectedValue === null || selectedValue === '') return '';
              const options: any[] = [];
              const collectOptions = (node: any) => {
                if (Array.isArray(node)) node.forEach(collectOptions);
                else if (node && typeof node === 'object' && node.type === 'option') options.push(node);
              };
              collectOptions(element.props?.children);
              const selected = options.find(o => String(o.props?.value ?? '') === String(selectedValue));
              return selected ? extractText(selected.props?.children, depth + 1) : String(selectedValue);
            }
            // First try to get text content from children
            if (element.props?.children !== undefined && element.props.children !== null) {
              const childText = extractText(element.props.children, depth + 1);
              if (childText) return childText;
            }
            // Also check for common text-containing props
            if (element.props?.value !== undefined && element.props.value !== null) {
              return String(element.props.value);
            }
            if (element.props?.title !== undefined && element.props.title !== null) {
              return String(element.props.title);
            }
          }

          // Check if it's a plain object with display properties
          if ('name' in element && element.name) return String(element.name);
          if ('label' in element && element.label) return String(element.label);
          if ('text' in element && element.text) return String(element.text);
        }

        return '';
      };

      const extractedText = extractText(renderedCell);
      // Only use extracted text if it's meaningful (not empty and not just whitespace)
      if (extractedText && extractedText.trim() && extractedText.trim() !== '0') {
        return extractedText.trim();
      }

      // Fall back to raw value
      const rawValue = cell.getValue();

      // Handle raw value formatting
      if (rawValue === null || rawValue === undefined) return '';
      if (typeof rawValue === 'string' || typeof rawValue === 'number') return String(rawValue);

      // If raw value is an object, try to extract a display value
      if (typeof rawValue === 'object') {
        if ('name' in rawValue && rawValue.name) return String(rawValue.name);
        if ('label' in rawValue && rawValue.label) return String(rawValue.label);
        if ('text' in rawValue && rawValue.text) return String(rawValue.text);
        // Convert arrays to comma-separated strings
        if (Array.isArray(rawValue)) return rawValue.join(', ');
        return JSON.stringify(rawValue);
      }

      return String(rawValue);
    };

    // Build one export row's data. For fields backed by a table column, pull the
    // value through the formatted-value path (cell renderer / meta.exportValue);
    // for export-only keys with no column, read raw row data.
    const buildRowData = (row: any) => {
      const rowData: any = {};
      exportFields.forEach(({ key, column }) => {
        try {
          if (column) {
            const cell = row.getAllCells().find((c: any) => c.column.id === column.id);
            if (cell) {
              if (column.columnDef.cell && typeof column.columnDef.cell === 'function') {
                try {
                  const renderedCell = renderCellForExport(column, cell);
                  rowData[key] = getFormattedValue(cell, renderedCell, row.original, column);
                } catch (renderError) {
                  const rawValue = cell.getValue();
                  rowData[key] = rawValue != null ? String(rawValue) : '';
                }
              } else {
                const rawValue = cell.getValue();
                rowData[key] = rawValue != null ? String(rawValue) : '';
              }
            } else {
              const rawValue = (row.original as any)[key];
              rowData[key] = rawValue != null ? String(rawValue) : '';
            }
          } else {
            const rawValue = (row.original as any)[key];
            rowData[key] = rawValue != null ? String(rawValue) : '';
          }
        } catch (error) {
          rowData[key] = '';
        }
      });
      return rowData;
    };

    // Flatten the (possibly grouped) row tree into the exact on-screen order.
    // When grouping is active, getSortedRowModel().rows are GROUP rows whose
    // detail rows live in `subRows` — walking only the top level (the old
    // behavior) emitted a single line per group and dropped every detail row.
    // We emit a header line per group (grouped column = group value + subtotal
    // count, matching the on-screen "John Smith (12)"; all other columns blank)
    // followed by its detail rows, recursing so multi-level grouping nests.
    // Detail rows carry their real values, so the grouped column repeats down
    // the group. Ungrouped tables are unaffected: every top-level row is a
    // normal row and this reduces to a plain per-row map.
    const data: any[] = [];
    const groupHeaderRowIdxs = new Set<number>();
    const walkRows = (list: any[]) => {
      for (const row of list) {
        if (row.getIsGrouped && row.getIsGrouped()) {
          const headerData = buildRowData(row);
          const gKey: string | undefined = row.groupingColumnId;
          // Group header shows ONLY the grouped column, like the on-screen
          // header where every other cell is hidden — blank the rest so no
          // stray aggregated/first-leaf value leaks in.
          for (const f of exportFields) {
            if (f.key !== gKey) headerData[f.key] = '';
          }
          if (gKey && exportFields.some(f => f.key === gKey)) {
            const base = headerData[gKey] != null ? String(headerData[gKey]) : '';
            headerData[gKey] = `${base} (${row.subRows.length})`.trim();
          }
          groupHeaderRowIdxs.add(data.length);
          data.push(headerData);
          walkRows(row.subRows);
          const footer = groupFooter?.(row.groupingValue, gKey || '', row.getLeafRows().map((r: { original: T }) => r.original));
          if (footer && exportFields.length) {
            groupHeaderRowIdxs.add(data.length);
            data.push({ [exportFields[0].key]: footer });
          }
        } else {
          data.push(buildRowData(row));
        }
      }
    };
    walkRows(rows);
    if (exportFooter) {
      groupHeaderRowIdxs.add(data.length);
      const footer = exportFooter(table.getFilteredRowModel().rows.map(row => row.original));
      // Preserve numeric zero through the shared exporter, which otherwise blanks falsy values.
      data.push(Object.fromEntries(Object.entries(footer).map(([key, value]) => [key, String(value)])));
    }

    // Create workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    // Sanitize sheet name: Excel forbids \ / * ? : [ ] and limits to 31 chars.
    // With an exportName the file keeps the full name (only the sheet is cut).
    const exportTitle = (exportName || cardTitle || 'Data').replace(/[\\/*?:\[\]]/g, ' ');
    const sheetName = exportTitle.slice(0, 31);
    const worksheet = workbook.addWorksheet(sheetName);

    // Add header row with readable column names
    worksheet.addRow(headers);

    // Style header row
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' }
      };
    });

    // Build ordered list of column keys matching headers
    const allExportKeys = exportFields.map(f => f.key);

    // Add data rows. Group-header lines get bold text + a light shade across the
    // full width so they stand out like the on-screen grouped headers.
    //
    // Every value reaches this point as display TEXT (the formatted-value path
    // above stringifies on purpose so dates/booleans export as seen). Currency
    // text — "$1,234.50", "-$250.00", "($250.00)" — therefore landed in Excel as
    // a string, which pivots/SUMs refuse until the user converts the column; so
    // did percentage text — "98%", "99.5%" — which additionally cannot be shown
    // AS a percentage or referenced as a number by a sheet built on the export.
    // Hand both kinds to Excel as numbers with a matching number format, so they
    // still read as dollars / percentages but behave as numbers. A percentage
    // exports the way Excel means one: 0.98 under a "0%" format. Only text that
    // is WHOLLY a currency or percentage qualifies, and a cell that merely
    // contains a "%" among other text stays text.
    //
    // Plain numbers ("42", "1,234", "12.50") convert too, but only in a column
    // whose DATA is numeric (exportsAsNumber: its values arrived as numbers) — a
    // digit string in a text column (zip, booking #, container) stays text so
    // its leading zeros survive. Without this every count, weight and pro # was
    // "number stored as text". Leaf rows: the filtered model sits below grouping.
    const leafRows = table.getFilteredRowModel().rows;
    const numericKeys = new Set(
      exportFields
        .filter(({ key, column }) => exportsAsNumber(
          leafRows.map(row => (column ? readAggValue(column, row) : (row.original as any)[key])),
          column?.columnDef.meta,
        ))
        .map(f => f.key),
    );
    const numericCell = (v: any, key: string): { value: number; numFmt: string } | null => {
      if (typeof v !== 'string') return null;
      const cur = parseCurrencyText(v);
      if (cur) return { value: cur.value, numFmt: cur.decimals > 0 ? '"$"#,##0.00' : '"$"#,##0' };
      const pct = parsePercentText(v);
      if (pct) return { value: pct.value, numFmt: percentNumFmt(pct.decimals) };
      const num = numericKeys.has(key) ? parseNumberText(v) : null;
      if (num) return { value: num.value, numFmt: numberNumFmt(num) };
      return null;
    };
    data.forEach((rowData, idx) => {
      const values = allExportKeys.map(key => rowData[key] || '');
      const numericCells = values.map((v, i) => numericCell(v, allExportKeys[i]));
      const excelRow = worksheet.addRow(values.map((v, i) => numericCells[i]?.value ?? v));
      numericCells.forEach((num, i) => {
        if (!num) return;
        excelRow.getCell(i + 1).numFmt = num.numFmt;
      });
      if (groupHeaderRowIdxs.has(idx)) {
        excelRow.font = { bold: true };
        for (let c = 1; c <= headers.length; c++) {
          excelRow.getCell(c).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF0F0F0' },
          };
        }
      }
    });

    // Auto-fit column widths (approximate)
    worksheet.columns.forEach((column, index) => {
      const header = headers[index] || '';
      let maxLength = header.length;
      data.forEach(row => {
        const cellValue = row[allExportKeys[index]] || '';
        maxLength = Math.max(maxLength, String(cellValue).length);
      });
      column.width = Math.min(Math.max(maxLength + 2, 10), 50);
    });

    // Generate filename with the LOCAL date (toISOString() is UTC — after
    // 7 pm Central it would stamp tomorrow's date).
    const timestamp = toLocalDateKey();
    // Runs of punctuation/spaces collapse to ONE underscore ("WHSE OPS - TRANS"
    // -> WHSE_OPS_TRANS, not WHSE_OPS___TRANS), none at the ends.
    const stem = (exportName ? exportTitle : sheetName)
      .replace(/[^a-z0-9]+/gi, '_')
      .replace(/^_+|_+$/g, '');
    const filename = `${stem || 'Data'}_${timestamp}.xlsx`;

    // Write to buffer and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    await saveBlob(blob, filename);
  };

  // Finalize bulk drag selection on mouseup
  useEffect(() => {
    const handleMouseUp = () => {
      const ds = dragSelectRef.current;
      if (!ds) return;
      const minIdx = Math.min(ds.startRowIdx, ds.endRowIdx);
      const maxIdx = Math.max(ds.startRowIdx, ds.endRowIdx);
      if (minIdx !== maxIdx) {
        const selectedRows = rows.slice(minIdx, maxIdx + 1).map(r => r.original);
        setBulkEditModal({ columnId: ds.columnId, bulkEditMeta: ds.bulkEditMeta, selectedRows });
      }
      setDragSelect(null);
    };
    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, [rows]);

  // Close filter menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target as Node)) {
        setActiveFilterColumn(null);
      }
    };

    if (activeFilterColumn) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeFilterColumn]);

  // The filter dropdown is portaled and floats free of its trigger, so it must
  // not outlive the trigger UI: if the filter triggers unmount while a dropdown
  // is open (funnel toggled off hides the filter row/chips; edit mode replaces
  // the header; collapsing hides the whole body — header filter row AND card
  // chip bar), close it — otherwise it lingers detached with no anchor.
  // Card mode adds one more trigger-unmount path: collapsing the Edit Fields
  // section takes the chip grid AND the Columns picker (whose popover is
  // anchored by columnToggleRef inside that section) with it.
  useEffect(() => {
    const cardFieldsHidden = !!renderCardRow && !showCardFields;
    // showColumnFilters gates the header filter row, which only exists in table
    // mode — in card mode the chips answer to Edit Fields (cardFieldsHidden),
    // so a false showColumnFilters must not force a card dropdown closed.
    if (activeFilterColumn && ((!renderCardRow && !showColumnFilters) || editMode || collapsed || cardFieldsHidden)) {
      setActiveFilterColumn(null);
    }
    if (showColumnToggle && cardFieldsHidden) {
      setShowColumnToggle(false);
    }
  }, [activeFilterColumn, showColumnFilters, editMode, collapsed, renderCardRow, showCardFields, showColumnToggle]);

  // Keep filter dropdown attached to its (sticky) anchor input while open.
  // Why: the dropdown is portaled with position:fixed at coordinates captured on click.
  // When the page or any ancestor scrolls, the sticky header's viewport position changes
  // but the fixed dropdown stays put, so they drift apart.
  useEffect(() => {
    if (!activeFilterColumn) return;
    let rafId: number | null = null;
    const reposition = () => {
      rafId = null;
      const anchor = filterAnchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const dropdownWidth = filterDropdownSize.width;
      const viewportWidth = window.innerWidth;
      const left = Math.min(rect.left, viewportWidth - dropdownWidth - 16);
      setFilterInputPosition({ top: rect.bottom + 4, left });
    };
    const onScrollOrResize = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(reposition);
    };
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [activeFilterColumn, filterDropdownSize.width]);

  // Close grouping menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideButton = groupingMenuRef.current?.contains(target);
      const isInsidePortal = groupingPortalRef.current?.contains(target);
      if (!isInsideButton && !isInsidePortal) {
        setShowGroupingMenu(false);
      }
    };

    if (showGroupingMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showGroupingMenu]);

  // Close expand-groups menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!expandMenuRef.current?.contains(target)) {
        setShowExpandMenu(false);
      }
    };

    if (showExpandMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExpandMenu]);

  // Close column toggle menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideButton = columnToggleRef.current?.contains(target);
      const isInsidePortal = columnTogglePortalRef.current?.contains(target);
      if (!isInsideButton && !isInsidePortal) {
        setShowColumnToggle(false);
        setColumnSearchFilter(''); // Clear search when closing
      }
    };

    if (showColumnToggle) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnToggle]);

  // Close height menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (heightMenuRef.current && !heightMenuRef.current.contains(event.target as Node)) {
        setShowHeightMenu(false);
      }
    };

    if (showHeightMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showHeightMenu]);

  // Close rows menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideButton = rowsMenuRef.current?.contains(target);
      const isInsidePortal = rowsPortalRef.current?.contains(target);
      if (!isInsideButton && !isInsidePortal) {
        setShowRowsMenu(false);
      }
    };

    if (showRowsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showRowsMenu]);

  // Close tab group menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideButton = tabGroupMenuRef.current?.contains(target);
      const isInsidePortal = tabGroupPortalRef.current?.contains(target);
      if (!isInsideButton && !isInsidePortal) {
        setShowTabGroupMenu(false);
      }
    };

    if (showTabGroupMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTabGroupMenu]);

  // Close multi-tab dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tabDropdownRef.current && !tabDropdownRef.current.contains(event.target as Node)) {
        setShowTabDropdown(false);
      }
    };
    if (showTabDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTabDropdown]);

  // Helper to get menu position from a ref element
  const getMenuPosition = (ref: React.RefObject<HTMLDivElement>) => {
    if (!ref.current) return { top: 0, left: 0 };
    const rect = ref.current.getBoundingClientRect();
    return {
      top: rect.bottom + window.scrollY + 8,
      left: Math.min(rect.right + window.scrollX - 224, window.innerWidth - 240), // 224 = typical menu width, keep 16px from edge
    };
  };

  // Helper to toggle a single filter value for a column
  const toggleFilterValue = (columnId: string, value: string) => {
    const column = table.getColumn(columnId);
    if (!column) return;

    const currentFilter = readSelection(columnId, column.getFilterValue());

    // Exclusion mode: the value is the set of EXCLUDED values (unset/empty
    // excludes nothing). Toggling adds/removes a single exclusion.
    if (getEffectiveFilterType(columnId) === 'exclusion') {
      let newFilter: string[] | undefined;
      if (!currentFilter || currentFilter.length === 0) {
        newFilter = [value];
      } else if (currentFilter.includes(value)) {
        newFilter = currentFilter.filter(v => v !== value);
        if (newFilter.length === 0) newFilter = undefined;
      } else {
        newFilter = [...currentFilter, value];
      }
      column.setFilterValue(newFilter);
      return;
    }

    // Dropdown mode. Two distinct empty-ish states, by design:
    //   • undefined  → "all values selected" (no narrowing; every box checked)
    //   • []         → "no values selected" (every box unchecked; e.g. Uncheck All)
    // so we must NOT collapse [] back to undefined. Only when the selection grows
    // to cover every faceted value do we collapse to undefined (the clean
    // no-filter state). Working against the faceted set keeps the stored value
    // free of options the user can't see.
    const facetValues = (distinctValues[columnId] || []).map(item => item.value);
    const selected = new Set<string>(currentFilter === undefined ? facetValues : currentFilter);

    if (selected.has(value)) selected.delete(value);
    else selected.add(value);

    const coversAllFacets = facetValues.length > 0 && facetValues.every(v => selected.has(v));
    column.setFilterValue(coversAllFacets ? undefined : [...selected]);
  };

  // Helper to clear a column filter
  const clearColumnFilter = (columnId: string) => {
    const column = table.getColumn(columnId);
    if (column) {
      column.setFilterValue(undefined);
      setFilterSearchText(prev => ({ ...prev, [columnId]: '' }));
    }
  };

  // Helper to handle text filter input with debounce
  const handleTextFilterChange = (columnId: string, value: string) => {
    // Update input immediately for responsive UI
    setTextFilterInputs(prev => ({ ...prev, [columnId]: value }));

    // Clear any existing debounce timeout
    if (textFilterDebounceRef.current[columnId]) {
      clearTimeout(textFilterDebounceRef.current[columnId]);
    }

    // Debounce the actual filter application
    textFilterDebounceRef.current[columnId] = setTimeout(() => {
      const column = table.getColumn(columnId);
      if (column) {
        // Set filter value as the text (empty string clears the filter)
        column.setFilterValue(value || undefined);
      }
    }, 300);
  };

  // Clear text filter input when filter is cleared
  const clearTextFilter = (columnId: string) => {
    setTextFilterInputs(prev => ({ ...prev, [columnId]: '' }));
    const column = table.getColumn(columnId);
    if (column) {
      column.setFilterValue(undefined);
    }
  };

  /** The number popover's current draft: what was typed this session, else the applied
   *  value rebuilt (a filter restored from saved settings), else the empty default. */
  const getNumberDraft = (columnId: string): NumberFilterDraft =>
    numberFilterInputs[columnId]
      ?? numberDraftFromValue(table.getColumn(columnId)?.getFilterValue())
      ?? DEFAULT_NUMBER_DRAFT;

  // Number mode applies live per change, like the text filter, but without the debounce:
  // a comparison against one bound is cheap, and the grid answering as the digits land
  // is the point ("< 5", "< 54", "< 540"...). An incomplete draft stores undefined.
  const applyNumberFilter = (columnId: string, draft: NumberFilterDraft) => {
    setNumberFilterInputs(prev => ({ ...prev, [columnId]: draft }));
    const column = table.getColumn(columnId);
    if (column) column.setFilterValue(numberValueFromDraft(draft));
  };

  const clearNumberFilter = (columnId: string) => {
    setNumberFilterInputs(prev => {
      const next = { ...prev };
      delete next[columnId];
      return next;
    });
    const column = table.getColumn(columnId);
    if (column) column.setFilterValue(undefined);
  };

  /** This column's pasted list is answered by the server, not by the loaded rows. */
  const isServerListColumn = useCallback(
    (columnId: string): boolean =>
      !!onListFilterRequest && !!listFilterServerColumns?.includes(columnId),
    [onListFilterRequest, listFilterServerColumnsKey], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Apply a pasted blob as this column's list. Unlike the text filter this is NOT
  // debounced per keystroke — it fires on Apply / Ctrl+Enter / blur, because
  // re-filtering a grid against a 500-value list on every character typed is work
  // nobody asked for.
  const applyListFilter = async (columnId: string, raw: string) => {
    setListFilterInputs(prev => ({ ...prev, [columnId]: raw }));
    const column = table.getColumn(columnId);
    if (!column) return;
    const values = parseListFilterValues(raw);

    // Capped grid: ask the table before matching anything. Filtering the loaded page
    // would report every value past the cap as a miss, and the user has no way to tell
    // that apart from one we genuinely never sent — a confident wrong answer, which is
    // worse than no answer at all.
    if (isServerListColumn(columnId)) {
      setListFilterPending(columnId);
      try {
        await onListFilterRequest!(columnId, values);
      } catch (error) {
        console.error('List filter request failed:', error);
      } finally {
        setListFilterPending(null);
      }
    }

    column.setFilterValue(values.length ? values : undefined);
  };

  const clearListFilter = (columnId: string) => {
    // Routed through applyListFilter so a server-resolved column also tells its host to
    // drop the scoped fetch — clearing only the local value would leave the grid showing
    // the list's rows with no filter on screen to explain them.
    void applyListFilter(columnId, '');
  };

  // Directly set filter type for a column, clearing any active filter
  const setColumnFilterType = (columnId: string, newType: ColumnFilterType) => {
    const currentType = userFilterTypes[columnId] ?? columnFilterTypes[columnId] ?? 'dropdown';
    // Flipping a list between include and exclude re-reads the SAME pasted values the
    // other way round, so both the list and the textarea survive. Every other switch
    // starts clean: a selection collected one way rarely means the same thing under
    // another, and silently reinterpreting it is worse than asking again.
    const keepListValue = isListFilterType(currentType) && isListFilterType(newType);

    const column = table.getColumn(columnId);
    if (column && !keepListValue) {
      column.setFilterValue(undefined);
    } else if (column && keepListValue) {
      // Include <-> exclude changes the filter FN while the stored values stay the
      // same, and TanStack memoizes the filtered row model on the VALUES. Without a
      // fresh array identity the grid would keep showing the include result under an
      // exclude filter — the flip would look like it did nothing.
      const current = column.getFilterValue() as string[] | undefined;
      if (current?.length) column.setFilterValue([...current]);
    }
    if (currentType === 'text') {
      setTextFilterInputs(prev => ({ ...prev, [columnId]: '' }));
    }
    if (isListFilterType(currentType) && !keepListValue) {
      setListFilterInputs(prev => ({ ...prev, [columnId]: '' }));
    }
    if (currentType === 'number') {
      setNumberFilterInputs(prev => {
        const next = { ...prev };
        delete next[columnId];
        return next;
      });
    }

    setUserFilterTypes(prev => ({ ...prev, [columnId]: newType }));
  };

  // Get effective filter type for a column (user preference > prop default > dropdown)
  const getEffectiveFilterType = (columnId: string): ColumnFilterType => {
    return userFilterTypes[columnId] ?? columnFilterTypes[columnId] ?? 'dropdown';
  };

  // Handle filter dropdown resize
  const handleFilterDropdownResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isResizingFilterRef.current = true;

    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = filterDropdownSize.width;
    const startHeight = filterDropdownSize.height;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const newWidth = Math.max(200, Math.min(500, startWidth + (moveEvent.clientX - startX)));
      const newHeight = Math.max(200, Math.min(600, startHeight + (moveEvent.clientY - startY)));
      setFilterDropdownSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      isResizingFilterRef.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [filterDropdownSize]);

  // Cleanup debounce timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(textFilterDebounceRef.current).forEach(timeout => {
        clearTimeout(timeout);
      });
    };
  }, []);

  // Helper to check all visible filter values (only affects items visible after search)
  const checkAllVisibleFilterValues = (columnId: string, visibleValues: string[]) => {
    const column = table.getColumn(columnId);
    if (!column) return;

    const currentFilter = readSelection(columnId, column.getFilterValue());

    // Exclusion mode: "Check All [Visible]" excludes those values.
    if (getEffectiveFilterType(columnId) === 'exclusion') {
      const next = new Set<string>(currentFilter || []);
      visibleValues.forEach(v => next.add(v));
      column.setFilterValue(next.size > 0 ? [...next] : undefined);
      return;
    }

    // Dropdown mode: undefined = all faceted values selected (an explicit []
    // means none selected). Add the visible values to the selection; if that
    // covers every faceted value, collapse to undefined (no narrowing).
    const facetValues = (distinctValues[columnId] || []).map(item => item.value);
    const selected = new Set<string>(currentFilter === undefined ? facetValues : currentFilter);
    visibleValues.forEach(v => selected.add(v));
    const coversAllFacets = facetValues.length > 0 && facetValues.every(v => selected.has(v));
    column.setFilterValue(coversAllFacets ? undefined : [...selected]);
  };

  // Helper to uncheck all visible filter values (only affects items visible after search)
  const uncheckAllVisibleFilterValues = (columnId: string, visibleValues: string[]) => {
    const column = table.getColumn(columnId);
    if (!column) return;

    const currentFilter = readSelection(columnId, column.getFilterValue());

    // Exclusion mode: stop excluding the visible values.
    if (getEffectiveFilterType(columnId) === 'exclusion') {
      if (!currentFilter) return;
      const next = currentFilter.filter(v => !visibleValues.includes(v));
      column.setFilterValue(next.length > 0 ? next : undefined);
      return;
    }

    // Dropdown mode: start from the effective selection (undefined = all faceted
    // values) and remove the visible ones. The result is stored literally — an
    // empty [] is kept (all boxes unchecked), NOT collapsed to undefined, so
    // "Uncheck All" actually clears the checks instead of re-selecting everything.
    // Only a selection that still covers every facet collapses to undefined.
    const facetValues = (distinctValues[columnId] || []).map(item => item.value);
    const selected = new Set<string>(currentFilter === undefined ? facetValues : currentFilter);
    visibleValues.forEach(v => selected.delete(v));
    const coversAllFacets = facetValues.length > 0 && facetValues.every(v => selected.has(v));
    column.setFilterValue(coversAllFacets ? undefined : [...selected]);
  };

  // Fetch distinct values from backend for a column
  // `force` recomputes even when a value set is already cached — used when the ROWS
  // underneath the cache have been replaced (a server-resolved list filter), where the
  // cached facets would answer the "not in this table" question against the old page.
  const fetchDistinctValues = async (columnId: string, force = false) => {
    if (!force && (distinctValues[columnId] || loadingDistinctValues[columnId])) {
      return; // Already loaded or loading
    }

    setLoadingDistinctValues(prev => ({ ...prev, [columnId]: true }));

    try {
      // Extract this column's display values from a set of rows. Uses flatRows so
      // sub-rows contribute their own values (else values that only appear on
      // sub-rows are missing and counts under-report). Array cell values
      // (multi-value columns like equipment/flags) expand to one entry each.
      const collectDisplayValues = (rows: any[]): string[] => {
        const out: string[] = [];
        rows.forEach((row) => {
          const value = row.getValue(columnId);
          if (Array.isArray(value)) {
            if (value.length === 0) {
              out.push('(None)');
            } else {
              value.forEach(v => out.push(toFilterDisplayValue(v, columnId)));
            }
          } else {
            out.push(toFilterDisplayValue(value, columnId));
          }
        });
        return out;
      };

      const column = table.getColumn(columnId);

      // DISPLAYED options + counts come from this column's FACETED row model:
      // TanStack's getFacetedRowModel applies every OTHER active column filter
      // plus the global search, but NOT this column's own filter. So the dropdown
      // lists only values actually present in the current cross-filtered result
      // set, with counts that match — instead of the full original set (e.g. once
      // Size=40 is applied, the Type dropdown no longer offers types that only
      // exist on other sizes). Falls back to all rows if faceting is unavailable.
      const facetedRows = column?.getFacetedRowModel().flatRows ?? table.getPreFilteredRowModel().flatRows;
      // Collapse case-variants into a single bucket (labeled with the most-frequent
      // casing, counts summed) and sort — see mergeFacetValuesCaseInsensitive.
      const distinctValuesArray = mergeFacetValuesCaseInsensitive(collectDisplayValues(facetedRows));

      setDistinctValues(prev => ({
        ...prev,
        [columnId]: distinctValuesArray
      }));

      // No auto-select: an unset (undefined) dropdown filter is rendered as
      // "all values checked" and treated as no narrowing (see the checkbox
      // `isSelected` logic and the toggle/check-all helpers). This keeps the
      // stored filter value scoped to the faceted values the user actually sees,
      // so the selected count is accurate and "Uncheck All" can never leave
      // hidden, out-of-facet values selected.
    } catch (error) {
      console.error(`Failed to calculate distinct values for ${columnId}:`, error);
      setDistinctValues(prev => ({ ...prev, [columnId]: [] }));
    } finally {
      setLoadingDistinctValues(prev => ({ ...prev, [columnId]: false }));
    }
  };

  // Clear cached distinct values when any filter/search changes
  // But keep the active filter column's values so the dropdown doesn't empty while selecting
  useEffect(() => {
    setDistinctValues(prev => {
      if (activeFilterColumn && prev[activeFilterColumn]) {
        // Keep only the active filter column's cached values
        return { [activeFilterColumn]: prev[activeFilterColumn] };
      }
      return {};
    });
  }, [globalFilter, columnFilters, currentActiveTabs, activeFilterColumn]); // eslint-disable-line react-hooks/exhaustive-deps

  // Date-tree expansion is per popover open, not persisted.
  useEffect(() => { setDateTreeToggles({}); }, [activeFilterColumn]);

  // Fetch distinct values when filter menu is opened
  useEffect(() => {
    if (activeFilterColumn) {
      fetchDistinctValues(activeFilterColumn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilterColumn]);

  // A server-resolved list filter replaces the rows under the facet cache, and that
  // cache is what the "not in this table" report is computed from — so it has to be
  // rebuilt once the new rows are committed. Keyed on `data` rather than chained onto
  // the request: the refetch resolves before React has re-rendered with the new rows,
  // so recomputing there would read the page we just replaced.
  useEffect(() => {
    if (!activeFilterColumn || !isServerListColumn(activeFilterColumn)) return;
    void fetchDistinctValues(activeFilterColumn, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, activeFilterColumn]);

  // Detect narrow layout for responsive pagination
  useEffect(() => {
    const checkWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setIsNarrow(width < 600);
      }
    };

    checkWidth();
    window.addEventListener('resize', checkWidth);

    // Use ResizeObserver for more accurate container size detection
    const observer = new ResizeObserver(checkWidth);
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', checkWidth);
      observer.disconnect();
    };
  }, []);

  // Load table settings on mount and initialize locked columns
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // True when the most recent load ATTEMPT failed (DB/network error or batch parent failure).
  // While true, autosave is blocked so defaults can't overwrite real settings.
  const [loadFailed, setLoadFailed] = useState(false);

  // Layout templates are just named settings payloads, so they ride the same
  // gates as settings persistence: a table that never persists, or one whose
  // settings load failed (where writing anything could clobber the real saved
  // view), must not offer to save or apply one.
  //
  // settingsReadOnly deliberately does NOT disqualify: a borrowed layout is still
  // a perfectly good thing to save under a name, and opening a View to tweak and
  // re-publish it is the manager's main authoring loop.
  const templatesAvailable = !!settingsKey && !disableSettingsPersistence && !loadFailed && !settingsLoadFailed;

  // Where named layouts are read from and written to. Falls back to settingsKey,
  // so a grid that passes nothing behaves exactly as before. Deliberately NOT used
  // anywhere else: settings persistence, the localStorage cache and the admin
  // defaults all stay on settingsKey, so pooling the Views cannot make two grids
  // share one saved arrangement.
  const layoutPoolKey = templateScopeKey || settingsKey;

  // Applies a saved-settings object to table state. `saved` falsy => apply defaults.
  // Reused by: initial load, cache fallback on load failure, and recovery re-apply.
  const applyLoadedSettings = useCallback((saved: any) => {
    if (saved) {
      // Merge saved pinned columns with locked columns (locked columns take priority)
      if (saved.pinnedColumns) {
        const userPinnedColumns = saved.pinnedColumns.filter((id: string) => !allLockedColumnIds.includes(id));
        setPinnedColumns([...lockedColumnsLeft, ...userPinnedColumns]);
      } else {
        setPinnedColumns([...lockedColumnsLeft]);
      }

      // Set right locked columns
      setPinnedColumnsRight([...lockedColumnsRight]);

      // Bold markings — drop ids that no longer match a column, so a renamed or
      // removed field can't linger in the saved blob forever.
      if (Array.isArray(saved.boldColumns)) {
        const columnIds = new Set(columns.map(col => (col as any).id || (col as any).accessorKey));
        setBoldColumns(saved.boldColumns.filter((id: string) => columnIds.has(id)));
      } else {
        setBoldColumns([]);
      }

      // Filter column visibility to only include columns that exist in the current column set
      // Also ensure locked columns are always visible
      if (saved.columnVisibility) {
        const columnIds = new Set(columns.map(col => (col as any).id || (col as any).accessorKey));
        const filteredVisibility: VisibilityState = {};
        for (const [colId, visible] of Object.entries(saved.columnVisibility)) {
          if (columnIds.has(colId)) {
            // Force locked columns to always be visible
            filteredVisibility[colId] = allLockedColumnIds.includes(colId) ? true : (visible as boolean);
          }
        }
        // Explicitly force all locked columns to be visible (even if not in saved settings)
        for (const lockedId of allLockedColumnIds) {
          filteredVisibility[lockedId] = true;
        }
        // Apply defaults for new columns not present in saved settings
        for (const [colId, visible] of Object.entries(defaultColumnVisibility)) {
          if (columnIds.has(colId) && !(colId in filteredVisibility)) {
            filteredVisibility[colId] = visible;
          }
        }
        setColumnVisibility(filteredVisibility);
      }
      // If no saved visibility, don't set any - all columns visible by default

      // Restore saved column order while ensuring locked columns stay in correct positions
      if (saved.columnOrder && saved.columnOrder.length > 0) {
        const lockedSet = new Set(allLockedColumnIds);
        const validColumnIds = new Set(leafColumnIds(columns as any[]));
        // Filter saved order to only valid, non-locked columns
        const savedNonLocked = saved.columnOrder.filter(
          (id: string) => !lockedSet.has(id) && validColumnIds.has(id)
        );
        // Add any new columns that weren't in the saved order
        const savedSet = new Set(saved.columnOrder);
        const newColumns = leafColumnIds(columns as any[])
          .filter(id => !lockedSet.has(id) && !savedSet.has(id));
        const restoredOrder = [
          ...lockedColumnsLeft,
          ...savedNonLocked,
          ...newColumns,
          ...lockedColumnsRight,
        ];
        setColumnOrder(restoredOrder);
      } else {
        setColumnOrder(defaultColumnOrder);
      }
      // Filter sorting and grouping to only include columns that exist
      const validColumnIds = new Set(leafColumnIds(columns as any[]));
      if (saved.sorting) {
        const filteredSorting = saved.sorting.filter((sort: { id: string }) => validColumnIds.has(sort.id));
        setSorting(filteredSorting);
      }
      if (saved.columnSizing) setColumnSizing(saved.columnSizing);
      if (saved.grouping) {
        const filteredGrouping = saved.grouping.filter((colId: string) => validColumnIds.has(colId));
        setGrouping(filteredGrouping);
      }
      if (saved.expanded !== undefined) setExpanded(saved.expanded);
      // Only restore saved pageSize if pagination is enabled
      if (enablePagination && saved.pageSize) setPagination(prev => ({ ...prev, pageSize: saved.pageSize }));
      // Restore per-table density override (new format), fall back to legacy effective value
      if (saved.superCompactOverride !== undefined) {
        setLocalSuperCompactOverride(saved.superCompactOverride);
      } else if (saved.superCompact !== undefined) {
        setIsSuperCompact(saved.superCompact);
      }
      // columnFitMode is intentionally not restored — it's derived from the
      // local override + global preference (see the derive effect above).
      if (saved.fitToWidthOverride !== undefined) setLocalFitToWidthOverride(saved.fitToWidthOverride);
      if (saved.filterTypes) setUserFilterTypes(saved.filterTypes);
      if (saved.filterDropdownSize) setFilterDropdownSize(saved.filterDropdownSize);
      if (saved.columnFilters) {
        const validFilters = saved.columnFilters.filter((f: { id: string }) => validColumnIds.has(f.id));
        setColumnFilters(validFilters);
      }
      if (saved.localFilterOverride !== undefined && saved.localFilterOverride !== null) {
        setLocalFilterOverride(saved.localFilterOverride);
      }
      if (saved.selectedTabGroupField) setSelectedTabGroupField(saved.selectedTabGroupField);
      if (saved.activeTab) setInternalActiveTabs([saved.activeTab]);
      if (saved.tabsCollapsed !== undefined) setTabsCollapsed(saved.tabsCollapsed);
      if (saved.showCardFields !== undefined) setShowCardFields(!!saved.showCardFields);
      // Totals bar. Calcs for columns that no longer exist are dropped, the same
      // as saved filters/sorts; a calc on a hidden column is kept, so unhiding
      // the column brings its total back.
      // null/absent = never touched here, so an admin default still applies.
      setShowAggFooterOverride(
        typeof saved.showAggFooter === 'boolean' ? saved.showAggFooter : null,
      );
      if (saved.aggCalcs && typeof saved.aggCalcs === 'object') {
        const validAggCalcs: Record<string, AggCalcSetting> = {};
        for (const [colId, calc] of Object.entries(saved.aggCalcs as Record<string, AggCalcSetting>)) {
          if (validColumnIds.has(colId) && calc) validAggCalcs[colId] = calc;
        }
        setAggCalcs(validAggCalcs);
      } else {
        setAggCalcs({});
      }
    } else {
      // No saved settings - set locked columns and proper column order
      setPinnedColumns([...lockedColumnsLeft]);
      setPinnedColumnsRight([...lockedColumnsRight]);
      setBoldColumns([]);
      // No saved settings (also the reset-to-defaults path): defer to the admin
      // default rather than forcing the bar off.
      setShowAggFooterOverride(null);
      setAggCalcs({});

      // Don't set columnVisibility - all columns visible by default

      // Always set column order with locked columns in correct positions
      const currentColumnIds = columns.map(col => (col as any).id || (col as any).accessorKey);
      if (allLockedColumnIds.length > 0) {
        const lockedSet = new Set(allLockedColumnIds);
        const nonLockedIds = currentColumnIds.filter(colId => !lockedSet.has(colId));
        const defaultOrder = [
          ...lockedColumnsLeft,
          ...nonLockedIds,
          ...lockedColumnsRight,
        ];
        setColumnOrder(defaultOrder);
      } else {
        // No locked columns - just use definition order
        setColumnOrder(currentColumnIds);
      }
    }
  }, [
    columns,
    allLockedColumnIds,
    lockedColumnsLeft,
    lockedColumnsRight,
    defaultColumnVisibility,
    defaultColumnOrder,
    enablePagination,
  ]);

  // Opt-in (seedLayoutFromCache): apply the user's cached layout BEFORE first paint so
  // legacy-mode tables don't flash defaults → saved settings while the individual API
  // fetch is in flight. Reuses applyLoadedSettings (same column-existence/locked filtering
  // as the API path); the async load effect below still fetches and reconciles. Runs in
  // useLayoutEffect (pre-paint) so the default layout is never visible. No-op on a cold
  // cache (new device) — the async fetch fills it in. Does NOT set settingsLoaded, so
  // autosave stays blocked until the real load completes.
  useLayoutEffect(() => {
    if (!seedLayoutFromCache || !settingsKey) return;
    if (settingsReady === false) return; // batch mode still waiting — parent seeds instead
    try {
      const cached = localStorage.getItem(userSettingsService.getTableCacheKey(settingsKey));
      if (cached) applyLoadedSettings(JSON.parse(cached));
    } catch {
      // Corrupt/absent cache — the async load effect below will apply real settings.
    }
    // Run once on mount only; the async effect handles reconciliation with the server.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (settingsLoaded) return;
    // If batch mode and still waiting (settingsReady === false), don't load yet
    if (settingsReady === false) return;
    // Persistence disabled → never load saved settings; keep the in-memory
    // defaults (defaultSorting, default column order, etc.). Mark loaded so
    // downstream effects that gate on settingsLoaded still run.
    if (disableSettingsPersistence) { setSettingsLoaded(true); return; }

    const loadSettings = async () => {
      // Instant pre-load from localStorage cache — overridden by API result below.
      // NOTE: if you add a field here, also add it to the saved.* restore in applyLoadedSettings (above).
      // Skipped entirely under settingsReadOnly: the layout is on loan from
      // initialSettings (a published View), and the user's OWN cached column
      // filters would otherwise bleed into it and silently narrow the results.
      try {
        const cached = settingsReadOnly
          ? null
          : localStorage.getItem(userSettingsService.getTableCacheKey(settingsKey));
        if (cached) {
          const cachedSettings = JSON.parse(cached);
          if (cachedSettings.columnFilters) {
            const validColumnIds = new Set(columns.map(col => (col as any).id || (col as any).accessorKey));
            const validFilters = cachedSettings.columnFilters.filter((f: { id: string }) => validColumnIds.has(f.id));
            setColumnFilters(validFilters);
          }
          if (cachedSettings.localFilterOverride !== undefined && cachedSettings.localFilterOverride !== null) {
            setLocalFilterOverride(cachedSettings.localFilterOverride);
          }
        }
      } catch {
        // Corrupt cache — silently skip, API will fix it
      }

      let saved = initialSettings;
      let failed = false;

      if (settingsLoadFailed) {
        // Batch parent reported a real fetch failure for this dashboard.
        failed = true;
      } else {
        try {
          // Legacy mode: fetch individually when no batch initialSettings provided.
          if (saved === undefined && settingsReady === undefined) {
            const settings = await userSettingsService.getUserSetting(`table_${settingsKey}`);
            saved = settings?.setting_value; // null on 404 => "no settings", NOT a failure
          }
        } catch (error) {
          console.error('Failed to load table settings:', error);
          failed = true;
        }
      }

      // On failure, prefer the user's real cached view over hard defaults.
      if (failed && saved === undefined) {
        try {
          const cached = localStorage.getItem(userSettingsService.getTableCacheKey(settingsKey));
          if (cached) saved = JSON.parse(cached);
        } catch {
          // Corrupt/absent cache — fall through to defaults.
        }
      }

      // No per-user record (new user, or just-reset) → seed from the admin
      // default for this bucket if one has been published. Existing users with
      // their own row are unaffected: `saved` wins whenever it is present.
      const effective = (saved === undefined || saved === null) ? defaultSettings : saved;

      try {
        applyLoadedSettings(effective);
      } catch (error) {
        console.error('Failed to apply table settings:', error);
        applyLoadedSettings(undefined); // last-resort defaults
      }

      setLoadFailed(failed);
      setSettingsLoaded(true);
    };

    loadSettings();
    // Note: initialSettings is intentionally not in deps - we only want to use it on initial mount
    // settingsLoaded guard prevents re-running anyway
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsKey, settingsReady]);

  // The totals-bar portion of the published grid default, kept as its own
  // per-column fallback layer rather than folded into the all-or-nothing settings
  // seed — so it still reaches users who already have a settings row, which is
  // everyone who has ever sorted or filtered. See aggEffectiveCalcs.
  //
  // Deliberately its OWN effect, not part of the settings load: when the host
  // page passes no `defaultSettings` (~71 of ~75 grids) the table fetches the
  // default for its own settingsKey, and that request must not delay the column
  // layout. The bar simply picks the layer up a beat later.
  useEffect(() => {
    let cancelled = false;
    const loadAggAdminDefaults = async () => {
      let blob: any = defaultSettings;
      if (blob === undefined) {
        if (!settingsKey || disableSettingsPersistence) return;
        try {
          blob = await userSettingsService.getDefaultSettingCached(settingsKey);
        } catch {
          return; // no published default, or unreachable — not an error
        }
      }
      if (cancelled) return;
      setAggAdminCalcs(
        blob?.aggCalcs && typeof blob.aggCalcs === 'object' ? blob.aggCalcs : {},
      );
      setAggAdminShowFooter(
        typeof blob?.showAggFooter === 'boolean' ? blob.showAggFooter : null,
      );
    };
    loadAggAdminDefaults();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsKey, disableSettingsPersistence, JSON.stringify(defaultSettings ?? null)]);

  // Track whether initial load is complete (to skip save on first load)
  const initialLoadCompleteRef = useRef(false);

  // Build the persisted settings payload from current state.
  // NOTE: if you add a field here, also restore it in applyLoadedSettings.
  const buildSettingsData = () => {
    // Only save user-pinned/ordered columns (locked columns stay in fixed positions)
    const userPinnedColumns = pinnedColumns.filter(id => !allLockedColumnIds.includes(id));
    const userColumnOrder = columnOrder.filter(id => !allLockedColumnIds.includes(id));
    return {
      pinnedColumns: userPinnedColumns,
      boldColumns,
      columnVisibility,
      columnOrder: userColumnOrder,
      sorting,
      columnSizing,
      grouping,
      expanded,
      pageSize: pagination.pageSize,
      superCompactOverride: localSuperCompactOverride,
      fitToWidthOverride: localFitToWidthOverride,
      filterTypes: userFilterTypes,
      filterDropdownSize,
      // A pasted list is session state, never part of a saved layout. This payload
      // auto-saves to the server a second after any filter change, so a 500-value
      // list would ride into the user's settings row on every paste and then be
      // replayed on load against data that has moved on. The MODE persists via
      // filterTypes; the values do not.
      columnFilters: columnFilters.filter(f => !isListFilterType(getEffectiveFilterType(f.id))),
      localFilterOverride,
      selectedTabGroupField,
      activeTab: internalActiveTabs[0] ?? null,
      tabsCollapsed,
      showCardFields,
      // Persist the tri-state override, not the resolved value — writing the
      // resolved boolean would freeze today's admin default into the user's row.
      showAggFooter: showAggFooterOverride,
      aggCalcs,
    };
  };

  // Derive the staged-aware field sets from the LIVE table: the visible leaf
  // columns in their current order (minus the select column, and INCLUDING
  // edit-mode staged adds, which exist only in columnsWithFilterFn), split into
  // the base column set and everything beyond it — i.e. the user/admin-added
  // dynamic fields, mapped back to DB field names for `additionalFields`.
  // Shared by "Save as Defaults" and the View / System View saves so they can't drift.
  const deriveStagedFieldSets = () => {
    const fields = table.getVisibleLeafColumns().map(c => c.id).filter(id => id !== '__select__');
    const addApiIds = new Set(additionalFields.map(getApiFieldName));
    const currentColIds = leafColumnIds(columns as any[]);
    const baseColIds = currentColIds.filter(id => !addApiIds.has(id));
    const baseSet = new Set(baseColIds);
    return {
      fields,
      baseColIds,
      // Fields beyond the base set (existing additional + staged adds).
      additionalFields: fields.filter(id => !baseSet.has(id)).map(id => getDbFieldName(id)),
    };
  };

  // A template must also restore the user-added dynamic columns, so it carries
  // additionalFields on top of the regular settings blob — computed the same
  // staged-aware way "Save as Defaults" does it, because "Save As View"'s
  // contract is "what you see is what you save".
  //
  // Deliberately NOT folded into buildSettingsData: that payload is what the
  // debounced autosave writes into user_settings for EVERY table, where the
  // requested field set is owned by the host (saveEdit / the column picker
  // notify it), not by the settings row.
  //
  // `layoutMode` records which kind of grid the snapshot came from. It exists
  // because the LC/Dispatch custom cards pool every named layout under ONE
  // settings_key (utils/customCardKeys.ts), so the key can no longer tell a Multi
  // Row layout from a table one the way `lc-card-` vs `lc-cards-` used to. The
  // two render from different column sets, so loading one into the other yields a
  // mostly-empty layout — this is what lets Load View at least label them.
  // Snapshots saved before this stamp carry no `layoutMode` and go unlabelled.
  // `fieldAliases` records what THIS grid's column ids meant, so a View loaded on
  // another grid resolves against what the columns ARE rather than against how
  // this page happened to spell them. Omitted when the host declares nothing —
  // an absent map means "infer from the ids", which is right for the
  // registry-driven grids and keeps the payload out of layouts that gain nothing
  // from it.
  const buildTemplateSnapshot = () => ({
    ...buildSettingsData(),
    fields: deriveStagedFieldSets().fields,
    additionalFields: deriveStagedFieldSets().additionalFields,
    layoutMode: isCardMode ? 'cards' : 'table',
    ...(fieldAliases ? { fieldAliases } : {}),
  });

  // Persist immediately (used by the debounced autosave and by edit-mode Save).
  //
  // `force` is the ONE way past settingsReadOnly, used by edit-mode Save: while a
  // borrowed layout (a published View) is on screen, an explicit Save means
  // "make this mine", which is the user's own escape hatch from the View. Every
  // other caller must leave their own row untouched.
  const persistSettings = async ({ force = false }: { force?: boolean } = {}) => {
    if (settingsReadOnly && !force) return;
    const settingsData = buildSettingsData();
    try {
      localStorage.setItem(userSettingsService.getTableCacheKey(settingsKey), JSON.stringify(settingsData));
    } catch {
      // localStorage full or unavailable — silently skip
    }
    try {
      await userSettingsService.saveUserSetting(`table_${settingsKey}`, settingsData);
      onSettingsSaved?.(`table_${settingsKey}`, settingsData);
    } catch (error) {
      console.error('Failed to save table settings:', error);
    }
  };

  // Save table settings when they change (debounced)
  useEffect(() => {
    // Don't save until settings are loaded, and NEVER save while the load failed —
    // otherwise the default fallback view would overwrite the user's real settings.
    if (!settingsLoaded || loadFailed) return;
    // Persistence disabled → never write settings for this table.
    if (disableSettingsPersistence) return;
    // Layout on loan (a published View) → the user's own row stays untouched.
    // Their tweaks are transient too, until they commit them with edit-mode Save.
    if (settingsReadOnly) return;

    // While editing columns, edits are buffered — Save/Cancel decides persistence.
    if (editMode) return;

    // Skip the first trigger (initial load) - only save on actual user changes
    if (!initialLoadCompleteRef.current) {
      initialLoadCompleteRef.current = true;
      return;
    }

    const timer = setTimeout(async () => {
      try {
        await persistSettings();
      } catch (error) {
        console.error('Failed to save table settings:', error);
      }
    }, 1000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    settingsLoaded,
    loadFailed,
    editMode,
    JSON.stringify(pinnedColumns),
    JSON.stringify(boldColumns),
    JSON.stringify(columnVisibility),
    JSON.stringify(columnOrder),
    JSON.stringify(sorting),
    JSON.stringify(columnSizing),
    JSON.stringify(grouping),
    JSON.stringify(expanded),
    pagination.pageSize,
    settingsKey,
    JSON.stringify(allLockedColumnIds),
    localSuperCompactOverride,
    localFitToWidthOverride,
    JSON.stringify(userFilterTypes),
    JSON.stringify(filterDropdownSize),
    JSON.stringify(columnFilters),
    localFilterOverride,
    selectedTabGroupField,
    internalActiveTabs[0],
    tabsCollapsed,
    showCardFields,
    showAggFooterOverride,
    JSON.stringify(aggCalcs),
  ]);

  // A column may opt out of the leftover-width distribution with
  // `meta: { fixedWidth: true }`. Every other column has flexGrow 1, so spare
  // horizontal space is shared out and a small `size` still renders wide — fine for
  // data, wrong for an icon-only column like a row-actions pencil, which should stay
  // exactly as wide as it needs to be. Opt-in, so no existing column changes.
  const isFixedWidth = (column: any): boolean =>
    !!(column?.columnDef?.meta as any)?.fixedWidth;

  // --- Column layout helpers (shared by edit mode + the admin defaults modal) ---
  const columnLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    // Short names first (the abbreviation, matching what the real header shows);
    // fall back to the display name only when there's no abbreviation.
    if (availableFields) for (const f of availableFields) {
      const id = getApiFieldName(f.fieldname);
      if (f.abbreviation && f.abbreviation.trim()) map.set(id, f.abbreviation.trim());
      else if (f.displayname) map.set(id, f.displayname);
    }
    // The actual column header wins — it's exactly the label shown in the table.
    for (const col of columns as any[]) {
      const id = col.id || col.accessorKey;
      if (id && typeof col.header === 'string' && col.header.trim()) map.set(id, col.header);
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, availableFields]);
  const labelFor = (id: string) => columnLabelMap.get(id) || id;

  // Picker labels for field-definition-backed columns get the same two-line
  // treatment as the custom filter dropdown: the full display name reads as the
  // label, with the short name (the abbreviation the real header shows) beneath
  // it, and both are searchable. A short name alone is often ambiguous.
  const fieldPickerLabels = useMemo(() => {
    const map = new Map<string, { label: string; description?: string }>();
    if (availableFields) for (const f of availableFields) {
      const id = getApiFieldName(f.fieldname);
      const full = (f.displayname || '').trim();
      const short = (f.abbreviation || '').trim();
      if (!full && !short) continue;
      map.set(id, {
        label: full || short,
        description: full && short && short !== full ? short : undefined,
      });
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableFields]);
  // Columns with no field definition (host-defined ones) keep their header label.
  const pickerLabelFor = (id: string): { label: string; description?: string } =>
    fieldPickerLabels.get(id) || { label: labelFor(id) };

  // The field universe the insert picker offers: existing columns (so a removed
  // one can be re-added) + any extra available fields.
  const buildAllFields = (): AddableField[] => {
    const seen = new Set<string>();
    const out: AddableField[] = [];
    for (const c of columns as any[]) {
      const id = c.id || c.accessorKey;
      if (id && !seen.has(id)) {
        seen.add(id);
        out.push({ id, ...pickerLabelFor(id), expands: availableFields?.find(f => getApiFieldName(f.fieldname) === id)?.expands });
      }
    }
    for (const f of (availableFields || [])) {
      const id = getApiFieldName(f.fieldname);
      if (!seen.has(id)) { seen.add(id); out.push({ id, ...pickerLabelFor(id), expands: f.expands }); }
    }
    return out;
  };

  // --- Per-user column edit mode (edits the REAL table in place, buffered) ---
  // Snapshot the editable layout on entry; Save commits, Cancel/Reset restores.
  const editBaselineRef = useRef<any>(null);
  const captureLayout = () => ({
    columnVisibility: { ...columnVisibility },
    columnOrder: [...columnOrder],
    columnSizing: { ...columnSizing },
    pinnedColumns: [...pinnedColumns],
    pinnedColumnsRight: [...pinnedColumnsRight],
    boldColumns: [...boldColumns],
    additionalFields: [...additionalFields],
  });
  const restoreLayout = (snap: any) => {
    if (!snap) return;
    setColumnVisibility(snap.columnVisibility);
    setColumnOrder(snap.columnOrder);
    setColumnSizing(snap.columnSizing);
    setPinnedColumns(snap.pinnedColumns);
    setPinnedColumnsRight(snap.pinnedColumnsRight);
    if (snap.boldColumns) setBoldColumns(snap.boldColumns);
    const a = (snap.additionalFields as string[]) || [];
    const changed = a.length !== additionalFields.length || a.some((f, i) => f !== additionalFields[i]);
    if (changed) onAdditionalFieldsChange?.(a);
  };

  // Auto-fit every visible column to its content (edit toolbar "Auto Fit Width").
  const autoFitAllColumns = useCallback(() => {
    const padding = 48;
    const sampleRows = rows.slice(0, 100);
    const sizing: Record<string, number> = {};
    for (const col of table.getVisibleLeafColumns()) {
      const headerText = typeof col.columnDef.header === 'string' ? col.columnDef.header : col.id;
      let max = measureTextWidth(headerText);
      for (const row of sampleRows) {
        const cell = row.getAllCells().find(c => c.column.id === col.id);
        if (cell) { const v = cell.getValue(); max = Math.max(max, measureTextWidth(v != null ? String(v) : '')); }
      }
      sizing[col.id] = Math.max(max + padding, 60);
    }
    setColumnSizing(prev => ({ ...prev, ...sizing }));
  }, [measureTextWidth, rows, table]);

  const enterEditMode = () => {
    editBaselineRef.current = captureLayout();
    setPendingAddedFields([]);
    setEditDirty(false);
    setEditMode(true);
  };
  const saveEdit = () => {
    // Save is the single commit point for the requested dynamic-field set: apply
    // staged adds AND drop any dynamic column removed (hidden) during this edit
    // session, then notify the host once so it fetches/collapses in a single
    // re-query. Layout (order/visibility/sizing) is already live; persist it.
    let nextAdditional = [...additionalFields];
    if (pendingAddedFields.length > 0) {
      const newAdds = pendingAddedFields.map(getDbFieldName).filter(db => !nextAdditional.includes(db));
      if (newAdds.length > 0) nextAdditional = [...nextAdditional, ...newAdds];
    }
    // A user-added dynamic column that was removed (trash → hidden) is dropped from
    // the request so the host stops fetching it and any row-expansion collapses.
    nextAdditional = nextAdditional.filter(db => columnVisibility[getApiFieldName(db)] !== false);
    const additionalChanged =
      nextAdditional.length !== additionalFields.length
      || nextAdditional.some((f, i) => f !== additionalFields[i]);
    if (additionalChanged) onAdditionalFieldsChange?.(nextAdditional);
    // The user just committed their own layout; it supersedes any settings blob
    // still parked waiting for a field set (a template load or reset whose refetch
    // never landed). Leaving it parked would let it fire on a later `columns`
    // change and silently overwrite what was just saved.
    reapplyDefaultsRef.current = undefined;
    // Under settingsReadOnly the layout on screen is borrowed (a published View),
    // and every other writer is suppressed. An explicit Save is the user saying
    // "make this mine": write it to their own row despite the guard, then tell the
    // host, which drops the View so the grid reverts to normal persistence. Doing
    // nothing here instead would make Save a visible button with no effect.
    if (settingsReadOnly) {
      void persistSettings({ force: true });
      onReadOnlyLayoutSaved?.();
    } else {
      persistSettings();
    }
    setPendingAddedFields([]);
    editBaselineRef.current = captureLayout();
    setEditDirty(false);
    setShowEditExitPrompt(false);
    setEditMode(false);
  };
  const cancelEdit = () => {
    restoreLayout(editBaselineRef.current);
    setPendingAddedFields([]); // discard staged blank columns (nothing was fetched/saved)
    setEditDirty(false);
    setShowEditExitPrompt(false);
    setEditMode(false);
  };
  // Applying a template discards any staged edit-mode changes and leaves edit
  // mode, then runs the snapshot through applyLoadedSettings — the same path
  // server settings take, so column-existence filtering, locked columns and
  // defaults for unknown fields all behave identically. Both calls are
  // synchronous setState batches and applyLoadedSettings runs second, so its
  // values win over the baseline cancelEdit restores. Leaving edit mode also
  // re-arms the debounced autosave effect, which persists + caches the applied
  // layout and fires onSettingsSaved.
  //
  // Unlike applyLoadedSettings's other callers (initial load / recovery), a
  // template applies over state the user has already customized, and several
  // fields are restored under a TRUTHY guard (`if (saved.activeTab)`,
  // `if (saved.selectedTabGroupField)`) while being absent from the cancelEdit
  // buffer. A snapshot legitimately saved with no tab context (buildSettingsData
  // writes activeTab: null) would then leave the CURRENT tab/grouping/override
  // live — a half-applied mix. So reset those to their neutral defaults first
  // and let applyLoadedSettings re-apply whatever the snapshot does carry:
  // "absent from the snapshot" must mean cleared, not left over. Neutral values
  // match the existing reset paths (globalResetTrigger / resetAllSettings) and
  // each state's own initializer. tabsCollapsed / showCardFields need no reset —
  // they are guarded on `!== undefined` and buildSettingsData always writes them.
  //
  // Tab grouping additionally notifies the parent, which applyLoadedSettings's
  // callers don't need to: on the LC and Dispatch dashboards it is a CONTROLLED
  // value (tabGroupingField comes from cardConfig.tabGrouping and this callback
  // writes back into it, which then drives bucket-specific field remapping on
  // the next refetch). Clearing our copy while cardConfig kept the old field
  // would silently desync the two. '' means "back to the prop/default" — NOT
  // 'none', which the dashboards read as "user explicitly disabled tabs".
  //
  // The text-filter DISPLAY values need the same treatment for the same reason.
  // textFilterInputs / filterSearchText are live, non-buffered state (not in the
  // cancelEdit buffer and not in a snapshot), while the filtering itself lives in
  // columnFilters, which applyLoadedSettings DOES restore. Left alone, loading a
  // filterless template leaves phantom text in the header filter boxes and the
  // dropdowns' search boxes over unfiltered data. Cleared exactly as
  // clearAllColumnFilters does it.
  const prepareTemplate = (snapshot: unknown) => prepareTableTemplate(snapshot, {
    columnIds: leafColumnIds(columns),
    additionalFields,
    availableFields,
    canAddFields: !!onAdditionalFieldsChange,
    fieldAliases,
  });
  const handleLoadTemplate = (sourceSnapshot: unknown) => {
    const prepared = prepareTemplate(sourceSnapshot);
    if (prepared.reason) return;
    const snapshot = prepared.snapshot;
    cancelEdit();
    setSelectedTabGroupField(tabGroupingField ?? null);
    onTabGroupingChange?.(tabGroupingField || '');
    setInternalActiveTabs(tabData[0]?.id ? [tabData[0].id] : []);
    setLocalFilterOverride(null);
    setTextFilterInputs({});
    setNumberFilterInputs({});
    setFilterSearchText({});
    // A template also carries the user-added dynamic columns it was saved with
    // (buildTemplateSnapshot), and those columns may not exist yet — only the host
    // can fetch them. So this mirrors resetAllSettings: when the requested field
    // set differs, park the snapshot and notify the host; the effect keyed on
    // [columns] re-applies it once the new columns arrive (applyLoadedSettings
    // filters to columns that exist AT APPLY TIME, so applying now would silently
    // drop them). Unchanged => apply inline.
    // prepareTemplate derives the destination additions from all referenced fields,
    // including source base columns that are dynamic on the destination.
    // cancelEdit() above cannot fight this: its restoreLayout only notifies when
    // the baseline's additionalFields differ from the current prop, and inside edit
    // mode nothing changes that prop (saveEdit is the only writer, and the column
    // picker's writer is card-mode-only, where edit mode does not exist).
    const targetAdditional: string[] = (snapshot?.additionalFields as string[]) || [];
    const additionalChanged =
      targetAdditional.length !== additionalFields.length
      || targetAdditional.some((f, i) => f !== additionalFields[i]);
    if (additionalChanged) {
      reapplyDefaultsRef.current = { snapshot, targetAdditional };
      onAdditionalFieldsChange?.(targetAdditional);
    } else {
      applyLoadedSettings(snapshot);
    }
  };
  const resetEditToCurrent = () => {
    restoreLayout(editBaselineRef.current);
    setPendingAddedFields([]);
    setEditDirty(false);
  };
  const toggleEditMode = () => {
    if (!editMode) { enterEditMode(); return; }
    if (editDirty) { setShowEditExitPrompt(true); return; }
    setEditMode(false);
  };

  // Track whether the buffered layout differs from the baseline (or there are staged adds).
  useEffect(() => {
    if (!editMode || !editBaselineRef.current) return;
    const layoutChanged = JSON.stringify(captureLayout()) !== JSON.stringify(editBaselineRef.current);
    setEditDirty(layoutChanged || pendingAddedFields.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, JSON.stringify(columnVisibility), JSON.stringify(columnOrder), JSON.stringify(columnSizing), JSON.stringify(pinnedColumns), JSON.stringify(pinnedColumnsRight), JSON.stringify(additionalFields), JSON.stringify(pendingAddedFields)]);

  // Reorder/resize/show-hide are available in BOTH modes: realtime (autosaved)
  // from the main screen, and buffered inside edit mode. (Edit mode adds the
  // insert/remove affordances, read-only cells, and Save/Cancel/Reset.)
  const editColumnsActive = true;

  // Whole-column tint during a reorder drag (header + every body cell of that
  // column): amber = the column being dragged, blue = the drop target. `!` so it
  // wins over the cell's own background. Applies in both modes.
  const columnDragClass = (id: string) =>
    draggedColumn === id
      ? '!bg-amber-100/70 dark:!bg-amber-900/30'
      : (dragOverColumn === id && draggedColumn !== id)
      ? '!bg-blue-100 dark:!bg-blue-900/30'
      : '';

  // Full-height resize line: the column whose right edge is hovered, OR the one
  // actively being resized. Drawn as an inset right shadow on the header + every
  // body cell of that column so the line runs the whole column in both views.
  const resizingColId = (table.getState().columnSizingInfo as any)?.isResizingColumn || null;
  const resizeEdgeColId = resizeHoverColId || resizingColId;
  const columnResizeEdgeClass = (id: string) =>
    resizeEdgeColId === id ? '!shadow-[inset_-2px_0_0_0_#3b82f6]' : '';

  // Full-column green wash for the column whose insert "+" is hovered (edit
  // mode). Applied to the header + every body cell so the whole column lights
  // up, showing the new column will land right after it. `!` beats cell bg.
  const columnInsertHoverClass = (id: string) =>
    insertHoverColId === id ? '!bg-green-100/60 dark:!bg-green-900/30' : '';

  // --- Persistent "+" insert columns (edit mode) ------------------------------
  // In edit mode we render a real, narrow column between every pair of (non-
  // pinned) data columns — not a floating handle over padding. The same cell is
  // injected into all four aligned flex rows (header, filter, body, footer) so
  // the lane reads as an actual column running top-to-bottom. Its header holds
  // the green "+"; hovering it washes the whole lane green to show where the new
  // column lands (right after `afterColId`).
  const ADD_COL_W = 16;
  // A "+" lane sits after column `colId` when both it and its right neighbour are
  // non-pinned (a trailing lane after the last column is fine). We never inject
  // inside the pinned block — that would throw off the sticky offset math.
  const canInsertColAfter = (colId: string, nextColId: string | undefined) =>
    editMode
    && enableColumnEditing
    && !pinnedColumns.includes(colId) && !pinnedColumnsRight.includes(colId)
    && (nextColId === undefined
      || (!pinnedColumns.includes(nextColId) && !pinnedColumnsRight.includes(nextColId)));
  const renderAddColCell = (afterColId: string, variant: 'header' | 'filter' | 'body' | 'footer') => {
    const hoverCls = columnInsertHoverClass(afterColId);
    const baseStyle: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: ADD_COL_W,
      minWidth: ADD_COL_W,
      maxWidth: ADD_COL_W,
      position: 'relative',
    };
    const border = 'border-x border-theme-border-primary';
    if (variant === 'header') {
      return (
        <th key={`add-h-${afterColId}`} style={baseStyle} className={`${border} ${hoverCls} bg-theme-bg-tertiary`}>
          <Tooltip content="Add Column" placement="top" offsetY={30}>
            <button
              aria-label="Add Column"
              onClick={(e) => { e.stopPropagation(); setColInsertSearch(''); setColInsertPicker({ afterId: afterColId, x: e.clientX, y: e.clientY }); }}
              onMouseEnter={() => setInsertHoverColId(afterColId)}
              onMouseLeave={() => setInsertHoverColId(prev => (prev === afterColId ? null : prev))}
              className="flex items-center justify-center w-full h-full text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 transition-colors"
            >
              <PlusIcon className="h-3.5 w-3.5" strokeWidth={3} />
            </button>
          </Tooltip>
        </th>
      );
    }
    if (variant === 'filter') {
      return <th key={`add-f-${afterColId}`} style={baseStyle} className={`border-b border-theme-border-primary ${border} ${hoverCls} bg-theme-bg-tertiary`} />;
    }
    if (variant === 'footer') {
      return <td key={`add-ft-${afterColId}`} style={baseStyle} className={`${border} ${hoverCls} bg-theme-bg-tertiary`} />;
    }
    return <td key={`add-b-${afterColId}`} data-add-col-cell="" style={baseStyle} className={`${border} ${hoverCls} bg-theme-bg-secondary`} />;
  };

  // --- Inline insert "+" (edit mode): pick a field to add after a column ---
  const [colInsertPicker, setColInsertPicker] = useState<{ afterId: string | null; x: number; y: number } | null>(null);
  const [colInsertSearch, setColInsertSearch] = useState('');
  const colInsertRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!colInsertPicker) return;
    const onDocClick = (e: MouseEvent) => {
      if (colInsertRef.current && !colInsertRef.current.contains(e.target as Node)) {
        setColInsertPicker(null); setColInsertSearch('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [colInsertPicker]);

  // Fields offerable for insert: everything not currently shown (hidden or absent).
  const insertableFields = useMemo(() => {
    if (!colInsertPicker) return [] as (AddableField & { inView: boolean })[];
    const currentColIds = new Set((columns as any[]).map(c => (c as any).id || (c as any).accessorKey).filter(Boolean));
    const isShown = (id: string) => (currentColIds.has(id) && columnVisibility[id] !== false) || pendingAddedFields.includes(id);
    // Offer every field. Ones already in the table can still be picked — they move
    // to the chosen position instead of being added again. Not-shown listed first.
    const all = buildAllFields()
      .map(f => ({ ...f, inView: isShown(f.id) }))
      .sort((a, b) => Number(a.inView) - Number(b.inView));
    const q = colInsertSearch.trim().toLowerCase();
    if (!q) return all;
    return all.filter(f =>
      f.label.toLowerCase().includes(q)
      || f.description?.toLowerCase().includes(q)
      || f.id.toLowerCase().includes(q)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colInsertPicker, colInsertSearch, columns, columnVisibility, availableFields, pendingAddedFields]);

  const insertColumnAfter = (field: AddableField, afterId: string | null) => {
    const apiId = field.id;
    // Inserting a column relative to itself is a no-op.
    if (apiId === afterId) { setColInsertPicker(null); setColInsertSearch(''); return; }
    const currentColIds = (columns as any[]).map(c => (c as any).id || (c as any).accessorKey).filter(Boolean);
    const isNew = !currentColIds.includes(apiId);
    setColumnOrder(prev => {
      const base = prev.length ? [...prev] : currentColIds;
      const without = base.filter(id => id !== apiId);
      if (afterId == null) { without.push(apiId); return without; }
      const at = without.indexOf(afterId);
      if (at < 0) without.push(apiId); else without.splice(at + 1, 0, apiId);
      return without;
    });
    setColumnVisibility(prev => ({ ...prev, [apiId]: true }));
    if (isNew) {
      // Stage the add — a blank placeholder column, no fetch/persist until Save.
      setPendingAddedFields(prev => (prev.includes(apiId) ? prev : [...prev, apiId]));
    }
    setColInsertPicker(null);
    setColInsertSearch('');
  };

  // Column reorder via drag (edit mode) — works from the header or any body cell.
  const onColDragStart = (colId: string) => {
    if (allLockedColumnIds.includes(colId)) return;
    isColumnDragging = true;
    setDraggedColumn(colId);
  };
  const onColDragEnd = () => {
    isColumnDragging = false;
    setDraggedColumn(null);
    setDragOverColumn(null);
  };
  const onColDragOverCol = (e: React.DragEvent, colId: string) => {
    if (!draggedColumn) return;
    e.preventDefault();
    if (!allLockedColumnIds.includes(colId)) setDragOverColumn(colId);
  };
  const onColDropOn = (targetColumnId: string) => {
    const draggedColumnId = draggedColumn;
    setDraggedColumn(null);
    setDragOverColumn(null);
    if (!draggedColumnId || draggedColumnId === targetColumnId) return;
    if (allLockedColumnIds.includes(draggedColumnId) || allLockedColumnIds.includes(targetColumnId)) return;
    const currentOrder = table.getState().columnOrder;
    const baseOrder = currentOrder.length ? [...currentOrder] : (columns as any[]).map(c => (c as any).id || (c as any).accessorKey);
    const lockedSet = new Set(allLockedColumnIds);
    const nonLocked = baseOrder.filter(id => !lockedSet.has(id));
    const di = nonLocked.indexOf(draggedColumnId);
    const ti = nonLocked.indexOf(targetColumnId);
    if (di !== -1 && ti !== -1) { nonLocked.splice(di, 1); nonLocked.splice(ti, 0, draggedColumnId); }
    setColumnOrder([...lockedColumnsLeft, ...nonLocked, ...lockedColumnsRight]);
    // Moving a previously-pinned column off its spot unpins it automatically.
    setPinnedColumns(prev => prev.filter(id => id !== draggedColumnId));
    setPinnedColumnsRight(prev => prev.filter(id => id !== draggedColumnId));
  };

  // Keep latest apply fn / initialSettings without re-subscribing the recovery effect.
  const applyLoadedSettingsRef = useRef(applyLoadedSettings);
  applyLoadedSettingsRef.current = applyLoadedSettings;
  const initialSettingsRef = useRef(initialSettings);
  initialSettingsRef.current = initialSettings;
  // Legacy-retry backoff attempt counter (survives re-renders within one outage).
  const retryAttemptRef = useRef(0);

  // Recovery: while in failed state, restore the real settings and re-enable saving.
  // - Batch mode (settingsReady !== undefined): parent owns retry. When it clears
  //   settingsLoadFailed and pushes fresh initialSettings, re-apply (DB always wins).
  // - Legacy mode (settingsReady === undefined): self-retry the fetch on backoff.
  useEffect(() => {
    if (!settingsLoaded || !loadFailed) return;

    // Batch mode: parent owns retry. Re-apply (DB always wins) once it clears the flag.
    if (settingsReady !== undefined) {
      if (settingsLoadFailed === false) {
        applyLoadedSettingsRef.current(initialSettingsRef.current); // may be undefined => defaults
        setLoadFailed(false);
      }
      return; // no timer in batch mode
    }

    // Legacy mode: self-retry with 15s -> 30s -> 60s (capped) backoff.
    // Deps exclude applyLoadedSettings/initialSettings (read via refs) so a parent
    // re-render can't cancel the in-flight timer or reset the backoff mid-outage.
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    retryAttemptRef.current = 0; // fresh outage starts the ramp over
    const schedule = () => {
      const delay = Math.min(15000 * 2 ** retryAttemptRef.current, 60000);
      timer = setTimeout(async () => {
        retryAttemptRef.current += 1;
        try {
          const settings = await userSettingsService.getUserSetting(`table_${settingsKey}`);
          if (cancelled) return;
          applyLoadedSettingsRef.current(settings?.setting_value);
          setLoadFailed(false); // recovered -> unblock saving
        } catch {
          if (!cancelled) schedule(); // still down -> keep retrying
        }
      }, delay);
    };
    schedule();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadFailed, settingsLoadFailed, settingsReady, settingsKey]);

  // Sync columnOrder when columns prop changes (e.g., additional fields added/removed via Show/Hide).
  // Without this, newly added columns are not in columnOrder so they cannot be dragged and their
  // position is never saved.
  useEffect(() => {
    if (!settingsLoaded) return;

    const allColumnIds = leafColumnIds(columns as any[]);
    const allColumnIdsSet = new Set(allColumnIds);

    setColumnOrder(prev => {
      const prevSet = new Set(prev);
      const newIds = allColumnIds.filter(id => !prevSet.has(id));
      // Staged adds (pendingAddedFields) are placeholder columns that only exist in
      // columnsWithFilterFn, never in the `columns` prop. Treat them as still-present
      // so this reconciliation doesn't strip them from columnOrder (which would shove
      // them to the end of the table when `columns` changes reference).
      const removedSet = new Set(
        prev.filter(id => !allColumnIdsSet.has(id) && !pendingAddedFields.includes(id))
      );

      if (newIds.length === 0 && removedSet.size === 0) return prev; // No change, avoid re-render

      // Remove deleted columns; insert new columns respecting pinnedColumns
      const lockedRightSet = new Set(lockedColumnsRight);
      const currentPinned = new Set(pinnedColumnsRef.current);
      const filtered = prev.filter(id => !removedSet.has(id));
      const rightLocked = filtered.filter(id => lockedRightSet.has(id));
      const nonRightLocked = filtered.filter(id => !lockedRightSet.has(id));

      // Split new IDs into pinned (go after last pinned column) and unpinned (go before right-locked)
      const newPinned = newIds.filter(id => currentPinned.has(id));
      const newUnpinned = newIds.filter(id => !currentPinned.has(id));

      // Find insertion point for pinned new columns: after the last currently-pinned column
      if (newPinned.length > 0) {
        let lastPinnedIndex = -1;
        for (let i = 0; i < nonRightLocked.length; i++) {
          if (currentPinned.has(nonRightLocked[i])) lastPinnedIndex = i;
        }
        const insertAt = lastPinnedIndex + 1;
        const before = nonRightLocked.slice(0, insertAt);
        const after = nonRightLocked.slice(insertAt);
        return [...before, ...newPinned, ...after, ...newUnpinned, ...rightLocked];
      }

      return [...nonRightLocked, ...newUnpinned, ...rightLocked];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, settingsLoaded]);

  // Deferred settings apply. When a reset (resetAllSettings) or a template load
  // (handleLoadTemplate) asks the host for a DIFFERENT dynamic-field set, the new
  // `columns` only arrive on a later render, and applyLoadedSettings filters to the
  // columns that exist AT APPLY TIME — so the blob is parked here and applied once
  // the host has delivered them. This is what restores admin/user-added fields'
  // visibility/order/width on a reset or template load.
  //
  // CORRELATION CONTRACT: a parked entry carries the field set it is waiting for
  // (`targetAdditional`), and only applies on a `columns` change where the
  // additionalFields prop actually MATCHES it. A bare "columns changed" is not
  // enough — several unrelated things change that identity (the host adding a field
  // via saveEdit, a data-shape refresh), and firing on the first one either stomps
  // the user's own just-committed edit or applies against a column set that still
  // lacks the awaited fields and then goes inert. On a mismatch the entry stays
  // parked, because the awaited refetch may still be in flight. A user-driven
  // layout commit (saveEdit) supersedes and clears it; the two parking callers
  // overwrite it with their own target.
  const reapplyDefaultsRef = useRef<{ snapshot: any; targetAdditional: string[] } | undefined>(undefined);
  useEffect(() => {
    const pending = reapplyDefaultsRef.current;
    if (!pending) return;
    const target = pending.targetAdditional || [];
    const arrived =
      target.length === additionalFields.length
      && target.every((f, i) => f === additionalFields[i]);
    if (!arrived) return; // host hasn't delivered this field set yet — keep waiting
    reapplyDefaultsRef.current = undefined;
    try {
      applyLoadedSettings(pending.snapshot || undefined);
    } catch (error) {
      console.error('Failed to re-apply default settings after column change:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  // --- "Save as Defaults" (admin) — publishes the current staged layout ----
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [showSaveDefaultConfirm, setShowSaveDefaultConfirm] = useState(false);
  const toastCtx = useContext(ToastContext);

  // Who may publish a grid default. Pages that wire `onSaveDefaults` keep their
  // existing gate exactly (they set `canEditDefaults` themselves, e.g. only for
  // shared/locked buckets). Every other grid becomes publishable off its own
  // settingsKey — that is what extends central configuration past the four wired
  // pages. Either way the button sits inside a PermissionsContainer that is
  // hidden by default, so only holders of 'edit-default-table-settings' see it.
  const canPublishDefaults = onSaveDefaults
    ? !!canEditDefaults && !!settingsKey
    : !!settingsKey && !disableSettingsPersistence && !settingsReadOnly;

  // Build the stored default blob from the CURRENT (staged) edit-mode layout and
  // publish it. Does not touch the user's own settings — they Save/Cancel after.
  const handleSaveAsDefaults = async () => {
    if (!canPublishDefaults) return;
    setSavingDefaults(true);
    try {
      // Visible columns in current order (incl. staged placeholders), the base
      // column set, and the fields beyond it — shared with buildTemplateSnapshot.
      const { fields, baseColIds, additionalFields: newAdditional } = deriveStagedFieldSets();
      const fieldSet = new Set(fields);
      const columnVisibility: Record<string, boolean> = {};
      for (const id of baseColIds) columnVisibility[id] = fieldSet.has(id);
      for (const id of fields) columnVisibility[id] = true;
      const sizing: Record<string, number> = {};
      for (const id of fields) { const w = columnSizing[id]; if (typeof w === 'number') sizing[id] = w; }
      const stored = {
        columnVisibility,
        columnOrder: fields,
        columnSizing: sizing,
        additionalFields: newAdditional,
        sorting,
        grouping, // row grouping; tab grouping stays code-driven
        // Totals bar. `showAggFooter` publishes the RESOLVED value, not this
        // admin's tri-state override — otherwise publishing while inheriting an
        // existing default (override still null) would silently switch it off.
        // `aggCalcs` publishes only explicit choices, so columns the admin left
        // alone keep inheriting from Field Manager instead of being frozen here.
        showAggFooter,
        aggCalcs,
      };
      if (onSaveDefaults) {
        await onSaveDefaults(settingsKey, stored);
      } else {
        // No host handler: publish directly. Permission is enforced server-side.
        await userSettingsService.saveDefaultSetting(settingsKey, stored);
      }
      // Reflect the new default locally — the `defaultSettings` prop (when there
      // is one) won't change until the host refetches.
      setAggAdminCalcs(aggCalcs);
      setAggAdminShowFooter(showAggFooter);
      toastCtx?.showToast({ type: 'success', message: `Global default updated for ${cardTitle || 'this table'}.` });
    } catch (error) {
      console.error('Failed to save default settings:', error);
      toastCtx?.showToast({ type: 'error', message: 'Failed to update the global default.' });
    } finally {
      setSavingDefaults(false);
      setShowSaveDefaultConfirm(false);
    }
  };

  const isHighlighted = fieldName && highlightedFieldId === fieldName;

  // Shared "Clear All Filters & Settings" handler, wired to the Clear All button
  // in both the primary and narrow-toolbar layouts (previously duplicated inline).
  const resetAllSettings = async () => {
    // Reset all table settings
    setGlobalFilter('');
    setColumnFilters([]);
    // Clear user-added (row) grouping, but restore the locked/default tab
    // grouping below so a card with a locked grouping (e.g. Per Diem, which
    // groups via tabs) keeps its grouping after a reset.
    setGrouping([]);
    setSorting(defaultSorting || []);
    setColumnSizing({});
    setGlobalShowColumnFilters(false);
    setSelectedTabGroupField(tabGroupingField ?? null);

    // Reset pinned columns to only locked columns
    setPinnedColumns([...lockedColumnsLeft]);
    setPinnedColumnsRight([...lockedColumnsRight]);

    // Reset column order with locked columns in correct positions
    const currentColumnIds = columns.map(col => (col as any).id || (col as any).accessorKey);
    if (allLockedColumnIds.length > 0) {
      const lockedSet = new Set(allLockedColumnIds);
      const nonLockedIds = currentColumnIds.filter(colId => !lockedSet.has(colId));
      const defaultOrder = [
        ...lockedColumnsLeft,
        ...nonLockedIds,
        ...lockedColumnsRight,
      ];
      setColumnOrder(defaultOrder);
    } else {
      setColumnOrder(currentColumnIds);
    }

    // Reset to default visibility (columns hidden by default
    // stay hidden; everything else is shown).
    setColumnVisibility(defaultColumnVisibility);
    table.getAllLeafColumns().forEach(column => {
      column.toggleVisibility(defaultColumnVisibility[column.id] !== false);
    });

    // Delete saved settings from database
    try {
      await userSettingsService.deleteUserSetting(`table_${settingsKey}`);
    } catch (error) {
      console.error('Failed to delete table settings:', error);
    }
    localStorage.removeItem(userSettingsService.getTableCacheKey(settingsKey));

    // Restore the admin-published default for this bucket (column set / order /
    // width / row grouping) when one exists; otherwise drop any user-added
    // columns and refetch with the base field set (collapsing row expansion).
    const targetAdditional: string[] = (defaultSettings?.additionalFields as string[]) || [];
    const additionalChanged =
      targetAdditional.length !== additionalFields.length ||
      targetAdditional.some((f, i) => f !== additionalFields[i]);

    if (defaultSettings) {
      if (additionalChanged) {
        // columns change on the next render; re-apply the full default then so
        // admin-added fields get their default visibility/order/width too.
        reapplyDefaultsRef.current = { snapshot: defaultSettings, targetAdditional };
        onAdditionalFieldsChange?.(targetAdditional);
      } else {
        applyLoadedSettings(defaultSettings);
      }
    } else if (additionalFields.length > 0) {
      onAdditionalFieldsChange?.([]);
    }

    // Preserve fit-to-view mode like superCompact: re-apply
    // fit widths after visibility/sizing resets settle so the
    // toggle button and the visual stay in sync.
    if (columnFitMode === 'fitToView') {
      setTimeout(() => applyFitToView(), 0);
    }

    // Notify parent of reset: row grouping from the default (else cleared),
    // keep the locked/default tab grouping.
    onGroupingChange?.(defaultSettings?.grouping || []);
    onTabGroupingChange?.(tabGroupingField || '');
    onVisibleColumnsChange?.(table.getAllLeafColumns().map(col => col.id));
  };

  // Shared Show/Hide column-picker option list. Rendered in both the primary
  // toolbar dropdown and the portal-rendered narrow-toolbar dropdown so the two
  // can never drift (they were previously duplicated inline, which caused
  // styling/behavior fixes to land in only one copy).
  const renderColumnPickerOptions = () => (
    availableFields && availableFields.length > 0 ? (
      // Dynamic columns mode: show all available fields
      (() => {
        // Get column IDs that are already in the table
        const existingColumnIds = new Set(table.getAllLeafColumns().map(col => col.id));
        const searchLower = columnSearchFilter.toLowerCase();

        // Build list of all fields (both existing columns and available fields)
        const allFields = availableFields
          .map(field => {
            const apiFieldName = getApiFieldName(field.fieldname);
            const isExistingColumn = existingColumnIds.has(apiFieldName);
            // Only call getColumn for columns that actually exist to avoid TanStack warnings
            const column = isExistingColumn ? table.getColumn(apiFieldName) : undefined;
            const isAdditionalField = additionalFields.includes(field.fieldname);
            // Check if column has an active filter
            const filterValue = column?.getFilterValue();
            const hasActiveFilter = filterValue !== undefined &&
              (Array.isArray(filterValue) ? filterValue.length > 0 : true);

            return {
              fieldname: field.fieldname,
              apiFieldName,
              displayName: field.displayname, // Use full display name
              expands: field.expands,
              column,
              isExistingColumn,
              isAdditionalField,
              isVisible: column ? column.getIsVisible() : isAdditionalField,
              hasActiveFilter,
            };
          })
          // Filter by search term
          .filter(field => !searchLower || field.displayName.toLowerCase().includes(searchLower))
          // Sort checked first, then alphabetically within each group
          .sort((a, b) => {
            if (a.isVisible !== b.isVisible) return a.isVisible ? -1 : 1;
            return a.displayName.localeCompare(b.displayName);
          });

        return allFields.map((field) => {
          // Disable unchecking if column has an active filter, is a
          // pinned/locked column, or is currently required for grouping
          // (row grouping or the active tab grouping). Hiding a grouped
          // column would break the grouping until a refresh.
          const isGroupLocked = grouping.includes(field.apiFieldName)
            || (!!selectedTabGroupField
                && (selectedTabGroupField === field.fieldname
                    || selectedTabGroupField === field.apiFieldName));
          const isLocked = allLockedColumnIds.includes(field.apiFieldName);
          const isDisabled = (field.isVisible && field.hasActiveFilter) || isLocked || isGroupLocked;

          return (
          <Tooltip
            key={field.fieldname}
            content={isGroupLocked ? "Required for grouping" : (isLocked ? "Locked column" : (isDisabled ? "Clear filter to hide column" : ""))}
            placement="left"
          >
            <label
              className={`flex items-center gap-2 px-2 py-1.5 rounded ${
                isDisabled
                  ? 'cursor-not-allowed opacity-60'
                  : 'hover:bg-theme-bg-tertiary cursor-pointer'
              }`}
            >
              <input
                type="checkbox"
                checked={field.isVisible}
                disabled={isDisabled}
                onChange={(e) => {
                  if (isDisabled) return;
                  const isChecked = e.target.checked;

                  // A user-added field stays managed through the fetch set even
                  // after it has been fetched into a real column. Unchecking it must
                  // drop it from additionalFields and refetch so its join is removed
                  // (collapsing any row expansion) — not merely hide the column.
                  if (field.isExistingColumn && field.column && !field.isAdditionalField) {
                    // Toggle base/core column visibility
                    field.column.toggleVisibility(isChecked);
                    setTimeout(() => {
                      const visibleCols = table.getAllLeafColumns()
                        .filter(col => col.getIsVisible())
                        .map(col => col.id);
                      onVisibleColumnsChange?.(visibleCols);
                    }, 0);
                  } else {
                    // This is an additional field - notify parent to refetch
                    const newAdditionalFields = isChecked
                      ? [...additionalFields, field.fieldname]
                      : additionalFields.filter(f => f !== field.fieldname);
                    onAdditionalFieldsChange?.(newAdditionalFields);
                  }
                }}
                className={`rounded border-theme-border-secondary text-blue-600 focus:ring-blue-500 ${
                  isDisabled ? 'cursor-not-allowed' : ''
                }`}
              />
              {/* Default/base columns render black. Fields that can be
                  added (blue, "+") or removed from the query (blue, "-")
                  render blue so a user-added column stays distinguishable
                  from the defaults even after it has been fetched into a
                  real column (which makes it an "existing" column too). */}
              <span className={`text-sm ${field.isExistingColumn && !field.isAdditionalField ? 'text-label' : 'text-blue-600 dark:text-blue-400'}`}>
                {field.displayName}
                {field.isAdditionalField
                  ? <span className="ml-1 text-xs text-gray-400">(-)</span>
                  : !field.isExistingColumn && <span className="ml-1 text-xs text-gray-400">(+)</span>}
                {!field.isExistingColumn && field.expands && <span className="ml-1 text-xs text-amber-600 dark:text-amber-500">(pros may dupe)</span>}
              </span>
            </label>
          </Tooltip>
        )});
      })()
    ) : (
      // Standard mode: show only existing columns
      [...table.getAllLeafColumns()]
        // Card mode: special columns (selection checkbox etc.) have no card
        // field to show/hide — keep them out of the picker.
        .filter((column) => !(renderCardRow && SPECIAL_COLUMN_IDS.has(column.id)))
        .filter((column) => {
          // Filter by search term
          if (!columnSearchFilter) return true;
          const header = typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id;
          return header.toLowerCase().includes(columnSearchFilter.toLowerCase());
        })
        .sort((a, b) => {
          const aVisible = a.getIsVisible();
          const bVisible = b.getIsVisible();
          if (aVisible !== bVisible) return aVisible ? -1 : 1;
          const aHeader = typeof a.columnDef.header === 'string' ? a.columnDef.header : a.id;
          const bHeader = typeof b.columnDef.header === 'string' ? b.columnDef.header : b.id;
          return aHeader.localeCompare(bHeader);
        })
        .map((column) => {
          // Check if column has an active filter
          const filterValue = column.getFilterValue();
          const hasActiveFilter = filterValue !== undefined &&
            (Array.isArray(filterValue) ? filterValue.length > 0 : true);
          const isVisible = column.getIsVisible();
          // Disable unchecking if column is visible and has active filter
          const isDisabled = isVisible && hasActiveFilter;

          return (
          <Tooltip
            key={column.id}
            content={isDisabled ? "Clear filter to hide column" : ""}
            placement="left"
          >
            <label
              className={`flex items-center gap-2 px-2 py-1.5 rounded ${
                isDisabled
                  ? 'cursor-not-allowed opacity-60'
                  : 'hover:bg-theme-bg-tertiary cursor-pointer'
              }`}
            >
              <input
                type="checkbox"
                checked={isVisible}
                disabled={isDisabled}
                onChange={(e) => {
                  if (isDisabled) return;
                  column.getToggleVisibilityHandler()(e);
                  // Notify parent of visible columns change
                  setTimeout(() => {
                    const visibleCols = table.getAllLeafColumns()
                      .filter(col => col.getIsVisible())
                      .map(col => col.id);
                    onVisibleColumnsChange?.(visibleCols);
                  }, 0);
                }}
                className={`rounded border-theme-border-secondary text-blue-600 focus:ring-blue-500 ${
                  isDisabled ? 'cursor-not-allowed' : ''
                }`}
              />
              <span className="text-sm text-label">
                {typeof column.columnDef.header === 'string'
                  ? column.columnDef.header
                  : column.id}
              </span>
            </label>
          </Tooltip>
        )})
    )
  );

  // Shared opener for the filter dropdown — used by the header filter inputs
  // (table mode) and the card-bar filter chips (card mode). Captures the
  // anchor's viewport position (clamped so the dropdown can't overflow the
  // right edge), records the anchor for the scroll/resize reposition effect,
  // and toggles the dropdown for the column.
  // forceOpen skips the usual toggle: callers that are not the filter cell itself
  // (the header context menu) mean "show me this", never "close what I just asked for".
  const openFilterDropdown = (columnId: string, anchorEl: HTMLElement, forceOpen = false) => {
    const rect = anchorEl.getBoundingClientRect();
    const dropdownWidth = filterDropdownSize.width;
    const viewportWidth = window.innerWidth;
    // Ensure dropdown doesn't overflow right edge of viewport
    const left = Math.min(rect.left, viewportWidth - dropdownWidth - 16);
    setFilterInputPosition({ top: rect.bottom + 4, left });
    filterAnchorRef.current = anchorEl;
    setActiveFilterColumn(!forceOpen && activeFilterColumn === columnId ? null : columnId);
  };

  // Single shared filter dropdown — anchored by activeFilterColumn/filterInputPosition,
  // opened from a header filter cell (table mode) or a card-mode filter chip.
  // Hoisted out of the header map so it works when the header isn't rendered.
  const renderActiveFilterDropdown = () => {
    if (!activeFilterColumn || !filterInputPosition) return null;
    const column = table.getColumn(activeFilterColumn);
    if (!column) return null;
    // The membership popovers below read a string[]; a number-mode value is an object
    // and is handled by its own branch, so it must never reach the facet list.
    const rawFilterValue = column.getFilterValue();
    const filterValue = Array.isArray(rawFilterValue) ? (rawFilterValue as string[]) : undefined;
    const backendValues = distinctValues[column.id] || [];
    const isLoadingValues = loadingDistinctValues[column.id] || false;
    const hasActiveFilter = filterValue && filterValue.length > 0;
    const isExclusionMode = getEffectiveFilterType(column.id) === 'exclusion';
    const searchText = filterSearchText[column.id] || '';

    // Date columns render the facets as a Year > Month > Day tri-state tree
    // instead of the flat list. Facet values are day keys (see
    // toFilterDisplayValue), so the tree is a pure regrouping of backendValues.
    const isDateTree = effectiveDateColumns.has(column.id);
    const selection = readSelection(column.id, filterValue);
    const isKeySelected = (key: string): boolean =>
      selection === undefined ? !isExclusionMode : facetSelectionIncludes(selection, key);
    const fullTree: DateTreeNode[] = isDateTree ? buildDateTree(backendValues) : [];
    const visibleTree = isDateTree ? filterTreeBySearch(fullTree, searchText) : [];
    const visibleTreeKeys = visibleTree.flatMap(leafKeys);

    // A group node toggles every day beneath it through the same helpers the
    // Check All / Uncheck All links use, so exclusion mode and the
    // "collapse to undefined when everything is checked" rule carry over.
    const toggleDateNode = (node: DateTreeNode, state: NodeCheckState) => {
      if (node.children.length === 0) {
        toggleFilterValue(column.id, node.key);
        return;
      }
      const keys = leafKeys(node);
      if (state === 'checked') uncheckAllVisibleFilterValues(column.id, keys);
      else checkAllVisibleFilterValues(column.id, keys);
    };

    const renderDateNode = (node: DateTreeNode, depth: number): React.ReactNode => {
      const isGroup = node.children.length > 0;
      const state = nodeCheckState(node, isKeySelected);
      // Searching auto-expands every surviving branch; otherwise years start
      // open and months closed unless the user toggled the chevron.
      const expanded = isGroup && (searchText.trim() !== '' || (dateTreeToggles[node.key] ?? node.level === 'year'));
      const isExcluded = isExclusionMode && state !== 'unchecked';
      return (
        <div key={node.key}>
          <div
            className={`flex items-center gap-1 rounded ${isExcluded ? 'opacity-60' : 'hover:bg-theme-bg-tertiary'}`}
            style={{ paddingLeft: depth * 16 + 4 }}
          >
            {isGroup ? (
              <button
                type="button"
                aria-label={`${expanded ? 'Collapse' : 'Expand'} ${node.label}`}
                onClick={() => setDateTreeToggles(prev => ({ ...prev, [node.key]: !expanded }))}
                className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 flex-shrink-0"
              >
                {expanded
                  ? <ChevronDownIcon className="h-3 w-3 text-muted" />
                  : <ChevronRightIcon className="h-3 w-3 text-muted" />}
              </button>
            ) : (
              <span className="w-4 flex-shrink-0" />
            )}
            <label className="flex items-center gap-2 flex-1 min-w-0 px-1 py-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={state === 'checked'}
                ref={(el) => { if (el) el.indeterminate = state === 'indeterminate'; }}
                onChange={() => toggleDateNode(node, state)}
                className={`rounded border-theme-border-secondary flex-shrink-0 ${isExclusionMode ? 'text-orange-500 focus:ring-orange-500' : 'text-blue-600 focus:ring-blue-500'}`}
              />
              <span className={`text-sm truncate ${isExcluded ? 'line-through text-gray-400 dark:text-gray-500' : 'text-label'}`}>
                {node.label}
              </span>
              <span className="text-xs text-muted ml-auto flex-shrink-0">({node.count})</span>
            </label>
          </div>
          {isGroup && expanded && node.children.map(child => renderDateNode(child, depth + 1))}
        </div>
      );
    };

    if (isListFilterType(getEffectiveFilterType(column.id))) {
      /* Match List Filter Dropdown — paste a list of values instead of ticking facets */
      const isListExclude = getEffectiveFilterType(column.id) === 'listExclude';
      const raw = listFilterInputs[column.id] ?? '';
      const pasted = parseListFilterValues(raw);
      const appliedValues = (filterValue as string[] | undefined) ?? [];
      // The textarea has been edited since Apply, so any count on screen would be
      // describing a list that is no longer the one being matched.
      const isDirty =
        pasted.map(v => v.toLowerCase()).join('\u0000') !==
        appliedValues.map(v => v.toLowerCase()).join('\u0000');
      // Reconciled against this column's FACET values — TanStack applies every OTHER
      // filter to them but not this column's own — so a miss means "not in this view"
      // regardless of whether the mode is include or exclude, and the denominator
      // doesn't move as the list narrows the grid.
      const { matched, unmatched } = reconcileListValues(
        pasted,
        backendValues.map(v => v.value),
      );
      // A server-resolved column searched the whole table, so a miss there means the
      // value is not IN the table — a stronger and more useful statement than the
      // client-side case, where the grid can only speak for the rows it holds.
      const isServerList = isServerListColumn(column.id);
      const isPending = listFilterPending === column.id;
      const missLabel = isServerList ? 'not in this table' : 'not in this view';
      const showSummary =
        appliedValues.length > 0 && !isDirty && !isLoadingValues && !isPending;

      return createPortal(
        <div
          ref={filterMenuRef}
          className="fixed bg-theme-bg-elevated border border-theme-border-secondary rounded-lg shadow-lg z-[9999] overflow-hidden"
          style={{
            width: filterDropdownSize.width,
            maxHeight: filterDropdownSize.height,
            top: filterInputPosition.top,
            left: filterInputPosition.left,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-2 flex flex-col" style={{ maxHeight: filterDropdownSize.height - 16 }}>
            {/* Header with type toggles and Clear */}
            <div className="flex items-center justify-between mb-2 px-2">
              <span className={`text-xs font-semibold ${isListExclude ? 'text-orange-600 dark:text-orange-400' : 'text-label'}`}>
                {isListExclude ? 'Exclude list of ' : 'Match list of '}{column.columnDef.header as string}
              </span>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-0.5">
                  <Tooltip content="Switch to dropdown filter" placement="top">
                    <button
                      onClick={() => setColumnFilterType(column.id, 'dropdown')}
                      className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      <Bars3BottomLeftIcon className="h-3.5 w-3.5 text-muted" />
                    </button>
                  </Tooltip>
                  <Tooltip content="Switch to text filter" placement="top">
                    <button
                      onClick={() => setColumnFilterType(column.id, 'text')}
                      className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      <TextFilterIcon className="h-3.5 w-3.5 text-muted" />
                    </button>
                  </Tooltip>
                  <Tooltip content="Switch to number filter (less than, between...)" placement="top">
                    <button
                      onClick={() => setColumnFilterType(column.id, 'number')}
                      aria-label="Switch to number filter"
                      className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      <CalculatorIcon className="h-3.5 w-3.5 text-muted" />
                    </button>
                  </Tooltip>
                </div>
                {(hasActiveFilter || raw !== '') && (
                  <button
                    onClick={() => clearListFilter(column.id)}
                    className="text-xs text-brand-red dark:text-brand-red hover:underline"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Include / Exclude — flipping keeps the pasted list and re-reads it the
                other way round, so it costs nothing to try both. */}
            <div className="flex items-center gap-1 mb-2 px-2" role="group" aria-label="List match mode">
              {([
                { mode: 'list' as const, label: 'Include' },
                { mode: 'listExclude' as const, label: 'Exclude' },
              ]).map(({ mode, label }) => {
                const active = isListExclude === (mode === 'listExclude');
                return (
                  <button
                    key={mode}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setColumnFilterType(column.id, mode)}
                    className={`px-2 py-0.5 text-xs rounded border ${
                      active
                        ? mode === 'listExclude'
                          ? 'bg-orange-100 dark:bg-orange-900/40 border-orange-400 text-orange-700 dark:text-orange-300'
                          : 'bg-blue-100 dark:bg-blue-900/40 border-blue-400 text-blue-700 dark:text-blue-300'
                        : 'border-theme-border-secondary text-muted hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            {/* What this view leaves out, stated before anything is pasted. A count of
                misses is only meaningful next to the scope it was counted in, and the
                user is about to read one. Suppressed on a server-resolved column, which
                gets the opposite reassurance under the summary. */}
            {!isServerList && listFilterScopeNote && (
              <div className="mb-2 px-2 py-1.5 rounded bg-theme-bg-secondary text-[0.7rem] leading-snug text-muted">
                {listFilterScopeNote}
              </div>
            )}

            {/* Paste box. Ctrl/Cmd+Enter applies from inside it, where Enter is a
                newline — same chord the Custom Search modal uses. */}
            <textarea
              aria-label={`Paste ${column.columnDef.header as string} values`}
              value={raw}
              onChange={(e) => setListFilterInputs(prev => ({ ...prev, [column.id]: e.target.value }))}
              onBlur={(e) => void applyListFilter(column.id, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  void applyListFilter(column.id, raw);
                }
              }}
              rows={6}
              autoFocus
              placeholder={'Paste values — one per line\nor comma-separated'}
              className="w-full px-2 py-1.5 text-xs border border-theme-border-secondary rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-theme-bg-primary text-heading resize-none flex-1 min-h-[72px] font-mono"
            />

            <div className="flex items-center justify-between gap-2 mt-2 px-1">
              <span className="text-xs text-muted">
                {pasted.length > 0 ? `${pasted.length} value${pasted.length === 1 ? '' : 's'}` : ''}
              </span>
              <button
                onClick={() => void applyListFilter(column.id, raw)}
                disabled={!isDirty || isPending}
                className="px-2 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-default"
              >
                {isPending ? 'Searching…' : 'Apply'}
              </button>
            </div>

            {/* Reconciliation — the reason this mode exists. A filter alone answers
                "here are 31 rows" and stays silent about the other 19, while the
                question being asked is "are all 50 of mine here?". Worded "not in this
                view", never "not found": a bucket's own scope (an LC queue holding only
                active tickets, a capped table holding its most recent page) legitimately
                excludes rows that do exist. */}
            {isPending && (
              <div className="mt-2 pt-2 border-t border-rule px-1 text-xs text-muted">
                Searching the whole table…
              </div>
            )}

            {showSummary && (
              <div className="mt-2 pt-2 border-t border-rule px-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-label">
                    <span className="font-semibold">{matched.length}</span> of {pasted.length} matched
                    {unmatched.length > 0 && (
                      <span className="text-amber-700 dark:text-amber-400">
                        {' · '}{unmatched.length} {missLabel}
                      </span>
                    )}
                  </span>
                  {unmatched.length > 0 && (
                    <button
                      onClick={() => copyTextToClipboard(unmatched.join('\n'))}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap"
                    >
                      Copy unmatched
                    </button>
                  )}
                </div>
                {unmatched.length > 0 && (
                  <div className="mt-1 max-h-24 overflow-y-auto text-xs text-muted font-mono leading-snug">
                    {unmatched.map(v => <div key={v}>{v}</div>)}
                  </div>
                )}
                {/* Say which question was actually answered. "Not in this table" and
                    "not in the rows this grid is holding" look identical in a count, and
                    they mean very different things to someone reconciling a list. */}
                <div className="mt-1.5 text-[0.7rem] leading-snug text-muted">
                  {isServerList
                    ? 'Searched the whole table, so anything listed above was never sent.'
                    : 'Matched against the rows loaded in this grid.'}
                </div>
              </div>
            )}
          </div>
          {/* Resize Handle */}
          <div
            onMouseDown={handleFilterDropdownResize}
            className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize flex items-center justify-center"
            style={{ touchAction: 'none' }}
          >
            <svg className="w-2.5 h-2.5 text-gray-400" viewBox="0 0 10 10" fill="currentColor">
              <path d="M9 1v8H1" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
        </div>,
        portalContainer || document.body
      );
    }

    if (getEffectiveFilterType(column.id) === 'number') {
      /* Number Filter Dropdown — operator + one or two bounds */
      const draft = getNumberDraft(column.id);
      const header = column.columnDef.header as string;
      const needsBound = numberFilterNeedsBound(draft.op);
      const needsSecond = numberFilterNeedsSecondBound(draft.op);
      const isApplied = isNumberFilterValue(column.getFilterValue());
      const boundInputClass = 'w-full px-2 py-1.5 text-sm border border-theme-border-secondary rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-theme-bg-primary text-heading';
      const closeOnEnter = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          setActiveFilterColumn(null);
        }
      };
      return createPortal(
        <div
          ref={filterMenuRef}
          className="fixed bg-theme-bg-elevated border border-theme-border-secondary rounded-lg shadow-lg z-[9999] overflow-hidden"
          style={{
            width: filterDropdownSize.width,
            maxHeight: filterDropdownSize.height,
            top: filterInputPosition.top,
            left: filterInputPosition.left,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-2">
            {/* Header with mode toggles and Clear */}
            <div className="flex items-center justify-between mb-2 px-2">
              <span className="text-xs font-semibold text-label">
                Filter by {header}
              </span>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-0.5">
                  <Tooltip content="Switch to dropdown filter" placement="top">
                    <button
                      onClick={() => setColumnFilterType(column.id, 'dropdown')}
                      className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      <Bars3BottomLeftIcon className="h-3.5 w-3.5 text-muted" />
                    </button>
                  </Tooltip>
                  <Tooltip content="Switch to exclusion filter" placement="top">
                    <button
                      onClick={() => setColumnFilterType(column.id, 'exclusion')}
                      className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      <NoSymbolIcon className="h-3.5 w-3.5 text-muted" />
                    </button>
                  </Tooltip>
                  <Tooltip content="Switch to text filter" placement="top">
                    <button
                      onClick={() => setColumnFilterType(column.id, 'text')}
                      className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      <TextFilterIcon className="h-3.5 w-3.5 text-muted" />
                    </button>
                  </Tooltip>
                  <Tooltip content="Switch to list filter (paste a list of values)" placement="top">
                    <button
                      onClick={() => setColumnFilterType(column.id, 'list')}
                      aria-label="Switch to list filter"
                      className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      <ClipboardDocumentListIcon className="h-3.5 w-3.5 text-muted" />
                    </button>
                  </Tooltip>
                </div>
                {(isApplied || draft.a !== '' || draft.b !== '') && (
                  <button
                    onClick={() => clearNumberFilter(column.id)}
                    className="text-xs text-brand-red dark:text-brand-red hover:underline"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            <div className="px-2 flex flex-col gap-2">
              <select
                aria-label={`${header} number filter operator`}
                value={draft.op}
                onChange={(e) => applyNumberFilter(column.id, { ...draft, op: e.target.value as NumberFilterOp })}
                className={boundInputClass}
              >
                {NUMBER_FILTER_OPS.map(({ op, label }) => (
                  <option key={op} value={op}>{label}</option>
                ))}
              </select>
              {needsBound && (
                <div className="flex items-center gap-2">
                  {needsSecond && <span className="text-xs text-muted w-10 shrink-0">From</span>}
                  <input
                    type="number"
                    inputMode="decimal"
                    aria-label={needsSecond ? `${header} from` : `${header} value`}
                    value={draft.a}
                    onChange={(e) => applyNumberFilter(column.id, { ...draft, a: e.target.value })}
                    onKeyDown={closeOnEnter}
                    placeholder={needsSecond ? 'Lowest' : 'Enter number...'}
                    autoFocus
                    className={boundInputClass}
                  />
                </div>
              )}
              {needsSecond && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted w-10 shrink-0">To</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    aria-label={`${header} to`}
                    value={draft.b}
                    onChange={(e) => applyNumberFilter(column.id, { ...draft, b: e.target.value })}
                    onKeyDown={closeOnEnter}
                    placeholder="Highest"
                    className={boundInputClass}
                  />
                </div>
              )}
              {/* Say what the grid is doing while the draft is incomplete, so an empty
                  box is not mistaken for a filter that silently matches nothing. */}
              <div className="text-[0.7rem] leading-snug text-muted">
                {isApplied
                  ? `Showing rows where ${header} is ${formatNumberFilterSummary(column.getFilterValue() as NumberFilterValue)}`
                  : needsSecond ? 'Enter both bounds to apply. Between includes both ends.' : 'Enter a number to apply.'}
              </div>
            </div>
          </div>
          {/* Resize Handle */}
          <div
            onMouseDown={handleFilterDropdownResize}
            className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize flex items-center justify-center"
            style={{ touchAction: 'none' }}
          >
            <svg className="w-2.5 h-2.5 text-gray-400" viewBox="0 0 10 10" fill="currentColor">
              <path d="M9 1v8H1" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
        </div>,
        portalContainer || document.body
      );
    }

    if (getEffectiveFilterType(column.id) === 'text') {
      /* Text Filter Dropdown */
      return createPortal(
        <div
          ref={filterMenuRef}
          className="fixed bg-theme-bg-elevated border border-theme-border-secondary rounded-lg shadow-lg z-[9999] overflow-hidden"
          style={{
            width: filterDropdownSize.width,
            maxHeight: filterDropdownSize.height,
            top: filterInputPosition.top,
            left: filterInputPosition.left,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-2">
            {/* Header with Toggle and Clear button */}
            <div className="flex items-center justify-between mb-2 px-2">
              <span className="text-xs font-semibold text-label">
                Filter by {column.columnDef.header as string}
              </span>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-0.5">
                  <Tooltip content="Switch to dropdown filter" placement="top">
                    <button
                      onClick={() => setColumnFilterType(column.id, 'dropdown')}
                      className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      <Bars3BottomLeftIcon className="h-3.5 w-3.5 text-muted" />
                    </button>
                  </Tooltip>
                  <Tooltip content="Switch to exclusion filter" placement="top">
                    <button
                      onClick={() => setColumnFilterType(column.id, 'exclusion')}
                      className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      <NoSymbolIcon className="h-3.5 w-3.5 text-muted" />
                    </button>
                  </Tooltip>
                  <Tooltip content="Switch to list filter (paste a list of values)" placement="top">
                    <button
                      onClick={() => setColumnFilterType(column.id, 'list')}
                      aria-label="Switch to list filter"
                      className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      <ClipboardDocumentListIcon className="h-3.5 w-3.5 text-muted" />
                    </button>
                  </Tooltip>
                  <Tooltip content="Switch to number filter (less than, between...)" placement="top">
                    <button
                      onClick={() => setColumnFilterType(column.id, 'number')}
                      aria-label="Switch to number filter"
                      className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      <CalculatorIcon className="h-3.5 w-3.5 text-muted" />
                    </button>
                  </Tooltip>
                </div>
                {textFilterInputs[column.id] && (
                  <button
                    onClick={() => clearTextFilter(column.id)}
                    className="text-xs text-brand-red dark:text-brand-red hover:underline"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Text input in dropdown */}
            <div className="px-2">
              <input
                type="text"
                value={textFilterInputs[column.id] || ''}
                onChange={(e) => handleTextFilterChange(column.id, e.target.value)}
                placeholder="Type to filter..."
                autoFocus
                className="w-full px-2 py-1.5 text-sm border border-theme-border-secondary rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-theme-bg-primary text-heading"
              />
            </div>
          </div>
          {/* Resize Handle */}
          <div
            onMouseDown={handleFilterDropdownResize}
            className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize flex items-center justify-center"
            style={{ touchAction: 'none' }}
          >
            <svg className="w-2.5 h-2.5 text-gray-400" viewBox="0 0 10 10" fill="currentColor">
              <path d="M9 1v8H1" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
        </div>,
        portalContainer || document.body
      );
    }

    /* Excel-Style Filter Dropdown */
    return createPortal(
      <div
        ref={filterMenuRef}
        className="fixed bg-theme-bg-elevated border border-theme-border-secondary rounded-lg shadow-lg z-[9999] overflow-hidden"
        style={{
          width: filterDropdownSize.width,
          maxHeight: filterDropdownSize.height,
          top: filterInputPosition.top,
          left: filterInputPosition.left,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-2 flex flex-col" style={{ maxHeight: filterDropdownSize.height - 16 }}>
          {/* Header with Toggle and Clear button */}
          <div className="flex items-center justify-between mb-2 px-2">
            <span className={`text-xs font-semibold ${isExclusionMode ? 'text-orange-600 dark:text-orange-400' : 'text-label'}`}>
              {isExclusionMode ? 'Exclude by ' : 'Filter by '}{column.columnDef.header as string}
            </span>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-0.5">
                {isExclusionMode ? (
                  <Tooltip content="Switch to dropdown filter" placement="top">
                    <button
                      onClick={() => setColumnFilterType(column.id, 'dropdown')}
                      className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      <Bars3BottomLeftIcon className="h-3.5 w-3.5 text-muted" />
                    </button>
                  </Tooltip>
                ) : (
                  <Tooltip content="Switch to exclusion filter" placement="top">
                    <button
                      onClick={() => setColumnFilterType(column.id, 'exclusion')}
                      className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      <NoSymbolIcon className="h-3.5 w-3.5 text-muted" />
                    </button>
                  </Tooltip>
                )}
                <Tooltip content="Switch to text filter" placement="top">
                  <button
                    onClick={() => setColumnFilterType(column.id, 'text')}
                    className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                  >
                    <TextFilterIcon className="h-3.5 w-3.5 text-muted" />
                  </button>
                </Tooltip>
                <Tooltip content="Switch to list filter (paste a list of values)" placement="top">
                  <button
                    onClick={() => setColumnFilterType(column.id, 'list')}
                    aria-label="Switch to list filter"
                    className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                  >
                    <ClipboardDocumentListIcon className="h-3.5 w-3.5 text-muted" />
                  </button>
                </Tooltip>
                <Tooltip content="Switch to number filter (less than, between...)" placement="top">
                  <button
                    onClick={() => setColumnFilterType(column.id, 'number')}
                    aria-label="Switch to number filter"
                    className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                  >
                    <CalculatorIcon className="h-3.5 w-3.5 text-muted" />
                  </button>
                </Tooltip>
              </div>
              {hasActiveFilter && (
                <button
                  onClick={() => clearColumnFilter(column.id)}
                  className="text-xs text-brand-red dark:text-brand-red hover:underline"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Search within filter */}
          <div className="mb-2">
            <input
              type="text"
              value={searchText}
              onChange={(e) =>
                setFilterSearchText(prev => ({
                  ...prev,
                  [column.id]: e.target.value
                }))
              }
              placeholder="Search values..."
              className="w-full px-2 py-1 text-xs border border-theme-border-secondary rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-theme-bg-primary text-heading"
            />
          </div>

          {/* Check All / Uncheck All buttons */}
          {!isLoadingValues && backendValues.length > 0 && (
            <div className="flex items-center gap-2 mb-2 px-1">
              <button
                onClick={() => {
                  const filterValueLabel = (column.columnDef.meta as any)?.filterValueLabel;
                  const visibleValues = isDateTree ? visibleTreeKeys : backendValues
                    .filter(item => {
                      if (!searchText) return true;
                      const displayLabel = filterValueLabel ? filterValueLabel(item.value) : item.value;
                      return displayLabel.toLowerCase().includes(searchText.toLowerCase());
                    })
                    .map(item => item.value);
                  checkAllVisibleFilterValues(column.id, visibleValues);
                }}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                Check All{searchText ? ' Visible' : ''}
              </button>
              <span className="text-gray-400">|</span>
              <button
                onClick={() => {
                  const filterValueLabel = (column.columnDef.meta as any)?.filterValueLabel;
                  const visibleValues = isDateTree ? visibleTreeKeys : backendValues
                    .filter(item => {
                      if (!searchText) return true;
                      const displayLabel = filterValueLabel ? filterValueLabel(item.value) : item.value;
                      return displayLabel.toLowerCase().includes(searchText.toLowerCase());
                    })
                    .map(item => item.value);
                  uncheckAllVisibleFilterValues(column.id, visibleValues);
                }}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                Uncheck All{searchText ? ' Visible' : ''}
              </button>
            </div>
          )}

          {/* Values List with Checkboxes */}
          <div className="space-y-1 overflow-y-auto flex-1">
            {isLoadingValues ? (
              <div className="flex items-center justify-center py-8">
                <div className="flex flex-col items-center gap-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span className="text-xs text-muted">Loading values...</span>
                </div>
              </div>
            ) : backendValues.length === 0 ? (
              <div className="text-center py-4 text-xs text-muted">
                No values available
              </div>
            ) : isDateTree ? (
              visibleTree.length === 0 ? (
                <div className="text-center py-4 text-xs text-muted">No matching dates</div>
              ) : (
                visibleTree.map(node => renderDateNode(node, 0))
              )
            ) : (
              <TanTableFacetList
                key={`${column.id}:${searchText}`}
                values={backendValues}
                searchText={searchText}
                filterValueLabel={(column.columnDef.meta as { filterValueLabel?: (value: string) => string })?.filterValueLabel}
                height={Math.max(80, filterDropdownSize.height - 150)}
                isSelected={key => filterValue === undefined
                  ? !isExclusionMode
                  : facetSelectionIncludes(filterValue, key)}
                isExclusionMode={isExclusionMode}
                onToggle={value => toggleFilterValue(column.id, value)}
              />
            )}
          </div>
        </div>
        {/* Resize Handle */}
        <div
          onMouseDown={handleFilterDropdownResize}
          className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize flex items-center justify-center"
          style={{ touchAction: 'none' }}
        >
          <svg className="w-2.5 h-2.5 text-gray-400" viewBox="0 0 10 10" fill="currentColor">
            <path d="M9 1v8H1" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </div>
      </div>,
      portalContainer || document.body
    );
  };

  // autoHeight: measure the card's own top offset and take the rest of the viewport.
  // A ResizeObserver on the body catches banners appearing, disappearing or rewrapping
  // above it — the case the fixed height gets wrong.
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null);
  useEffect(() => {
    if (!autoHeight || collapsed) { setMeasuredHeight(null); return; }
    const measure = () => {
      const el = containerRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      // A floor keeps the card usable rather than collapsing to nothing when the page
      // above is unusually tall.
      setMeasuredHeight(Math.max(240, Math.round(window.innerHeight - top - autoHeightGap)));
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    ro.observe(document.body);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [autoHeight, autoHeightGap, collapsed]);

  return (
    <div
      ref={containerRef}
      style={measuredHeight !== null ? { height: measuredHeight } : undefined}
      data-field-name={fieldName || undefined}
      className={`bg-theme-bg-primary shadow rounded-lg border border-theme-border-primary flex flex-col overflow-hidden max-w-full ${
      collapsed ? 'h-auto' :
      measuredHeight !== null ? '' :
      fillContainer ? 'h-full' :
      cardHeight === 'sm' ? 'h-[30vh]' :
      cardHeight === 'md' ? 'h-[50vh]' :
      'h-[calc(100vh-280px)]'
    } ${
      isHighlighted
        ? 'ring-4 ring-amber-500 dark:ring-amber-400 shadow-[0_0_20px_rgba(245,158,11,0.8)] animate-[pulse_1s_ease-in-out_3]'
        : ''
    }`}>
      {showFieldNames && fieldName && (
        <FieldNameBadge
          fieldName={fieldName}
          isHighlighted={!!isHighlighted}
          variant="corner"
        />
      )}
      {/* Card Header */}
      <div className={`px-4 py-3 ${tableHeaderBgColor || 'bg-cmp-table-titlebar'} rounded-t-lg flex items-center justify-between flex-shrink-0 gap-2 ${fillContainer ? 'cursor-move' : ''}`}>
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <h2 className="text-sm font-bold text-cmp-table-titlebar-fg truncate">
            {cardTitle || 'Data Table'}
            {!loading && !hideTitleCount && (
              <span className="text-xs font-normal text-gray-300 ml-2">
                {(globalFilter || columnFilters.length > 0) && table.getFilteredRowModel().rows.length !== data.length
                  ? `(${table.getFilteredRowModel().rows.length} of ${data.length})`
                  : `(${data.length})`
                }
              </span>
            )}
            {loading && (
              <span className="ml-2 text-blue-300">Loading<span className="animate-ellipsis"></span> {formatTime(elapsedSeconds)}</span>
            )}
            {!loading && loadedInSeconds !== null && (
              <span className="ml-2 text-green-300">Loaded in {formatTime(loadedInSeconds)}</span>
            )}
          </h2>
          {/* Card-mode sort, parked right of the title. Card mode suppresses the
              column header (no sort headers), so this is the only sort affordance
              for cards. Styled for the slate header, and flex-shrink-0 so the
              title truncates instead of the control being squashed. */}
          {renderCardRow && (
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {/* The gap (not a trailing space) separates label from select —
                  a text-node space would be collapsed away by flex layout. */}
              <span className="text-xs text-gray-200">Sort:</span>
              <select
                aria-label="Sort cards by"
                value={sorting[0]?.id ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setSorting(v ? [{ id: v, desc: sorting[0]?.desc ?? false }] : []);
                }}
                className="max-w-[10rem] px-2 py-1 text-xs rounded bg-slate-800/60 text-white border border-slate-500 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {(() => {
                  // Card mode is deliberately single-sort — a select change
                  // replaces any persisted multi-sort from table mode.
                  const sortableCols = table.getVisibleLeafColumns()
                    .filter((c) => !SPECIAL_COLUMN_IDS.has(c.id) && c.getCanSort());
                  const sortedId = sorting[0]?.id;
                  // A sort persisted on a now-hidden column still applies; keep it
                  // selectable as "(hidden)" so the select never renders blank
                  // while the direction arrow shows. Display-only — sorting state
                  // is shared with table mode and must not be auto-cleared here.
                  const orphaned = sortedId && !sortableCols.some((c) => c.id === sortedId) ? sortedId : null;
                  const orphanedCol = orphaned ? table.getColumn(orphaned) : null;
                  return (
                    <>
                      <option value="">None</option>
                      {sortableCols.map((c) => (
                        <option key={c.id} value={c.id}>
                          {typeof c.columnDef.header === 'string' ? c.columnDef.header : c.id}
                        </option>
                      ))}
                      {orphaned && (
                        <option value={orphaned}>
                          {`${orphanedCol && typeof orphanedCol.columnDef.header === 'string' ? orphanedCol.columnDef.header : orphaned} (hidden)`}
                        </option>
                      )}
                    </>
                  );
                })()}
              </select>
              {sorting[0] && (
                <button
                  aria-label="Toggle sort direction"
                  title={sorting[0].desc ? 'Descending — click for ascending' : 'Ascending — click for descending'}
                  onClick={() => setSorting([{ id: sorting[0].id, desc: !sorting[0].desc }])}
                  className="px-2 py-1 text-xs rounded bg-slate-600 text-white border border-slate-500 hover:bg-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {sorting[0].desc ? '↓' : '↑'}
                </button>
              )}
            </div>
          )}
          {/* Per-card view controls (e.g. dispatcher filter) - left-aligned next to the title */}
          {headerExtra}
          {/* Tab Dropdown - only show when tabs are enabled and collapsed */}
          {effectiveShowTabs && tabsCollapsed && effectiveTabData.length > 0 && (
            <>
              {multiTabSelect ? (
                <div className="relative" ref={tabDropdownRef}>
                  <button
                    onClick={() => setShowTabDropdown(!showTabDropdown)}
                    className="text-xs px-2 py-1 rounded bg-slate-600 text-white border border-slate-500 hover:bg-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {currentActiveTabs.length === 1
                      ? effectiveTabData.find(t => t.id === currentActiveTabs[0])?.label || 'Select'
                      : `${currentActiveTabs.length} tabs selected`}
                  </button>
                  {showTabDropdown && (
                    <div className="absolute left-0 mt-1 w-48 bg-theme-bg-elevated border border-theme-border-primary rounded-lg shadow-lg z-50">
                      <div className="p-1">
                        {effectiveTabData.map(tab => {
                          const tabCount = tabCounts[tab.id] ?? 0;
                          const isSelected = currentActiveTabs.includes(tab.id);
                          const isLastSelected = isSelected && currentActiveTabs.length <= 1;
                          return (
                            <label
                              key={tab.id}
                              className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs ${
                                isLastSelected
                                  ? 'cursor-not-allowed opacity-60'
                                  : 'hover:bg-theme-bg-tertiary cursor-pointer'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={isLastSelected}
                                onChange={() => handleTabChange(tab.id)}
                                className="rounded border-theme-border-secondary text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-label">
                                {tab.label} ({tabCount})
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <select
                  value={currentActiveTab}
                  onChange={(e) => handleTabChange(e.target.value)}
                  className="text-xs px-2 py-1 rounded bg-slate-600 text-white border border-slate-500 hover:bg-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {effectiveTabData.map(tab => {
                    const tabCount = tabCounts[tab.id] ?? 0;
                    const isSelected = tab.id === currentActiveTab;
                    return (
                      <option key={tab.id} value={tab.id}>
                        {tab.label}{!isSelected && ` (${tabCount})`}
                      </option>
                    );
                  })}
                </select>
              )}
              {/* Tabs Toggle Button - only show when tabs are collapsed */}
              <button
                onClick={() => setTabsCollapsed(!tabsCollapsed)}
                className="p-1 hover:bg-slate-600 dark:hover:bg-slate-900 rounded transition-colors"
                title="Show tabs"
              >
                <CalendarIcon className="h-5 w-5 text-white" />
              </button>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Search Input and Toggle - only show inline when card is wide */}
          {!isNarrow && (
            <>
              {/* Active-filter warning chip - shown when filters are applied but the filter bar is hidden.
                  Table mode only: its "show the filters" action sets localFilterOverride, which card
                  mode ignores (the chips answer to Edit Fields), so card mode always takes the
                  clear-all branch below instead — exactly one indicator, and it can still clear. */}
              {enableColumnFilters && activeColumnFilters.length > 0 && !renderCardRow && !showColumnFilters && (
                <Tooltip content={`Filtered: ${activeFilterColumnLabels.join(', ')} — click to show filters`} placement="left" offsetY={30}>
                  <span className="flex items-center gap-0.5 pl-1.5 pr-0.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/60 border border-amber-400 dark:border-amber-600 text-amber-800 dark:text-amber-200 whitespace-nowrap">
                    <button
                      aria-label="Show active column filters"
                      onClick={() => setLocalFilterOverride(true)}
                      className="flex items-center gap-1 text-[0.625rem] font-semibold leading-none hover:underline"
                    >
                      <FunnelIconSolid className="h-3 w-3" />
                      {activeColumnFilters.length} filter{activeColumnFilters.length > 1 ? 's' : ''}
                    </button>
                    <button
                      aria-label="Clear all column filters"
                      onClick={clearAllColumnFilters}
                      className="p-0.5 rounded hover:bg-amber-200 dark:hover:bg-amber-800"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                </Tooltip>
              )}
              {/* When the filter bar is open, offer a quick "clear all filters"
                  that clears only filters (unlike Clear All, which also wipes
                  settings) — otherwise there's no fast filters-only clear.
                  Card mode always uses this branch (see above). */}
              {enableColumnFilters && activeColumnFilters.length > 0 && (renderCardRow || showColumnFilters) && (
                <Tooltip content={`Filtered: ${activeFilterColumnLabels.join(', ')} — clear all filters`} placement="left" offsetY={30}>
                  <button
                    aria-label="Clear all column filters"
                    onClick={clearAllColumnFilters}
                    className="flex items-center gap-1 pl-1.5 pr-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/60 border border-amber-400 dark:border-amber-600 text-amber-800 dark:text-amber-200 whitespace-nowrap text-[0.625rem] font-semibold leading-none hover:bg-amber-200 dark:hover:bg-amber-800"
                  >
                    <XMarkIcon className="h-3 w-3" />
                    Clear {activeColumnFilters.length} filter{activeColumnFilters.length > 1 ? 's' : ''}
                  </button>
                </Tooltip>
              )}
              {/* Custom-filter clear, the sibling of the column clear above. Indigo, not
                  amber: the two narrow the grid in different ways (one re-queries, one
                  hides fetched rows) and a user clearing one should never think they
                  cleared the other. */}
              {customFilterCount > 0 && onClearCustomFilters && (
                <Tooltip content="Clear the custom filter applied to this bucket" placement="left" offsetY={30}>
                  <button
                    aria-label="Clear custom filters"
                    onClick={onClearCustomFilters}
                    className="flex items-center gap-1 pl-1.5 pr-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/60 border border-indigo-400 dark:border-indigo-600 text-indigo-800 dark:text-indigo-200 whitespace-nowrap text-[0.625rem] font-semibold leading-none hover:bg-indigo-200 dark:hover:bg-indigo-800"
                  >
                    <XMarkIcon className="h-3 w-3" />
                    Clear Custom Filters
                  </button>
                </Tooltip>
              )}
              {searchPrefix}
              {/* Search Input - always visible when search is enabled */}
              {enableSearch && (
                <div className="relative w-48">
                  <input
                    type="text"
                    value={globalFilter ?? ''}
                    onChange={(e) => { activityUserActedRef.current = true; setGlobalFilter(e.target.value); }}
                    placeholder={searchPlaceholder}
                    className="w-full pl-2 pr-6 py-0.5 text-sm border border-theme-border-secondary rounded bg-theme-bg-primary text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  {globalFilter ? (
                    <button
                      type="button"
                      aria-label="Clear search"
                      title="Clear search"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { activityUserActedRef.current = true; setGlobalFilter(''); }}
                      className="absolute inset-y-0 right-1 flex items-center text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    >
                      <XMarkIcon className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                </div>
              )}
            </>
          )}

          {/* Icons shown inline when card is wide (>= 600px) */}
          {!isNarrow && (
            <>
          {/* Tab Grouping Toggle - only show if allowTabFieldSelection is true */}
          {showTabs && allowTabFieldSelection && (
            <div className="relative" ref={tabGroupMenuRef}>
              <Tooltip content={selectedTabGroupField ? `Grouped by: ${selectedTabGroupField}` : 'Group Data into Tabs'} placement="left" offsetY={30}>
                <button
                  onClick={() => setShowTabGroupMenu(!showTabGroupMenu)}
                  className={`inline-flex items-center justify-center p-1 rounded transition-colors ${
                    selectedTabGroupField
                      ? 'bg-blue-600 dark:bg-blue-700'
                      : 'hover:bg-slate-600 dark:hover:bg-slate-900'
                  }`}
                >
                  <TabGroupIcon className="h-4 w-4 text-white" />
                </button>
              </Tooltip>

              {showTabGroupMenu && (
                <div className="absolute right-0 mt-2 w-64 bg-theme-bg-elevated border border-theme-border-primary rounded-lg shadow-lg z-50">
                  <div className="p-3 border-b border-theme-border-primary">
                    <h3 className="text-sm font-semibold text-heading">
                      Group Data by Field
                    </h3>
                  </div>
                  <div className="max-h-64 overflow-y-auto p-2">
                    {/* Clear Selection Option */}
                    {selectedTabGroupField && (
                      <>
                        <button
                          onClick={() => {
                            setSelectedTabGroupField(null);
                            setShowTabGroupMenu(false);
                            onTabGroupingChange?.('none');  // Use 'none' to explicitly indicate no tab grouping
                          }}
                          className="w-full px-3 py-2 text-sm text-left rounded hover:bg-theme-bg-tertiary text-brand-red dark:text-brand-red font-semibold"
                        >
                          Clear Grouping
                        </button>
                        <div className="border-t border-theme-border-primary my-2" />
                      </>
                    )}

                    {/* Field Options */}
                    {[...table.getAllLeafColumns()]
                      .filter(col => {
                        // Exclude action columns and non-visible columns
                        const header = typeof col.columnDef.header === 'string' ? col.columnDef.header : col.id;
                        return header.toLowerCase() !== 'actions' && col.getIsVisible();
                      })
                      .sort((a, b) => {
                        const aHeader = typeof a.columnDef.header === 'string' ? a.columnDef.header : a.id;
                        const bHeader = typeof b.columnDef.header === 'string' ? b.columnDef.header : b.id;
                        return aHeader.localeCompare(bHeader);
                      })
                      .map((column) => {
                        const isSelected = selectedTabGroupField === column.id;
                        const header = typeof column.columnDef.header === 'string'
                          ? column.columnDef.header
                          : column.id;

                        return (
                          <button
                            key={column.id}
                            onClick={() => {
                              setSelectedTabGroupField(column.id);
                              setShowTabGroupMenu(false);
                              onTabGroupingChange?.(column.id);
                            }}
                            className={`w-full px-3 py-2 text-sm text-left rounded hover:bg-theme-bg-tertiary ${
                              isSelected
                                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-semibold'
                                : 'text-label'
                            }`}
                          >
                            {header}
                            {isSelected && (
                              <span className="ml-2 text-xs">✓</span>
                            )}
                          </button>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Show Deleted Toggle */}
          {showDeletedToggle && onShowDeletedChange && (
            <Tooltip content={showDeleted ? 'Hide Deleted Items' : 'Show Deleted Items'} placement="left" offsetY={30}>
              <button
                onClick={() => onShowDeletedChange(!showDeleted)}
                className={`inline-flex items-center justify-center p-1 rounded transition-colors ${
                  showDeleted
                    ? 'bg-brand-red dark:bg-brand-red'
                    : 'hover:bg-slate-600 dark:hover:bg-slate-900'
                }`}
              >
                <TrashIcon className="h-4 w-4 text-white" />
              </button>
            </Tooltip>
          )}

          {/* Show Archived Toggle */}
          {showArchivedToggle && onShowArchivedChange && (
            <Tooltip content={showArchived ? 'Hide Archived Items' : 'Show Archived Items'} placement="left" offsetY={30}>
              <button
                onClick={() => onShowArchivedChange(!showArchived)}
                className={`inline-flex items-center justify-center p-1 rounded transition-colors ${
                  showArchived
                    ? 'bg-blue-600 dark:bg-blue-600'
                    : 'hover:bg-slate-600 dark:hover:bg-slate-900'
                }`}
              >
                <ArchiveBoxIcon className="h-4 w-4 text-white" />
              </button>
            </Tooltip>
          )}

          {/* Row Filter Toggle (page-owned narrowing filter) */}
          {rowFilterToggle && (
            <Tooltip
              content={rowFilterToggle.active ? rowFilterToggle.onTooltip : rowFilterToggle.offTooltip}
              placement="left"
              offsetY={30}
            >
              <button
                onClick={() => rowFilterToggle.onChange(!rowFilterToggle.active)}
                className={`inline-flex items-center justify-center p-1 rounded transition-colors ${
                  rowFilterToggle.active
                    ? 'bg-amber-600 dark:bg-amber-600'
                    : 'hover:bg-slate-600 dark:hover:bg-slate-900'
                }`}
              >
                <rowFilterToggle.Icon className="h-4 w-4 text-white" />
              </button>
            </Tooltip>
          )}


          {/* Card mode reuses this slot: Column Edit Mode is meaningless without
              column headers, so the same TableIcon toggles the card fields
              section instead. NOT wrapped in the columnToggleRef div — in card
              mode that ref belongs to the fields section's Columns popover, and
              a second attachment would break its outside-click close. */}
          {renderCardRow && (
            <Tooltip content={showCardFields ? 'Hide Fields' : 'Edit Fields'} placement="left" offsetY={30}>
              <button
                onClick={() => setShowCardFields((s) => !s)}
                aria-label="Edit Fields"
                aria-pressed={showCardFields}
                className={`inline-flex items-center justify-center p-1 rounded transition-colors ${showCardFields ? 'bg-blue-600 hover:bg-blue-700' : 'hover:bg-slate-600 dark:hover:bg-slate-900'}`}
              >
                <TableIcon className="h-4 w-4 text-white" />
              </button>
            </Tooltip>
          )}

          {/* Column Edit Mode toggle (was Show/Hide Columns) */}
          {enableColumnEditing && !renderCardRow && (
            <div className="relative" ref={columnToggleRef}>
              <Tooltip content={editMode ? 'Exit Column Edit Mode' : 'Column Edit Mode'} placement="left" offsetY={30}>
                <button
                  onClick={toggleEditMode}
                  aria-label="Column Edit Mode"
                  className={`inline-flex items-center justify-center p-1 rounded transition-colors ${editMode ? 'bg-blue-600 hover:bg-blue-700' : 'hover:bg-slate-600 dark:hover:bg-slate-900'}`}
                >
                  <TableIcon className="h-4 w-4 text-white" />
                </button>
              </Tooltip>

            {showColumnToggle && (
              <div className="absolute right-0 mt-2 w-64 bg-theme-bg-elevated border border-theme-border-primary rounded-lg shadow-lg z-50">
                <div className="p-3 border-b border-theme-border-primary">
                  <h3 className="text-sm font-semibold text-heading mb-2">
                    Toggle Columns
                  </h3>
                  {(() => {
                    // Check if any column has an active filter
                    const hasAnyActiveFilter = table.getAllLeafColumns().some(col => {
                      const filterValue = col.getFilterValue();
                      return filterValue !== undefined &&
                        (Array.isArray(filterValue) ? filterValue.length > 0 : true);
                    });

                    return (
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            table.getAllLeafColumns().forEach(col => col.toggleVisibility(true));
                            setTimeout(() => {
                              const visibleCols = table.getAllLeafColumns()
                                .filter(col => col.getIsVisible())
                                .map(col => col.id);
                              onVisibleColumnsChange?.(visibleCols);
                            }, 0);
                          }}
                          className="flex-1 px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                        >
                          Check All
                        </button>
                        <Tooltip
                          content={hasAnyActiveFilter ? "Clear all filters first" : ""}
                          placement="bottom"
                        >
                          <button
                            onClick={() => {
                              if (hasAnyActiveFilter) return;
                              // Hide all columns except locked ones
                              table.getAllLeafColumns().forEach(col => {
                                if (!allLockedColumnIds.includes(col.id)) {
                                  col.toggleVisibility(false);
                                }
                              });
                              setTimeout(() => {
                                const visibleCols = table.getAllLeafColumns()
                                  .filter(col => col.getIsVisible())
                                  .map(col => col.id);
                                onVisibleColumnsChange?.(visibleCols);
                              }, 0);
                            }}
                            disabled={hasAnyActiveFilter}
                            className={`flex-1 px-2 py-1 text-xs rounded transition-colors ${
                              hasAnyActiveFilter
                                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                : 'bg-gray-600 hover:bg-gray-700 text-white'
                            }`}
                          >
                            Uncheck All
                          </button>
                        </Tooltip>
                      </div>
                    );
                  })()}
                </div>
                {/* Search box for filtering columns */}
                <div className="px-3 pb-2">
                  <input
                    type="text"
                    value={columnSearchFilter}
                    onChange={(e) => {
                      setColumnSearchFilter(e.target.value);
                      columnListRef.current?.scrollTo(0, 0);
                    }}
                    placeholder="Search columns..."
                    className="w-full px-2 py-1 text-sm border border-theme-border-secondary rounded bg-theme-bg-primary text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    autoFocus
                  />
                </div>
                <div ref={columnListRef} className="max-h-64 overflow-y-auto p-2">
                  {renderColumnPickerOptions()}
                </div>
              </div>
            )}
            </div>
          )}

          {/* Group By */}
          {enableGrouping && validGroupableColumns.length > 0 && (
            <div className="relative" ref={groupingMenuRef}>
              <Tooltip content="Group By" placement="left" offsetY={30}>
                <button
                  onClick={() => setShowGroupingMenu(!showGroupingMenu)}
                  className="inline-flex items-center justify-center p-1 hover:bg-slate-600 dark:hover:bg-slate-900 rounded transition-colors"
                >
                  <Squares2X2Icon className="h-4 w-4 text-white" />
                </button>
              </Tooltip>

              {showGroupingMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-theme-bg-elevated border border-theme-border-primary rounded-lg shadow-lg z-50">
                  <div className="p-3 border-b border-theme-border-primary">
                    <h3 className="text-sm font-semibold text-heading">
                      Group By Column
                    </h3>
                  </div>
                  <div className="max-h-64 overflow-y-auto p-2">
                    {validGroupableColumns.map((columnId) => {
                      const column = table.getColumn(columnId);
                      if (!column) return null;

                      const isGrouped = grouping.includes(columnId);
                      const groupIndex = grouping.indexOf(columnId);
                      const groupOrder = groupIndex >= 0 ? groupIndex + 1 : null;

                      return (
                        <button
                          key={columnId}
                          onClick={() => {
                            if (isGrouped) {
                              const newGrouping = grouping.filter(g => g !== columnId);
                              setGrouping(newGrouping);
                              onGroupingChange?.(newGrouping);
                            } else {
                              const newGrouping = [...grouping, columnId];
                              setGrouping(newGrouping);
                              onGroupingChange?.(newGrouping);
                            }
                          }}
                          className={`w-full flex items-center justify-between px-2 py-1.5 hover:bg-theme-bg-tertiary rounded cursor-pointer ${
                            isGrouped ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                          }`}
                        >
                          <span className="text-sm text-label">
                            {(column.columnDef.meta as any)?.groupLabel
                              ?? (typeof column.columnDef.header === 'string'
                                ? column.columnDef.header
                                : columnId)}
                          </span>
                          {isGrouped && (
                            <span className="text-xs text-blue-600 dark:text-blue-400 font-semibold">
                              {groupOrder}
                            </span>
                          )}
                        </button>
                      );
                    })}
                    {grouping.length > 0 && (
                      <>
                        <div className="border-t border-theme-border-primary my-2" />
                        <button
                          onClick={() => {
                            setGrouping([]);
                            onGroupingChange?.([]);
                          }}
                          className="w-full px-2 py-1.5 text-sm text-brand-red dark:text-brand-red hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                        >
                          Clear All Groups
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Expand/Collapse Groups - dropdown listing each grouped column */}
          {(enableGrouping && grouping.length > 0 || !!effectiveSubRowsGroupLabel) && (
            <div className="relative" ref={expandMenuRef}>
              <Tooltip content="Expand / Collapse Groups" placement="left" offsetY={30}>
                <button
                  aria-label="Expand / Collapse Groups"
                  onClick={() => setShowExpandMenu(!showExpandMenu)}
                  className="inline-flex items-center justify-center p-1 hover:bg-slate-600 dark:hover:bg-slate-900 rounded transition-colors"
                >
                  <svg className="h-4 w-4 text-white" viewBox="-4.67 -4.67 33.33 33.33" fill="currentColor">
                    <path d="M20,14h-2c-2.21,0-4,1.79-4,4h-5c-1.65,0-3-1.35-3-3v-5c2.21,0,4-1.79,4-4v-2C10,1.79,8.21,0,6,0h-2C1.79,0,0,1.79,0,4v2c0,2.21,1.79,4,4,4v5c0,2.76,2.24,5,5,5h5c0,2.21,1.79,4,4,4h2c2.21,0,4-1.79,4-4v-2c0-2.21-1.79-4-4-4ZM2,6v-2c0-1.1,.9-2,2-2h2c1.1,0,2,.9,2,2v2c0,1.1-.9,2-2,2h-2c-1.1,0-2-.9-2-2Zm20,14c0,1.1-.9,2-2,2h-2c-1.1,0-2-.9-2-2v-2c0-1.1,.9-2,2-2h2c1.1,0,2,.9,2,2v2Z"/>
                  </svg>
                </button>
              </Tooltip>

              {showExpandMenu && (() => {
                const flatGroupRows = table.getPreExpandedRowModel().flatRows.filter(r => r.getIsGrouped());
                const flatParentRows = effectiveSubRowsGroupLabel
                  ? table.getPreExpandedRowModel().flatRows.filter(r => !r.getIsGrouped() && r.subRows.length > 0)
                  : [];
                const isLevelExpanded = (depth: number) => {
                  if (expanded === true) return true;
                  const rows = flatGroupRows.filter(r => r.depth === depth);
                  return rows.length > 0 && rows.every(r => r.getIsExpanded());
                };
                const toggleLevel = (depth: number) => {
                  const currentlyAllExpanded = isLevelExpanded(depth);
                  const next: Record<string, boolean> = {};
                  flatGroupRows.forEach(r => {
                    if (r.depth === depth) {
                      if (!currentlyAllExpanded) next[r.id] = true;
                    } else if (r.getIsExpanded()) {
                      next[r.id] = true;
                    }
                  });
                  flatParentRows.forEach(r => { if (r.getIsExpanded()) next[r.id] = true; });
                  setExpanded(next);
                };
                const isSubRowsExpanded = expanded === true || (flatParentRows.length > 0 && flatParentRows.every(r => r.getIsExpanded()));
                const toggleSubRows = () => {
                  const next: Record<string, boolean> = {};
                  flatGroupRows.forEach(r => { if (r.getIsExpanded()) next[r.id] = true; });
                  if (!isSubRowsExpanded) {
                    flatParentRows.forEach(r => { next[r.id] = true; });
                  }
                  setExpanded(next);
                };
                const setAll = (value: boolean) => {
                  if (value) {
                    setExpanded(true);
                  } else {
                    setExpanded({});
                  }
                };
                const allExpanded = expanded === true || (flatGroupRows.length > 0 && flatGroupRows.every(r => r.getIsExpanded()));
                return (
                  <div className="absolute right-0 mt-2 w-56 bg-theme-bg-elevated border border-theme-border-primary rounded-lg shadow-lg z-50">
                    <div className="p-3 border-b border-theme-border-primary">
                      <h3 className="text-sm font-semibold text-heading">
                        Expand / Collapse Groups
                      </h3>
                    </div>
                    <div className="max-h-64 overflow-y-auto p-2">
                      {grouping.map((columnId, depth) => {
                        const column = table.getColumn(columnId);
                        const label = (column?.columnDef.meta as any)?.groupLabel
                          ?? (typeof column?.columnDef.header === 'string'
                            ? column.columnDef.header
                            : columnId);
                        const levelExpanded = isLevelExpanded(depth);
                        return (
                          <button
                            key={columnId}
                            onClick={() => toggleLevel(depth)}
                            className={`w-full flex items-center justify-between px-2 py-1.5 hover:bg-theme-bg-tertiary rounded cursor-pointer ${
                              levelExpanded ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                            }`}
                          >
                            <span className="text-sm text-label truncate">
                              {label}
                            </span>
                            <span className="text-xs text-blue-600 dark:text-blue-400 font-semibold flex-shrink-0 ml-2">
                              {levelExpanded ? 'Expanded' : 'Collapsed'}
                            </span>
                          </button>
                        );
                      })}
                      {effectiveSubRowsGroupLabel && (
                        <button
                          onClick={toggleSubRows}
                          className={`w-full flex items-center justify-between px-2 py-1.5 hover:bg-theme-bg-tertiary rounded cursor-pointer ${
                            isSubRowsExpanded ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                          }`}
                        >
                          <span className="text-sm text-label truncate">
                            {effectiveSubRowsGroupLabel}
                          </span>
                          <span className="text-xs text-blue-600 dark:text-blue-400 font-semibold flex-shrink-0 ml-2">
                            {isSubRowsExpanded ? 'Expanded' : 'Collapsed'}
                          </span>
                        </button>
                      )}
                      <div className="border-t border-theme-border-primary my-2" />
                      <button
                        onClick={() => setAll(!allExpanded)}
                        className="w-full px-2 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-theme-bg-tertiary rounded"
                      >
                        {allExpanded ? 'Collapse All' : 'Expand All'}
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Rows Per Page - only show when pagination is enabled */}
          {enablePagination && enableRowsPerPage && (
            <div className="relative" ref={rowsMenuRef}>
              <Tooltip content={`Rows Per Page (${pagination.pageSize >= data.length ? 'All' : pagination.pageSize})`} placement="left" offsetY={30}>
                <button
                  aria-label="Rows Per Page"
                  onClick={() => setShowRowsMenu(!showRowsMenu)}
                  className="min-w-[24px] h-5 px-1 flex items-center justify-center hover:bg-slate-600 dark:hover:bg-slate-900 rounded transition-colors border border-white/50"
                >
                  <span className="text-[0.625rem] text-white font-semibold leading-none">
                    {pagination.pageSize >= data.length ? 'All' : pagination.pageSize}
                  </span>
                </button>
              </Tooltip>

              {showRowsMenu && (
                <div className="absolute right-0 mt-2 w-32 bg-theme-bg-elevated border border-theme-border-primary rounded-lg shadow-lg z-50">
                  <div className="p-3 border-b border-theme-border-primary">
                    <h3 className="text-sm font-semibold text-heading">
                      Rows Per Page
                    </h3>
                  </div>
                  <div className="p-2">
                    {validatedRowsPerPageOptions.map((size) => (
                      <button
                        key={size}
                        onClick={() => {
                          setPagination(prev => ({ ...prev, pageSize: size, pageIndex: 0 }));
                          setShowRowsMenu(false);
                        }}
                        className={`w-full px-3 py-2 text-sm text-left rounded hover:bg-theme-bg-tertiary ${
                          pagination.pageSize === size && pagination.pageSize !== data.length
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-semibold'
                            : 'text-label'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                    {/* Show All option */}
                    {!hideAllRowsOption && (
                      <button
                        onClick={() => {
                          setPagination(prev => ({ ...prev, pageSize: Number.MAX_SAFE_INTEGER, pageIndex: 0 }));
                          setShowRowsMenu(false);
                        }}
                        className={`w-full px-3 py-2 text-sm text-left rounded hover:bg-theme-bg-tertiary ${
                          pagination.pageSize >= data.length
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-semibold'
                            : 'text-label'
                        }`}
                      >
                        All
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Column Filters Toggle — table mode only. In card mode it drove
              nothing the user could see (no <thead> filter row; the chips are
              owned by Edit Fields), so it read as a broken button. */}
          {enableColumnFilters && !renderCardRow && (
            <Tooltip content={`${showColumnFilters ? (localFilterOverride !== null ? 'Hide Column Filters (this table only)' : 'Hide Column Filters') : localFilterOverride === false ? 'Column Filters off for this table (shift+click to remove override)' : 'Show Column Filters (shift+click for this table only)'}${activeColumnFilters.length > 0 ? ` — Filtered: ${activeFilterColumnLabels.join(', ')}` : ''}`} placement="left" offsetY={30}>
              <button
                aria-label={showColumnFilters ? 'Hide Column Filters' : 'Show Column Filters'}
                onClick={(e) => {
                  if (e.shiftKey) {
                    const newValue = !showColumnFilters;
                    setLocalFilterOverride(newValue);
                  } else {
                    setLocalFilterOverride(null);
                    setGlobalShowColumnFilters(!showColumnFilters);
                  }
                }}
                className={`inline-flex items-center justify-center p-1 rounded transition-colors ${
                  showColumnFilters
                    ? localFilterOverride !== null
                      ? 'bg-purple-600 dark:bg-purple-700'
                      : 'bg-blue-600 dark:bg-blue-700'
                    : 'hover:bg-slate-600 dark:hover:bg-slate-900'
                }`}
              >
                <span className="relative inline-flex">
                  <FunnelIcon className={`h-4 w-4 ${showColumnFilters ? 'text-white' : localFilterOverride === false ? 'text-gray-400 dark:text-gray-500' : 'text-white'}`} />
                  {localFilterOverride === false && (
                    <svg className="absolute inset-0 w-full h-full pointer-events-none text-gray-400 dark:text-gray-500" viewBox="0 0 24 24">
                      <line x1="3" y1="21" x2="21" y2="3" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                  )}
                  {activeColumnFilters.length > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[14px] h-3.5 px-0.5 rounded-full bg-amber-500 text-white text-[0.5625rem] font-bold leading-none pointer-events-none">
                      {activeColumnFilters.length}
                    </span>
                  )}
                </span>
              </button>
            </Tooltip>
          )}

          {/* Totals Bar (Σ) — table mode only: card mode has no visible columns
              for the calculations to sit under. */}
          {enableTotalsBar && !renderCardRow && (
            <Tooltip content={showAggFooter ? 'Hide Totals Bar' : 'Totals Bar'} placement="left" offsetY={30}>
              <button
                aria-label={showAggFooter ? 'Hide Totals Bar' : 'Show Totals Bar'}
                onClick={() => {
                  setShowAggFooterOverride(!showAggFooter);
                  setAggPicker(null);
                }}
                className={`inline-flex items-center justify-center p-1 rounded transition-colors ${
                  showAggFooter ? 'bg-blue-600 dark:bg-blue-700' : 'hover:bg-slate-600 dark:hover:bg-slate-900'
                }`}
              >
                <SigmaIcon className="h-4 w-4 text-white" />
              </button>
            </Tooltip>
          )}

          {/* Super Compact + Fit Mode toggles — column-density/width controls are
              meaningless in card mode (the card owns its own layout), so hide them. */}
          {!renderCardRow && (<>
          <Tooltip content={effectiveSuperCompact ? (localSuperCompactOverride !== null ? 'Normal Density (this table only)' : 'Normal Density') : localSuperCompactOverride === false ? 'Super Compact off for this table (shift+click to remove override)' : 'Super Compact (shift+click for this table only)'} placement="left" offsetY={30}>
            <button
              aria-label={effectiveSuperCompact ? 'Normal Density' : 'Super Compact'}
              onClick={(e) => {
                const newValue = !effectiveSuperCompact;
                if (e.shiftKey) {
                  setLocalSuperCompactOverride(newValue);
                  setIsSuperCompact(newValue);
                } else {
                  setLocalSuperCompactOverride(null);
                  setGlobalSuperCompact(newValue);
                  onSuperCompactChange?.(newValue);
                }
              }}
              className={`inline-flex items-center justify-center p-1 rounded transition-colors ${
                effectiveSuperCompact
                  ? localSuperCompactOverride !== null
                    ? 'bg-purple-600 dark:bg-purple-700'
                    : 'bg-blue-600 dark:bg-blue-700'
                  : 'hover:bg-slate-600 dark:hover:bg-slate-900'
              }`}
            >
              <span className="relative inline-flex">
                <svg className={`h-4 w-4 ${effectiveSuperCompact ? 'text-white' : localSuperCompactOverride === false ? 'text-gray-400 dark:text-gray-500' : 'text-white'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <line x1="3" y1="4" x2="21" y2="4" />
                  <line x1="3" y1="9.33" x2="21" y2="9.33" />
                  <line x1="3" y1="14.67" x2="21" y2="14.67" />
                  <line x1="3" y1="20" x2="21" y2="20" />
                </svg>
                {localSuperCompactOverride === false && (
                  <svg className="absolute inset-0 w-full h-full pointer-events-none text-gray-400 dark:text-gray-500" viewBox="0 0 24 24">
                    <line x1="3" y1="21" x2="21" y2="3" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                  </svg>
                )}
              </span>
            </button>
          </Tooltip>

          {/* Toggle Column Fit Mode */}
          <Tooltip content={columnFitMode === 'fitToView' ? (localFitToWidthOverride !== null ? 'Fit to Content (this table only)' : 'Fit to Content') : localFitToWidthOverride === false ? 'Fit All in View off for this table (shift+click to remove override)' : 'Fit All in View (shift+click for this table only)'} placement="left" offsetY={30}>
            <button
              aria-label={columnFitMode === 'fitToView' ? 'Fit to Content' : 'Fit All in View'}
              onClick={handleToggleColumnFit}
              className={`inline-flex items-center justify-center p-1 rounded transition-colors ${
                columnFitMode === 'fitToView'
                  ? localFitToWidthOverride !== null
                    ? 'bg-purple-600 dark:bg-purple-700'
                    : 'bg-blue-600 dark:bg-blue-700'
                  : 'hover:bg-slate-600 dark:hover:bg-slate-900'
              }`}
            >
              <span className="relative inline-flex">
                <svg className={`h-4 w-4 ${columnFitMode === 'fitToView' ? 'text-white' : localFitToWidthOverride === false ? 'text-gray-400 dark:text-gray-500' : 'text-white'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="4" y1="12" x2="10" y2="12" />
                  <polyline points="8,6 4,12 8,18" />
                  <line x1="20" y1="12" x2="14" y2="12" />
                  <polyline points="16,6 20,12 16,18" />
                  <line x1="12" y1="4" x2="12" y2="20" />
                </svg>
                {localFitToWidthOverride === false && (
                  <svg className="absolute inset-0 w-full h-full pointer-events-none text-gray-400 dark:text-gray-500" viewBox="0 0 24 24">
                    <line x1="3" y1="21" x2="21" y2="3" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                  </svg>
                )}
              </span>
            </button>
          </Tooltip>
          </>)}

          {/* Sub-header Bar Toggle — opens the host's own bar (e.g. the Documents
              view's request filters). Same affordance as the action bar toggle, and
              deliberately sitting next to it. */}
          {subHeaderBar && (
            <Tooltip content={subBarOpen ? `Hide ${subHeaderBar.label}` : `Show ${subHeaderBar.label}`} placement="left" offsetY={30}>
              <button
                aria-label={subBarOpen ? `Hide ${subHeaderBar.label}` : `Show ${subHeaderBar.label}`}
                aria-expanded={subBarOpen}
                onClick={() => setSubBarOpen((o) => !o)}
                className={`relative inline-flex items-center justify-center p-1 rounded transition-colors ${
                  subBarOpen ? 'bg-blue-600 dark:bg-blue-700' : 'hover:bg-slate-600 dark:hover:bg-slate-900'
                }`}
              >
                <span className="block h-4 w-4 text-white">{subHeaderBar.icon}</span>
                {/* A filter narrowing the table has to stay visible even when the bar
                    that set it is collapsed. */}
                {subHeaderBar.active && !subBarOpen && (
                  <span
                    aria-hidden
                    className="absolute right-0 top-0 h-1.5 w-1.5 rounded-full bg-amber-500 ring-1 ring-white dark:ring-gray-800"
                  />
                )}
              </button>
            </Tooltip>
          )}

          {/* Action Bar Toggle — opens the inline bulk-action row (scoped to
              this table's visible/filtered rows). */}
          {hasBulkActions && (
            <Tooltip content={actionBarOpen ? 'Hide Actions' : 'Show Actions'} placement="left" offsetY={30}>
              <button
                aria-label={actionBarOpen ? 'Hide Actions' : 'Show Actions'}
                aria-expanded={actionBarOpen}
                onClick={() => setActionBarOpen((o) => !o)}
                className={`inline-flex items-center justify-center p-1 rounded transition-colors ${
                  actionBarOpen ? 'bg-blue-600 dark:bg-blue-700' : 'hover:bg-slate-600 dark:hover:bg-slate-900'
                }`}
              >
                <ActionBarToggleIcon className="h-4 w-4 text-white" />
              </button>
            </Tooltip>
          )}

          {traceAllControl?.(() => table.getFilteredRowModel().rows.map((r) => r.original))}
          {/* Export to Excel */}
          {enableExcelExport && (
            <Tooltip content={excelExportLoading ? "Exporting workbook..." : loading ? "Loading data..." : !onExcelExport && data.length === 0 ? "No data to export" : "Export to Excel"} placement="left" offsetY={30}>
              <button
                aria-label="Export to Excel"
                onClick={exportToExcel}
                disabled={loading || excelExportLoading || (!onExcelExport && data.length === 0)}
                className={`inline-flex items-center justify-center p-1 rounded transition-colors ${
                  loading || excelExportLoading || (!onExcelExport && data.length === 0)
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-green-600 dark:hover:bg-green-700'
                }`}
              >
                <ExcelIcon className="h-4 w-4 text-white" />
              </button>
            </Tooltip>
          )}

          {/* Edit Filters Button */}
          {showEditFiltersButton && onEditFilters && (
            <Tooltip content={editFiltersButtonTooltip} placement="left" offsetY={30}>
              <button
                aria-label={editFiltersButtonTooltip || 'Edit Filters'}
                onClick={onEditFilters}
                className={`inline-flex items-center justify-center p-1 rounded transition-colors ${editFiltersActive
                  ? 'bg-blue-600 dark:bg-blue-700'
                  : 'hover:bg-blue-600 dark:hover:bg-blue-700'}`}
              >
                {editFiltersButtonIcon === 'plus' ? (
                  <PlusIcon className="h-4 w-4 text-white" />
                ) : editFiltersButtonIcon === 'cog' ? (
                  <Cog6ToothIcon className="h-4 w-4 text-white" />
                ) : (
                  <PencilIcon className="h-4 w-4 text-white" />
                )}
              </button>
            </Tooltip>
          )}

          {/* Duplicate Button */}
          {onDuplicate && (
            <Tooltip content={duplicateTooltip} placement="left" offsetY={30}>
              <button
                aria-label={duplicateTooltip || 'Duplicate'}
                onClick={onDuplicate}
                className="inline-flex items-center justify-center p-1 hover:bg-purple-600 dark:hover:bg-purple-700 rounded transition-colors"
              >
                <svg className="h-4 w-4 text-white" viewBox="-2.06 -2.06 36.11 36.11" fill="currentColor">
                  <path d="m20 29h-14a3 3 0 0 1 -3-3v-14a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v14a3 3 0 0 1 -3 3zm-14-18a1 1 0 0 0 -1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-14a1 1 0 0 0 -1-1z"></path>
                  <path d="m10 8a1 1 0 0 1 -1-1v-1a3 3 0 0 1 3-3h1a1 1 0 0 1 0 2h-1a1 1 0 0 0 -1 1v1a1 1 0 0 1 -1 1z"></path>
                  <path d="m26 23h-1a1 1 0 0 1 0-2h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 2 0v1a3 3 0 0 1 -3 3z"></path>
                  <path d="m28 8a1 1 0 0 1 -1-1v-1a1 1 0 0 0 -1-1h-1a1 1 0 0 1 0-2h1a3 3 0 0 1 3 3v1a1 1 0 0 1 -1 1z"></path>
                  <path d="m28 16a1 1 0 0 1 -1-1v-4a1 1 0 0 1 2 0v4a1 1 0 0 1 -1 1z"></path>
                  <path d="m21 5h-4a1 1 0 0 1 0-2h4a1 1 0 0 1 0 2z"></path>
                  <path d="m16 20h-6a1 1 0 0 1 0-2h6a1 1 0 0 1 0 2z"></path>
                  <path d="m13 23a1 1 0 0 1 -1-1v-6a1 1 0 0 1 2 0v6a1 1 0 0 1 -1 1z"></path>
                </svg>
              </button>
            </Tooltip>
          )}

          {/* Lock Card Button */}
          {onToggleCardLock && (
            <Tooltip content={cardLocked ? 'Unlock Card' : 'Lock Card'} placement="left" offsetY={30}>
              <button
                aria-label={cardLocked ? 'Unlock Card' : 'Lock Card'}
                onClick={() => onToggleCardLock(!cardLocked)}
                className={`inline-flex items-center justify-center p-1 rounded transition-colors ${
                  cardLocked
                    ? 'hover:bg-gray-600 dark:hover:bg-gray-700'
                    : 'hover:bg-gray-600 dark:hover:bg-gray-700'
                }`}
              >
                {cardLocked ? (
                  <LockClosedIcon className="h-4 w-4 text-white" />
                ) : (
                  <LockOpenIcon className="h-4 w-4 text-white" />
                )}
              </button>
            </Tooltip>
          )}
          </>
        )}

          {/* Icons Toggle Button - Grid icon to show/hide toolbar (only when card is narrow) */}
          {isNarrow && (
            <Tooltip content={iconsExpanded ? 'Hide Toolbar' : 'Show Toolbar'} placement="left" offsetY={30}>
              <button
                onClick={() => setIconsExpanded(!iconsExpanded)}
                className="inline-flex items-center justify-center p-1 rounded transition-colors hover:bg-slate-600 dark:hover:bg-slate-900"
              >
                <svg className={`h-4 w-4 ${iconsExpanded ? 'text-black dark:text-white' : 'text-white'}`} viewBox="-99.56 -99.56 711.11 711.11" fill="currentColor">
                  <path d="M42.667,373.333H96c23.564,0,42.667,19.103,42.667,42.667v53.333C138.667,492.898,119.564,512,96,512H42.667C19.103,512,0,492.898,0,469.333V416C0,392.436,19.103,373.333,42.667,373.333z"/>
                  <path d="M416,373.333h53.333C492.898,373.333,512,392.436,512,416v53.333C512,492.898,492.898,512,469.333,512H416c-23.564,0-42.667-19.102-42.667-42.667V416C373.333,392.436,392.436,373.333,416,373.333z"/>
                  <path d="M42.667,186.667H96c23.564,0,42.667,19.103,42.667,42.667v53.333c0,23.564-19.103,42.667-42.667,42.667H42.667C19.103,325.333,0,306.231,0,282.667v-53.333C0,205.769,19.103,186.667,42.667,186.667z"/>
                  <path d="M416,186.667h53.333c23.564,0,42.667,19.103,42.667,42.667v53.333c0,23.564-19.102,42.667-42.667,42.667H416c-23.564,0-42.667-19.103-42.667-42.667v-53.333C373.333,205.769,392.436,186.667,416,186.667z"/>
                  <path d="M42.667,0H96c23.564,0,42.667,19.103,42.667,42.667V96c0,23.564-19.103,42.667-42.667,42.667H42.667C19.103,138.667,0,119.564,0,96V42.667C0,19.103,19.103,0,42.667,0z"/>
                  <path d="M229.333,373.333h53.333c23.564,0,42.667,19.103,42.667,42.667v53.333c0,23.564-19.103,42.667-42.667,42.667h-53.333c-23.564,0-42.667-19.102-42.667-42.667V416C186.667,392.436,205.769,373.333,229.333,373.333z"/>
                  <path d="M229.333,186.667h53.333c23.564,0,42.667,19.103,42.667,42.667v53.333c0,23.564-19.103,42.667-42.667,42.667h-53.333c-23.564,0-42.667-19.103-42.667-42.667v-53.333C186.667,205.769,205.769,186.667,229.333,186.667z"/>
                  <path d="M229.333,0h53.333c23.564,0,42.667,19.103,42.667,42.667V96c0,23.564-19.103,42.667-42.667,42.667h-53.333c-23.564,0-42.667-19.103-42.667-42.667V42.667C186.667,19.103,205.769,0,229.333,0z"/>
                  <path d="M416,0h53.333C492.898,0,512,19.103,512,42.667V96c0,23.564-19.102,42.667-42.667,42.667H416c-23.564,0-42.667-19.103-42.667-42.667V42.667C373.333,19.103,392.436,0,416,0z"/>
                </svg>
              </button>
            </Tooltip>
          )}

          {/* Card Height Settings - Always visible in title bar */}
          {onCardHeightChange && (
            <div className="relative" ref={heightMenuRef}>
              <Tooltip content="Card Height" placement="left" offsetY={30}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowHeightMenu(!showHeightMenu);
                  }}
                  className="inline-flex items-center justify-center p-1 hover:bg-slate-600 dark:hover:bg-slate-900 rounded transition-colors"
                >
                  <svg className="h-4 w-4 text-white" viewBox="-4.92 -5.01 33.74 33.74" fill="currentColor">
                    <path d="M21,15h-2c-1.65,0-3,1.35-3,3v2c0,1.65,1.35,3,3,3h2c1.65,0,3-1.35,3-3v-2c0-1.65-1.35-3-3-3Zm1,5c0,.55-.45,1-1,1h-2c-.55,0-1-.45-1-1v-2c0-.55,.45-1,1-1h2c.55,0,1,.45,1,1v2ZM21,1h-4c-1.65,0-3,1.35-3,3v4c0,1.65,1.35,3,3,3h4c1.65,0,3-1.35,3-3V4c0-1.65-1.35-3-3-3Zm1,7c0,.55-.45,1-1,1h-4c-.55,0-1-.45-1-1V4c0-.55,.45-1,1-1h4c.55,0,1,.45,1,1v4Zm-10.31-3.72c.4,.38,.42,1.01,.04,1.41-.2,.21-.46,.31-.72,.31-.25,0-.5-.09-.69-.28l-3.31-3.15V23c0,.55-.45,1-1,1s-1-.45-1-1V2.57L1.69,5.72c-.4,.38-1.03,.37-1.41-.03-.38-.4-.37-1.03,.04-1.41L3.9,.86c1.15-1.15,3.05-1.15,4.22,.02l3.57,3.4Z"/>
                  </svg>
                </button>
              </Tooltip>

              {/* Height Menu Dropdown */}
              {showHeightMenu && (
                <div className="absolute right-0 mt-2 origin-top-right rounded-md bg-theme-bg-elevated shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                  <div className="px-4 py-2 border-b border-theme-border-primary">
                    <p className="text-xs font-semibold text-label">Card Height</p>
                  </div>
                  <div className="flex gap-1 p-2">
                    <button
                      onClick={() => {
                        onCardHeightChange('sm');
                        setShowHeightMenu(false);
                      }}
                      className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
                        cardHeight === 'sm'
                          ? 'bg-blue-600 text-white'
                          : 'bg-theme-bg-secondary text-label hover:bg-theme-bg-tertiary'
                      }`}
                    >
                      SM
                    </button>
                    <button
                      onClick={() => {
                        onCardHeightChange('md');
                        setShowHeightMenu(false);
                      }}
                      className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
                        cardHeight === 'md'
                          ? 'bg-blue-600 text-white'
                          : 'bg-theme-bg-secondary text-label hover:bg-theme-bg-tertiary'
                      }`}
                    >
                      MD
                    </button>
                    <button
                      onClick={() => {
                        onCardHeightChange('lg');
                        setShowHeightMenu(false);
                      }}
                      className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
                        cardHeight === 'lg'
                          ? 'bg-blue-600 text-white'
                          : 'bg-theme-bg-secondary text-label hover:bg-theme-bg-tertiary'
                      }`}
                    >
                      LG
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Collapse Toggle Button */}
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className="p-1 hover:bg-slate-600 dark:hover:bg-slate-900 rounded transition-colors"
            >
              {collapsed ? (
                <ChevronDownIcon className="h-5 w-5 text-white" />
              ) : (
                <ChevronUpIcon className="h-5 w-5 text-white" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Secondary Toolbar - shows when card is narrow and icons are expanded */}
      {isNarrow && iconsExpanded && !collapsed && (
        <div className="px-4 py-2 bg-[rgb(229,231,235)] dark:bg-slate-700 border-t border-slate-300 dark:border-slate-600 flex items-center gap-2 flex-shrink-0 flex-wrap overflow-visible">
          {/* Active-filter warning chip - table mode only (see primary toolbar) */}
          {enableColumnFilters && activeColumnFilters.length > 0 && !renderCardRow && !showColumnFilters && (
            <Tooltip content={`Filtered: ${activeFilterColumnLabels.join(', ')} — click to show filters`} placement="left" offsetY={30}>
              <span className="flex items-center gap-0.5 pl-1.5 pr-0.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/60 border border-amber-400 dark:border-amber-600 text-amber-800 dark:text-amber-200 whitespace-nowrap">
                <button
                  aria-label="Show active column filters"
                  onClick={() => setLocalFilterOverride(true)}
                  className="flex items-center gap-1 text-[0.625rem] font-semibold leading-none hover:underline"
                >
                  <FunnelIconSolid className="h-3 w-3" />
                  {activeColumnFilters.length}
                </button>
                <button
                  aria-label="Clear all column filters"
                  onClick={clearAllColumnFilters}
                  className="p-0.5 rounded hover:bg-amber-200 dark:hover:bg-amber-800"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            </Tooltip>
          )}
          {/* Open filter bar: quick filters-only clear (see primary toolbar).
              Card mode always uses this branch. */}
          {enableColumnFilters && activeColumnFilters.length > 0 && (renderCardRow || showColumnFilters) && (
            <Tooltip content={`Filtered: ${activeFilterColumnLabels.join(', ')} — clear all filters`} placement="left" offsetY={30}>
              <button
                aria-label="Clear all column filters"
                onClick={clearAllColumnFilters}
                className="flex items-center gap-1 pl-1.5 pr-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/60 border border-amber-400 dark:border-amber-600 text-amber-800 dark:text-amber-200 whitespace-nowrap text-[0.625rem] font-semibold leading-none hover:bg-amber-200 dark:hover:bg-amber-800"
              >
                <XMarkIcon className="h-3 w-3" />
                Clear {activeColumnFilters.length}
              </button>
            </Tooltip>
          )}
          {customFilterCount > 0 && onClearCustomFilters && (
            <Tooltip content="Clear the custom filter applied to this bucket" placement="left" offsetY={30}>
              <button
                aria-label="Clear custom filters"
                onClick={onClearCustomFilters}
                className="flex items-center gap-1 pl-1.5 pr-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/60 border border-indigo-400 dark:border-indigo-600 text-indigo-800 dark:text-indigo-200 whitespace-nowrap text-[0.625rem] font-semibold leading-none hover:bg-indigo-200 dark:hover:bg-indigo-800"
              >
                <XMarkIcon className="h-3 w-3" />
                Clear Custom
              </button>
            </Tooltip>
          )}
          {searchPrefix}
          {/* Search Input - always visible in secondary toolbar when narrow */}
          {enableSearch && (
            <div className="relative flex-1 min-w-0">
              <input
                type="text"
                value={globalFilter ?? ''}
                onChange={(e) => { activityUserActedRef.current = true; setGlobalFilter(e.target.value); }}
                placeholder={searchPlaceholder}
                className="w-full pl-2 pr-6 py-0.5 text-sm border border-theme-border-secondary rounded bg-theme-bg-primary text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {globalFilter ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  title="Clear search"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { activityUserActedRef.current = true; setGlobalFilter(''); }}
                  className="absolute inset-y-0 right-1 flex items-center text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </div>
          )}

          {/* Tab Grouping Toggle */}
          {showTabs && allowTabFieldSelection && (
            <div className="relative" ref={tabGroupMenuRef}>
              <Tooltip content={selectedTabGroupField ? `Grouped by: ${selectedTabGroupField}` : 'Group Data into Tabs'} placement="left" offsetY={30}>
                <button
                  onClick={() => setShowTabGroupMenu(!showTabGroupMenu)}
                  className={`inline-flex items-center justify-center p-1 rounded transition-colors ${
                    selectedTabGroupField
                      ? 'bg-blue-600 dark:bg-blue-700'
                      : 'hover:bg-slate-300 dark:hover:bg-slate-800'
                  }`}
                >
                  <TabGroupIcon className={`h-4 w-4 ${selectedTabGroupField ? 'text-white' : 'text-gray-700 dark:text-white'}`} />
                </button>
              </Tooltip>
            </div>
          )}

          {/* Show Deleted Toggle */}
          {showDeletedToggle && onShowDeletedChange && (
            <Tooltip content={showDeleted ? 'Hide Deleted Items' : 'Show Deleted Items'} placement="left" offsetY={30}>
              <button
                onClick={() => onShowDeletedChange(!showDeleted)}
                className={`inline-flex items-center justify-center p-1 rounded transition-colors ${
                  showDeleted
                    ? 'bg-brand-red dark:bg-brand-red'
                    : 'hover:bg-slate-300 dark:hover:bg-slate-800'
                }`}
              >
                <TrashIcon className={`h-4 w-4 ${showDeleted ? 'text-white' : 'text-gray-700 dark:text-white'}`} />
              </button>
            </Tooltip>
          )}

          {/* Show Archived Toggle (narrow toolbar) */}
          {showArchivedToggle && onShowArchivedChange && (
            <Tooltip content={showArchived ? 'Hide Archived Items' : 'Show Archived Items'} placement="left" offsetY={30}>
              <button
                onClick={() => onShowArchivedChange(!showArchived)}
                className={`inline-flex items-center justify-center p-1 rounded transition-colors ${
                  showArchived
                    ? 'bg-blue-600 dark:bg-blue-600'
                    : 'hover:bg-slate-300 dark:hover:bg-slate-800'
                }`}
              >
                <ArchiveBoxIcon className={`h-4 w-4 ${showArchived ? 'text-white' : 'text-gray-700 dark:text-white'}`} />
              </button>
            </Tooltip>
          )}

          {/* Row Filter Toggle (narrow toolbar) */}
          {rowFilterToggle && (
            <Tooltip
              content={rowFilterToggle.active ? rowFilterToggle.onTooltip : rowFilterToggle.offTooltip}
              placement="left"
              offsetY={30}
            >
              <button
                onClick={() => rowFilterToggle.onChange(!rowFilterToggle.active)}
                className={`inline-flex items-center justify-center p-1 rounded transition-colors ${
                  rowFilterToggle.active
                    ? 'bg-amber-600 dark:bg-amber-600'
                    : 'hover:bg-slate-300 dark:hover:bg-slate-800'
                }`}
              >
                <rowFilterToggle.Icon className={`h-4 w-4 ${rowFilterToggle.active ? 'text-white' : 'text-gray-700 dark:text-white'}`} />
              </button>
            </Tooltip>
          )}

          {/* Card fields toggle (narrow toolbar) — see the wide-toolbar note. */}
          {renderCardRow && (
            <Tooltip content={showCardFields ? 'Hide Fields' : 'Edit Fields'} placement="left" offsetY={30}>
              <button
                onClick={() => setShowCardFields((s) => !s)}
                aria-label="Edit Fields"
                aria-pressed={showCardFields}
                className={`inline-flex items-center justify-center p-1 rounded transition-colors ${showCardFields ? 'bg-blue-600 hover:bg-blue-700' : 'hover:bg-slate-300 dark:hover:bg-slate-800'}`}
              >
                <TableIcon className={`h-4 w-4 ${showCardFields ? 'text-white' : 'text-gray-700 dark:text-white'}`} />
              </button>
            </Tooltip>
          )}

          {/* Column Edit Mode toggle (narrow toolbar) */}
          {enableColumnEditing && !renderCardRow && (
            <div className="relative" ref={columnToggleRef}>
              <Tooltip content={editMode ? 'Exit Column Edit Mode' : 'Column Edit Mode'} placement="left" offsetY={30}>
                <button
                  onClick={toggleEditMode}
                  aria-label="Column Edit Mode"
                  className={`inline-flex items-center justify-center p-1 rounded transition-colors ${editMode ? 'bg-blue-600 hover:bg-blue-700' : 'hover:bg-slate-300 dark:hover:bg-slate-800'}`}
                >
                  <TableIcon className={`h-4 w-4 ${editMode ? 'text-white' : 'text-gray-700 dark:text-white'}`} />
                </button>
              </Tooltip>
            </div>
          )}

          {/* Group By */}
          {enableGrouping && validGroupableColumns.length > 0 && (
            <div className="relative" ref={groupingMenuRef}>
              <Tooltip content="Group By" placement="left" offsetY={30}>
                <button
                  onClick={() => setShowGroupingMenu(!showGroupingMenu)}
                  className="inline-flex items-center justify-center p-1 hover:bg-slate-300 dark:hover:bg-slate-800 rounded transition-colors"
                >
                  <Squares2X2Icon className="h-4 w-4 text-gray-700 dark:text-white" />
                </button>
              </Tooltip>
            </div>
          )}

          {/* Expand/Collapse Groups - dropdown listing each grouped column */}
          {(enableGrouping && grouping.length > 0 || !!effectiveSubRowsGroupLabel) && (
            <div className="relative" ref={expandMenuRef}>
              <Tooltip content="Expand / Collapse Groups" placement="left" offsetY={30}>
                <button
                  aria-label="Expand / Collapse Groups"
                  onClick={() => setShowExpandMenu(!showExpandMenu)}
                  className="inline-flex items-center justify-center p-1 hover:bg-slate-300 dark:hover:bg-slate-800 rounded transition-colors"
                >
                  <svg className="h-4 w-4 text-gray-700 dark:text-white" viewBox="-4.67 -4.67 33.33 33.33" fill="currentColor">
                    <path d="M20,14h-2c-2.21,0-4,1.79-4,4h-5c-1.65,0-3-1.35-3-3v-5c2.21,0,4-1.79,4-4v-2C10,1.79,8.21,0,6,0h-2C1.79,0,0,1.79,0,4v2c0,2.21,1.79,4,4,4v5c0,2.76,2.24,5,5,5h5c0,2.21,1.79,4,4,4h2c2.21,0,4-1.79,4-4v-2c0-2.21-1.79-4-4-4ZM2,6v-2c0-1.1,.9-2,2-2h2c1.1,0,2,.9,2,2v2c0,1.1-.9,2-2,2h-2c-1.1,0-2-.9-2-2Zm20,14c0,1.1-.9,2-2,2h-2c-1.1,0-2-.9-2-2v-2c0-1.1,.9-2,2-2h2c1.1,0,2,.9,2,2v2Z"/>
                  </svg>
                </button>
              </Tooltip>

              {showExpandMenu && (() => {
                const flatGroupRows = table.getPreExpandedRowModel().flatRows.filter(r => r.getIsGrouped());
                const flatParentRows = effectiveSubRowsGroupLabel
                  ? table.getPreExpandedRowModel().flatRows.filter(r => !r.getIsGrouped() && r.subRows.length > 0)
                  : [];
                const isLevelExpanded = (depth: number) => {
                  if (expanded === true) return true;
                  const rows = flatGroupRows.filter(r => r.depth === depth);
                  return rows.length > 0 && rows.every(r => r.getIsExpanded());
                };
                const toggleLevel = (depth: number) => {
                  const currentlyAllExpanded = isLevelExpanded(depth);
                  const next: Record<string, boolean> = {};
                  flatGroupRows.forEach(r => {
                    if (r.depth === depth) {
                      if (!currentlyAllExpanded) next[r.id] = true;
                    } else if (r.getIsExpanded()) {
                      next[r.id] = true;
                    }
                  });
                  flatParentRows.forEach(r => { if (r.getIsExpanded()) next[r.id] = true; });
                  setExpanded(next);
                };
                const isSubRowsExpanded = expanded === true || (flatParentRows.length > 0 && flatParentRows.every(r => r.getIsExpanded()));
                const toggleSubRows = () => {
                  const next: Record<string, boolean> = {};
                  flatGroupRows.forEach(r => { if (r.getIsExpanded()) next[r.id] = true; });
                  if (!isSubRowsExpanded) {
                    flatParentRows.forEach(r => { next[r.id] = true; });
                  }
                  setExpanded(next);
                };
                const setAll = (value: boolean) => {
                  if (value) {
                    setExpanded(true);
                  } else {
                    setExpanded({});
                  }
                };
                const allExpanded = expanded === true || (flatGroupRows.length > 0 && flatGroupRows.every(r => r.getIsExpanded()));
                return (
                  <div className="absolute right-0 mt-2 w-56 bg-theme-bg-elevated border border-theme-border-primary rounded-lg shadow-lg z-50">
                    <div className="p-3 border-b border-theme-border-primary">
                      <h3 className="text-sm font-semibold text-heading">
                        Expand / Collapse Groups
                      </h3>
                    </div>
                    <div className="max-h-64 overflow-y-auto p-2">
                      {grouping.map((columnId, depth) => {
                        const column = table.getColumn(columnId);
                        const label = (column?.columnDef.meta as any)?.groupLabel
                          ?? (typeof column?.columnDef.header === 'string'
                            ? column.columnDef.header
                            : columnId);
                        const levelExpanded = isLevelExpanded(depth);
                        return (
                          <button
                            key={columnId}
                            onClick={() => toggleLevel(depth)}
                            className={`w-full flex items-center justify-between px-2 py-1.5 hover:bg-theme-bg-tertiary rounded cursor-pointer ${
                              levelExpanded ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                            }`}
                          >
                            <span className="text-sm text-label truncate">
                              {label}
                            </span>
                            <span className="text-xs text-blue-600 dark:text-blue-400 font-semibold flex-shrink-0 ml-2">
                              {levelExpanded ? 'Expanded' : 'Collapsed'}
                            </span>
                          </button>
                        );
                      })}
                      {effectiveSubRowsGroupLabel && (
                        <button
                          onClick={toggleSubRows}
                          className={`w-full flex items-center justify-between px-2 py-1.5 hover:bg-theme-bg-tertiary rounded cursor-pointer ${
                            isSubRowsExpanded ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                          }`}
                        >
                          <span className="text-sm text-label truncate">
                            {effectiveSubRowsGroupLabel}
                          </span>
                          <span className="text-xs text-blue-600 dark:text-blue-400 font-semibold flex-shrink-0 ml-2">
                            {isSubRowsExpanded ? 'Expanded' : 'Collapsed'}
                          </span>
                        </button>
                      )}
                      <div className="border-t border-theme-border-primary my-2" />
                      <button
                        onClick={() => setAll(!allExpanded)}
                        className="w-full px-2 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-theme-bg-tertiary rounded"
                      >
                        {allExpanded ? 'Collapse All' : 'Expand All'}
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Rows Per Page - only show when pagination is enabled */}
          {enablePagination && enableRowsPerPage && (
            <div className="relative" ref={rowsMenuRef}>
              <Tooltip content={`Rows Per Page (${pagination.pageSize >= data.length ? 'All' : pagination.pageSize})`} placement="left" offsetY={30}>
                <button
                  aria-label="Rows Per Page"
                  onClick={() => setShowRowsMenu(!showRowsMenu)}
                  className="min-w-[24px] h-5 px-1 flex items-center justify-center hover:bg-slate-300 dark:hover:bg-slate-800 rounded transition-colors border border-gray-500 dark:border-white/50"
                >
                  <span className="text-[0.625rem] text-gray-700 dark:text-white font-semibold leading-none">
                    {pagination.pageSize >= data.length ? 'All' : pagination.pageSize}
                  </span>
                </button>
              </Tooltip>
            </div>
          )}

          {/* Column Filters Toggle — table mode only (see wide toolbar note) */}
          {enableColumnFilters && !renderCardRow && (
            <Tooltip content={`${showColumnFilters ? (localFilterOverride !== null ? 'Hide Column Filters (this table only)' : 'Hide Column Filters') : localFilterOverride === false ? 'Column Filters off for this table (shift+click to remove override)' : 'Show Column Filters (shift+click for this table only)'}${activeColumnFilters.length > 0 ? ` — Filtered: ${activeFilterColumnLabels.join(', ')}` : ''}`} placement="left" offsetY={30}>
              <button
                aria-label={showColumnFilters ? 'Hide Column Filters' : 'Show Column Filters'}
                onClick={(e) => {
                  if (e.shiftKey) {
                    const newValue = !showColumnFilters;
                    setLocalFilterOverride(newValue);
                  } else {
                    setLocalFilterOverride(null);
                    setGlobalShowColumnFilters(!showColumnFilters);
                  }
                }}
                className={`inline-flex items-center justify-center p-1 rounded transition-colors ${
                  showColumnFilters
                    ? localFilterOverride !== null
                      ? 'bg-purple-600 dark:bg-purple-700'
                      : 'bg-blue-600 dark:bg-blue-700'
                    : 'hover:bg-slate-300 dark:hover:bg-slate-800'
                }`}
              >
                <span className="relative inline-flex">
                  <FunnelIcon className={`h-4 w-4 ${showColumnFilters ? 'text-white' : localFilterOverride === false ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-white'}`} />
                  {localFilterOverride === false && (
                    <svg className="absolute inset-0 w-full h-full pointer-events-none text-gray-400 dark:text-gray-500" viewBox="0 0 24 24">
                      <line x1="3" y1="21" x2="21" y2="3" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                  )}
                  {activeColumnFilters.length > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[14px] h-3.5 px-0.5 rounded-full bg-amber-500 text-white text-[0.5625rem] font-bold leading-none pointer-events-none">
                      {activeColumnFilters.length}
                    </span>
                  )}
                </span>
              </button>
            </Tooltip>
          )}

          {/* Totals Bar (Σ) — table mode only (see wide toolbar note) */}
          {enableTotalsBar && !renderCardRow && (
            <Tooltip content={showAggFooter ? 'Hide Totals Bar' : 'Totals Bar'} placement="left" offsetY={30}>
              <button
                aria-label={showAggFooter ? 'Hide Totals Bar' : 'Show Totals Bar'}
                onClick={() => {
                  setShowAggFooterOverride(!showAggFooter);
                  setAggPicker(null);
                }}
                className={`inline-flex items-center justify-center p-1 rounded transition-colors ${
                  showAggFooter ? 'bg-blue-600 dark:bg-blue-700' : 'hover:bg-slate-300 dark:hover:bg-slate-800'
                }`}
              >
                <SigmaIcon className={`h-4 w-4 ${showAggFooter ? 'text-white' : 'text-gray-700 dark:text-white'}`} />
              </button>
            </Tooltip>
          )}

          {/* Super Compact + Fit Mode toggles — hidden in card mode (see wide toolbar note) */}
          {!renderCardRow && (<>
          <Tooltip content={effectiveSuperCompact ? (localSuperCompactOverride !== null ? 'Normal Density (this table only)' : 'Normal Density') : localSuperCompactOverride === false ? 'Super Compact off for this table (shift+click to remove override)' : 'Super Compact (shift+click for this table only)'} placement="left" offsetY={30}>
            <button
              aria-label={effectiveSuperCompact ? 'Normal Density' : 'Super Compact'}
              onClick={(e) => {
                const newValue = !effectiveSuperCompact;
                if (e.shiftKey) {
                  setLocalSuperCompactOverride(newValue);
                  setIsSuperCompact(newValue);
                } else {
                  setLocalSuperCompactOverride(null);
                  setGlobalSuperCompact(newValue);
                  onSuperCompactChange?.(newValue);
                }
              }}
              className={`inline-flex items-center justify-center p-1 rounded transition-colors ${
                effectiveSuperCompact
                  ? localSuperCompactOverride !== null
                    ? 'bg-purple-600 dark:bg-purple-700'
                    : 'bg-blue-600 dark:bg-blue-700'
                  : 'hover:bg-slate-300 dark:hover:bg-slate-800'
              }`}
            >
              <span className="relative inline-flex">
                <svg className={`h-4 w-4 ${effectiveSuperCompact ? 'text-white' : localSuperCompactOverride === false ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-white'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <line x1="3" y1="4" x2="21" y2="4" />
                  <line x1="3" y1="9.33" x2="21" y2="9.33" />
                  <line x1="3" y1="14.67" x2="21" y2="14.67" />
                  <line x1="3" y1="20" x2="21" y2="20" />
                </svg>
                {localSuperCompactOverride === false && (
                  <svg className="absolute inset-0 w-full h-full pointer-events-none text-gray-400 dark:text-gray-500" viewBox="0 0 24 24">
                    <line x1="3" y1="21" x2="21" y2="3" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                  </svg>
                )}
              </span>
            </button>
          </Tooltip>

          {/* Toggle Column Fit Mode */}
          <Tooltip content={columnFitMode === 'fitToView' ? (localFitToWidthOverride !== null ? 'Fit to Content (this table only)' : 'Fit to Content') : localFitToWidthOverride === false ? 'Fit All in View off for this table (shift+click to remove override)' : 'Fit All in View (shift+click for this table only)'} placement="left" offsetY={30}>
            <button
              aria-label={columnFitMode === 'fitToView' ? 'Fit to Content' : 'Fit All in View'}
              onClick={handleToggleColumnFit}
              className={`inline-flex items-center justify-center p-1 rounded transition-colors ${
                columnFitMode === 'fitToView'
                  ? localFitToWidthOverride !== null
                    ? 'bg-purple-600 dark:bg-purple-700'
                    : 'bg-blue-600 dark:bg-blue-700'
                  : 'hover:bg-slate-300 dark:hover:bg-slate-800'
              }`}
            >
              <span className="relative inline-flex">
                <svg className={`h-4 w-4 ${columnFitMode === 'fitToView' ? 'text-white' : localFitToWidthOverride === false ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-white'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="4" y1="12" x2="10" y2="12" />
                  <polyline points="8,6 4,12 8,18" />
                  <line x1="20" y1="12" x2="14" y2="12" />
                  <polyline points="16,6 20,12 16,18" />
                  <line x1="12" y1="4" x2="12" y2="20" />
                </svg>
                {localFitToWidthOverride === false && (
                  <svg className="absolute inset-0 w-full h-full pointer-events-none text-gray-400 dark:text-gray-500" viewBox="0 0 24 24">
                    <line x1="3" y1="21" x2="21" y2="3" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                  </svg>
                )}
              </span>
            </button>
          </Tooltip>
          </>)}

          {/* Sub-header Bar Toggle — opens the host's own bar (e.g. the Documents
              view's request filters). Same affordance as the action bar toggle, and
              deliberately sitting next to it. */}
          {subHeaderBar && (
            <Tooltip content={subBarOpen ? `Hide ${subHeaderBar.label}` : `Show ${subHeaderBar.label}`} placement="left" offsetY={30}>
              <button
                aria-label={subBarOpen ? `Hide ${subHeaderBar.label}` : `Show ${subHeaderBar.label}`}
                aria-expanded={subBarOpen}
                onClick={() => setSubBarOpen((o) => !o)}
                className={`relative inline-flex items-center justify-center p-1 rounded transition-colors ${
                  subBarOpen ? 'bg-blue-600 dark:bg-blue-700' : 'hover:bg-slate-300 dark:hover:bg-blue-700'
                }`}
              >
                <span className={`block h-4 w-4 ${subBarOpen ? 'text-white' : 'text-gray-700 dark:text-white'}`}>{subHeaderBar.icon}</span>
                {/* A filter narrowing the table has to stay visible even when the bar
                    that set it is collapsed. */}
                {subHeaderBar.active && !subBarOpen && (
                  <span
                    aria-hidden
                    className="absolute right-0 top-0 h-1.5 w-1.5 rounded-full bg-amber-500 ring-1 ring-white dark:ring-gray-800"
                  />
                )}
              </button>
            </Tooltip>
          )}

          {/* Action Bar Toggle — opens the inline bulk-action row (scoped to
              this table's visible/filtered rows). */}
          {hasBulkActions && (
            <Tooltip content={actionBarOpen ? 'Hide Actions' : 'Show Actions'} placement="left" offsetY={30}>
              <button
                aria-label={actionBarOpen ? 'Hide Actions' : 'Show Actions'}
                aria-expanded={actionBarOpen}
                onClick={() => setActionBarOpen((o) => !o)}
                className={`inline-flex items-center justify-center p-1 rounded transition-colors ${
                  actionBarOpen ? 'bg-blue-600 dark:bg-blue-700' : 'hover:bg-slate-300 dark:hover:bg-blue-700'
                }`}
              >
                <ActionBarToggleIcon className={`h-4 w-4 ${actionBarOpen ? 'text-white' : 'text-gray-700 dark:text-white'}`} />
              </button>
            </Tooltip>
          )}

          {traceAllControl?.(() => table.getFilteredRowModel().rows.map((r) => r.original))}
          {/* Export to Excel */}
          {enableExcelExport && (
            <Tooltip content={excelExportLoading ? "Exporting workbook..." : loading ? "Loading data..." : !onExcelExport && data.length === 0 ? "No data to export" : "Export to Excel"} placement="left" offsetY={30}>
              <button
                aria-label="Export to Excel"
                onClick={exportToExcel}
                disabled={loading || excelExportLoading || (!onExcelExport && data.length === 0)}
                className={`inline-flex items-center justify-center p-1 rounded transition-colors ${
                  loading || excelExportLoading || (!onExcelExport && data.length === 0)
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-green-600 dark:hover:bg-green-700'
                }`}
              >
                <ExcelIcon className="h-4 w-4 text-green-600 dark:text-white" />
              </button>
            </Tooltip>
          )}

          {/* Edit Filters Button */}
          {showEditFiltersButton && onEditFilters && (
            <Tooltip content={editFiltersButtonTooltip} placement="left" offsetY={30}>
              <button
                aria-label={editFiltersButtonTooltip || 'Edit Filters'}
                onClick={onEditFilters}
                className={`inline-flex items-center justify-center p-1 rounded transition-colors ${editFiltersActive
                  ? 'bg-blue-600 dark:bg-blue-700'
                  : 'hover:bg-slate-300 dark:hover:bg-blue-700'}`}
              >
                {editFiltersButtonIcon === 'plus' ? (
                  <PlusIcon className="h-4 w-4 text-gray-700 dark:text-white" />
                ) : editFiltersButtonIcon === 'cog' ? (
                  <Cog6ToothIcon className="h-4 w-4 text-gray-700 dark:text-white" />
                ) : (
                  <PencilIcon className="h-4 w-4 text-gray-700 dark:text-white" />
                )}
              </button>
            </Tooltip>
          )}

          {/* Duplicate Button */}
          {onDuplicate && (
            <Tooltip content={duplicateTooltip} placement="left" offsetY={30}>
              <button
                aria-label={duplicateTooltip || 'Duplicate'}
                onClick={onDuplicate}
                className="inline-flex items-center justify-center p-1 hover:bg-slate-300 dark:hover:bg-purple-700 rounded transition-colors"
              >
                <svg className="h-4 w-4 text-purple-600 dark:text-white" viewBox="-2.06 -2.06 36.11 36.11" fill="currentColor">
                  <path d="m20 29h-14a3 3 0 0 1 -3-3v-14a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v14a3 3 0 0 1 -3 3zm-14-18a1 1 0 0 0 -1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-14a1 1 0 0 0 -1-1z"></path>
                  <path d="m10 8a1 1 0 0 1 -1-1v-1a3 3 0 0 1 3-3h1a1 1 0 0 1 0 2h-1a1 1 0 0 0 -1 1v1a1 1 0 0 1 -1 1z"></path>
                  <path d="m26 23h-1a1 1 0 0 1 0-2h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 2 0v1a3 3 0 0 1 -3 3z"></path>
                  <path d="m28 8a1 1 0 0 1 -1-1v-1a1 1 0 0 0 -1-1h-1a1 1 0 0 1 0-2h1a3 3 0 0 1 3 3v1a1 1 0 0 1 -1 1z"></path>
                  <path d="m28 16a1 1 0 0 1 -1-1v-4a1 1 0 0 1 2 0v4a1 1 0 0 1 -1 1z"></path>
                  <path d="m21 5h-4a1 1 0 0 1 0-2h4a1 1 0 0 1 0 2z"></path>
                  <path d="m16 20h-6a1 1 0 0 1 0-2h6a1 1 0 0 1 0 2z"></path>
                  <path d="m13 23a1 1 0 0 1 -1-1v-6a1 1 0 0 1 2 0v6a1 1 0 0 1 -1 1z"></path>
                </svg>
              </button>
            </Tooltip>
          )}

          {/* Lock Card Button */}
          {onToggleCardLock && (
            <Tooltip content={cardLocked ? 'Unlock Card' : 'Lock Card'} placement="left" offsetY={30}>
              <button
                aria-label={cardLocked ? 'Unlock Card' : 'Lock Card'}
                onClick={() => onToggleCardLock(!cardLocked)}
                className={`inline-flex items-center justify-center p-1 rounded transition-colors ${
                  cardLocked
                    ? 'hover:bg-gray-600 dark:hover:bg-gray-700'
                    : 'hover:bg-gray-600 dark:hover:bg-gray-700'
                }`}
              >
                {cardLocked ? (
                  <LockClosedIcon className="h-4 w-4 text-white" />
                ) : (
                  <LockOpenIcon className="h-4 w-4 text-white" />
                )}
              </button>
            </Tooltip>
          )}
        </div>
      )}

      {/* Host sub-header bar (e.g. Documents request filters). Rendered ABOVE the
          action bar on purpose: it changes what the table holds, which is what the
          action bar's "N in view" then counts and acts on. */}
      {subBarOpen && !collapsed && subHeaderBar && (
        <div className="border-y border-slate-300 dark:border-slate-600">
          {subHeaderBar.content}
        </div>
      )}

      {/* Bulk Action Bar — inline row of mass actions scoped to this table's
          visible (filtered) rows. Toggled by the action-bar icon in the toolbar. */}
      {actionBarOpen && !collapsed && hasBulkActions && (
        <TableActionBar
          actions={bulkActions!}
          getFilteredRows={() => table.getFilteredRowModel().rows.map((r) => r.original)}
          visibleCount={table.getFilteredRowModel().rows.length}
        />
      )}

      {/* Edit-mode toolbar — buffered Save/Cancel for column layout changes. */}
      {editMode && !collapsed && (
        <div className="flex items-center justify-between gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 border-y border-blue-200 dark:border-blue-800">
          <span
            className="text-xs font-medium text-blue-700 dark:text-blue-300 whitespace-nowrap truncate"
            title="Drag a header or any cell to reorder · drag a column edge to resize (double-click to auto-fit) · + to insert · × to remove"
          >
            Editing columns{editDirty ? ' · unsaved' : ''}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={(e) => { setColInsertSearch(''); setColInsertPicker({ afterId: null, x: e.clientX, y: e.clientY }); }}
              className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white"
              title="Add a column at the end"
            >
              <PlusIcon className="h-3.5 w-3.5" /> Add column
            </button>
            <button
              type="button"
              onClick={autoFitAllColumns}
              className="text-xs px-2 py-1 rounded border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-800/40"
              title="Expand every column to fit its content"
            >
              Auto Fit Width
            </button>
            <button
              type="button"
              onClick={resetEditToCurrent}
              disabled={!editDirty}
              className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-highlight disabled:opacity-40"
              title="Revert changes back to your current settings"
            >
              Reset to Current
            </button>
            {/* Reset To Defaults — clears filters/sorting/grouping/layout back to
                defaults (distinct from "Reset to Current", which only reverts the
                staged edits). Reuses the existing reset confirmation dialog. */}
            {enableReset && (
              <button
                type="button"
                onClick={() => setShowResetConfirm(true)}
                className="text-xs px-2 py-1 rounded border border-red-300 dark:border-red-700 text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30"
                title="Clear all filters & settings and restore the default layout (cannot be undone)"
              >
                Reset To Defaults
              </button>
            )}
            {/* Save as Defaults (admin) — publishes the current staged layout as the
                system-wide default. Permission-gated; the user's own Save/Cancel
                remain separate. */}
            {canPublishDefaults && (
              <PermissionsContainer
                componentId="edit-default-table-settings"
                componentName="Edit Default Table Settings"
                defaultHidden
              >
                <button
                  type="button"
                  onClick={() => setShowSaveDefaultConfirm(true)}
                  disabled={savingDefaults}
                  className="text-xs px-2 py-1 rounded bg-amber-500 hover:bg-amber-600 text-white font-medium disabled:opacity-50"
                  title="Save this layout as the system-wide default for all users"
                >
                  Save as Defaults
                </button>
              </PermissionsContainer>
            )}
            {/* "Save As System View" — publish this layout org-wide for the
                Custom Search / Custom Filter View dropdown. Only the grid a
                System View drives sets enableViews, and the component permission
                hides the button from everyone who is not a manager
                (defaultHidden), so a user who was never granted it sees nothing
                rather than a button that 403s.

                TERMINOLOGY: the UI says "System View" for what the code and DB
                call a VIEW (is_view=1), and plain "View" for what they call a
                TEMPLATE (is_view=0). The identifiers, the column, the endpoints
                and componentId="save-search-view" all keep the original names —
                renaming a seeded component id would orphan the prod grant. Read
                every "template" in this file as the user-facing "View". */}
            {enableViews && templatesAvailable && (
              <PermissionsContainer
                componentId="save-search-view"
                componentName="Save Search Views"
                defaultHidden
              >
                <button
                  type="button"
                  onClick={() => setShowSaveView(true)}
                  className="text-xs px-2 py-1 rounded border border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-200 hover:bg-indigo-100 dark:hover:bg-indigo-800/40"
                  title="Publish this layout as a System View, available to everyone in Custom Search / Custom Filter"
                >
                  Save As System View
                </button>
              </PermissionsContainer>
            )}
            {/* The user's own named layouts — "Views" in the UI, templates in the
                code. Gated on the same flags as settings persistence (see
                templatesAvailable). */}
            {templatesAvailable && (
              <>
                <button
                  type="button"
                  onClick={() => setShowSaveTemplate(true)}
                  className="text-xs px-2 py-1 rounded border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-800/40"
                  title="Save the current layout as one of your named Views"
                >
                  Save As View
                </button>
                <button
                  type="button"
                  onClick={() => setShowLoadTemplate(true)}
                  className="text-xs px-2 py-1 rounded border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-800/40"
                  title="Apply one of your own or a shared View"
                >
                  Load View
                </button>
                <button
                  type="button"
                  onClick={() => setShowManageViews(true)}
                  className="text-xs px-2 py-1 rounded border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-800/40"
                  title="Rename, share or delete your Views"
                >
                  Manage Views
                </button>
              </>
            )}
            <button
              type="button"
              onClick={cancelEdit}
              className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-highlight"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveEdit}
              className="text-xs px-3 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white font-medium"
            >
              Save
            </button>
          </div>
        </div>
      )}

      {/* Card-mode fields section — columns picker + filter chips. Card mode
          suppresses the column header (sort headers + filter row), so this bar
          is the only column-visibility / filter / field-order affordance for
          cards. It is tall (one chip cell per visible column), so it is hidden
          until the toolbar's "Edit Fields" button opens it. Sort lives in the
          card header, not here.

          "Edit Fields" (showCardFields) is the SOLE owner of this section: the
          chips deliberately do NOT also consult showColumnFilters. They used to,
          which made the toolbar funnel a no-op in card mode — the section is
          closed by default, so toggling the funnel changed nothing anywhere
          (card mode has no <thead>, so the header filter row it drives does not
          exist here) and users reported "show/hide filters isn't working". The
          funnel is now hidden in card mode instead; showColumnFilters survives
          untouched as the table-mode/global preference. */}
      {renderCardRow && !collapsed && showCardFields && (
        <div className="px-3 py-2 border-b border-theme-border-primary bg-theme-bg-secondary">
          <div className="flex flex-wrap items-center gap-2">
          {/* Columns picker — revives the showColumnToggle host (replaced by
              Column Edit Mode in table mode, which is hidden for cards). Reuses
              its state + outside-click close via columnToggleRef; the table-mode
              hosts are !renderCardRow-gated, so the ref is free in card mode.

              Deliberately NOT gated on enableColumnFilters/cardVisibleColumns
              like the hint and chip grid below: this picker is the only way to
              bring a column back, so gating it on "has visible columns" would
              trap anyone who removed the last chip with no way out short of
              wiping their saved settings. It rides the Edit Fields toggle only. */}
          <div className="relative" ref={columnToggleRef}>
            <button
              aria-label="Show/hide columns"
              aria-expanded={showColumnToggle}
              onClick={() => setShowColumnToggle((s) => !s)}
              className={`px-2 py-1 text-xs border rounded transition-colors ${
                showColumnToggle
                  ? 'border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400 bg-theme-bg-primary'
                  : 'border-theme-border-secondary text-label bg-theme-bg-primary hover:bg-theme-bg-tertiary'
              }`}
            >
              Columns
            </button>
            {showColumnToggle && (
              <div className="absolute left-0 mt-2 w-64 bg-theme-bg-elevated border border-theme-border-primary rounded-lg shadow-lg z-50">
                {/* Search box for filtering columns */}
                <div className="px-3 pt-3 pb-1">
                  <input
                    type="text"
                    value={columnSearchFilter}
                    onChange={(e) => setColumnSearchFilter(e.target.value)}
                    placeholder="Search columns..."
                    className="w-full px-2 py-1 text-sm border border-theme-border-secondary rounded bg-theme-bg-primary text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="max-h-64 overflow-y-auto p-2">
                  {renderColumnPickerOptions()}
                </div>
              </div>
            )}
          </div>
          {/* Right-aligned hint for the chip grid's drag-to-reorder. Shares the
              grid's own condition so it never advertises badges that aren't there. */}
          {enableColumnFilters && cardVisibleColumns.length > 0 && (
            <span className="ml-auto text-xs text-muted whitespace-nowrap">
              Drag badges around to rearrange view
            </span>
          )}
          </div>

          {/* Filter chips — a 10-column grid mirroring the card's own field grid,
              so chip column N sits above card field column N. It therefore iterates
              `cardVisibleColumns` (the SAME ordered list handed to renderCardRow),
              not the filterable-only subset: a non-filterable column still gets a
              same-sized static cell so the alignment holds.

              Every cell is a drag handle wired to the shared column-reorder
              handlers (used by the table-mode header/body in edit mode). Card mode
              has no edit mode, so they're wired unconditionally here — a drop
              rewrites columnOrder, which repositions the field on every card, in
              this grid, and in the Single Row View table at once. */}
          {enableColumnFilters && cardVisibleColumns.length > 0 && (
            <div data-testid="card-chip-grid" className="grid grid-cols-10 gap-1 mt-2">
              {cardVisibleColumns.map(({ id, label }) => {
                const col = table.getColumn(id);
                const canFilter = !!col?.getCanFilter();
                const fv = col?.getFilterValue();
                const chipActive = fv !== undefined && (!Array.isArray(fv) || fv.length > 0);
                const locked = allLockedColumnIds.includes(id);
                const dragTint = draggedColumn === id
                  ? 'opacity-50 ring-1 ring-amber-400'
                  : (dragOverColumn === id && draggedColumn !== id)
                  ? 'ring-1 ring-blue-500'
                  : '';
                return (
                  <div
                    key={id}
                    data-chip-col={id}
                    className={`min-w-0 rounded ${dragTint}`}
                    draggable={!locked}
                    onDragStart={() => onColDragStart(id)}
                    onDragOver={(e) => onColDragOverCol(e, id)}
                    onDragEnd={onColDragEnd}
                    onDrop={(e) => { e.preventDefault(); onColDropOn(id); }}
                  >
                    {/* The badge's border/background live on this wrapper, not on
                        the trigger, so the remove X sits INSIDE the badge outline
                        and the whole pill reads as one control. Still a flex row of
                        SIBLINGS, never nesting: a <button> inside a <button> is
                        invalid HTML and misbehaves (clicks and a11y both). */}
                    <div
                      className={`flex items-center min-w-0 border rounded transition-colors ${
                        chipActive
                          ? 'border-blue-500 dark:border-blue-400 bg-theme-bg-primary'
                          : canFilter
                          ? 'border-theme-border-secondary bg-theme-bg-primary hover:bg-theme-bg-tertiary'
                          : 'border-dashed border-theme-border-secondary bg-theme-bg-primary'
                      }`}
                    >
                    {canFilter ? (
                      <button
                        aria-label={`Filter by ${label}${chipActive ? ', filtered' : ''}`}
                        aria-expanded={activeFilterColumn === id}
                        onClick={(e) => openFilterDropdown(id, e.currentTarget)}
                        className={`flex-1 min-w-0 truncate text-left pl-2 pr-1 py-1 text-xs rounded-l focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                          chipActive
                            ? 'text-blue-600 dark:text-blue-400'
                            : 'text-label'
                        }`}
                        title={label}
                      >
                        {label}
                        {chipActive && (Array.isArray(fv) ? ` (${fv.length})` : ' •')}
                      </button>
                    ) : (
                      <span
                        className="flex-1 min-w-0 truncate pl-2 pr-1 py-1 text-xs text-gray-400 dark:text-gray-500"
                        title={label}
                      >
                        {label}
                      </span>
                    )}
                    {/* Remove the column from the view. Same path as unchecking it
                        in the Columns picker (visibility, persisted by the existing
                        settings flow), so it stays re-addable there. */}
                    {!locked && !!col?.getCanHide() && (
                      <button
                        type="button"
                        aria-label={`Remove ${label} column`}
                        title={`Remove ${label} column`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation(); // never also open the filter dropdown
                          col?.toggleVisibility(false);
                          setTimeout(() => {
                            onVisibleColumnsChange?.(
                              table.getAllLeafColumns().filter((c) => c.getIsVisible()).map((c) => c.id),
                            );
                          }, 0);
                        }}
                        className="flex-shrink-0 mr-1 p-0.5 rounded text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 focus:outline-none focus:ring-1 focus:ring-red-500 focus:text-red-600 dark:focus:text-red-400 transition-colors"
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Table (edited in place while in edit mode) */}
      {!collapsed && (
        <div className="flex-1 flex bg-theme-bg-primary relative min-h-0">
          {/* Vertical Tabs - Left Side */}
          {effectiveShowTabs && effectiveTabData.length > 0 && (
            <div className={`${tabsCollapsed ? 'w-0' : 'w-48'} overflow-hidden overflow-y-auto transition-all duration-300 border-r border-theme-border-primary flex-shrink-0`}>
              {!tabsCollapsed && effectiveTabData.map((tab, index) => {
                const isFirstTab = index === 0;
                const isActive = currentActiveTabs.includes(tab.id);
                const isPastDue = tab.id === 'past-due';
                const isToday = tab.id === 'today';

                // Check if this is tomorrow's date tab
                let isTomorrow = false;
                if (tab.id.startsWith('date-')) {
                  const dateStr = tab.id.replace('date-', '');
                  const tabDate = new Date(dateStr + 'T00:00:00');
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const tomorrow = new Date(today);
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  isTomorrow = tabDate.getTime() === tomorrow.getTime();
                }

                // Get count for this tab (respects global/column filters)
                const tabCount = tabCounts[tab.id] ?? 0;

                // Determine tab color classes
                const getTabClasses = () => {
                  if (isPastDue || isToday) {
                    return isActive
                      ? 'bg-brand-red text-white dark:bg-brand-red'
                      : 'bg-brand-red/10 text-brand-red dark:bg-brand-red/30 dark:text-brand-red hover:bg-brand-red/20 dark:hover:bg-brand-red/40';
                  }
                  if (isTomorrow) {
                    return isActive
                      ? 'bg-brand-orange text-white dark:bg-brand-orange'
                      : 'bg-brand-orange/10 text-brand-orange dark:bg-brand-orange/30 dark:text-brand-orange hover:bg-brand-orange/20 dark:hover:bg-brand-orange/40';
                  }
                  return isActive
                    ? 'bg-slate-700 text-white dark:bg-slate-600'
                    : 'bg-theme-bg-primary text-gray-600 dark:text-gray-400 hover:bg-theme-bg-tertiary';
                };

                // Determine chevron color
                const getChevronClasses = () => {
                  if (isActive) return 'text-white';
                  if (isPastDue || isToday) return 'text-brand-red dark:text-brand-red';
                  if (isTomorrow) return 'text-brand-orange dark:text-brand-orange';
                  return 'text-gray-600 dark:text-gray-400';
                };

                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`
                      w-full px-4 py-2 text-sm font-medium text-left relative
                      transition-all duration-200 border-b border-theme-border-secondary
                      ${getTabClasses()}
                    `}
                  >
                    {tab.label} ({tabCount})
                    {/* Collapse Toggle Button - Show only on first tab */}
                    {isFirstTab && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          setTabsCollapsed(!tabsCollapsed);
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-white/20 dark:hover:bg-scrim/20 rounded transition-colors"
                        title={tabsCollapsed ? 'Expand tabs' : 'Collapse tabs'}
                      >
                        <ChevronRightIcon className={`h-4 w-4 transition-transform duration-300 ${tabsCollapsed ? '' : 'rotate-180'} ${getChevronClasses()}`} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Tab Content - Right Side */}
          <div ref={tableContainerRef} className="tantable-scroll flex-1 flex flex-col overflow-auto relative">
            {/* Loading overlay - shows on top of existing data */}
            {loading && (
              <div className="absolute inset-0 bg-white/70 dark:bg-slate-900/70 z-50 flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 font-medium">Refreshing...</p>
                </div>
              </div>
            )}
            <table className="w-full text-sm" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
            {/* Card mode: no column header — the card renders its own field labels */}
            {!renderCardRow && (
            <thead className={`${tableHeaderBgColor || 'bg-cmp-table-header'} sticky top-0 z-30`}>
              {table.getHeaderGroups().map((headerGroup, hgIndex, hgArray) => (
                <React.Fragment key={headerGroup.id}>
                  <tr style={{ display: 'flex', width: '100%' }}>
                    {/* Indicator header cell */}
                    {showIndicator && (
                      <th
                        className={`border-b border-theme-border-primary bg-theme-bg-tertiary ${gridlineV ? 'border-r ' : ''}${anyGridline ? 'tantable-gridline' : ''}`}
                        style={{ width: '12px', minWidth: '12px', flexShrink: 0 }}
                      />
                    )}
                    {headerGroup.headers.map((header, headerIndex) => {
                      const { column } = header;
                      const isPinnedLeft = pinnedColumns.includes(column.id);
                      const isPinnedRight = pinnedColumnsRight.includes(column.id);
                      const isPinned = isPinnedLeft || isPinnedRight;
                      const pinnedLeft = isPinnedLeft ? (pinnedPositions[column.id] ?? 0) : undefined;
                      const pinnedRight = isPinnedRight ? (pinnedPositionsRight[column.id] ?? 0) : undefined;
                      const hasFilter = column.getFilterValue() !== undefined && (Array.isArray(column.getFilterValue()) ? (column.getFilterValue() as any[]).length > 0 : true);
                      // Add a little left padding on the first header so content
                      // isn't flush against the card border.
                      const firstColPadding = headerIndex === 0 && !showIndicator ? 'pl-3' : '';

                      // Get alignment from column meta (default to left). The
                      // header th is display:flex, so include a justify-* class
                      // to actually position the content (text-* alone doesn't
                      // move flex children).
                      const alignment = (column.columnDef.meta as any)?.align || 'left';
                      const alignmentClass = alignment === 'center' ? 'text-center justify-center' : alignment === 'right' ? 'text-right justify-end' : 'text-left';

                      const addAfterCol = canInsertColAfter(column.id, headerGroup.headers[headerIndex + 1]?.column.id);

                      return (
                        <React.Fragment key={`hwrap-${header.id}`}>
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          ref={(el) => {
                            headerRefs.current[column.id] = el;
                          }}
                          style={{
                            display: 'flex',
                            // For grouped headers, scale flexGrow by the number of leaf
                            // descendants so the parent group's width matches the sum of
                            // its leaf header widths in the row below (otherwise extra
                            // space is split per-cell and the rows misalign).
                            flexGrow: columnFitMode === 'fitToView' || isFixedWidth(header.column) ? 0 : (header.colSpan || 1),
                            flexShrink: 0,
                            flexBasis: header.getSize(),
                            minWidth: columnFitMode === 'fitToView' ? (header.column.columnDef.minSize ?? 0) : header.getSize(),
                            maxWidth: columnFitMode === 'fitToView' || isFixedWidth(header.column) ? header.getSize() : undefined,
                            position: isPinned ? 'sticky' : 'relative',
                            left: isPinnedLeft ? `${pinnedLeft}px` : undefined,
                            right: isPinnedRight ? `${pinnedRight}px` : undefined,
                            // In edit mode lift non-pinned headers above pinned ones (40)
                            // so each column's add/remove icons (a child z-index trapped in
                            // this th's stacking context) are never covered by a neighboring
                            // pinned/locked column header.
                            zIndex: isPinned ? 40 : (editMode ? 41 : 1),
                            willChange: isPinned ? 'transform' : undefined,
                            overflow: columnFitMode === 'fitToView' ? 'hidden' : undefined,
                          }}
                          className={`group ${alignmentClass} ${firstColPadding} font-semibold border-b border-theme-border-primary whitespace-nowrap ${
                            (column.columnDef.meta as any)?.separatorLeftClass || ''
                            } ${(column.columnDef.meta as any)?.dividerClass || ''
                          } ${
                            hasFilter
                              ? 'bg-slate-50 dark:bg-slate-900'
                              : ((column.columnDef.meta as any)?.headerBgClass || 'bg-cmp-table-header')
                          } ${
                            isPinned ? 'text-cmp-table-header-fg' : 'text-cmp-table-header-fg'
                          } ${columnDragClass(column.id)} ${columnResizeEdgeClass(column.id)} ${editMode ? 'border-x border-theme-border-primary' : ''} ${gridlineV ? 'border-r ' : ''}${gridlineH ? 'border-b ' : ''}${anyGridline ? 'border-theme-border-primary tantable-gridline' : ''} ${flashColumnId === column.id ? 'column-copy-flash' : ''}`}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            setContextMenu({ columnId: column.id, x: e.clientX, y: e.clientY, anchor: e.currentTarget });
                          }}
                          onDragOver={(e) => onColDragOverCol(e, column.id)}
                          onDragLeave={(e) => { e.stopPropagation(); setDragOverColumn(null); }}
                          onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onColDropOn(column.id); }}
                        >
                          {header.isPlaceholder ? null : (
                            <div
                              draggable={editMode && !allLockedColumnIds.includes(column.id)}
                              onDragStart={editMode ? () => onColDragStart(column.id) : undefined}
                              onDragEnd={editMode ? onColDragEnd : undefined}
                              className={`flex items-center gap-1 overflow-hidden ${effectiveSuperCompact ? 'px-1 py-0.5' : 'px-0 py-1.5'} ${editMode ? 'pl-3 pr-5 min-h-[2.25rem] cursor-move' : ''}`}
                            >
                              {/* Header Text */}
                              <div
                                className={
                                  !editMode && header.column.getCanSort()
                                    ? 'cursor-pointer select-none hover:text-blue-600 dark:hover:text-blue-400 flex-1'
                                    : 'flex-1'
                                }
                                onClick={editMode ? undefined : (e) => header.column.getToggleSortingHandler()?.(e)}
                              >
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                              </div>
                              {/* Active filter glyph - always visible so hidden filters aren't forgotten */}
                              {activeColumnFilters.some(f => f.id === header.column.id) && (
                                <FunnelIconSolid className="h-3 w-3 flex-shrink-0 text-amber-500 dark:text-amber-400" />
                              )}
                              {/* Debug overlay showing column pixel width - click to set */}
                              {effectiveDebugCellWidth && (
                                <button
                                  className="absolute top-0 right-1 text-[0.5625rem] bg-red-500 text-white px-1 rounded-b opacity-70 z-50 hover:opacity-100"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDebugWidthColumn({ id: column.id, width: header.getSize() });
                                  }}
                                >
                                  {header.getSize()}
                                </button>
                              )}

                              {/* Pin/Unpin button - hidden in edit mode, for locked columns, and when pinning is disabled */}
                              {!editMode && enableColumnPinning && !allLockedColumnIds.includes(column.id) && (
                                <button
                                  onClick={() => toggleCustomPin(column.id)}
                                  className="p-1 hover:bg-theme-bg-tertiary rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                  title={isPinnedLeft ? 'Unpin column' : 'Pin column'}
                                >
                                  {isPinnedLeft ? (
                                    <LockClosedIcon className="h-3 w-3" />
                                  ) : (
                                    <LockOpenIcon className="h-3 w-3" />
                                  )}
                                </button>
                              )}

                              {/* Sort Icon - far right (hidden in edit mode) */}
                              {!editMode && header.column.getCanSort() && (
                                <span
                                  className="text-gray-400 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 flex items-center gap-0.5"
                                  onClick={(e) => header.column.getToggleSortingHandler()?.(e)}
                                >
                                  {/* Multi-sort priority badge - only shown when 2+ columns are sorted */}
                                  {effectiveSorting.length > 1 && header.column.getIsSorted() && (
                                    <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-gray-200 dark:bg-gray-600 text-[0.625rem] font-semibold text-gray-600 dark:text-gray-300">
                                      {effectiveSorting.findIndex(s => s.id === header.column.id) + 1}
                                    </span>
                                  )}
                                  {{
                                    asc: <ChevronUpIcon className="h-4 w-4" />,
                                    desc: <ChevronDownIcon className="h-4 w-4" />,
                                  }[header.column.getIsSorted() as string] ?? (
                                    <ChevronUpDownIcon className="h-4 w-4" />
                                  )}
                                </span>
                              )}

                              {/* Per-column trailing header control (meta.headerTrailing).
                                  Rendered AFTER the pin and sort icons so it lands at the
                                  far right of the header, which is where a column-wide
                                  action belongs — the icons before it act on the column's
                                  presentation, this acts on its data. Opt-in: nothing
                                  renders unless a column sets it.

                                  Columns want this INSTEAD of a function `header`, because
                                  several places (column picker, export, autosize) read the
                                  header only when it is a string and fall back to the
                                  column id otherwise. */}
                              {!editMode && (column.columnDef.meta as any)?.headerTrailing}

                              {/* Edit mode: remove this column (red minus, right end of
                                  the header). Staged adds drop from the pending list; real
                                  columns are hidden. */}
                              {editMode && !allLockedColumnIds.includes(column.id) && (
                                <Tooltip
                                  content={`Delete Column: ${(column.columnDef.meta as any)?.groupLabel ?? (typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id)}`}
                                  placement="top"
                                  offsetY={30}
                                >
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (pendingAddedFields.includes(column.id)) {
                                        setPendingAddedFields(prev => prev.filter(id => id !== column.id));
                                      } else {
                                        // Buffer the removal (hide now). Save is the commit point:
                                        // saveEdit drops any hidden user-added dynamic column from the
                                        // requested set (so the host stops fetching it + re-queries).
                                        // Cancel restores it. So a trash click never re-queries mid-edit.
                                        setColumnVisibility(prev => ({ ...prev, [column.id]: false }));
                                      }
                                    }}
                                    className="ml-auto flex-shrink-0 p-0.5 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </Tooltip>
                              )}
                            </div>
                          )}

                          {/* Column Resize Handle */}
                          {editColumnsActive && (
                            <div
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                header.getResizeHandler()(e);
                              }}
                              onTouchStart={(e) => {
                                e.stopPropagation();
                                header.getResizeHandler()(e);
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                const headerText = typeof header.column.columnDef.header === 'string'
                                  ? header.column.columnDef.header
                                  : header.column.id;
                                handleAutoSizeColumn(header.column.id, headerText);
                              }}
                              onMouseEnter={() => setResizeHoverColId(header.column.id)}
                              onMouseLeave={() => setResizeHoverColId(prev => (prev === header.column.id ? null : prev))}
                              className="absolute top-0 right-0 h-full w-2 cursor-col-resize select-none touch-none z-50 hover:bg-blue-400/40"
                              style={{
                                userSelect: 'none',
                                transform: 'translateX(50%)',
                              }}
                              title="Double-click to auto-size"
                            />
                          )}
                        </th>
                        {addAfterCol && renderAddColCell(column.id, 'header')}
                        </React.Fragment>
                      );
                    })}
                  </tr>
                  {/* Column Filter Row — render once, under the leaf (last) header
                      group only. With grouped/nested headers there are multiple header
                      groups; rendering this per group would duplicate filter inputs for
                      columns that span rows (e.g. a top-level leaf column). */}
                  {showColumnFilters && !editMode && hgIndex === hgArray.length - 1 && (
                    <tr style={{ display: 'flex', width: '100%' }}>
                      {headerGroup.headers.map((header, fIdx) => {
                        const rawFilterValue = header.column.getFilterValue();
                        const numberFilter = isNumberFilterValue(rawFilterValue) ? rawFilterValue : undefined;
                        const filterValue = numberFilter ? undefined : (rawFilterValue as string[] | undefined);
                        const isPinnedLeft = pinnedColumns.includes(header.column.id);
                        const isPinnedRight = pinnedColumnsRight.includes(header.column.id);
                        const isPinned = isPinnedLeft || isPinnedRight;
                        const pinnedLeft = isPinnedLeft ? (pinnedPositions[header.column.id] ?? 0) : undefined;
                        const pinnedRight = isPinnedRight ? (pinnedPositionsRight[header.column.id] ?? 0) : undefined;
                        const hasActiveFilter = !!numberFilter || (filterValue && filterValue.length > 0);
                        const headerFilterType = getEffectiveFilterType(header.column.id);
                        const isNumberMode = headerFilterType === 'number';
                        const isExclusionMode = isExcludeFilterType(headerFilterType);
                        const isListMode = isListFilterType(headerFilterType);
                        const addAfterFilter = canInsertColAfter(header.column.id, headerGroup.headers[fIdx + 1]?.column.id);

                        return (
                          <React.Fragment key={`fwrap-${header.id}`}>
                          <th
                            key={`filter-${header.id}`}
                            style={{
                              display: 'flex',
                              flexGrow: columnFitMode === 'fitToView' || isFixedWidth(header.column) ? 0 : 1,
                              flexShrink: 0,
                              flexBasis: header.getSize(),
                              minWidth: columnFitMode === 'fitToView' ? (header.column.columnDef.minSize ?? 0) : header.getSize(),
                              maxWidth: columnFitMode === 'fitToView' || isFixedWidth(header.column) ? header.getSize() : undefined,
                              position: isPinned ? 'sticky' : 'relative',
                              left: isPinnedLeft ? `${pinnedLeft}px` : undefined,
                              right: isPinnedRight ? `${pinnedRight}px` : undefined,
                              zIndex: isPinned ? 40 : 1,
                              willChange: isPinned ? 'transform' : undefined,
                              overflow: columnFitMode === 'fitToView' ? 'hidden' : undefined,
                            }}
                            className={`${(header.column.columnDef.meta as any)?.dividerClass || ''} border-b border-theme-border-primary ${((header.column.columnDef.meta as any)?.headerBgClass) || 'bg-cmp-table-header'} ${gridlineV ? 'border-r ' : ''}${gridlineH ? 'border-b ' : ''}${anyGridline ? 'tantable-gridline' : ''}`}
                          >
                            {header.column.getCanFilter() && (
                              <div className={`relative ${effectiveSuperCompact ? 'px-1 py-0.5' : 'px-0 py-2'}`}>
                                {getEffectiveFilterType(header.column.id) === 'text' ? (
                                  /* Text Input Filter */
                                  <>
                                    <input
                                      type="text"
                                      value={textFilterInputs[header.column.id] || ''}
                                      onChange={(e) => handleTextFilterChange(header.column.id, e.target.value)}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openFilterDropdown(header.column.id, e.currentTarget);
                                      }}
                                      placeholder="Type to filter..."
                                      className={`w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-theme-bg-primary text-gray-900 dark:text-white ${
                                        textFilterInputs[header.column.id]
                                          ? 'border-blue-500 dark:border-blue-400'
                                          : 'border-theme-border-secondary'
                                      }`}
                                    />
                                    {textFilterInputs[header.column.id] && (
                                      <button
                                        onClick={() => clearTextFilter(header.column.id)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                                        style={{ right: effectiveSuperCompact ? '0.5rem' : '1.25rem' }}
                                      >
                                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                      </button>
                                    )}
                                    {/* Text Filter Dropdown is rendered once at the
                                        component root via renderActiveFilterDropdown() */}
                                  </>
                                ) : (
                                  /* Dropdown Filter (existing code) */
                                  <>
                                    {/* Filter Input */}
                                    <input
                                      type="text"
                                      value={
                                        numberFilter
                                          ? formatNumberFilterSummary(numberFilter)
                                          : hasActiveFilter && filterValue
                                            ? isListMode
                                              ? `list (${filterValue.length})`
                                              : `${filterValue.length} ${isExclusionMode ? 'excluded' : 'selected'}`
                                            : ''
                                      }
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openFilterDropdown(header.column.id, e.currentTarget);
                                      }}
                                      readOnly
                                      placeholder={isListMode ? 'Paste list...' : isNumberMode ? 'Number...' : 'Filter...'}
                                      className={`w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 bg-theme-bg-primary text-gray-900 dark:text-white cursor-pointer ${
                                        hasActiveFilter
                                          ? isExclusionMode
                                            ? 'border-orange-500 dark:border-orange-400 focus:ring-orange-500'
                                            : 'border-blue-500 dark:border-blue-400 focus:ring-blue-500'
                                          : 'border-theme-border-secondary focus:ring-blue-500'
                                      }`}
                                    />

                                    {/* Excel-Style Filter Dropdown is rendered once at the
                                        component root via renderActiveFilterDropdown() */}
                                  </>
                                )}
                              </div>
                            )}
                          </th>
                          {addAfterFilter && renderAddColCell(header.column.id, 'filter')}
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </thead>
            )}
            {/* end !renderCardRow header gate */}
            <tbody
              // In edit mode cells are display-only — done per-cell (so the cell can
              // still initiate a column drag) rather than blocking the whole body.
              className={editMode ? 'select-none' : ''}
              style={disableVirtualization ? {} : {
                display: 'grid',
                height: `${rowVirtualizer.getTotalSize()}px`,
                position: 'relative',
              }}
            >
              {rows.length === 0 ? (
                <tr style={{ display: 'flex' }}>
                  <td
                    colSpan={columns.length}
                    className={`text-center text-gray-500 dark:text-gray-400 ${effectiveSuperCompact ? 'px-1 py-2' : 'px-4 py-8'}`}
                    style={{ width: '100%' }}
                  >
                    {errorMessage && !loading ? (
                      <div className="flex flex-col items-center gap-2">
                        <span className="text-red-600 dark:text-red-400">{errorMessage}</span>
                        {onRetry && (
                          <button
                            type="button"
                            onClick={onRetry}
                            className="text-xs px-3 py-1 rounded font-medium bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            Retry
                          </button>
                        )}
                      </div>
                    ) : loading ? (
                      // Under the Refreshing overlay: a first load with nothing
                      // yet must not read as an empty result.
                      'Loading…'
                    ) : (
                      'No data available'
                    )}
                  </td>
                </tr>
              ) : (
                (disableVirtualization
                  ? displayRows.map((entry, displayIndex) => ({ ...entry, displayIndex, virtualRow: undefined as ReturnType<typeof rowVirtualizer.getVirtualItems>[0] | undefined }))
                  : rowVirtualizer.getVirtualItems().map((virtualRow) => ({ ...displayRows[virtualRow.index], displayIndex: virtualRow.index, virtualRow: virtualRow as ReturnType<typeof rowVirtualizer.getVirtualItems>[0] | undefined }))
                ).map(({ row, index: rowIndex, displayIndex, footer, virtualRow }) => {
                  if (footer) return (
                    <tr key={`${row.id}-footer`} data-index={displayIndex}
                      ref={disableVirtualization ? undefined : rowVirtualizer.measureElement}
                      style={disableVirtualization ? { display: 'flex' } : {
                        display: 'flex', position: 'absolute', top: virtualRow?.start, left: 0, width: '100%',
                      }} className="bg-theme-bg-secondary border-b border-theme-border-primary font-semibold">
                      <td colSpan={row.getVisibleCells().length} className="px-3 py-2 text-sm text-label" style={{ width: '100%' }}>{footer}</td>
                    </tr>
                  );
                  const isGroupRow = row.getIsGrouped();

                  // Card mode: each data row is ONE full-width cell containing the card.
                  // No zebra, border, indicator cell or onRowClick — the card owns its own
                  // chrome. Group rows deliberately fall through to the standard row markup
                  // so group headers/expanders keep working.
                  if (renderCardRow && !isGroupRow) {
                    return (
                      <tr
                        key={row.id}
                        data-index={displayIndex}
                        ref={disableVirtualization ? undefined : rowVirtualizer.measureElement}
                        style={disableVirtualization ? { display: 'flex' } : {
                          display: 'flex',
                          position: 'absolute',
                          top: virtualRow?.start,
                          left: 0,
                          width: '100%',
                        }}
                      >
                        <td className="p-0 border-0" style={{ width: '100%' }}>
                          {renderCardRow(row.original, {
                            visibleColumnIds: cardVisibleColumnIds,
                            visibleColumns: cardVisibleColumns,
                            // flexRender (not a direct call): a function renderer
                            // comes back as an element React then renders — right
                            // for the DOM, unlike the export's text extraction.
                            renderCell: (columnId: string) => {
                              const cell = row.getAllCells().find((c) => c.column.id === columnId);
                              return cell
                                ? flexRender(cell.column.columnDef.cell, cell.getContext())
                                : undefined;
                            },
                          })}
                        </td>
                      </tr>
                    );
                  }

                  // Get custom className for grouped rows
                  let customGroupedRowClassName = '';
                  if (isGroupRow && getGroupedRowClassName) {
                    const groupedCell = row.getVisibleCells().find(cell => cell.getIsGrouped());
                    if (groupedCell) {
                      const groupValue = groupedCell.getValue();
                      const columnId = groupedCell.column.id;
                      customGroupedRowClassName = getGroupedRowClassName(groupValue, columnId, row.subRows.map(r => r.original));
                    }
                  }

                  const shouldApplyZebra = effectiveZebraStriping && !isGroupRow && rowIndex % 2 === 0;
                  const customRowClassName = getRowClassName?.(row.original, { zebra: shouldApplyZebra }) || '';

                  // Sub-row detection (from getSubRows, not grouping)
                  const isSubRow = row.depth > 0;
                  // Row can expand if it has sub-rows OR if renderExpandedContent is provided (for custom expansion)
                  const canExpandSubRows = row.getCanExpand() && !isGroupRow; // Parent row with sub-rows
                  const canExpandContent = !!renderExpandedContent && !isGroupRow && !isSubRow; // Custom expanded content
                  const canExpand = canExpandSubRows || canExpandContent;

                  // Determine if this is the last sub-row among siblings
                  const parentRow = row.getParentRow?.();
                  const isLastSubRow = isSubRow && parentRow
                    ? parentRow.subRows[parentRow.subRows.length - 1]?.id === row.id
                    : false;

                  // Check if this row should show expanded content (custom content, not sub-rows)
                  const hasExpandedContent = renderExpandedContent && canExpandContent && row.getIsExpanded();

                  return (
                    <tr
                      key={row.id}
                      data-index={displayIndex}
                      data-row-index={row.index}
                      title={isGroupRow ? undefined : getRowTitle?.(row.original)}
                      ref={disableVirtualization ? undefined : rowVirtualizer.measureElement}
                      onClick={(e) => {
                        if (isGroupRow) {
                          row.getToggleExpandedHandler()();
                        } else if (canExpand && renderExpandedContent) {
                          // Expansion handled by chevron button only
                        } else {
                          onRowClick?.(row.original, e);
                        }
                      }}
                      style={disableVirtualization ? {
                        display: 'flex',
                        flexWrap: renderExpandedContent ? 'wrap' : undefined,
                      } : {
                        display: 'flex',
                        flexWrap: renderExpandedContent ? 'wrap' : undefined,
                        position: 'absolute',
                        top: virtualRow?.start,
                        left: 0,
                        width: '100%',
                      }}
                      className={`border-b border-theme-border-primary ${
                        isGroupRow ? `font-semibold cursor-pointer ${customGroupedRowClassName || `group-row-depth-${Math.min(row.depth, 3)}`}`
                        : isSubRow ? 'bg-gray-50 dark:bg-gray-800/50'
                        : onRowClick ? 'cursor-pointer' : ''
                      } ${shouldApplyZebra && !customRowClassName.includes('bg-') ? zebraClass : ''} ${customRowClassName}`}
                    >
                      {/* Indicator cell */}
                      {showIndicator && (() => {
                        const indicatorInfo = getIndicatorInfo?.(row.original);
                        const indicatorBgClass = indicatorInfo?.color || (shouldApplyZebra ? zebraClass : 'bg-theme-bg-primary');
                        return indicatorInfo?.label ? (
                          <td
                            className={`p-0 ${indicatorBgClass} ${gridlineV ? 'border-r ' : ''}${gridlineH ? 'border-b ' : ''}${anyGridline ? 'border-theme-border-primary tantable-gridline' : ''}`}
                            style={{ width: '12px', minWidth: '12px', flexShrink: 0 }}
                          >
                            <Tooltip content={indicatorInfo.label} placement="right">
                              <div className="w-full h-full min-h-[1.5rem] cursor-help"></div>
                            </Tooltip>
                          </td>
                        ) : (
                          <td
                            className={`p-0 ${indicatorBgClass} ${gridlineV ? 'border-r ' : ''}${gridlineH ? 'border-b ' : ''}${anyGridline ? 'border-theme-border-primary tantable-gridline' : ''}`}
                            style={{ width: '12px', minWidth: '12px', flexShrink: 0 }}
                          />
                        );
                      })()}
                      {row.getVisibleCells().map((cell, cellIndex, cellsArr) => {
                        const isGroupedCell = cell.getIsGrouped();
                        const isAggregatedCell = cell.getIsAggregated();
                        const isPlaceholderCell = cell.getIsPlaceholder();
                        const isFirstCell = cellIndex === 0;

                        // Sub-row column visibility check
                        const shouldShowSubRowContent = !isSubRow || !subRowColumns || subRowColumns.includes(cell.column.id);
                        const isPinnedLeft = pinnedColumns.includes(cell.column.id);
                        const isPinnedRight = pinnedColumnsRight.includes(cell.column.id);
                        const isPinned = isPinnedLeft || isPinnedRight;
                        const pinnedLeft = isPinnedLeft ? (pinnedPositions[cell.column.id] ?? 0) : undefined;
                        const pinnedRight = isPinnedRight ? (pinnedPositionsRight[cell.column.id] ?? 0) : undefined;
                        const hasFilter = cell.column.getFilterValue() !== undefined && (Array.isArray(cell.column.getFilterValue()) ? (cell.column.getFilterValue() as any[]).length > 0 : true);

                        const cellZebraClass = (!hasFilter && shouldApplyZebra) ? zebraClass : '';
                        // When a consumer passes a row-level bg via getRowClassName,
                        // make the cell transparent so the row tint shows through —
                        // otherwise the cell's default bg-theme-bg-primary paints over it.
                        const hasCustomRowBg = customRowClassName.includes('bg-');
                        // Per-column tint via meta.cellBgClass — applies to non-grouped,
                        // unfiltered cells. meta.cellBgClassZebra (optional) supplies a
                        // deeper shade for the alternating zebra row so tinted columns
                        // still stripe when zebra striping is on.
                        const colTintBg = (cell.column.columnDef.meta as any)?.cellBgClass as string | undefined;
                        const colTintBgZebra = (cell.column.columnDef.meta as any)?.cellBgClassZebra as string | undefined;
                        // For sticky columns, apply zebra striping to maintain consistency
                        // For grouped rows, only apply custom className to the grouped cell itself
                        const cellBgClass = isGroupRow && isGroupedCell && customGroupedRowClassName
                          ? customGroupedRowClassName
                          : isGroupRow
                          ? `group-row-depth-${Math.min(row.depth, 3)}`
                          // A consumer's row tint (getRowClassName) outranks the
                          // filtered-column hint: a marked row must read as marked
                          // in every column, including the one being filtered on.
                          : hasCustomRowBg
                          // Non-pinned cells go transparent so the row tint shows;
                          // pinned/sticky cells must stay OPAQUE (they overlap
                          // scrolling columns), so paint them with the row tint.
                          ? (isPinned ? customRowClassName : 'bg-transparent')
                          : hasFilter
                          ? 'bg-slate-50 dark:bg-slate-900'
                          : colTintBg
                          ? (shouldApplyZebra && colTintBgZebra ? colTintBgZebra : colTintBg)
                          : isPinned
                          ? (cellZebraClass || 'bg-theme-bg-primary')
                          : (cellZebraClass || 'bg-theme-bg-primary');

                        // Bulk drag-edit: read column opt-in meta and compute selection state
                        const cellBulkMeta = (cell.column.columnDef.meta as any)?.bulkEdit as BulkEditMeta | undefined;
                        const isBulkSelected = dragSelect !== null
                          && cell.column.id === dragSelect.columnId
                          && rowIndex >= Math.min(dragSelect.startRowIdx, dragSelect.endRowIdx)
                          && rowIndex <= Math.max(dragSelect.startRowIdx, dragSelect.endRowIdx);

                        // Get alignment from column meta (default to left). The
                        // cell td is display:flex, so include a justify-* class
                        // — text-* alone won't move the shrink-to-fit content
                        // div (this is why centered columns previously sat left
                        // while their footer totals were centered).
                        const cellAlignment = (cell.column.columnDef.meta as any)?.align || 'left';
                        const cellAlignmentClass = cellAlignment === 'center' ? 'text-center justify-center' : cellAlignment === 'right' ? 'text-right justify-end' : 'text-left';

                        // Date proximity highlighting: red = today or past, orange = +1 day, yellow = +2 days.
                        // Handles ISO (YYYY-MM-DD) and MM/DD[/YY] forms, each with an optional time suffix.
                        // MM/DD without a year assumes the current year (matches the backend's date formatting).
                        const cellMeta = cell.column.columnDef.meta as any;
                        let dateHighlightClass = '';
                        if (cellMeta?.highlightDateProximity) {
                          const cellVal = cell.getValue() as string;
                          if (cellVal && cellVal !== '0000-00-00' && cellVal !== '0000-00-00 00:00:00') {
                            const datePart = String(cellVal).trim().split(' ')[0].split('T')[0];
                            let parsed: Date | null = null;
                            if (datePart.includes('-')) {
                              const [y, m, d] = datePart.split('-').map(Number);
                              if (y && m && d) parsed = new Date(y, m - 1, d);
                            } else if (datePart.includes('/')) {
                              const segs = datePart.split('/').map(Number);
                              const [m, d] = segs;
                              if (m && d) {
                                const year = segs.length >= 3 && segs[2]
                                  ? (segs[2] < 100 ? 2000 + segs[2] : segs[2])
                                  : new Date().getFullYear();
                                parsed = new Date(year, m - 1, d);
                              }
                            }
                            if (parsed && !isNaN(parsed.getTime())) {
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              parsed.setHours(0, 0, 0, 0);
                              const diffDays = Math.round((parsed.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                              if (diffDays <= 0) {
                                dateHighlightClass = 'bg-brand-red text-white';
                              } else if (diffDays === 1) {
                                dateHighlightClass = 'bg-brand-orange text-white';
                              } else if (diffDays === 2) {
                                dateHighlightClass = 'bg-brand-yellow text-gray-900';
                              }
                            }
                          }
                        }
                        if (!dateHighlightClass && cellMeta?.highlightValues) {
                          const cellVal = String(cell.getValue() ?? '');
                          if ((cellMeta.highlightValues as string[]).includes(cellVal)) {
                            dateHighlightClass = 'bg-brand-orange text-white';
                          }
                        }
                        // Generic per-cell background: a column can supply meta.cellBgFn(value, row)
                        // returning a bg/text class (or '' for none) — used e.g. for out-of-order
                        // milestone highlighting that depends on other fields in the same row.
                        if (!dateHighlightClass && typeof cellMeta?.cellBgFn === 'function') {
                          const cls = cellMeta.cellBgFn(cell.getValue(), cell.row.original);
                          if (cls) dateHighlightClass = cls;
                        }
                        const finalCellBgClass = dateHighlightClass || cellBgClass;
                        // Highlight a pro cell green while it is collected and the collector panel is open.
                        const isCollectedProCell = proCollectorOpen && cell.column.id === PRO_COLUMN_ID &&
                          collectedPros.includes(String(cell.getValue() ?? '').trim());
                        const addAfterBody = canInsertColAfter(cell.column.id, cellsArr[cellIndex + 1]?.column.id);

                        return (
                          <React.Fragment key={`bwrap-${cell.id}`}>
                          <td
                            key={cell.id}
                            draggable={editMode && !isGroupRow && !allLockedColumnIds.includes(cell.column.id)}
                            onDragStart={editMode ? () => onColDragStart(cell.column.id) : undefined}
                            onDragEnd={editMode ? onColDragEnd : undefined}
                            onDragOver={editMode ? (e) => onColDragOverCol(e, cell.column.id) : undefined}
                            onDrop={editMode ? (e) => { e.preventDefault(); onColDropOn(cell.column.id); } : undefined}
                            onMouseDown={cellBulkMeta ? (e) => {
                              if (e.button !== 2) return;
                              e.preventDefault();
                              setDragSelect({
                                columnId: cell.column.id,
                                bulkEditMeta: cellBulkMeta,
                                startRowIdx: rowIndex,
                                endRowIdx: rowIndex,
                              });
                            } : undefined}
                            onMouseEnter={cellBulkMeta ? () => {
                              const ds = dragSelectRef.current;
                              if (!ds || cell.column.id !== ds.columnId) return;
                              setDragSelect(prev => prev ? { ...prev, endRowIdx: rowIndex } : null);
                            } : undefined}
                            onContextMenu={cellBulkMeta ? (e) => e.preventDefault() : undefined}
                            onClickCapture={cell.column.id === PRO_COLUMN_ID ? (e) => {
                              if (!(e.ctrlKey || e.metaKey)) return; // plain click → normal cell behavior
                              handleProCellModifierClick(e, cell.getValue());
                            } : undefined}
                            style={isGroupRow && !isGroupedCell ? { display: 'none' } : isGroupedCell ? {
                              display: 'flex',
                              flexGrow: 1,
                              flexShrink: 1,
                              flexBasis: 0,
                              minWidth: 0,
                              overflow: 'hidden',
                              userSelect: dragSelect ? 'none' : undefined,
                            } : {
                              display: 'flex',
                              flexGrow: columnFitMode === 'fitToView' || isFixedWidth(cell.column) ? 0 : 1,
                              flexShrink: 0,
                              flexBasis: cell.column.getSize(),
                              minWidth: columnFitMode === 'fitToView' ? (cell.column.columnDef.minSize ?? 0) : cell.column.getSize(),
                              maxWidth: columnFitMode === 'fitToView' || isFixedWidth(cell.column) ? cell.column.getSize() : undefined,
                              position: isPinned ? 'sticky' : 'relative',
                              left: isPinnedLeft ? `${pinnedLeft}px` : undefined,
                              right: isPinnedRight ? `${pinnedRight}px` : undefined,
                              zIndex: isPinned ? 20 : 1,
                              willChange: isPinned ? 'transform' : undefined,
                              overflow: columnFitMode === 'fitToView' ? 'hidden' : undefined,
                              userSelect: dragSelect ? 'none' : undefined,
                            }}
                            className={`${(cell.column.columnDef.meta as any)?.dividerClass || ''} whitespace-nowrap align-top ${isBulkSelected ? 'ring-2 ring-blue-500 ring-inset bg-blue-100/40 dark:bg-blue-900/40' : isCollectedProCell ? 'bg-green-200 dark:bg-green-800/60' : finalCellBgClass} ${cellAlignmentClass} ${
                              isFirstCell && !showIndicator ? 'pl-3' : ''
                            } ${
                              isGroupRow && customGroupedRowClassName ? '' : 'hover:brightness-95 dark:hover:brightness-110 transition-[filter] duration-75'
                            } ${
                              isGroupedCell ? 'font-semibold' : boldColumns.includes(cell.column.id) ? 'font-bold' : ''
                            } ${
                              isGroupRow ? '' : isPinned ? 'text-body' : 'text-body'
                            } ${editMode && !isGroupRow ? 'border-x border-theme-border-primary' : ''} ${gridlineV ? 'border-r ' : ''}${gridlineH ? 'border-b ' : ''}${anyGridline ? 'border-theme-border-primary tantable-gridline' : ''} ${flashColumnId === cell.column.id ? 'column-copy-flash' : ''} ${columnDragClass(cell.column.id)} ${columnResizeEdgeClass(cell.column.id)} ${editMode && !isGroupRow ? '[&_*]:pointer-events-none cursor-move' : ''} ${!isGroupRow && getCellClassName ? getCellClassName(row.original, cell.column.id) : ''}`}
                          >
                            {isGroupedCell ? (
                              <div className={`flex items-center gap-2 overflow-hidden ${effectiveSuperCompact ? 'px-1 py-0.5' : 'px-0 py-3'}`} style={{ paddingLeft: row.depth > 0 ? `${row.depth * 36}px` : undefined }}>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    row.getToggleExpandedHandler()();
                                  }}
                                  className={`p-0.5 rounded flex-shrink-0 ${
                                    customGroupedRowClassName ? '' : 'hover:bg-cmp-table-row-hover'
                                  }`}
                                >
                                  {row.getIsExpanded() ? (
                                    <ChevronDownIcon className="h-4 w-4" />
                                  ) : (
                                    <ChevronRightIcon className="h-4 w-4" />
                                  )}
                                </button>
                                <span className="overflow-hidden">
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                  )}
                                </span>
                                <span className={`font-normal flex-shrink-0 ${
                                  customGroupedRowClassName ? 'text-white' : `group-row-subtext-${Math.min(row.depth, 3)}`
                                }`}>
                                  ({row.subRows.length})
                                </span>
                              </div>
                            ) : isAggregatedCell ? (
                              <div className={`overflow-hidden truncate ${effectiveSuperCompact ? 'px-1 py-0.5' : 'px-0 py-3'} ${getSubRows && canExpand && isFirstCell ? 'flex items-center' : ''}`}>
                                {/* For parent rows with sub-rows, render expand toggle and cell value directly */}
                                {getSubRows && canExpand && isFirstCell && !alwaysExpanded && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      row.getToggleExpandedHandler()();
                                    }}
                                    className="p-0.5 mr-1 rounded hover:bg-theme-bg-tertiary inline-flex items-center"
                                  >
                                    {row.getIsExpanded() ? (
                                      <ChevronDownIcon className="h-4 w-4" />
                                    ) : (
                                      <ChevronRightIcon className="h-4 w-4" />
                                    )}
                                  </button>
                                )}
                                {getSubRows && canExpand ? (
                                  // For parent rows with sub-rows, call cell renderer directly (flexRender returns nothing)
                                  typeof cell.column.columnDef.cell === 'function'
                                    ? cell.column.columnDef.cell(cell.getContext())
                                    : cell.getValue()
                                ) : (
                                  flexRender(
                                    cell.column.columnDef.aggregatedCell ??
                                      cell.column.columnDef.cell,
                                    cell.getContext()
                                  )
                                )}
                              </div>
                            ) : (isPlaceholderCell && !getSubRows) ? null : (
                              <div className={`tantable-cell-body overflow-hidden flex items-center ${effectiveSuperCompact ? 'px-1 py-0.5' : 'px-0 py-3'} ${
                                // multiline cells stretch to the full cell width — the td is a
                                // flex container, so without flex-1 this wrapper hugs its content.
                                // Cells holding a form control (an in-place editor) stretch too, via
                                // the `.tantable-cell-body:has(input, select, textarea)` rule in
                                // index.css: otherwise a w-full input can never track the column
                                // size and stays ~20 characters wide however far the column is
                                // dragged. Every other cell keeps hugging, so nothing else moves.
                                (cell.column.columnDef.meta as any)?.multiline ? 'flex-1 min-w-0' : ''
                              } ${
                                isSubRow && isFirstCell ? `sub-row-tree-connector ${isLastSubRow ? 'sub-row-last' : ''}` : ''
                              }`}>
                                {/* Expand toggle for parent rows with sub-rows */}
                                {canExpand && isFirstCell && !alwaysExpanded && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      row.getToggleExpandedHandler()();
                                    }}
                                    className="p-0.5 mr-1 rounded hover:bg-theme-bg-tertiary inline-flex items-center flex-shrink-0"
                                  >
                                    {row.getIsExpanded() ? (
                                      <ChevronDownIcon className="h-4 w-4" />
                                    ) : (
                                      <ChevronRightIcon className="h-4 w-4" />
                                    )}
                                  </button>
                                )}
                                {/* Cell content - hidden for sub-rows in non-visible columns */}
                                {shouldShowSubRowContent ? (() => {
                                  const meta = cell.column.columnDef.meta as any;
                                  // meta.isDate opts a column into the shared date formatter
                                  // (MM/DD or MM/DD/YY for non-current year, with optional HH:MM).
                                  // Supply a custom `cell` renderer instead if you need bespoke formatting.
                                  let content: React.ReactNode;
                                  if (meta?.isDate) {
                                    const raw = cell.getValue() as string;
                                    // meta.hideMidnightTime: render the time only when one was
                                    // actually recorded. For senttoaccountingdate an exact 00:00
                                    // means "no time" — a row stamped before migration 487, or a
                                    // future-dated stamp meaning "from the start of that day" —
                                    // and printing 00:00 would read as "arrived at midnight".
                                    const showTime = meta.showTime !== false
                                      && !(meta.hideMidnightTime && hasNoClockTime(raw));
                                    content = formatDate(raw, showTime);
                                  } else if (isPlaceholderCell && getSubRows && canExpand) {
                                    // For placeholder cells in parent rows with sub-rows, flexRender
                                    // returns nothing — render directly from the cell value/renderer.
                                    const cellDef = cell.column.columnDef.cell;
                                    content = typeof cellDef === 'function'
                                      ? cellDef(cell.getContext())
                                      : (cell.getValue() as React.ReactNode);
                                  } else {
                                    content = flexRender(cell.column.columnDef.cell, cell.getContext());
                                  }
                                  // meta.multiline opts out of single-line truncation so multi-line
                                  // cell content (e.g. a stacked Bill To list) shows in full and the
                                  // virtualized row grows to fit.
                                  return meta?.multiline ? (
                                    <div className="min-w-0 flex-1 whitespace-normal break-words">{content}</div>
                                  ) : (
                                    <TruncatedCell cellValue={cell.getValue()} disableTooltip={meta?.disableTruncationTooltip}>
                                      {content}
                                    </TruncatedCell>
                                  );
                                })() : null}
                              </div>
                            )}
                          </td>
                          {/* NOT on a grouped row. Every other cell there is
                              display:none (see the style below) while the grouped cell
                              flex-grows to fill the row — so an insert cell left in the
                              flow is the only sibling still claiming width, and N of
                              them (one per non-pinned column, INCLUDING the ones
                              scrolled out of view) shorten the group bar by N *
                              ADD_COL_W. That read as a group header that stops
                              mid-table, and got worse with every column the user
                              added. */}
                          {addAfterBody && !isGroupRow && renderAddColCell(cell.column.id, 'body')}
                          </React.Fragment>
                        );
                      })}
                      {/* Custom expanded content - renders below the row cells */}
                      {hasExpandedContent && (
                        <td
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            width: '100%',
                            flexBasis: '100%',
                          }}
                          className="bg-theme-bg-secondary border-t border-theme-border-primary"
                        >
                          {renderExpandedContent(row.original)}
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
            {(showColumnFooter || aggFooterActive) && (
              <tfoot className="sticky bottom-0 z-30 bg-theme-bg-tertiary border-t-2 border-theme-border-primary">
                {showColumnFooter && (
                <tr style={{ display: 'flex' }} className="font-semibold">
                  {showIndicator && (
                    <td
                      style={{ width: '12px', minWidth: '12px', flexShrink: 0 }}
                      className={`${gridlineV ? 'border-r ' : ''}${anyGridline ? 'border-theme-border-primary tantable-gridline' : ''}`}
                    />
                  )}
                  {table.getVisibleLeafColumns().map((column, colIdx, colsArr) => {
                    const footerDef = column.columnDef.footer;
                    const colAlignment = (column.columnDef.meta as any)?.align || 'left';
                    const alignClass =
                      colAlignment === 'center' ? 'text-center' :
                      colAlignment === 'right' ? 'text-right' : 'text-left';
                    const isPinnedLeft = pinnedColumns.includes(column.id);
                    const isPinnedRight = pinnedColumnsRight.includes(column.id);
                    const isPinned = isPinnedLeft || isPinnedRight;
                    const pinnedLeft = isPinnedLeft ? (pinnedPositions[column.id] ?? 0) : undefined;
                    const pinnedRight = isPinnedRight ? (pinnedPositionsRight[column.id] ?? 0) : undefined;
                    const colTintBg = (column.columnDef.meta as any)?.cellBgClass as string | undefined;
                    const addAfterFooter = canInsertColAfter(column.id, colsArr[colIdx + 1]?.id);
                    return (
                      <React.Fragment key={`ftwrap-${column.id}`}>
                      <td
                        key={column.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          flexGrow: columnFitMode === 'fitToView' ? 0 : 1,
                          flexShrink: 0,
                          flexBasis: column.getSize(),
                          minWidth: columnFitMode === 'fitToView' ? (column.columnDef.minSize ?? 0) : column.getSize(),
                          maxWidth: columnFitMode === 'fitToView' ? column.getSize() : undefined,
                          position: isPinned ? 'sticky' : 'relative',
                          left: isPinnedLeft ? `${pinnedLeft}px` : undefined,
                          right: isPinnedRight ? `${pinnedRight}px` : undefined,
                          zIndex: isPinned ? 31 : undefined,
                        }}
                        className={`${(column.columnDef.meta as any)?.dividerClass || ''} whitespace-nowrap ${alignClass} ${colTintBg ?? 'bg-theme-bg-tertiary'} ${
                          colIdx === 0 && !showIndicator ? 'pl-3' : ''
                        } ${effectiveSuperCompact ? 'px-1 py-1' : 'px-3 py-2'} text-gray-900 dark:text-gray-100 ${
                          gridlineV ? 'border-r ' : ''
                        }${anyGridline ? 'border-theme-border-primary tantable-gridline' : ''}`}
                      >
                        <div className={`w-full ${alignClass}`}>
                          {typeof footerDef === 'function'
                            ? (footerDef as any)({ table, column, header: null })
                            : (footerDef ?? null)}
                        </div>
                      </td>
                      {addAfterFooter && renderAddColCell(column.id, 'footer')}
                      </React.Fragment>
                    );
                  })}
                </tr>
                )}
                {/* Totals bar: user-configured per-column calculations. Sizing,
                    pinning and tint logic mirror the static footer row above. */}
                {aggFooterActive && (
                <tr style={{ display: 'flex' }} className="font-semibold" data-testid="agg-footer-row">
                  {showIndicator && (
                    <td
                      style={{ width: '12px', minWidth: '12px', flexShrink: 0 }}
                      className={`${gridlineV ? 'border-r ' : ''}${anyGridline ? 'border-theme-border-primary tantable-gridline' : ''}`}
                    />
                  )}
                  {table.getVisibleLeafColumns().map((column, colIdx) => {
                    const colAlignment = ((column.columnDef.meta as any)?.align || 'left') as
                      'left' | 'center' | 'right';
                    const alignClass =
                      colAlignment === 'center' ? 'text-center' :
                      colAlignment === 'right' ? 'text-right' : 'text-left';
                    const isPinnedLeft = pinnedColumns.includes(column.id);
                    const isPinnedRight = pinnedColumnsRight.includes(column.id);
                    const isPinned = isPinnedLeft || isPinnedRight;
                    const colTintBg = (column.columnDef.meta as any)?.cellBgClass as string | undefined;
                    const aggType = aggColumnTypes[column.id];
                    const eligible = !SPECIAL_COLUMN_IDS.has(column.id) && aggType !== 'none' && aggType !== undefined;
                    return (
                      <td
                        key={`agg-${column.id}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          flexGrow: columnFitMode === 'fitToView' ? 0 : 1,
                          flexShrink: 0,
                          flexBasis: column.getSize(),
                          minWidth: columnFitMode === 'fitToView' ? (column.columnDef.minSize ?? 0) : column.getSize(),
                          maxWidth: columnFitMode === 'fitToView' ? column.getSize() : undefined,
                          position: isPinned ? 'sticky' : 'relative',
                          left: isPinnedLeft ? `${pinnedPositions[column.id] ?? 0}px` : undefined,
                          right: isPinnedRight ? `${pinnedPositionsRight[column.id] ?? 0}px` : undefined,
                          zIndex: isPinned ? 31 : undefined,
                        }}
                        className={`${(column.columnDef.meta as any)?.dividerClass || ''} whitespace-nowrap ${alignClass} ${colTintBg ?? 'bg-theme-bg-tertiary'} ${
                          colIdx === 0 && !showIndicator ? 'pl-3' : ''
                        } ${effectiveSuperCompact ? 'px-1 py-0.5' : 'px-3 py-1.5'} text-gray-900 dark:text-gray-100 ${
                          gridlineV ? 'border-r ' : ''
                        }${anyGridline ? 'border-theme-border-primary tantable-gridline' : ''}`}
                      >
                        {eligible && (
                          <AggFooterCell
                            columnId={column.id}
                            columnLabel={columnLabelMap.get(column.id) ?? column.id}
                            calc={aggEffectiveCalcs[column.id] ?? null}
                            display={aggResults[column.id] ?? null}
                            align={colAlignment}
                            compact={effectiveSuperCompact}
                            hint={column.id === aggHintColumnId ? aggScopeHint : null}
                            alwaysShowAdd={Object.keys(aggEffectiveCalcs).length === 0}
                            onOpen={(columnId, anchor) => {
                              const rect = anchor.getBoundingClientRect();
                              setAggPicker({ columnId, x: rect.left, y: rect.top });
                            }}
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
                )}
              </tfoot>
            )}
          </table>
          </div>
        </div>
      )}

      {/* Virtualized tables can show host actions without enabling pagination. */}
      {!enablePagination && !collapsed && footerLeftContent && (
        <div ref={footerRef} className="flex flex-wrap items-center gap-3 px-4 py-3 bg-theme-bg-secondary border-t border-theme-border-primary rounded-b-lg flex-shrink-0">
          {footerLeftContent}
        </div>
      )}

      {/* Pagination Footer - only show when pagination is enabled */}
      {enablePagination && !collapsed && !loading && rows.length > 0 && (
        <div ref={footerRef} className="flex items-center justify-between px-4 py-3 bg-theme-bg-secondary border-t border-theme-border-primary rounded-b-lg sm:px-6 flex-shrink-0">
          {paginationStyle === 'fullWidth' ? (
            <>
              {/* Previous Button (Left Side) */}
              <div className="flex w-0 flex-1">
                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className="inline-flex items-center gap-2 text-sm font-medium text-muted hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                  Previous
                </button>
              </div>

              {/* Page Numbers (Center) */}
              <div className="flex items-center gap-1">
                {(() => {
                  const pages: (number | string)[] = [];
                  const maxButtons = isNarrow ? 3 : 5;

                  if (totalPages <= maxButtons) {
                    // Show all pages
                    for (let i = 1; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    // Always show first page
                    pages.push(1);

                    // Show current page neighbors
                    const start = Math.max(2, currentPage - 1);
                    const end = Math.min(totalPages - 1, currentPage + 1);

                    if (start > 2) {
                      pages.push('...');
                    }

                    for (let i = start; i <= end; i++) {
                      pages.push(i);
                    }

                    if (end < totalPages - 1) {
                      pages.push('...');
                    }

                    // Always show last page
                    pages.push(totalPages);
                  }

                  return pages.map((page, idx) => {
                    if (page === '...') {
                      return (
                        <span key={`ellipsis-${idx}`} className="px-2 text-sm text-muted">
                          ...
                        </span>
                      );
                    }

                    const pageNum = page as number;
                    const isActive = pageNum === currentPage;

                    return (
                      <button
                        key={pageNum}
                        onClick={() => table.setPageIndex(pageNum - 1)}
                        className={`px-3 py-1 text-sm font-medium ${
                          isActive
                            ? 'text-blue-600 dark:text-blue-400'
                            : 'text-muted hover:text-gray-700 dark:hover:text-gray-300'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  });
                })()}
              </div>

              {/* Next Button (Right Side) */}
              <div className="flex w-0 flex-1 justify-end">
                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className="inline-flex items-center gap-2 text-sm font-medium text-muted hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </div>
            </>
          ) : (
            <>
              {/* compactWithButtons Layout */}
              {/* Mobile: Simple Previous/Next */}
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className="relative inline-flex items-center rounded-md border border-theme-border-secondary bg-theme-bg-primary px-4 py-2 text-sm font-medium text-label hover:bg-theme-bg-tertiary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className="relative ml-3 inline-flex items-center rounded-md border border-theme-border-secondary bg-theme-bg-primary px-4 py-2 text-sm font-medium text-label hover:bg-theme-bg-tertiary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>

              {/* Desktop: Left content + Right pagination */}
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                {/* Left Side: Custom Content */}
                <div className="flex items-center gap-3">
                  {footerLeftContent}
                </div>

                {/* Right Side: Results text + Pagination */}
                <div className="flex items-center gap-4">
                  {/* Results Text */}
                  <div>
                    <p className="text-sm text-label">
                      Showing{' '}
                      <span className="font-medium">
                        {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}
                      </span>
                      {' '}to{' '}
                      <span className="font-medium">
                        {Math.min(
                          (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                          table.getFilteredRowModel().rows.length
                        )}
                      </span>
                      {' '}of{' '}
                      <span className="font-medium">{table.getFilteredRowModel().rows.length}</span>
                      {' '}results
                    </p>
                  </div>

                  {/* Pagination Controls */}
                  <nav aria-label="Pagination" className="isolate inline-flex -space-x-px rounded-md shadow-sm">
                    {/* Previous Button */}
                    <button
                      onClick={() => table.previousPage()}
                      disabled={!table.getCanPreviousPage()}
                      className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-theme-border-secondary hover:bg-theme-bg-tertiary focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeftIcon className="h-5 w-5" />
                    </button>

                    {/* Page Numbers */}
                    {(() => {
                      const pages: (number | string)[] = [];
                      const maxButtons = 5;

                      if (totalPages <= maxButtons) {
                        for (let i = 1; i <= totalPages; i++) {
                          pages.push(i);
                        }
                      } else {
                        pages.push(1);
                        const start = Math.max(2, currentPage - 1);
                        const end = Math.min(totalPages - 1, currentPage + 1);

                        if (start > 2) pages.push('...');
                        for (let i = start; i <= end; i++) pages.push(i);
                        if (end < totalPages - 1) pages.push('...');
                        pages.push(totalPages);
                      }

                      return pages.map((page, idx) => {
                        if (page === '...') {
                          return (
                            <span
                              key={`ellipsis-${idx}`}
                              className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-label ring-1 ring-inset ring-theme-border-secondary focus:outline-offset-0"
                            >
                              ...
                            </span>
                          );
                        }

                        const pageNum = page as number;
                        const isActive = pageNum === currentPage;

                        return (
                          <button
                            key={pageNum}
                            onClick={() => table.setPageIndex(pageNum - 1)}
                            aria-current={isActive ? 'page' : undefined}
                            className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold focus:z-20 ${
                              isActive
                                ? 'z-10 bg-blue-600 text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                                : 'text-body ring-1 ring-inset ring-theme-border-secondary hover:bg-theme-bg-tertiary focus:outline-offset-0'
                            } ${idx === 0 ? 'hidden md:inline-flex' : ''}`}
                          >
                            {pageNum}
                          </button>
                        );
                      });
                    })()}

                    {/* Next Button */}
                    <button
                      onClick={() => table.nextPage()}
                      disabled={!table.getCanNextPage()}
                      className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-theme-border-secondary hover:bg-theme-bg-tertiary focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRightIcon className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Portal-rendered dropdown menus for narrow mode */}
      {isNarrow && showTabGroupMenu && tabGroupMenuRef.current && createPortal(
        <div
          ref={tabGroupPortalRef}
          className="fixed w-64 bg-theme-bg-elevated border border-theme-border-primary rounded-lg shadow-lg z-[9999]"
          style={{
            top: tabGroupMenuRef.current.getBoundingClientRect().bottom + 8,
            left: Math.min(
              tabGroupMenuRef.current.getBoundingClientRect().right - 256,
              window.innerWidth - 272
            ),
          }}
        >
          <div className="p-3 border-b border-theme-border-primary">
            <h3 className="text-sm font-semibold text-heading">
              Group Data by Field
            </h3>
          </div>
          <div className="max-h-64 overflow-y-auto p-2">
            {selectedTabGroupField && (
              <>
                <button
                  onClick={() => {
                    setSelectedTabGroupField(null);
                    setShowTabGroupMenu(false);
                    onTabGroupingChange?.('');
                  }}
                  className="w-full px-3 py-2 text-sm text-left rounded hover:bg-theme-bg-tertiary text-brand-red dark:text-brand-red font-semibold"
                >
                  Clear Grouping
                </button>
                <div className="border-t border-theme-border-primary my-2" />
              </>
            )}
            {[...table.getAllLeafColumns()]
              .filter(col => {
                const header = typeof col.columnDef.header === 'string' ? col.columnDef.header : col.id;
                return header.toLowerCase() !== 'actions' && col.getIsVisible();
              })
              .sort((a, b) => {
                const aHeader = typeof a.columnDef.header === 'string' ? a.columnDef.header : a.id;
                const bHeader = typeof b.columnDef.header === 'string' ? b.columnDef.header : b.id;
                return aHeader.localeCompare(bHeader);
              })
              .map((column) => {
                const isSelected = selectedTabGroupField === column.id;
                const header = typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id;
                return (
                  <button
                    key={column.id}
                    onClick={() => {
                      setSelectedTabGroupField(column.id);
                      setShowTabGroupMenu(false);
                      onTabGroupingChange?.(column.id);
                    }}
                    className={`w-full px-3 py-2 text-sm text-left rounded hover:bg-theme-bg-tertiary ${
                      isSelected
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-semibold'
                        : 'text-label'
                    }`}
                  >
                    {header}
                    {isSelected && <span className="ml-2 text-xs">✓</span>}
                  </button>
                );
              })}
          </div>
        </div>,
        portalContainer || document.body
      )}

      {/* !renderCardRow: in card mode columnToggleRef anchors the card bar's own
          Columns popover — rendering this portal too would duplicate the picker. */}
      {isNarrow && !renderCardRow && showColumnToggle && columnToggleRef.current && createPortal(
        <div
          ref={columnTogglePortalRef}
          className="fixed w-56 bg-theme-bg-elevated border border-theme-border-primary rounded-lg shadow-lg z-[9999]"
          style={{
            top: columnToggleRef.current.getBoundingClientRect().bottom + 8,
            left: Math.min(
              columnToggleRef.current.getBoundingClientRect().right - 224,
              window.innerWidth - 240
            ),
          }}
        >
          <div className="p-3 border-b border-theme-border-primary">
            <h3 className="text-sm font-semibold text-heading mb-2">
              Toggle Columns
            </h3>
            {(() => {
              // Check if any column has an active filter
              const hasAnyActiveFilter = table.getAllLeafColumns().some(col => {
                const filterValue = col.getFilterValue();
                return filterValue !== undefined &&
                  (Array.isArray(filterValue) ? filterValue.length > 0 : true);
              });

              return (
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      table.getAllLeafColumns().forEach(col => col.toggleVisibility(true));
                      setTimeout(() => {
                        const visibleCols = table.getAllLeafColumns()
                          .filter(col => col.getIsVisible())
                          .map(col => col.id);
                        onVisibleColumnsChange?.(visibleCols);
                      }, 0);
                    }}
                    className="flex-1 px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                  >
                    Check All
                  </button>
                  <Tooltip
                    content={hasAnyActiveFilter ? "Clear all filters first" : ""}
                    placement="bottom"
                  >
                    <button
                      onClick={() => {
                        if (hasAnyActiveFilter) return;
                        // Hide all columns except locked ones
                        table.getAllLeafColumns().forEach(col => {
                          if (!allLockedColumnIds.includes(col.id)) {
                            col.toggleVisibility(false);
                          }
                        });
                        setTimeout(() => {
                          const visibleCols = table.getAllLeafColumns()
                            .filter(col => col.getIsVisible())
                            .map(col => col.id);
                          onVisibleColumnsChange?.(visibleCols);
                        }, 0);
                      }}
                      disabled={hasAnyActiveFilter}
                      className={`flex-1 px-2 py-1 text-xs rounded transition-colors ${
                        hasAnyActiveFilter
                          ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                          : 'bg-gray-600 hover:bg-gray-700 text-white'
                      }`}
                    >
                      Uncheck All
                    </button>
                  </Tooltip>
                </div>
              );
            })()}
          </div>
          {/* Search box for filtering columns */}
          <div className="px-3 pt-2 pb-1">
            <input
              type="text"
              value={columnSearchFilter}
              onChange={(e) => {
                setColumnSearchFilter(e.target.value);
                columnListRef.current?.scrollTo(0, 0);
              }}
              placeholder="Search columns..."
              className="w-full px-2 py-1 text-sm border border-theme-border-secondary rounded bg-theme-bg-primary text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
              autoFocus
            />
          </div>
          <div ref={columnListRef} className="max-h-64 overflow-y-auto p-2">
            {renderColumnPickerOptions()}
          </div>
        </div>,
        portalContainer || document.body
      )}

      {isNarrow && showGroupingMenu && groupingMenuRef.current && createPortal(
        <div
          ref={groupingPortalRef}
          className="fixed w-56 bg-theme-bg-elevated border border-theme-border-primary rounded-lg shadow-lg z-[9999]"
          style={{
            top: groupingMenuRef.current.getBoundingClientRect().bottom + 8,
            left: Math.min(
              groupingMenuRef.current.getBoundingClientRect().right - 224,
              window.innerWidth - 240
            ),
          }}
        >
          <div className="p-3 border-b border-theme-border-primary">
            <h3 className="text-sm font-semibold text-heading">Group By</h3>
          </div>
          <div className="max-h-64 overflow-y-auto p-2">
            {grouping.length > 0 && (
              <>
                <button
                  onClick={() => {
                    setGrouping([]);
                    setShowGroupingMenu(false);
                    onGroupingChange?.([]);
                  }}
                  className="w-full px-3 py-2 text-sm text-left rounded hover:bg-theme-bg-tertiary text-brand-red dark:text-brand-red font-semibold"
                >
                  Clear Grouping
                </button>
                <div className="border-t border-theme-border-primary my-2" />
              </>
            )}
            {validGroupableColumns.map((columnId) => {
              const column = table.getColumn(columnId);
              if (!column) return null;
              const isGrouped = grouping.includes(columnId);
              const header = (column.columnDef.meta as any)?.groupLabel
                ?? (typeof column.columnDef.header === 'string' ? column.columnDef.header : columnId);
              return (
                <button
                  key={columnId}
                  onClick={() => {
                    const newGrouping = isGrouped
                      ? grouping.filter(id => id !== columnId)
                      : [...grouping, columnId];
                    setGrouping(newGrouping);
                    onGroupingChange?.(newGrouping);
                  }}
                  className={`w-full px-3 py-2 text-sm text-left rounded hover:bg-theme-bg-tertiary ${
                    isGrouped
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                      : 'text-label'
                  }`}
                >
                  {header}
                  {isGrouped && <span className="ml-2 text-xs">✓</span>}
                </button>
              );
            })}
          </div>
        </div>,
        portalContainer || document.body
      )}

      {enablePagination && isNarrow && showRowsMenu && rowsMenuRef.current && createPortal(
        <div
          ref={rowsPortalRef}
          className="fixed w-32 bg-theme-bg-elevated border border-theme-border-primary rounded-lg shadow-lg z-[9999]"
          style={{
            top: rowsMenuRef.current.getBoundingClientRect().bottom + 8,
            left: Math.min(
              rowsMenuRef.current.getBoundingClientRect().right - 128,
              window.innerWidth - 144
            ),
          }}
        >
          <div className="p-2">
            {validatedRowsPerPageOptions.map((size) => (
              <button
                key={size}
                onClick={() => {
                  table.setPageSize(size);
                  setShowRowsMenu(false);
                }}
                className={`w-full px-3 py-2 text-sm text-left rounded hover:bg-theme-bg-tertiary ${
                  pagination.pageSize === size && pagination.pageSize !== data.length
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-semibold'
                    : 'text-label'
                }`}
              >
                {size} rows
              </button>
            ))}
            {/* Show All option */}
            {!hideAllRowsOption && (
              <button
                onClick={() => {
                  table.setPageSize(Number.MAX_SAFE_INTEGER);
                  setShowRowsMenu(false);
                }}
                className={`w-full px-3 py-2 text-sm text-left rounded hover:bg-theme-bg-tertiary ${
                  pagination.pageSize >= data.length
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-semibold'
                    : 'text-label'
                }`}
              >
                All rows
              </button>
            )}
          </div>
        </div>,
        portalContainer || document.body
      )}
      {bulkEditModal && (
        <BulkEditModal
          columnId={bulkEditModal.columnId}
          bulkEditMeta={bulkEditModal.bulkEditMeta}
          selectedRows={bulkEditModal.selectedRows}
          onCancel={() => setBulkEditModal(null)}
          onApply={(value) => {
            onBulkEdit?.(bulkEditModal.columnId, bulkEditModal.selectedRows, value);
            setBulkEditModal(null);
          }}
        />
      )}
      {/* Totals bar calc picker */}
      {aggFooterActive && aggPicker && (
        <AggCalcPicker
          columnLabel={columnLabelMap.get(aggPicker.columnId) ?? aggPicker.columnId}
          calcs={calcsForType(aggColumnTypes[aggPicker.columnId] ?? 'text')}
          current={aggEffectiveCalcs[aggPicker.columnId] ?? null}
          anchor={{ x: aggPicker.x, y: aggPicker.y }}
          onPick={(calc) => setAggCalcForColumn(aggPicker.columnId, calc)}
          onClose={() => setAggPicker(null)}
        />
      )}
      {/* Column Context Menu */}
      {contextMenu && (() => {
        const col = table.getColumn(contextMenu.columnId);
        if (!col) return null;
        return (
          <ColumnContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            column={col}
            isPinnedLeft={pinnedColumns.includes(contextMenu.columnId)}
            isPinnedRight={pinnedColumnsRight.includes(contextMenu.columnId)}
            isLocked={allLockedColumnIds.includes(contextMenu.columnId)}
            onCopyColumn={() => {
              handleCopyColumn(contextMenu.columnId, false);
              setContextMenu(null);
            }}
            onCopyAllRows={() => {
              handleCopyColumn(contextMenu.columnId, true);
              setContextMenu(null);
            }}
            onCopyColumnCommaDelimited={() => {
              handleCopyColumn(contextMenu.columnId, false, true);
              setContextMenu(null);
            }}
            onCopyAllRowsCommaDelimited={() => {
              handleCopyColumn(contextMenu.columnId, true, true);
              setContextMenu(null);
            }}
            onFilterByList={col.getCanFilter() ? () => {
              const { columnId, anchor } = contextMenu;
              setColumnFilterType(columnId, 'list');
              // The paste box lives in the filter row, and an effect closes the popover
              // whenever that row is hidden — so a user who reached this from the header
              // menu with filters off would watch it vanish on open.
              setShowColumnFilters(true);
              setContextMenu(null);
              openFilterDropdown(columnId, anchor, true);
            } : undefined}
            onSortAsc={() => {
              col.toggleSorting(false);
              setContextMenu(null);
            }}
            onSortDesc={() => {
              col.toggleSorting(true);
              setContextMenu(null);
            }}
            onTogglePinLeft={() => {
              toggleCustomPin(contextMenu.columnId);
              setContextMenu(null);
            }}
            onTogglePinRight={() => {
              toggleCustomPinRight(contextMenu.columnId);
              setContextMenu(null);
            }}
            isBold={boldColumns.includes(contextMenu.columnId)}
            onToggleBold={() => {
              toggleBoldColumn(contextMenu.columnId);
              setContextMenu(null);
            }}
            onHide={() => {
              // Outside edit mode (realtime) a user-added dynamic column is removed
              // from the request (stops fetch + collapses fan-out). In edit mode the
              // hide is buffered and committed on Save, like the trash button.
              const dbName = getDbFieldName(col.id);
              if (!editMode && additionalFields.includes(dbName)) {
                onAdditionalFieldsChange?.(additionalFields.filter(f => f !== dbName));
              } else {
                col.toggleVisibility(false);
              }
              setContextMenu(null);
            }}
            canEdit={canEditColumn?.(contextMenu.columnId) ?? false}
            isEditing={isColumnEditing?.(contextMenu.columnId) ?? false}
            onToggleEdit={() => {
              onToggleColumnEditing?.(contextMenu.columnId);
              setContextMenu(null);
            }}
            portalContainer={portalContainer}
            allowPin={enableColumnPinning}
          />
        );
      })()}
      {/* Pro # collector panel (Ctrl/Cmd+click on a pronumber cell) */}
      {proCollectorOpen && collectedPros.length > 0 && (
        <ProCollectorPopover
          pros={collectedPros}
          onClear={() => {
            setCollectedPros([]);
            setProCollectorOpen(false);
          }}
          onCopy={() => copyTextToClipboard(collectedPros.join(', '))}
          onOpenV1={() => {
            window.open(
              `${config.LEGACY_CMS_URL}/index_cms.pl?action=savecustomsearch&contlist=${collectedPros.join(',')}&searchtype=pronumber`,
              '_blank',
              'noopener,noreferrer'
            );
          }}
          onClose={() => setProCollectorOpen(false)}
          portalContainer={portalContainer}
        />
      )}
      {/* Debug column width modal */}
      {debugWidthColumn && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-scrim/30" onClick={() => setDebugWidthColumn(null)}>
          <div
            ref={debugWidthDrag.panelRef}
            style={debugWidthDrag.panelStyle}
            className="bg-panel rounded-lg shadow-xl w-64 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader
              title={`Column: ${debugWidthColumn.id}`}
              onClose={() => setDebugWidthColumn(null)}
              dragHandleProps={debugWidthDrag.dragHandleProps}
            />
            <div className="p-4">
            <input
              type="number"
              autoFocus
              defaultValue={debugWidthColumn.width}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const val = parseInt((e.target as HTMLInputElement).value, 10);
                  if (!isNaN(val) && val > 0) {
                    setColumnSizing(prev => ({ ...prev, [debugWidthColumn.id]: val }));
                  }
                  setDebugWidthColumn(null);
                } else if (e.key === 'Escape') {
                  setDebugWidthColumn(null);
                }
              }}
            />
            <div className="text-[0.625rem] text-gray-400 mt-1">Press Enter to apply, Esc to cancel</div>
            </div>
          </div>
        </div>
      )}
      <WarningConfirmModal
        open={showResetConfirm}
        title="Reset table settings?"
        message={<>This clears your filters, sorting, grouping, column sizes, and column layout for <span className="font-semibold text-heading">{cardTitle || 'this table'}</span> back to the defaults. This can't be undone.</>}
        confirmLabel="Reset settings"
        cancelLabel="Cancel"
        onConfirm={() => {
          setShowResetConfirm(false);
          // Leave column edit mode on a reset. The staged-layout baseline is
          // captured on entry, so keeping it would let Cancel / "Reset to
          // Current" undo the reset. Drop the buffer so the freshly-applied
          // defaults become the clean baseline.
          setEditMode(false);
          setPendingAddedFields([]);
          setEditDirty(false);
          editBaselineRef.current = null;
          resetAllSettings();
        }}
        onCancel={() => setShowResetConfirm(false)}
      />
      <WarningConfirmModal
        open={showSaveDefaultConfirm}
        title="Update global defaults?"
        message={<>Saving this will update the global defaults for all users for the <span className="font-semibold text-heading">{cardTitle || 'this'}</span> table. Existing users who have customized this table keep their own settings; new users and anyone who resets will get this layout. Continue?</>}
        confirmLabel="Save defaults"
        cancelLabel="Cancel"
        onConfirm={handleSaveAsDefaults}
        onCancel={() => setShowSaveDefaultConfirm(false)}
      />
      {/* Layout templates. buildTemplateSnapshot is called at save time, so the
          snapshot includes whatever is staged in edit mode right now — including
          staged dynamic-column adds, which ride in its additionalFields. */}
      <SaveTemplateModal
        isOpen={showSaveTemplate}
        onClose={() => setShowSaveTemplate(false)}
        settingsKey={layoutPoolKey}
        buildSnapshot={buildTemplateSnapshot}
      />
      <LoadTemplateModal
        isOpen={showLoadTemplate}
        onClose={() => setShowLoadTemplate(false)}
        settingsKey={layoutPoolKey}
        onLoad={handleLoadTemplate}
        getUnavailableReason={(snapshot) => prepareTemplate(snapshot).reason}
      />
      {/* Manage Views: rename / share / delete. Load View is load-only; this
          is where its rows are edited. Lists the caller's own Views and, for a
          save-search-view holder, the published System Views. */}
      <ManageViewsModal
        isOpen={showManageViews}
        onClose={() => setShowManageViews(false)}
        settingsKey={layoutPoolKey}
      />
      {/* Save As System View. Same snapshot builder as "Save As View" — the two
          differ in who may write them and who sees them, not in shape. */}
      <SaveViewModal
        isOpen={showSaveView}
        onClose={() => setShowSaveView(false)}
        settingsKey={layoutPoolKey}
        buildSnapshot={buildTemplateSnapshot}
      />
      {/* Shared per-column filter dropdown — single instance, works with or without the header */}
      {renderActiveFilterDropdown()}
      {/* Inline insert-column picker */}
      {colInsertPicker && createPortal(
        <div
          ref={colInsertRef}
          style={{ left: Math.min(colInsertPicker.x, window.innerWidth - 300), top: Math.min(colInsertPicker.y, window.innerHeight - 340) }}
          className="fixed z-[9999] w-72 bg-panel border border-rule rounded-lg shadow-2xl"
        >
          <div className="p-2 border-b border-gray-100 dark:border-gray-700">
            <input
              autoFocus
              value={colInsertSearch}
              onChange={(e) => setColInsertSearch(e.target.value)}
              placeholder="Search fields to add…"
              className="w-full text-sm px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-heading"
            />
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {insertableFields.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-400">No more fields to add</div>
            ) : (
              insertableFields.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => insertColumnAfter(f, colInsertPicker.afterId)}
                  className="w-full flex items-center justify-between gap-2 text-left px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                  title={f.inView ? 'Already in view — moves it here' : 'Add this column here'}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">
                      {f.label}
                      {f.expands && <span className="ml-2 text-[0.625rem] text-amber-600">(pros may dupe)</span>}
                    </span>
                    {f.description && (
                      <span className="block truncate text-xs text-muted">{f.description}</span>
                    )}
                  </span>
                  {f.inView && <span className="shrink-0 text-[0.625rem] text-gray-400 italic">in view → move</span>}
                </button>
              ))
            )}
          </div>
        </div>,
        document.body
      )}

      {showEditExitPrompt && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div className="absolute inset-0 bg-scrim/50" onClick={() => setShowEditExitPrompt(false)} />
          <div
            ref={editExitDrag.panelRef}
            style={editExitDrag.panelStyle}
            className="relative bg-panel rounded-lg shadow-xl w-full max-w-sm mx-4 overflow-hidden"
          >
            <ModalHeader
              title="Unsaved column changes"
              onClose={() => setShowEditExitPrompt(false)}
              dragHandleProps={editExitDrag.dragHandleProps}
            />
            <div className="p-6">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              You have unsaved changes to the column layout for{' '}
              <span className="font-semibold text-heading">{cardTitle || 'this table'}</span>. Save them?
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowEditExitPrompt(false)}
                className="px-3 py-2 text-sm font-medium text-label bg-panel border border-gray-300 dark:border-gray-600 rounded-md hover:bg-highlight"
              >
                Keep editing
              </button>
              <button
                onClick={cancelEdit}
                className="px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-panel border border-gray-300 dark:border-gray-600 rounded-md hover:bg-highlight"
              >
                Discard
              </button>
              <button
                onClick={saveEdit}
                className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Save
              </button>
            </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}) as <T>(props: TanTableProps<T>) => React.ReactElement;
